import { Fragment, useState, useEffect, useRef, useMemo, useCallback } from "react";
import { RTR_RUN, RTR_SPORT, RTR_PROFILES, RTR_PROFILE_LABELS, RTR_SPORT_COMPOSANTES, RTR_VERSION, PLYO_JORIS } from "./rtrRules";
import { computeRtrRun, computeRtrSport } from "./rtrEngine";
import { MRSS_THRESHOLD } from "./mrssEngine";
import { createLcaApi } from "./lcaData";
import {
  TRANSITION_KEYS, phaseOfTest, buildMeasureIndex, buildRtrRunMeasurements, buildRtrSportMeasurements,
  computeMrssFromMeasures, RTR_RUN_MAP, RTR_SPORT_MAP, rtrSourceInfo, MRSS_TRIAL_SOURCES, trialsOf,
  valueFromTrials, sportDelayMonths, buildMrssInputs, splitFresh, countGivingWay, buildRtrRunInput, buildRtrSportInput,
  givingWayWindowStart, GIVING_WAY_QUESTION, phaseEntryDate, weeksSince, epicPct, rpeStableFromLog, adherenceFromLog, vsPreopBaseline,
} from "./measureMap";

// ── COLORS ──────────────────────────────────────────────
const navy    = "#0f2040";
const navyL   = "#1a3460";
const orange  = "#f97316";
const white   = "#ffffff";
const bg      = "#f0f4f8";
const textCol = "#1a2f5a";
const muted   = "#64748b";
const success = "#22c55e";
const border  = "#e2e8f0";
const purple  = "#7c3aed";

// ── SUPABASE CONFIG ──────────────────────────────────────
const SUPABASE_URL = "https://riqvrkvhjwxvriayemcb.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJpcXZya3Zoand4dnJpYXllbWNiIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODMxODAzMTksImV4cCI6MjA5ODc1NjMxOX0.FvZh2Wj3ykFpbLGlDhwkAK8Z8b4wzjEpIsfeIqHGIn4";

// Token de session Supabase Auth — rempli après connexion réussie via sbSignIn(),
// vidé à la déconnexion. Tant qu'il est vide, les appels utilisent la clé anon
// (accès public, restreint par les policies RLS).
// Le jeton d'accès expire (1 h par défaut) : il est renouvelé automatiquement avec le
// refresh_token (avant expiration, et sur tout 401). Sans cela, après une heure d'inactivité,
// chaque lecture échoue en « 401 JWT expired » (bug signalé le 24/10/2026).
let sbAccessToken = null;
let sbRefreshToken = null, sbExpiresAt = 0, sbRefreshing = null, sbOnExpired = null;
function sbSetAccessToken(token) {
  sbAccessToken = token;
  if (!token) { sbRefreshToken = null; sbExpiresAt = 0; }
}
function sbSetSession(auth) {
  sbAccessToken = auth?.access_token || null;
  sbRefreshToken = auth?.refresh_token || null;
  sbExpiresAt = auth?.expires_at ? auth.expires_at * 1000 : auth?.expires_in ? Date.now() + auth.expires_in * 1000 : 0;
}
function sbSetOnSessionExpired(fn) { sbOnExpired = fn; }
// Un seul renouvellement à la fois (plusieurs requêtes peuvent expirer ensemble)
function sbRefreshSession() {
  if (!sbRefreshToken) return Promise.resolve(false);
  if (!sbRefreshing) sbRefreshing = (async () => {
    try {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
        method: "POST",
        headers: { "apikey": SUPABASE_ANON_KEY, "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: sbRefreshToken }),
      });
      if (!res.ok) throw new Error(`refresh ${res.status}`);
      sbSetSession(await res.json());
      return true;
    } catch (e) {
      sbSetAccessToken(null);
      if (sbOnExpired) sbOnExpired();
      return false;
    } finally {
      sbRefreshing = null;
    }
  })();
  return sbRefreshing;
}
// fetch authentifié : renouvelle le jeton s'il expire dans moins d'une minute, et réessaie
// une fois après renouvellement si la base répond 401.
async function sbFetch(url, opts = {}) {
  if (sbAccessToken && sbRefreshToken && Date.now() > sbExpiresAt - 60000) await sbRefreshSession();
  const withAuth = () => ({ ...opts, headers: { ...(opts.headers || {}), ...(sbAccessToken ? { Authorization: `Bearer ${sbAccessToken}` } : {}) } });
  let res = await fetch(url, withAuth());
  if (res.status === 401 && sbRefreshToken && await sbRefreshSession()) res = await fetch(url, withAuth());
  return res;
}
function getSbHeaders() {
  return {
    "apikey": SUPABASE_ANON_KEY,
    "Authorization": `Bearer ${sbAccessToken || SUPABASE_ANON_KEY}`,
    "Content-Type": "application/json",
  };
}
// Accès au schéma relationnel des tests (test_sessions / test_results / v_dashboard)
const lcaApi = createLcaApi({ url: SUPABASE_URL, getHeaders: getSbHeaders, fetchImpl: (u, o) => sbFetch(u, o) });

// Connexion réelle via Supabase Auth (email + mot de passe créés dans le dashboard
// Supabase, PAS dans le code). Retourne le token de session en cas de succès.
async function sbSignIn(email, password) {
  const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
    method: "POST",
    headers: { "apikey": SUPABASE_ANON_KEY, "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    let detail = "";
    try { const j = await res.json(); detail = j.error_description || j.msg || j.error || ""; } catch (e) {}
    throw new Error(detail || `Erreur Supabase Auth (${res.status})`);
  }
  return res.json(); // { access_token, refresh_token, expires_in, user, ... }
}

// Convertit une ligne Supabase (snake_case) → objet patient App.js (camelCase).
// Les mesures (scoring, MRSS, RTR, questionnaires) ne vivent PLUS sur la ligne patient :
// elles sont lues dans test_sessions/test_results (src/lcaData.js). Les anciennes colonnes
// JSONB scoring_values/mrss_values/rtr_values/psycho_scores et last_scoring_* ne sont
// plus ni lues ni écrites (vidées le 15/10/2026). La dernière date/phase de scoring et
// la dernière décision viennent de la vue v_dashboard (champs dérivés, jamais persistés).
function sbRowToPatient(row) {
  return {
    id: row.id,
    name: row.name,
    sport: row.sport || "",
    dateOp: row.date_op || "",
    greffon: row.greffon || "",
    cote: row.cote || "",
    phaseId: row.phase_id || 1,
    sessions: row.sessions || 0,
    total: row.total || 0,
    goalShort: row.goal_short || "",
    goalMid: row.goal_mid || "",
    goalLong: row.goal_long || "",
    sessionHistory: row.session_history || [],
    rtrProfile: row.rtr_profile || null,
    authUserId: row.auth_user_id || null,
    patientEmail: row.patient_email || null,
    status: row.status || "active",
  };
}

// Convertit un objet patient App.js (camelCase) → ligne Supabase (snake_case) pour update/insert
function patientToSbRow(p) {
  return {
    name: p.name,
    sport: p.sport || null,
    date_op: p.dateOp || null,
    greffon: p.greffon || null,
    cote: p.cote || null,
    phase_id: p.phaseId || 1,
    sessions: p.sessions || 0,
    total: p.total || 0,
    goal_short: p.goalShort || null,
    goal_mid: p.goalMid || null,
    goal_long: p.goalLong || null,
    session_history: p.sessionHistory || [],
    rtr_profile: p.rtrProfile || null,
    auth_user_id: p.authUserId || null,
    patient_email: p.patientEmail || null,
    status: PATIENT_STATUS[p.status] ? p.status : "active",
  };
}

// Statut de suivi (colonne patients.status, migration add_patient_status du 21/10/2026).
// Soft delete : rien n'est effacé, la fiche et l'historique de tests restent réactivables.
const PATIENT_STATUS = {
  active:   { label:"Actif",    short:"Actifs",    bg:"#dcfce7", color:"#15803d" },
  paused:   { label:"En pause", short:"En pause",  bg:"#fef3c7", color:"#b45309" },
  archived: { label:"Archivé",  short:"Archivés",  bg:"#e2e8f0", color:"#475569" },
};
function patientStatus(p) { return (p && PATIENT_STATUS[p.status]) ? p.status : "active"; }

// Version déployée (commit Vercel) — affichée sous « Patients » pour vérifier qu'un téléphone n'affiche pas une version en cache
const APP_VERSION = (process.env.REACT_APP_VERCEL_GIT_COMMIT_SHA || "local").slice(0, 7);

// Tri / index alphabétique de la liste patients (accents ignorés : « Émile » → E)
const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ#".split("");
function patientLetter(name) {
  const c = (name || "").trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").charAt(0).toUpperCase();
  return c >= "A" && c <= "Z" ? c : "#";
}
function sortPatientsAlpha(list) {
  return [...list].sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr", { sensitivity:"base" }));
}

// Suppression définitive (RPC serveur admin_delete_patient : réservée aux praticiens,
// efface fiche + tests + décisions + dérobements + compte de connexion du patient).
async function sbAdminDeletePatient(id) {
  const res = await sbFetch(`${SUPABASE_URL}/rest/v1/rpc/admin_delete_patient`, {
    method: "POST",
    headers: getSbHeaders(),
    body: JSON.stringify({ p_patient_id: id }),
  });
  if (!res.ok) {
    let detail = "";
    try { const j = await res.json(); detail = j.message || j.hint || ""; } catch (e) {}
    if (res.status === 404) detail = "fonction serveur absente (SQL de suppression pas encore exécuté dans Supabase)";
    throw new Error(detail || `Erreur suppression (${res.status})`);
  }
}

// Champs dérivés de v_dashboard (dernière session de tests + dernière décision)
function dashRowToFields(d) {
  return {
    lastScoringDate: d?.last_session_date || null,
    lastScoringPhase: d?.last_session_phase ?? null,
    lastDecision: d?.last_decision || null,
    lastGate: d?.last_gate || null,
    lastDecisionDate: d?.last_decision_date || null,
  };
}

async function sbFetchPatients() {
  const res = await sbFetch(`${SUPABASE_URL}/rest/v1/patients?select=*&order=created_at.asc`, {
    headers: getSbHeaders(),
  });
  if (!res.ok) throw new Error(`Supabase fetch error: ${res.status}`);
  const rows = await res.json();
  return rows.map(sbRowToPatient);
}

async function sbUpdatePatient(id, patient) {
  const res = await sbFetch(`${SUPABASE_URL}/rest/v1/patients?id=eq.${id}`, {
    method: "PATCH",
    headers: { ...getSbHeaders(), "Prefer": "return=representation" },
    body: JSON.stringify(patientToSbRow(patient)),
  });
  if (!res.ok) throw new Error(`Supabase update error: ${res.status}`);
  const rows = await res.json();
  return rows[0] ? sbRowToPatient(rows[0]) : patient;
}

async function sbInsertPatient(patient) {
  const res = await sbFetch(`${SUPABASE_URL}/rest/v1/patients`, {
    method: "POST",
    headers: { ...getSbHeaders(), "Prefer": "return=representation" },
    body: JSON.stringify(patientToSbRow(patient)),
  });
  if (!res.ok) throw new Error(`Supabase insert error: ${res.status}`);
  const rows = await res.json();
  return sbRowToPatient(rows[0]);
}

// ── SUPABASE AUTH PATIENT ────────────────────────────────
// Crée un compte Supabase Auth pour un patient (email + mot de passe temporaire choisi
// par l'admin). Utilise l'endpoint public /signup avec la clé anon — la session retournée
// est IGNORÉE (on ne touche pas à sbAccessToken, l'admin reste connecté).
// Prérequis Supabase : "Allow new users to sign up" activé, "Confirm email" désactivé.
async function sbSignUpPatient(email, password) {
  const res = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
    method: "POST",
    headers: { "apikey": SUPABASE_ANON_KEY, "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data.msg || data.error_description || data.error || `Erreur création compte (${res.status})`;
    throw new Error(/already|exists|registered/i.test(msg) ? "Un compte existe déjà avec cet email." : msg);
  }
  // Selon la config Supabase, l'id est dans data.user.id ou data.id
  const uid = data.user?.id || data.id;
  if (!uid) throw new Error("Compte créé mais identifiant introuvable. Vérifie que 'Confirm email' est désactivé dans Supabase.");
  return uid;
}

// ── MOT DE PASSE : oubli / changement ────────────────────
// URL publique de l'app (utilisée dans l'email de bienvenue et comme retour du lien de réinitialisation)
// Domaine propre (23/10/2026) : certains Wi-Fi bloquent *.vercel.app (plage IP 216.198.79.x / 64.29.17.x).
// app.paramedsport.be → CNAME cname.vercel-dns.com (plage 76.76.21.x / 66.33.60.x) — ne pas passer au
// CNAME « recommandé » vercel-dns-017.com, qui pointe vers la plage bloquée.
const APP_PUBLIC_URL = "https://app.paramedsport.be";
function appBaseUrl() {
  // Lien de réinitialisation : toujours vers le domaine public, sauf en développement local
  try {
    const o = window.location.origin || "";
    return /localhost|127\.0\.0\.1/.test(o) ? o : APP_PUBLIC_URL;
  } catch (e) { return APP_PUBLIC_URL; }
}

// Envoie l'email Supabase "réinitialiser le mot de passe". Le lien ramène sur l'app avec
// #access_token=…&type=recovery dans l'URL (géré par readRecoveryFromUrl + ResetPasswordScreen).
// ⚠️ Prérequis Supabase : l'URL de l'app doit figurer dans Auth → URL Configuration (Site URL /
// Redirect URLs) et un SMTP personnalisé doit être configuré pour écrire à des adresses hors équipe.
async function sbRequestPasswordReset(email) {
  const redirect = encodeURIComponent(appBaseUrl());
  const res = await fetch(`${SUPABASE_URL}/auth/v1/recover?redirect_to=${redirect}`, {
    method: "POST",
    headers: { "apikey": SUPABASE_ANON_KEY, "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const msg = data.msg || data.error_description || data.error || `Erreur (${res.status})`;
    throw new Error(/rate|limit|seconds/i.test(msg) ? "Trop de demandes — réessaie dans quelques minutes." : msg);
  }
}

// Change le mot de passe du compte connecté (token de session ou token de récupération)
async function sbUpdatePassword(accessToken, password) {
  const res = await sbFetch(`${SUPABASE_URL}/auth/v1/user`, {
    method: "PUT",
    headers: { "apikey": SUPABASE_ANON_KEY, "Authorization": `Bearer ${accessToken}`, "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const msg = data.msg || data.error_description || data.error || `Erreur (${res.status})`;
    if (/different from the old|same/i.test(msg)) throw new Error("Le nouveau mot de passe doit être différent de l'ancien.");
    if (/expired|invalid|jwt/i.test(msg)) throw new Error("Lien ou session expiré(e) — redemande un lien de réinitialisation.");
    throw new Error(msg);
  }
}

// Lit le fragment d'URL renvoyé par le lien de réinitialisation Supabase
function readRecoveryFromUrl() {
  try {
    const h = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));
    if (h.get("type") === "recovery" && h.get("access_token")) return { token: h.get("access_token") };
    if (h.get("error_code") || h.get("error_description")) {
      const expired = /expired/i.test(h.get("error_code") || "") || /expired/i.test(h.get("error_description") || "");
      return { error: expired ? "Ce lien a expiré. Redemande un lien depuis « Mot de passe oublié ? »." : (h.get("error_description") || "Lien invalide.").replace(/\+/g, " ") };
    }
  } catch (e) {}
  return null;
}
function clearUrlHash() {
  try { window.history.replaceState(null, "", window.location.pathname + window.location.search); } catch (e) {}
}

// Email de bienvenue patient (texte brut, utilisé en mailto: / WhatsApp / copie)
// Prénom deviné depuis la fiche : "NOM Prénom" (2 mots) → dernier mot ; sinon premier mot.
// Toujours modifiable dans l'écran d'envoi.
function guessFirstName(name) {
  const w = (name || "").trim().split(/\s+/).filter(Boolean);
  if (w.length === 0) return "";
  return w.length === 2 ? w[1] : w[0];
}
function buildWelcomeMessage({ name, firstName, email, password }) {
  const prenom = (firstName !== undefined ? firstName : guessFirstName(name)).trim();
  const subject = "Bienvenue sur Campus LCA – vos accès personnels";
  const body =
`Bonjour${prenom ? " " + prenom : ""},

Bienvenue sur Campus LCA, l'application qui va vous accompagner tout au long de votre rééducation du ligament croisé.

Vous y retrouverez votre phase de rééducation, vos séances et l'évolution de vos tests, au fil de nos bilans.

Vos accès :
• Application : ${APP_PUBLIC_URL}
• Identifiant : ${email}
• Mot de passe temporaire : ${password}

Lors de votre première connexion, allez dans l'onglet « Profil », puis « Changer mon mot de passe » pour choisir le vôtre. En cas d'oubli, utilisez le lien « Mot de passe oublié ? » sur l'écran de connexion.

Astuce : ajoutez l'application à l'écran d'accueil de votre téléphone pour l'ouvrir en un geste (iPhone : bouton Partager → « Sur l'écran d'accueil » ; Android : menu ⋮ → « Ajouter à l'écran d'accueil »).

Une question ? Répondez simplement à cet email.

À très vite au cabinet,

Jérôme Tubez
Kinésithérapeute du sport – Paramed'Sport`;
  return { subject, body };
}

// Récupère LA ligne patient liée au compte connecté (RLS : un patient ne voit que la sienne)
async function sbFetchOwnPatientRow(uid) {
  const res = await sbFetch(`${SUPABASE_URL}/rest/v1/patients?select=*&auth_user_id=eq.${uid}&limit=1`, {
    headers: getSbHeaders(),
  });
  if (!res.ok) throw new Error(`Supabase fetch error: ${res.status}`);
  const rows = await res.json();
  return rows[0] ? sbRowToPatient(rows[0]) : null;
}

// Vérifie si le compte connecté est praticien (présent dans la table public.admins)
async function sbIsAdmin(uid) {
  const res = await sbFetch(`${SUPABASE_URL}/rest/v1/admins?select=user_id&user_id=eq.${uid}&limit=1`, {
    headers: getSbHeaders(),
  });
  if (!res.ok) return false;
  const rows = await res.json();
  return rows.length > 0;
}


// ── STATIC DATA ─────────────────────────────────────────
// Le compte admin ne vit plus ici — il est créé dans Supabase Auth (Authentication > Users)
// et validé via sbSignIn(). Ne reste ici que le compte de démo patient (ne touche pas la table patients).
const USERS = {
  "patient@campuslca.be": { password: "patient", role: "patient", name: "Thomas" },
};

const PHASES = [
  { id: 0, label: "Phase 0",           sub: "Pré-opératoire & préparation chirurgie" },
  { id: 1, label: "Phase 1 — Bloc 1",  sub: "Extension jambe, activation quadri, lutte AMI" },
  { id: 2, label: "Phase 2 — Bloc 2",  sub: "Renforcement progressif & proprioception" },
  { id: 3, label: "Phase 3 — Bloc 3",  sub: "Force, pliométrie & course rectiligne" },
  { id: 4, label: "Phase 4 — Bloc 4",  sub: "Réathlétisation & changements de direction" },
  { id: 5, label: "Phase 5 — Bloc 5",  sub: "Reprise sport & validation critères RTS" },
];

const ALL_CATS = ["Échauffement","Quadriceps","Ischio-jambiers","Fessiers","Gainage","Proprioception","Pliométrie","Cardio","Changement de direction","Dos","Bras"];

// Types de greffon LCA — référentiel unique réutilisé partout (fiche patient, MRSS, scoring tool)
const GREFFON_OPTS = [
  { v:"DT4",        l:"DT4 (Ischio-jambiers ×4)" },
  { v:"DIDT",       l:"DIDT (Droit interne + Demi-tendineux)" },
  { v:"KJ",         l:"KJ (Tendon rotulien)" },
  { v:"QT",         l:"QT (Tendon quadricipital)" },
  { v:"allogreffe", l:"Allogreffe" },
  { v:"autre",      l:"Autre" },
];
const GREFFON_LABELS = Object.fromEntries(GREFFON_OPTS.map(o => [o.v, o.l]));

const TESTS_LIB = [
  { id:1,  name:"IKDC Subjectif",       cat:"Fonctionnel",     freq:"M2 / M6 / M9 / RTS",  desc:"Évaluation subjective de la fonction du genou. Score /100. Seuil clinique : >65/100 avant RTS.", seuil:">65/100" },
  { id:2,  name:"KOOS",                 cat:"Fonctionnel",     freq:"M3 / M6 / M9",        desc:"5 sous-scores (douleur, symptômes, AVQ, sport, qualité de vie). Référence internationale LCA.", seuil:">70/100 chaque sous-score" },
  { id:3,  name:"ACL-RSI",              cat:"Psychologique",   freq:"M4 / M6 / RTS",       desc:"Return to Sport after Injury scale. Mesure peur, confiance, émotions. Seuil critique : <56% = support psycho renforcé.", seuil:">56%" },
  { id:4,  name:"TSK-11",               cat:"Psychologique",   freq:"M3 / M6",             desc:"Tampa Scale for Kinesiophobia. 11 items, score /44. Seuil : ≥28 = kinésiophobie significative.", seuil:"<28/44" },
  { id:5,  name:"Single Hop Test",      cat:"Pliométrie",      freq:"M5 / M6 / RTS",       desc:"Saut horizontal monopodal distance maximale. LSI = (opéré/sain) × 100. Seuil RTS : LSI >90%.", seuil:"LSI >90%" },
  { id:6,  name:"Triple Hop Test",      cat:"Pliométrie",      freq:"M5 / M6 / RTS",       desc:"3 sauts consécutifs monopodaux. Plus prédictif que le single hop pour la confiance motrice.", seuil:"LSI >90%" },
  { id:7,  name:"6m Timed Hop",         cat:"Pliométrie",      freq:"M5 / M6 / RTS",       desc:"Temps pour parcourir 6m en sauts monopodaux. LSI calculé sur le temps (sain/opéré).", seuil:"LSI >90%" },
  { id:8,  name:"Crossover Hop",        cat:"Pliométrie",      freq:"M6 / RTS",            desc:"3 sauts en zigzag au-dessus d'une ligne. Évalue contrôle frontal et transversal.", seuil:"LSI >90%" },
  { id:9,  name:"LSI global",           cat:"Composite",       freq:"RTS",                 desc:"Limb Symmetry Index composite des 4 hop tests. Critère fort pour décision RTS.", seuil:">90%" },
  { id:10, name:"Force isométrique quadri", cat:"Force",       freq:"M3 / M5 / M6 / RTS",  desc:"Dynamomètre handheld ou isométrique. LSI quadri. Seuil RTS : LSI >90% (idéalement >90% + >80% ischio).", seuil:"LSI >90%" },
  { id:11, name:"Force isométrique ischio",cat:"Force",        freq:"M3 / M5 / M6 / RTS",  desc:"Rapport ischio/quadri (H:Q ratio). Seuil : >60% côté opéré. Fondamental pour prévention re-rupture.", seuil:"H:Q >60%" },
  { id:12, name:"Y-Balance Test",        cat:"Proprioception", freq:"M4 / M6",             desc:"Évalue l'équilibre dynamique en 3 directions. LSI composite >90%. Bonne sensibilité re-rupture.", seuil:"LSI >90%" },
];

const QUESTIONNAIRES_LIB = [
  { id:1,  name:"Bilan initial LCA",          cat:"Intake",        timing:"J0 — avant 1ère séance",  desc:"Antécédents, chirurgie, attentes, niveau sportif pré-blessure. Déclenche le plan personnalisé.",   formUrl:"#", status:"active" },
  { id:2,  name:"Consentement éclairé + RGPD",cat:"Légal",         timing:"J0",                      desc:"Consentement aux soins, traitement des données, conditions du programme Campus LCA.",              formUrl:"#", status:"active" },
  { id:3,  name:"Suivi douleur hebdomadaire",  cat:"Monitoring",    timing:"Chaque semaine",          desc:"EVA douleur, épanchement, sommeil, fatigue, tolérance à l'effort. Alertes automatiques >4/10.",    formUrl:"#", status:"active" },
  { id:4,  name:"Satisfaction Phase 1",        cat:"Satisfaction",  timing:"Fin Phase 1 (~S4)",       desc:"Satisfaction globale, clarté du programme, relation kiné. Permet d'ajuster avant Phase 2.",        formUrl:"#", status:"active" },
  { id:5,  name:"Check-in mensuel",            cat:"Monitoring",    timing:"1x/mois",                 desc:"Progression perçue, confiance, douleur, sommeil, objectifs. Génère un rapport automatique.",       formUrl:"#", status:"active" },
  { id:6,  name:"Questionnaire RTS final",     cat:"RTS",           timing:"M6-9 avant reprise",      desc:"Readiness to Return to Sport. Combine ACL-RSI, confiance, peur, objectifs sportifs post-reprise.", formUrl:"#", status:"active" },
  { id:7,  name:"Satisfaction globale",        cat:"Satisfaction",  timing:"M12",                     desc:"NPS, résultat final perçu, recommandation, témoignage. Sert de base aux futures testimonials.",    formUrl:"#", status:"active" },
];

const MONTH_NAMES = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const DAY_NAMES   = ["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"];

const INIT_EXLIB = [
  { id:1,  name:"Contraction quadri isométrique", cats:["Quadriceps","Échauffement"],   ytId:"dQw4w9WgXcQ", desc:"Allongé, contracter le quadri en poussant le creux poplité vers le sol. Maintien 5s." },
  { id:2,  name:"Élévation jambe tendue",         cats:["Quadriceps"],                  ytId:"dQw4w9WgXcQ", desc:"Jambe opérée à 45° genou tendu. Contrôle en descente." },
  { id:3,  name:"Fentes avant",                   cats:["Quadriceps","Fessiers"],       ytId:"dQw4w9WgXcQ", desc:"Pas en avant, flexion genou 90°. Contrôle strict du valgus." },
  { id:4,  name:"Gainage antérieur",              cats:["Gainage"],                     ytId:"dQw4w9WgXcQ", desc:"Planche avant sur avant-bras. Maintien sans compensation lombaire." },
  { id:5,  name:"Hip thrust",                     cats:["Fessiers","Ischio-jambiers"],  ytId:"dQw4w9WgXcQ", desc:"Épaules sur banc, barbell sur bassin. Squeeze fessiers en haut." },
  { id:6,  name:"Leg press bilatéral",            cats:["Quadriceps","Fessiers"],       ytId:"dQw4w9WgXcQ", desc:"Presse à jambes bilatérale. Amplitude sécurisée selon tolérance." },
  { id:7,  name:"Nordic curl",                    cats:["Ischio-jambiers"],             ytId:"dQw4w9WgXcQ", desc:"Partner aux chevilles. Descente lente et contrôlée, priorité excentrique." },
  { id:8,  name:"Proprioception unipodal",        cats:["Proprioception"],              ytId:"dQw4w9WgXcQ", desc:"Appui unipodal plan stable puis instable. Contrôle valgus, bras le long du corps." },
  { id:9,  name:"Squat isométrique mur",          cats:["Quadriceps","Fessiers"],       ytId:"dQw4w9WgXcQ", desc:"Dos au mur, pieds à 60cm. Descendre à 60° de flexion, maintien isométrique." },
  { id:10, name:"Vélo stationnaire",              cats:["Échauffement","Cardio"],       ytId:"dQw4w9WgXcQ", desc:"Résistance faible, selle haute. Mobilité articulaire + activation cardio." },
];

const INIT_PROGS = [
  { id:1, phaseId:1, name:"Séance A — Activation initiale", dur:35, exos:[
    { exId:10, p:{ series:1, reps:null, charge:null, duree:600, cote:"bilatéral", repos:0 } },
    { exId:1,  p:{ series:3, reps:20,  charge:null, duree:null, cote:"opéré",    repos:60 } },
    { exId:2,  p:{ series:3, reps:15,  charge:null, duree:null, cote:"opéré",    repos:60 } },
    { exId:4,  p:{ series:3, reps:null, charge:null, duree:30, cote:"bilatéral", repos:45 } },
    { exId:8,  p:{ series:3, reps:null, charge:null, duree:30, cote:"opéré",    repos:45 } },
  ]},
  { id:2, phaseId:2, name:"Séance B — Renforcement S6", dur:50, exos:[
    { exId:10, p:{ series:1, reps:null, charge:null, duree:600, cote:"bilatéral", repos:0 } },
    { exId:6,  p:{ series:4, reps:10,  charge:30,  duree:null, cote:"bilatéral", repos:90 } },
    { exId:9,  p:{ series:3, reps:null, charge:null, duree:45, cote:"bilatéral", repos:60 } },
    { exId:5,  p:{ series:3, reps:12,  charge:40,  duree:null, cote:"bilatéral", repos:75 } },
    { exId:7,  p:{ series:3, reps:8,   charge:null, duree:null, cote:"bilatéral", repos:90 } },
    { exId:8,  p:{ series:3, reps:null, charge:null, duree:40, cote:"opéré",    repos:45 } },
  ]},
  { id:3, phaseId:3, name:"Séance C — Pliométrie intro", dur:55, exos:[
    { exId:10, p:{ series:1, reps:null, charge:null, duree:600, cote:"bilatéral", repos:0 } },
    { exId:9,  p:{ series:4, reps:null, charge:null, duree:60, cote:"bilatéral", repos:60 } },
    { exId:3,  p:{ series:4, reps:10,  charge:20,  duree:null, cote:"bilatéral", repos:75 } },
    { exId:5,  p:{ series:4, reps:12,  charge:60,  duree:null, cote:"bilatéral", repos:90 } },
    { exId:7,  p:{ series:3, reps:10,  charge:null, duree:null, cote:"bilatéral", repos:90 } },
    { exId:8,  p:{ series:4, reps:null, charge:null, duree:45, cote:"opéré",    repos:45 } },
  ]},
];

const INIT_SESSIONS = [
  { id:1, progId:2, dateStr:"2025-03-25", status:"todo" },
  { id:2, progId:1, dateStr:"2025-03-22", status:"done" },
  { id:3, progId:2, dateStr:"2025-03-20", status:"done" },
  { id:4, progId:3, dateStr:"2025-03-27", status:"todo" },
  { id:5, progId:1, dateStr:"2025-03-29", status:"todo" },
];

const INIT_PATIENTS = [
  { id:1, name:"Lucas D.",  email:"lucas@campuslca.be",   phaseId:3, semaine:10, greffon:"DT4",  chirurgien:"Dr. Lepiece", dateOp:"2024-10-01", sport:"Football", niveau:"Semi-pro",    sessions:5, total:6,  sessionHistory:[], goalShort:"LSI quad ≥ 90%", goalMid:"Valider hop tests", goalLong:"Retour compétition football — oct 2025" },
  { id:2, name:"Marie L.",  email:"marie@campuslca.be",   phaseId:1, semaine:2,  greffon:"KJ",   chirurgien:"Dr. Netten",  dateOp:"2025-02-15", sport:"Running",  niveau:"Amateur",     sessions:1, total:6,  sessionHistory:[], scoringValues:{}, goalShort:"Extension 0° complète", goalMid:"Course continue 15 min", goalLong:"Reprise running 10km — été 2025" },
  { id:3, name:"Thomas D.", email:"patient@campuslca.be", phaseId:2, semaine:6,  greffon:"DT4",  chirurgien:"Dr. Lepiece", dateOp:"2025-01-10", sport:"Football", niveau:"Compétition", sessions:2, total:4,  sessionHistory:[], scoringValues:{}, goalShort:"LSI quad ≥ 70%", goalMid:"Single hop ≥ 80% LSI", goalLong:"Retour football D3 — oct 2025" },
];

const WEEK_DAYS = ["L","M","M","J","V","S","D"];
const BANNER_H  = 42;

// ── UTILS ────────────────────────────────────────────────
// Mot de passe temporaire lisible (sans caractères ambigus O/0, l/1) pour les accès patients
function genTempPassword() {
  const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  let p = "";
  const arr = new Uint32Array(10);
  (window.crypto || {}).getRandomValues ? window.crypto.getRandomValues(arr) : arr.forEach((_, i) => arr[i] = Math.floor(Math.random()*1e9));
  for (let i = 0; i < 10; i++) p += chars[arr[i] % chars.length];
  return p;
}

function fmtT(s) { return `${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`; }
function fmtMod(p) {
  const pts = [];
  if (p.series) pts.push(`${p.series}×`);
  if (p.reps)   pts.push(`${p.reps} rép.`);
  if (p.duree)  pts.push(fmtT(p.duree));
  if (p.charge) pts.push(`${p.charge} kg`);
  if (p.cote)   pts.push(p.cote);
  return pts.join(" · ") || "—";
}
// test_sessions.phase = phase de départ de la transition évaluée (2 → bilan P2→P3)
function scoringPhaseLabel(phase) {
  const tr = ["preop→p1","p1→p2","p2→p3","p3→p4"][phase];
  return tr ? tr.replace("→", " → ").toUpperCase() : `Phase ${phase}`;
}
const DECISION_COLOR = { GO:"#16a34a", GO_CONDITIONNEL:"#d97706", NO_GO:"#dc2626", STOP:"#dc2626", REGRESSION:"#dc2626" };
function phaseColor(id) { return ["#6366f1","#0ea5e9","#f97316","#22c55e","#f59e0b","#ef4444"][id] || orange; }
function getDaysInMonth(year, month) { return new Date(year, month + 1, 0).getDate(); }
function getFirstDayOfMonth(year, month) { const d = new Date(year, month, 1).getDay(); return d === 0 ? 6 : d - 1; }
function dateStrToDisplay(s) { if (!s) return ""; const [y,m,d] = s.split("-"); return `${d}/${m}/${y}`; }

// ── ICONS ────────────────────────────────────────────────
function Icon({ name, size = 20, color = "currentColor" }) {
  const w = size; const h = size;
  const c = { width:w, height:h, viewBox:"0 0 24 24", fill:"none", stroke:color, strokeWidth:"2", strokeLinecap:"round", strokeLinejoin:"round" };
  const d = {
    home:      <svg {...c}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
    dumbbell:  <svg {...c}><path d="M6 4v16M18 4v16M3 8h3M18 8h3M3 16h3M18 16h3M6 8h12v8H6z"/></svg>,
    calendar:  <svg {...c}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
    user:      <svg {...c}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
    logout:    <svg {...c}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
    play:      <svg width={w} height={h} viewBox="0 0 24 24" fill={color} stroke="none"><polygon points="5 3 19 12 5 21 5 3"/></svg>,
    check:     <svg {...c} strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>,
    arrow:     <svg {...c}><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>,
    arrowL:    <svg {...c}><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>,
    arrowR:    <svg {...c}><polyline points="9 18 15 12 9 6"/></svg>,
    lock:      <svg {...c}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
    mail:      <svg {...c}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
    eye:       <svg {...c}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
    eyeOff:    <svg {...c}><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>,
    trophy:    <svg {...c}><polyline points="8 21 12 21 16 21"/><line x1="12" y1="17" x2="12" y2="21"/><path d="M7 4H3a2 2 0 0 0-2 2v3a5 5 0 0 0 5 5 5 5 0 0 0 5-5V4"/><path d="M17 4h4a2 2 0 0 1 2 2v3a5 5 0 0 1-5 5 5 5 0 0 1-5-5V4"/><rect x="7" y="4" width="10" height="8" rx="2"/></svg>,
    people:    <svg {...c}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    folder:    <svg {...c}><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>,
    doc:       <svg {...c}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
    form:      <svg {...c}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M7 8h10M7 12h10M7 16h6"/></svg>,
    sign:      <svg {...c}><path d="M20 19.5v.5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8.5L18 5.5"/><polyline points="14 2 14 8 20 8"/><path d="M10.42 12.61a2.1 2.1 0 1 1 2.97 2.97L7.95 21 4 22l.99-3.95z"/></svg>,
    warning:   <svg {...c}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
    link:      <svg {...c}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
    plus:      <svg {...c}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
    filter:    <svg {...c}><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>,
    edit:      <svg {...c}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
    timer:     <svg {...c}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    settings:  <svg {...c}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
    list:      <svg {...c}><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>,
    close:     <svg {...c}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
    flag:      <svg {...c}><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>,
    move:      <svg {...c}><polyline points="5 9 2 12 5 15"/><polyline points="9 5 12 2 15 5"/><polyline points="15 19 12 22 9 19"/><polyline points="19 9 22 12 19 15"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="12" y1="2" x2="12" y2="22"/></svg>,
    assign:    <svg {...c}><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/></svg>,
    info:      <svg {...c}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>,
    stat:      <svg {...c}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
    target:    <svg {...c}><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
    chart:     <svg {...c}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  };
  return d[name] || null;
}

// ── SHARED COMPONENTS ────────────────────────────────────
function LogoBadge() {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
      <span style={{ background:orange, color:white, fontWeight:900, fontSize:12, padding:"2px 8px", borderRadius:4 }}>CAMPUS</span>
      <span style={{ color:white, fontWeight:700, fontSize:16 }}>LCA</span>
    </div>
  );
}

function PreviewBanner({ patientName, onBack }) {
  return (
    <div style={{ position:"fixed", top:0, left:"50%", transform:"translateX(-50%)", width:"100%", maxWidth:430, zIndex:999, background:"linear-gradient(135deg,#f97316,#ea580c)", height:BANNER_H, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 16px", boxShadow:"0 4px 16px rgba(249,115,22,0.4)" }}>
      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
        <Icon name="eye" size={15} color={white} />
        <span style={{ color:white, fontSize:12, fontWeight:700 }}>Mode aperçu · {patientName}</span>
      </div>
      <button onClick={onBack} style={{ background:"rgba(255,255,255,0.2)", border:"none", borderRadius:8, padding:"5px 12px", color:white, fontWeight:700, fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", gap:5 }}>
        <Icon name="arrowL" size={13} color={white} />
        Retour admin
      </button>
    </div>
  );
}

// Bouton de déconnexion global — rendu sur TOUS les écrans une fois connecté
// (sauf séance en cours, pour éviter une perte de données accidentelle).
// Demande confirmation avant de déconnecter.
function LogoutFab({ onLogout, top = 12 }) {
  return (
    <button
      onClick={() => { if (window.confirm("Se déconnecter de Campus LCA ?")) onLogout(); }}
      title="Se déconnecter"
      aria-label="Se déconnecter"
      style={{ position:"fixed", top, right:"max(12px, calc(50% - 215px + 12px))", zIndex:150,
        width:34, height:34, borderRadius:"50%", border:"1px solid rgba(255,255,255,0.25)",
        background:"rgba(15,32,64,0.55)", backdropFilter:"blur(4px)",
        display:"flex", alignItems:"center", justifyContent:"center",
        cursor:"pointer", boxShadow:"0 2px 10px rgba(0,0,0,0.25)" }}>
      <Icon name="logout" size={16} color={white} />
    </button>
  );
}

function PatientNav({ active, onNav }) {
  const items = [
    { id:"home", label:"Accueil", icon:"home" },
    { id:"programme", label:"Programme", icon:"dumbbell" },
    { id:"calendrier", label:"Calendrier", icon:"calendar" },
    { id:"documents", label:"Documents", icon:"folder" },
    { id:"profil", label:"Profil", icon:"user" },
  ];
  return (
    <nav style={{ position:"fixed", bottom:0, left:"50%", transform:"translateX(-50%)", width:"100%", maxWidth:430, background:white, borderTop:`1px solid ${border}`, display:"flex", zIndex:100, boxShadow:"0 -4px 20px rgba(0,0,0,0.08)" }}>
      {items.map(it => (
        <button key={it.id} onClick={() => onNav(it.id)} style={{ flex:1, padding:"8px 2px 11px", border:"none", background:"none", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:3, color:active === it.id ? orange : muted }}>
          <Icon name={it.icon} size={19} color={active === it.id ? orange : muted} />
          <span style={{ fontSize:9, fontWeight:active === it.id ? 700 : 400 }}>{it.label}</span>
        </button>
      ))}
    </nav>
  );
}

function AdminNav({ active, onNav }) {
  const items = [
    { id:"dashboard",  label:"Dashboard",  icon:"home" },
    { id:"patients",   label:"Patients",   icon:"people" },
    { id:"programmes", label:"Programmes", icon:"list" },
    { id:"biblio",     label:"Biblio",     icon:"folder" },
    { id:"settings",   label:"Réglages",   icon:"settings" },
  ];
  return (
    <nav style={{ position:"fixed", bottom:0, left:"50%", transform:"translateX(-50%)", width:"100%", maxWidth:430, background:white, borderTop:`1px solid ${border}`, display:"flex", zIndex:100, boxShadow:"0 -4px 20px rgba(0,0,0,0.08)" }}>
      {items.map(it => (
        <button key={it.id} onClick={() => onNav(it.id)} style={{ flex:1, padding:"8px 2px 11px", border:"none", background:"none", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:3, color:active === it.id ? orange : muted }}>
          <Icon name={it.icon} size={18} color={active === it.id ? orange : muted} />
          <span style={{ fontSize:9, fontWeight:active === it.id ? 700 : 400 }}>{it.label}</span>
        </button>
      ))}
    </nav>
  );
}

function PageHeader({ title, sub, extraTop = 0, onBack = null }) {
  const pt = extraTop + 22;
  return (
    <div style={{ background:`linear-gradient(135deg,${navy},${navyL})`, padding:`${pt}px 20px 24px`, position:"relative", overflow:"hidden" }}>
      <div style={{ position:"absolute", top:-30, right:-30, width:120, height:120, borderRadius:"50%", background:"rgba(249,115,22,0.1)" }} />
      {onBack && (
        <button onClick={onBack} style={{ background:"rgba(255,255,255,0.12)", border:"none", borderRadius:8, padding:"5px 10px", color:white, fontSize:12, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:5, marginBottom:12 }}>
          <Icon name="arrowL" size={13} color={white} />
          Retour
        </button>
      )}
      <div style={{ marginBottom:4 }}><LogoBadge /></div>
      <h2 style={{ color:white, fontWeight:800, fontSize:20, margin:"6px 0 2px" }}>{title}</h2>
      {sub && <p style={{ color:"rgba(255,255,255,0.5)", fontSize:12, margin:0 }}>{sub}</p>}
    </div>
  );
}

function ProgressBar({ value, max, color: col = orange }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ background:"#e2e8f0", borderRadius:99, height:5 }}>
      <div style={{ width:`${pct}%`, height:"100%", borderRadius:99, background:col }} />
    </div>
  );
}

function Badge({ label, bg: bgc, color: col }) {
  return <span style={{ background:bgc, color:col, fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:5 }}>{label}</span>;
}

function RPEPicker({ value, onChange, compact = false }) {
  return (
    <div>
      {!compact && (
        <div style={{ marginBottom:8, fontSize:11, color:muted }}>
          Appuyez sur le score RPE ressenti (méthode Foster CR10)
        </div>
      )}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(5, 1fr)", gap:compact ? 4 : 6 }}>
        {RPE_SCALE.map(r => {
          const selected = value === r.value;
          return (
            <button
              key={r.value}
              onClick={() => onChange(r.value)}
              style={{
                padding: compact ? "6px 2px" : "10px 4px",
                borderRadius:10,
                border: selected ? `2px solid ${r.color}` : `1.5px solid ${r.color}40`,
                background: selected ? r.bg : white,
                cursor:"pointer",
                display:"flex",
                flexDirection:"column",
                alignItems:"center",
                gap:2,
                boxShadow: selected ? `0 0 0 2px ${r.color}40` : "none",
                transition:"all 0.15s",
              }}
            >
              <span style={{ fontSize: compact ? 16 : 20, fontWeight:800, color:r.color, lineHeight:1 }}>{r.value}</span>
              {!compact && <span style={{ fontSize:8, color:r.color, textAlign:"center", lineHeight:1.2 }}>{r.label}</span>}
            </button>
          );
        })}
      </div>
      {value > 0 && (
        <div style={{ marginTop:8, padding:"6px 10px", borderRadius:8, background:RPE_SCALE[value-1].bg, display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:18, fontWeight:800, color:RPE_SCALE[value-1].color }}>{value}/10</span>
          <span style={{ fontSize:12, color:RPE_SCALE[value-1].color, fontWeight:600 }}>{RPE_SCALE[value-1].label}</span>
        </div>
      )}
    </div>
  );
}


// ── SESSION LOAD ADMIN v3 ────────────────────────────────────────────────

// ── SESSION LOAD ADMIN v3 ────────────────────────────────────────────────

const RPE_CFG = [
  {v:1,  label:"Repos",                  short:"1",  col:"#16a34a", bg:"#f0fdf4"},
  {v:2,  label:"Très facile",            short:"2",  col:"#22c55e", bg:"#f0fdf4"},
  {v:3,  label:"Facile",                 short:"3",  col:"#86efac", bg:"#dcfce7"},
  {v:4,  label:"Plutôt facile",          short:"4",  col:"#bef264", bg:"#f7fee7"},
  {v:5,  label:"Modéré",                 short:"5",  col:"#ca8a04", bg:"#fefce8"},
  {v:6,  label:"Difficile",              short:"6",  col:"#d97706", bg:"#fffbeb"},
  {v:7,  label:"Assez difficile",        short:"7",  col:"#ea580c", bg:"#fff7ed"},
  {v:8,  label:"Très difficile",         short:"8",  col:"#ef4444", bg:"#fef2f2"},
  {v:9,  label:"Extrêmement difficile",  short:"9",  col:"#dc2626", bg:"#fef2f2"},
  {v:10, label:"Maximum absolu",         short:"10", col:"#991b1b", bg:"#fff1f2"},
];

function RPEButtons({ value, onChange }) {
  return (
    <div>
      <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:6 }}>
        {RPE_CFG.map(r => (
          <button key={r.v} onClick={() => onChange(r.v)}
            style={{ width:44, height:44, borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:14,
              border: value===r.v ? `2px solid ${r.col}` : `1px solid ${border}`,
              background: value===r.v ? r.bg : white, color: value===r.v ? r.col : muted,
              display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
              transition:"all 0.15s" }}>
            {r.v}
          </button>
        ))}
      </div>
      {value > 0 && (
        <div style={{ fontSize:11, color: RPE_CFG[value-1].col, fontWeight:600 }}>
          RPE {value}/10 — {RPE_CFG[value-1].label}
        </div>
      )}
    </div>
  );
}

function LoadChartSVG({ weeklyData }) {
  const W = 320, H = 130, padL = 34, padB = 22, padR = 10, padT = 12;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;
  const maxUA = Math.max(...weeklyData.map(w => w.ua), 500);
  const n = weeklyData.length;
  const barW = Math.min(42, (chartW / n) * 0.55);
  const slotW = chartW / n;

  const barColor = (ua) => ua > 2000 ? "#ef4444" : ua > 1400 ? "#f59e0b" : "#22c55e";

  // Points for ACWR line (week 4 only has meaningful ACWR)
  const linePoints = weeklyData
    .map((w, i) => {
      if (!w.acwr) return null;
      const x = padL + i * slotW + slotW / 2;
      const y = padT + chartH - (Math.min(w.acwr, 2) / 2) * chartH;
      return `${x},${y}`;
    })
    .filter(Boolean)
    .join(" ");

  // Y-axis ticks for UA
  const yTicks = [0, Math.round(maxUA / 2), maxUA];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display:"block" }}>
      {/* Grid lines */}
      {yTicks.map((t, i) => {
        const y = padT + chartH - (t / maxUA) * chartH;
        return (
          <g key={i}>
            <line x1={padL} y1={y} x2={W - padR} y2={y} stroke={border} strokeWidth={0.5} strokeDasharray="3,3" />
            <text x={padL - 3} y={y + 3} textAnchor="end" fontSize={7} fill={muted}>{t}</text>
          </g>
        );
      })}
      {/* Axes */}
      <line x1={padL} y1={padT} x2={padL} y2={padT + chartH} stroke={border} strokeWidth={1} />
      <line x1={padL} y1={padT + chartH} x2={W - padR} y2={padT + chartH} stroke={border} strokeWidth={1} />
      {/* Right axis label */}
      <text x={W - padR + 2} y={padT + chartH - (1.3 / 2) * chartH} fontSize={7} fill="#378ADD">1.3</text>
      <text x={W - padR + 2} y={padT + chartH - (1.5 / 2) * chartH} fontSize={7} fill="#f59e0b">1.5</text>
      {/* Danger zones (ACWR) */}
      <rect x={padL} y={padT + chartH - (1.5/2)*chartH} width={chartW} height={(0.2/2)*chartH} fill="#f59e0b" opacity={0.07} />
      <rect x={padL} y={padT} width={chartW} height={chartH - (1.5/2)*chartH} fill="#ef4444" opacity={0.05} />
      {/* Bars */}
      {weeklyData.map((w, i) => {
        const bh = (w.ua / maxUA) * chartH;
        const x = padL + i * slotW + (slotW - barW) / 2;
        const y = padT + chartH - bh;
        const col = barColor(w.ua);
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={bh} fill={col} rx={3} opacity={0.85} />
            {w.ua > 0 && <text x={x + barW / 2} y={y - 3} textAnchor="middle" fontSize={7} fill={col} fontWeight="700">{w.ua}</text>}
            <text x={padL + i * slotW + slotW / 2} y={H - 5} textAnchor="middle" fontSize={8} fill={muted}>{w.label}</text>
          </g>
        );
      })}
      {/* ACWR line */}
      {linePoints && (
        <polyline points={linePoints} fill="none" stroke="#378ADD" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      )}
      {weeklyData.map((w, i) => {
        if (!w.acwr) return null;
        const x = padL + i * slotW + slotW / 2;
        const y = padT + chartH - (Math.min(w.acwr, 2) / 2) * chartH;
        return <circle key={i} cx={x} cy={y} r={3.5} fill="#378ADD" stroke={white} strokeWidth={1.5} />;
      })}
      {/* ACWR label */}
      <text x={padL + 4} y={padT + 8} fontSize={7} fill="#378ADD">ACWR</text>
    </svg>
  );
}

function SessionLoadAdmin({ patient, onBack, onUpdatePatient }) {
  const sessions = patient.sessionHistory || [];
  const [activeTab, setActiveTab] = useState("seances");
  const [showNewForm, setShowNewForm] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [goals, setGoals] = useState({
    short: patient.goalShort || "",
    mid: patient.goalMid || "",
    long: patient.goalLong || "",
  });
  const [editingGoals, setEditingGoals] = useState(false);
  const [newSession, setNewSession] = useState({
    date: new Date().toISOString().split("T")[0],
    title: "", type: "cabinet", durationMin: "", rpe: 0,
    exercises: [{ name:"", series:"", reps:"", charge:"", tempo:"", pain:"0" }],
    remarks: "",
  });

  // Métriques globales
  const metrics = useMemo(() => {
    const now = new Date(); const mspd = 86400000;
    const acute7 = sessions.filter(s => (now - new Date(s.date)) / mspd <= 7).reduce((s, x) => s + (x.load||0), 0);
    const chronic28 = sessions.filter(s => (now - new Date(s.date)) / mspd <= 28).reduce((s, x) => s + (x.load||0), 0);
    const chronic = chronic28 / 4;
    const acwr = chronic > 0 ? acute7 / chronic : 0;
    const pains = sessions.filter(s => (now - new Date(s.date)) / mspd <= 7).flatMap(s => (s.exercises||[]).map(e => Number(e.pain)||0));
    const avgPain = pains.length ? pains.reduce((a,b) => a+b, 0) / pains.length : 0;
    return { acute7, chronic, acwr, avgPain };
  }, [sessions]);

  // Données hebdomadaires pour le graphique (4 dernières semaines)
  const weeklyData = useMemo(() => {
    const now = new Date();
    const weeks = [];
    for (let i = 3; i >= 0; i--) {
      const weekEnd = new Date(now.getTime() - i * 7 * 86400000);
      const weekStart = new Date(weekEnd.getTime() - 7 * 86400000);
      const ws = sessions.filter(s => { const d = new Date(s.date); return d >= weekStart && d < weekEnd; });
      const ua = ws.reduce((sum, s) => sum + (s.load||0), 0);
      weeks.push({ label: `S-${i===0?"0":i}`, ua });
    }
    // ACWR rolling sur les 4 semaines
    weeks.forEach((w, i) => {
      if (i >= 1) {
        const all = weeks.slice(0, i+1);
        const chronic = all.reduce((s,x) => s + x.ua, 0) / all.length;
        w.acwr = chronic > 0 ? +(w.ua / chronic).toFixed(2) : 0;
      }
    });
    return weeks;
  }, [sessions]);

  const acwrColor = metrics.acwr <= 1.3 ? "#1D9E75" : metrics.acwr <= 1.5 ? "#BA7517" : "#ef4444";
  const acwrLabel = metrics.acwr <= 1.3 ? "Zone optimale" : metrics.acwr <= 1.5 ? "Zone de vigilance" : "Zone à risque";

  function addExercise() {
    setNewSession(p => ({ ...p, exercises: [...p.exercises, { name:"", series:"", reps:"", charge:"", tempo:"", pain:"0" }] }));
  }
  function setEx(i, key, val) {
    const exs = [...newSession.exercises];
    exs[i] = { ...exs[i], [key]: val };
    setNewSession(p => ({ ...p, exercises: exs }));
  }
  function removeEx(i) {
    setNewSession(p => ({ ...p, exercises: p.exercises.filter((_,idx) => idx!==i) }));
  }

  function saveSession() {
    if (!newSession.title || !newSession.durationMin || !newSession.rpe) return;
    const load = newSession.rpe * Number(newSession.durationMin);
    const session = {
      id: Date.now(), ...newSession, load,
      date: newSession.date,
    };
    const updated = { ...patient, sessionHistory: [session, ...sessions], goalShort: goals.short, goalMid: goals.mid, goalLong: goals.long };
    onUpdatePatient(updated);
    setShowNewForm(false);
    setNewSession({ date: new Date().toISOString().split("T")[0], title:"", type:"cabinet", durationMin:"", rpe:0, exercises:[{ name:"", series:"", reps:"", charge:"", tempo:"", pain:"0" }], remarks:"" });
  }

  function saveGoals() {
    onUpdatePatient({ ...patient, goalShort: goals.short, goalMid: goals.mid, goalLong: goals.long });
    setEditingGoals(false);
  }

  const rpeInfo = (rpe) => RPE_CFG[rpe-1] || { col: muted, label: "—" };

  const th = { padding:"8px 10px", fontSize:11, color:muted, fontWeight:600, textAlign:"left", background:bg, borderBottom:`1px solid ${border}` };
  const td = { padding:"8px 10px", fontSize:12, color:textCol, borderBottom:`1px solid ${border}` };

  return (
    <div style={{ fontFamily:"'Segoe UI',sans-serif", paddingBottom:100 }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px", borderBottom:`1px solid ${border}`, background:white, position:"sticky", top:0, zIndex:10 }}>
        <button onClick={onBack} style={{ background:"none", border:"none", cursor:"pointer", fontSize:13, color:muted, padding:0 }}>← Retour</button>
        <span style={{ color:muted }}>›</span>
        <span style={{ fontSize:15, fontWeight:600, color:textCol }}>{patient.name}</span>
        <span style={{ fontSize:12, padding:"3px 10px", borderRadius:99, background:success+"15", color:"#166534" }}>Suivi charge</span>
      </div>

      <div style={{ padding:"16px" }}>
        {/* Métriques */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginBottom:14 }}>
          {[
            { label:"Charge aiguë (7j)", val:`${metrics.acute7} UA`, col:textCol },
            { label:"Charge chronique", val:`${Math.round(metrics.chronic)} UA/sem`, col:textCol },
            { label:"ACWR", val: metrics.acwr > 0 ? metrics.acwr.toFixed(2) : "—", col: acwrColor, sub: acwrLabel },
            { label:"Douleur moy (7j)", val: metrics.avgPain > 0 ? `${metrics.avgPain.toFixed(1)}/10` : "—", col: metrics.avgPain<=2?"#1D9E75":metrics.avgPain<=4?"#BA7517":"#ef4444" },
          ].map((m,i) => (
            <div key={i} style={{ background:bg, borderRadius:10, padding:"10px 12px" }}>
              <div style={{ fontSize:10, color:muted, marginBottom:3, lineHeight:1.2 }}>{m.label}</div>
              <div style={{ fontSize:18, fontWeight:700, color:m.col }}>{m.val}</div>
              {m.sub && <div style={{ fontSize:9, color:m.col }}>{m.sub}</div>}
            </div>
          ))}
        </div>

        {/* Légende ACWR */}
        <div style={{ display:"flex", gap:14, fontSize:10, color:muted, marginBottom:10 }}>
          {[["#1D9E75","Zone verte (≤1.3)"],["#f59e0b","Zone orange (1.3–1.5)"],["#ef4444","Zone rouge (>1.5)"]].map(([c,l])=>(
            <span key={l} style={{ display:"flex", alignItems:"center", gap:4 }}><span style={{ width:8, height:8, borderRadius:2, background:c, display:"inline-block" }} />{l}</span>
          ))}
          <span style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:4 }}><span style={{ width:16, height:2, background:"#378ADD", display:"inline-block" }} />ACWR</span>
        </div>

        {/* Graphique */}
        <div style={{ background:white, border:`1px solid ${border}`, borderRadius:12, padding:"12px", marginBottom:14 }}>
          <div style={{ fontSize:11, color:muted, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:8 }}>Évolution charge hebdomadaire (UA)</div>
          <LoadChartSVG weeklyData={weeklyData} />
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", borderBottom:`1px solid ${border}`, marginBottom:14 }}>
          {["seances","objectifs"].map(t => (
            <button key={t} onClick={() => setActiveTab(t)}
              style={{ fontSize:13, padding:"8px 16px", background:"none", border:"none", cursor:"pointer",
                borderBottom: activeTab===t ? `2px solid ${navy}` : "2px solid transparent",
                color: activeTab===t ? textCol : muted, fontWeight: activeTab===t ? 600 : 400 }}>
              {t==="seances" ? "Séances" : "Objectifs"}
            </button>
          ))}
        </div>

        {/* Tab Séances */}
        {activeTab==="seances" && (
          <div>
            <button onClick={() => setShowNewForm(p=>!p)}
              style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, padding:"8px 16px", borderRadius:8, border:`1px solid ${border}`, background:white, color:textCol, cursor:"pointer", marginBottom:14 }}>
              {showNewForm ? "▲ Annuler" : "+ Nouvelle séance"}
            </button>

            {showNewForm && (
              <div style={{ border:`1.5px solid ${orange}`, borderRadius:12, padding:"14px", marginBottom:14, background:"#fff7ed" }}>
                <div style={{ fontSize:13, fontWeight:600, color:orange, marginBottom:12 }}>Nouvelle séance</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:12 }}>
                  <div>
                    <label style={{ fontSize:11, color:muted, display:"block", marginBottom:4 }}>Date</label>
                    <input type="date" value={newSession.date} onChange={e=>setNewSession(p=>({...p,date:e.target.value}))}
                      style={{ width:"100%", fontSize:13, padding:"6px 8px", borderRadius:6, border:`1px solid ${border}` }} />
                  </div>
                  <div>
                    <label style={{ fontSize:11, color:muted, display:"block", marginBottom:4 }}>Type</label>
                    <select value={newSession.type} onChange={e=>setNewSession(p=>({...p,type:e.target.value}))}
                      style={{ width:"100%", fontSize:13, padding:"6px 8px", borderRadius:6, border:`1px solid ${border}`, background:white }}>
                      <option value="cabinet">Cabinet kiné</option>
                      <option value="domicile">Domicile</option>
                      <option value="terrain">Terrain / club</option>
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom:10 }}>
                  <label style={{ fontSize:11, color:muted, display:"block", marginBottom:4 }}>Titre de la séance</label>
                  <input type="text" value={newSession.title} onChange={e=>setNewSession(p=>({...p,title:e.target.value}))}
                    placeholder="Ex: Hypertrophie quadriceps"
                    style={{ width:"100%", fontSize:13, padding:"6px 8px", borderRadius:6, border:`1px solid ${border}` }} />
                </div>
                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:11, color:muted, display:"block", marginBottom:4 }}>Durée (minutes)</label>
                  <input type="number" value={newSession.durationMin} onChange={e=>setNewSession(p=>({...p,durationMin:e.target.value}))}
                    placeholder="55" min="5" max="180"
                    style={{ width:100, fontSize:13, padding:"6px 8px", borderRadius:6, border:`1px solid ${border}` }} />
                </div>

                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:11, color:muted, display:"block", marginBottom:6 }}>RPE global (Foster CR10)</label>
                  <RPEButtons value={newSession.rpe} onChange={rpe => setNewSession(p=>({...p, rpe}))} />
                </div>

                {newSession.rpe > 0 && newSession.durationMin && (
                  <div style={{ background:orange+"15", border:`1px solid ${orange}`, borderRadius:8, padding:"8px 12px", marginBottom:12, fontSize:12, color:orange, fontWeight:600 }}>
                    Charge estimée : {newSession.rpe} × {newSession.durationMin} = <strong>{newSession.rpe * Number(newSession.durationMin)} UA</strong>
                  </div>
                )}

                <div style={{ marginBottom:8, fontSize:12, fontWeight:600, color:textCol }}>Exercices</div>
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11, marginBottom:8 }}>
                    <thead>
                      <tr>{["Exercice","Séries","Reps","Charge","Tempo","RPE ex.","Douleur /10"].map(h => <th key={h} style={th}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {newSession.exercises.map((ex, i) => (
                        <tr key={i}>
                          {["name","series","reps","charge","tempo"].map(k => (
                            <td key={k} style={td}>
                              <input type="text" value={ex[k]||""} onChange={e=>setEx(i,k,e.target.value)}
                                style={{ width:"100%", fontSize:11, padding:"3px 4px", border:`1px solid ${border}`, borderRadius:4 }} />
                            </td>
                          ))}
                          <td style={td}>
                            <input type="number" value={ex.rpe||""} onChange={e=>setEx(i,"rpe",e.target.value)} min="0" max="10"
                              style={{ width:40, fontSize:11, padding:"3px 4px", border:`1px solid ${border}`, borderRadius:4 }} />
                          </td>
                          <td style={td}>
                            <input type="number" value={ex.pain||""} onChange={e=>setEx(i,"pain",e.target.value)} min="0" max="10"
                              style={{ width:40, fontSize:11, padding:"3px 4px", border:`1px solid ${border}`, borderRadius:4 }} />
                          </td>
                          <td style={td}>
                            {i>0 && <button onClick={()=>removeEx(i)} style={{ background:"none", border:"none", cursor:"pointer", color:"#ef4444", fontSize:14 }}>×</button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button onClick={addExercise} style={{ fontSize:11, padding:"5px 12px", borderRadius:6, border:`1px solid ${border}`, background:white, cursor:"pointer", marginBottom:10 }}>+ Exercice</button>

                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:11, color:muted, display:"block", marginBottom:4 }}>Remarques</label>
                  <textarea value={newSession.remarks} onChange={e=>setNewSession(p=>({...p,remarks:e.target.value}))}
                    rows={2} style={{ width:"100%", fontSize:12, padding:"6px 8px", borderRadius:6, border:`1px solid ${border}`, resize:"vertical" }} />
                </div>

                <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
                  <button onClick={()=>setShowNewForm(false)} style={{ fontSize:13, padding:"8px 18px", borderRadius:8, border:`1px solid ${border}`, background:"none", cursor:"pointer", color:muted }}>Annuler</button>
                  <button onClick={saveSession}
                    disabled={!newSession.title || !newSession.durationMin || !newSession.rpe}
                    style={{ fontSize:13, padding:"8px 18px", borderRadius:8, cursor:"pointer", fontWeight:600,
                      border:`1px solid ${orange}`, background:orange, color:white,
                      opacity:(!newSession.title||!newSession.durationMin||!newSession.rpe)?0.4:1 }}>
                    Enregistrer
                  </button>
                </div>
              </div>
            )}

            {sessions.length === 0 && !showNewForm && (
              <div style={{ textAlign:"center", padding:"32px", color:muted, fontSize:13 }}>
                Aucune séance enregistrée.<br/>Cliquez sur "+ Nouvelle séance" pour commencer.
              </div>
            )}

            {sessions.map(s => {
              const ri = rpeInfo(s.rpe);
              const isExp = expanded === s.id;
              const typLabel = s.type==="cabinet"?"Cabinet":s.type==="domicile"?"Domicile":"Terrain";
              return (
                <div key={s.id} style={{ border:`1px solid ${border}`, borderRadius:12, marginBottom:10, overflow:"hidden" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 14px", cursor:"pointer" }}
                    onClick={()=>setExpanded(isExp?null:s.id)}>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600, color:textCol }}>{s.title || "Séance"}</div>
                      <div style={{ fontSize:11, color:muted, marginTop:2 }}>
                        {s.date} · {s.durationMin} min · {typLabel}
                        {s.rpe > 0 && <span style={{ color:ri.col, fontWeight:600 }}> · RPE {s.rpe}/10</span>}
                        {s.load > 0 && <span style={{ color:muted }}> · {s.load} UA</span>}
                      </div>
                    </div>
                    <span style={{ fontSize:18, color:muted }}>{isExp ? "▲" : "▼"}</span>
                  </div>
                  {isExp && (
                    <div style={{ padding:"0 14px 12px", borderTop:`1px solid ${border}` }}>
                      {s.exercises && s.exercises.length > 0 && (
                        <div style={{ overflowX:"auto", marginTop:8 }}>
                          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
                            <thead><tr>{["Exercice","Séries","Reps","Charge","Tempo","RPE","Douleur"].map(h=><th key={h} style={th}>{h}</th>)}</tr></thead>
                            <tbody>
                              {s.exercises.map((ex,i)=>(
                                <tr key={i}>
                                  {["name","series","reps","charge","tempo","rpe","pain"].map(k=>(
                                    <td key={k} style={td}>{ex[k]||"—"}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {s.remarks && <div style={{ fontSize:12, color:muted, fontStyle:"italic", marginTop:8 }}>💬 {s.remarks}</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Tab Objectifs */}
        {activeTab==="objectifs" && (
          <div>
            <div style={{ background:bg, borderRadius:12, padding:"14px", marginBottom:12 }}>
              {editingGoals ? (
                <div>
                  {[["Court terme",  "short"],["Moyen terme", "mid"],["Long terme",  "long"]].map(([lbl, key]) => (
                    <div key={key} style={{ marginBottom:10 }}>
                      <label style={{ fontSize:11, color:muted, display:"block", marginBottom:4 }}>{lbl}</label>
                      <input type="text" value={goals[key]} onChange={e => setGoals(p=>({...p,[key]:e.target.value}))}
                        style={{ width:"100%", fontSize:13, padding:"6px 8px", borderRadius:6, border:`1px solid ${border}` }} />
                    </div>
                  ))}
                  <div style={{ display:"flex", gap:8 }}>
                    <button onClick={saveGoals} style={{ fontSize:12, padding:"6px 16px", borderRadius:6, border:`1px solid ${success}`, background:success+"15", color:"#166534", cursor:"pointer", fontWeight:600 }}>Enregistrer</button>
                    <button onClick={()=>setEditingGoals(false)} style={{ fontSize:12, padding:"6px 16px", borderRadius:6, border:`1px solid ${border}`, background:"none", cursor:"pointer", color:muted }}>Annuler</button>
                  </div>
                </div>
              ) : (
                <div>
                  {[["Court terme", patient.goalShort],["Moyen terme", patient.goalMid],["Long terme", patient.goalLong]].map(([lbl, val]) => (
                    <div key={lbl} style={{ display:"flex", gap:12, marginBottom:10, alignItems:"flex-start" }}>
                      <span style={{ color:muted, minWidth:100, fontSize:12, flexShrink:0 }}>{lbl}</span>
                      <span style={{ fontSize:13, color:textCol }}>{val || "—"}</span>
                    </div>
                  ))}
                  <button onClick={() => setEditingGoals(true)} style={{ fontSize:12, padding:"6px 14px", borderRadius:6, border:`1px solid ${border}`, background:white, cursor:"pointer", color:textCol }}>✏️ Modifier les objectifs</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── QUESTIONNAIRE ARRAYS (partagés Scoring ↔ MRSS) ────────────────────

const IKDC_ITEMS = [
  {t:"1. Niveau d'activité max. sans douleur significative", o:[["4","Très intense (pivot/saut)"],["3","Intense (ski, tennis)"],["2","Modérée (course)"],["1","Légère (marche)"],["0","Aucune (douleur)"]]},
  {t:"2. Fréquence des douleurs (4 dern. sem.) — 0=constant → 10=jamais", o:Array.from({length:11},(_,i)=>[String(i),String(i)])},
  {t:"3. Sévérité de la douleur — 0=pire douleur → 10=aucune", o:Array.from({length:11},(_,i)=>[String(i),String(i)])},
  {t:"4. Genou raide / gonflé", o:[["4","Pas du tout"],["3","Légèrement"],["2","Modérément"],["1","Beaucoup"],["0","Extrêmement"]]},
  {t:"5. Activité max. sans gonflement", o:[["4","Très intense"],["3","Intense"],["2","Modérée"],["1","Légère"],["0","Aucune"]]},
  {t:"6. Blocage / accrochage", o:[["1","Non"],["0","Oui"]]},
  {t:"7. Activité max. sans dérobement", o:[["4","Très intense"],["3","Intense"],["2","Modérée"],["1","Légère"],["0","Aucune"]]},
  {t:"8. Activité max. pratiquée régulièrement", o:[["4","Très intense"],["3","Intense"],["2","Modérée"],["1","Légère"],["0","Aucune"]]},
  {t:"9a. Monter les escaliers", o:[["4","Pas du tout difficile"],["3","Minime"],["2","Modérée"],["1","Extrême"],["0","Incapable"]]},
  {t:"9b. Descendre les escaliers", o:[["4","Pas du tout difficile"],["3","Minime"],["2","Modérée"],["1","Extrême"],["0","Incapable"]]},
  {t:"9c. S'agenouiller", o:[["4","Pas du tout difficile"],["3","Minime"],["2","Modérée"],["1","Extrême"],["0","Incapable"]]},
  {t:"9d. S'accroupir", o:[["4","Pas du tout difficile"],["3","Minime"],["2","Modérée"],["1","Extrême"],["0","Incapable"]]},
  {t:"9e. S'asseoir genou fléchi", o:[["4","Pas du tout difficile"],["3","Minime"],["2","Modérée"],["1","Extrême"],["0","Incapable"]]},
  {t:"9f. Se lever d'une chaise", o:[["4","Pas du tout difficile"],["3","Minime"],["2","Modérée"],["1","Extrême"],["0","Incapable"]]},
  {t:"9g. Courir tout droit", o:[["4","Pas du tout difficile"],["3","Minime"],["2","Modérée"],["1","Extrême"],["0","Incapable"]]},
  {t:"9h. Sauter / réception jambe atteinte", o:[["4","Pas du tout difficile"],["3","Minime"],["2","Modérée"],["1","Extrême"],["0","Incapable"]]},
  {t:"9i. Démarrer/s'arrêter vite", o:[["4","Pas du tout difficile"],["3","Minime"],["2","Modérée"],["1","Extrême"],["0","Incapable"]]},
  {t:"10. Fonction actuelle du genou — 0=incapable → 10=normal", o:Array.from({length:11},(_,i)=>[String(i),String(i)])},
];

const RSI_ITEMS = [
  "1. Confiance pour retrouver votre niveau antérieur",
  "2. Probabilité de vous reblesser (0=très probable → 100=pas du tout)",
  "3. Nervosité à l'idée de jouer (0=très nerveux → 100=pas du tout)",
  "4. Confiance que le genou ne lâchera pas",
  "5. Confiance pour jouer sans crainte pour le genou",
  "6. Frustration de devoir penser à votre genou (0=très → 100=pas du tout)",
  "7. Peur de vous reblesser (0=très → 100=pas du tout)",
  "8. Confiance que le genou tiendra sous pression",
  "9. Peur de vous blesser accidentellement (0=très → 100=pas du tout)",
  "10. Pensées de chirurgie/rééduc qui vous freinent (0=tout le temps → 100=jamais)",
  "11. Confiance en votre capacité à bien performer",
  "12. Sensation de détente à l'idée de jouer",
];

const TSK_ITEMS = [
  "1. J'ai peur de me blesser si je fais de l'exercice.",
  "2. Si j'essayais de surmonter, ma douleur augmenterait.",
  "3. Mon corps me dit qu'il y a quelque chose de dangereux.",
  "4. On ne prend pas mon problème médical assez au sérieux.",
  "5. Mon accident a mis mon corps en danger pour la vie.",
  "6. La douleur signifie toujours que je me suis blessé.",
  "7. Être prudent et éviter les mouvements inutiles est le plus sûr.",
  "8. Je n'aurais pas si mal s'il n'y avait pas un danger dans mon corps.",
  "9. La douleur m'indique quand arrêter pour ne pas me blesser.",
  "10. Je ne peux pas faire ce que font les gens normaux, je me blesse trop facilement.",
  "11. Personne ne devrait faire d'exercice en ayant mal.",
];

// KOS-ADLS (Irrgang 1998) — 14 items, 0-5 ; score = Σ ÷ 70 × 100. Traduction de travail,
// à remplacer par la version française validée si Jérôme en dispose.
const KOS_SYMPTOM_OPTS = [["5","Je n'ai pas ce symptôme"],["4","Je l'ai, sans effet sur mes activités"],["3","Il gêne légèrement mes activités"],["2","Il gêne modérément mes activités"],["1","Il gêne fortement mes activités"],["0","Il m'empêche toute activité quotidienne"]];
const KOS_FUNC_OPTS = [["5","Pas difficile"],["4","Un peu difficile"],["3","Assez difficile"],["2","Très difficile"],["1","Extrêmement difficile"],["0","Impossible"]];
const KOS_ITEMS = [
  ...["Douleur", "Raideur", "Gonflement", "Dérobement / instabilité du genou", "Faiblesse", "Boiterie"].map((t, i) => ({ t: `${i + 1}. Symptôme — ${t}`, o: KOS_SYMPTOM_OPTS })),
  ...["Marcher", "Monter les escaliers", "Descendre les escaliers", "Rester debout", "Se mettre à genoux (appui sur l'avant du genou)", "S'accroupir", "S'asseoir genou plié", "Se lever d'une chaise"].map((t, i) => ({ t: `${i + 7}. Activité — ${t}`, o: KOS_FUNC_OPTS })),
];
const sumAnswers = (answers, prefix, n) => {
  const xs = Array.from({ length: n }, (_, i) => parseFloat(answers[`${prefix}${i}`]));
  return { xs, filled: xs.filter(x => !isNaN(x)) };
};
const round1 = x => Math.round(x * 10) / 10;
// Un questionnaire = items + calcul du score + lignes du scoring où le score s'inscrit (`targets`).
// Préfixes d'items identiques à l'ancien bloc du scoring (réponses déjà stockées compatibles).
const CLINICAL_QUESTIONNAIRES = [
  { id: "kos", name: "KOS-ADLS", sub: "Activités quotidiennes — Irrgang 1998 — 14 items (0-5), score = Σ ÷ 70 × 100", prefix: "kos_q", unit: "%",
    items: KOS_ITEMS, score: a => { const { xs } = sumAnswers(a, "kos_q", 14); return xs.every(x => !isNaN(x)) ? round1(xs.reduce((s, x) => s + x, 0) / 70 * 100) : null; },
    targets: [{ tr: "p1→p2", row: "kos_adls", label: "P1→P2 — jalon S2-3 (cible ≥ 65 %)" }] },
  { id: "rsi", name: "ACL-RSI", sub: "Confiance psychologique — Webster 2008 — 12 items (0-100), score = moyenne", prefix: "rsi_q", unit: "/100",
    items: RSI_ITEMS.map(t => ({ t, o: Array.from({ length: 11 }, (_, j) => [String(j * 10), String(j * 10)]) })),
    score: a => { const { xs } = sumAnswers(a, "rsi_q", 12); return xs.every(x => !isNaN(x)) ? round1(xs.reduce((s, x) => s + x, 0) / 12) : null; },
    targets: [{ tr: "p2→p3", row: "aclrsi_p2", label: "P2→P3 (cible ≥ 65)" }, { tr: "p3→p4", row: "aclrsi_p3", label: "P3→P4 — MRSS + RTS" }, { tr: "p4→p5", row: "aclrsi", label: "P4→P5 (cible ≥ 80)" }] },
  { id: "tsk", name: "TSK-11", sub: "Kinésiophobie — Woby 2005 — 11 items (1-4), score = Σ /44 (≤ 18 = acceptable)", prefix: "tsk_q", unit: "/44",
    items: TSK_ITEMS.map(t => ({ t, o: [1, 2, 3, 4].map(n => [String(n), `${n} — ${["", "Pas du tout d'accord", "Plutôt pas d'accord", "Plutôt d'accord", "Tout à fait d'accord"][n]}`]) })),
    score: a => { const { xs } = sumAnswers(a, "tsk_q", 11); return xs.every(x => !isNaN(x)) ? xs.reduce((s, x) => s + x, 0) : null; },
    targets: [{ tr: "p3→p4", row: "tsk11_p3", label: "P3→P4 — MRSS + gates RTR-RUN et RTR-SPORT" }] },
  { id: "ikdc", name: "IKDC subjectif", sub: "Anderson 2006 — 18 items, score = Σ ÷ 87 × 100 (≥ 16 items répondus)", prefix: "ikdc_q", unit: "/100",
    items: IKDC_ITEMS, score: a => { const { filled } = sumAnswers(a, "ikdc_q", IKDC_ITEMS.length); return filled.length >= 16 ? round1(filled.reduce((s, x) => s + x, 0) / 87 * 100) : null; },
    targets: [{ tr: "p2→p3", row: "ikdc_p2", label: "P2→P3 (cible > 64)" }, { tr: "p3→p4", row: "ikdc_p3", label: "P3→P4 — MRSS" }] },
];

// Remplissage d'un questionnaire pour un patient : le score s'écrit dans test_results
// (ligne `${tr}_${row}`, session du patient à la phase de départ de la transition et à la date choisie).
function QuestionnaireFiller({ q, patients = [], onClose }) {
  const realPatients = patients.filter(p => typeof p.id === "string");
  const [pid, setPid] = useState(realPatients[0]?.id || "");
  const patient = realPatients.find(p => p.id === pid);
  const defaultTarget = () => {
    const tr = TRANSITIONS[patient?.phaseId] ;
    return (q.targets.find(t => t.tr === tr) || q.targets[0]).tr;
  };
  const [tr, setTr] = useState(defaultTarget);
  const [date, setDate] = useState(todayISO());
  const [answers, setAnswers] = useState({});
  const [status, setStatus] = useState(null); // null | saving | saved | erreur
  useEffect(() => { setTr(defaultTarget()); }, [pid]);
  const score = q.score(answers);
  const target = q.targets.find(t => t.tr === tr);
  const answered = Object.values(answers).filter(v => v !== "").length;
  async function save() {
    setStatus("saving");
    try {
      const key = `${tr}_${target.row}`;
      const s = await lcaApi.ensureSession(pid, TRANSITIONS.indexOf(tr), date);
      const crit = CRITERIA_BY_KEY[key]?.c;
      await lcaApi.upsertResults([{ session_id: s.id, test_id: key, valeur: { v: score, items: answers }, statut: crit ? (isPass(crit, score) ? "ATTEINT" : "NON_ATTEINT") : null, date_override: null }]);
      setStatus("saved");
    } catch (e) {
      setStatus(`Erreur : ${e.message}`);
    }
  }
  const fld = { width:"100%", padding:"8px 10px", borderRadius:8, border:`1px solid ${border}`, fontSize:13, boxSizing:"border-box", background:white };
  return (
    <div style={{ background:white, borderRadius:13, border:`1.5px solid ${orange}55`, padding:13, marginBottom:12 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
        <div>
          <div style={{ fontWeight:800, color:textCol, fontSize:15 }}>{q.name}</div>
          <div style={{ fontSize:11, color:muted, marginTop:2 }}>{q.sub}</div>
        </div>
        <button onClick={onClose} style={{ background:"none", border:"none", color:muted, fontSize:18, cursor:"pointer" }}>✕</button>
      </div>
      {!realPatients.length ? <div style={{ fontSize:12, color:"#dc2626" }}>Aucun patient enregistré.</div> : (<>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
          <label style={{ fontSize:11, color:muted }}>Patient
            <select value={pid} onChange={e => { setPid(e.target.value); setStatus(null); }} style={fld}>
              {realPatients.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          <label style={{ fontSize:11, color:muted }}>Date du questionnaire
            <input type="date" value={date} onChange={e => { setDate(e.target.value); setStatus(null); }} style={fld} />
          </label>
        </div>
        <label style={{ fontSize:11, color:muted, display:"block", marginBottom:10 }}>Le score s'inscrit dans le scoring
          <select value={tr} onChange={e => { setTr(e.target.value); setStatus(null); }} style={fld}>
            {q.targets.map(t => <option key={t.tr} value={t.tr}>{t.label}</option>)}
          </select>
        </label>
        <div style={{ border:`1px solid ${border}`, borderRadius:9, overflow:"hidden", marginBottom:10 }}>
          {q.items.map((item, i) => {
            const k = `${q.prefix}${i}`;
            return (
              <div key={k} style={{ padding:"8px 10px", borderBottom:`1px solid ${border}`, background: answers[k] !== undefined && answers[k] !== "" ? "#f0fdf4" : white }}>
                <div style={{ fontSize:12.5, color:textCol, marginBottom:5, lineHeight:1.4 }}>{item.t}</div>
                <select value={answers[k] ?? ""} onChange={e => { setAnswers(a => ({ ...a, [k]: e.target.value })); setStatus(null); }} style={fld}>
                  <option value="">—</option>
                  {item.o.map(([v, l]) => <option key={v} value={v}>{l}{l === v ? "" : ` (${v})`}</option>)}
                </select>
              </div>
            );
          })}
        </div>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
          <span style={{ fontSize:13, fontWeight:800, color: score !== null ? "#16a34a" : muted }}>
            {score !== null ? `Score : ${String(score).replace(".", ",")} ${q.unit}` : `${answered}/${q.items.length} items`}
          </span>
          <button onClick={save} disabled={score === null || !pid || status === "saving" || status === "saved"}
            style={{ padding:"10px 16px", borderRadius:10, border:"none", background: score === null ? "#cbd5e1" : orange, color:white, fontWeight:800, fontSize:13, cursor: score === null ? "default" : "pointer" }}>
            {status === "saving" ? "Enregistrement…" : status === "saved" ? "✓ Enregistré" : "Enregistrer dans le scoring"}
          </button>
        </div>
        {status === "saved" && <div style={{ fontSize:11.5, color:"#16a34a", marginTop:6 }}>Score inscrit pour {patient?.name} dans {target.label} (bilan du {date.split("-").reverse().join("/")}).</div>}
        {status && status.startsWith("Erreur") && <div style={{ fontSize:11.5, color:"#dc2626", marginTop:6 }}>{status}</div>}
      </>)}
    </div>
  );
}

// ── SCORING TOOL (Campus LCA v5.0 — intégral) ──────────────────────────

// ============================================================
// CAMPUS LCA — ACL Scoring Tool v5.0
// Paramed'Sport brand · Melbourne 2.0 · ACL Rehab Excel
// ============================================================

// ── BRAND TOKENS ─────────────────────────────────────────────
const C = {
  orange:    "#f97316",
  orangeL:   "#fb923c",
  orangeDim: "#ea580c",
  black:     "#ffffff",
  anthracite:"#64748b",
  greyLight: "#cbd5e1",
  greyWhite: "#f0f4f8",
  blue:      "#0f2040",
  blueMid:   "#1e40af",
  blueLight: "#dbeafe",
  green:     "#16a34a",
  greenL:    "#22c55e",
  greenBg:   "#f0fdf4",
  red:       "#dc2626",
  redL:      "#ef4444",
  redBg:     "#fef2f2",
  amber:     "#d97706",
  amberL:    "#f59e0b",
  amberBg:   "#fffbeb",
  white:     "#ffffff",
  surface:   "#f8fafc",
  border:    "#e2e8f0",
  borderMid: "#cbd5e1",
  textPrimary:   "#1a2f5a",
  textSecondary: "#64748b",
  textMuted:     "#94a3b8",
};

// ── CRITICALITY ───────────────────────────────────────────────
const CRIT = {
  critical:    { label:"CRITIQUE",   color:C.red,    bg:C.redBg,   border:"#FECACA", dot:"●" },
  important:   { label:"IMPORTANT",  color:C.amber,  bg:C.amberBg, border:"#FDE68A", dot:"◆" },
  informative: { label:"INFORMATIF", color:C.blue,   bg:C.blueLight,border:"#BAE6FD",dot:"○" },
};

// ── HELPERS ───────────────────────────────────────────────────
const lsi = (h, i) => (!h || h === 0) ? null : Math.round((Math.min(h,i) / Math.max(h,i)) * 100);
const deficit = (h, i) => (!h || !i || h === 0) ? null : Math.round((1 - Math.min(h,i)/Math.max(h,i)) * 100);
const isInjuredStronger = (h, i, side) => side === "left" ? i > h : i < h; // depends on which side is injured

const W = { critical:3, important:2, informative:1 };

// ── SCORING PHASES ──────────────────────────────────────────
const SCR_PHASES = [
  { id:0, key:"preop", label:"PRÉ-OP", name:"Préhabilitation",      color:C.anthracite, accent:C.greyLight, weeks:"Pré-op" },
  { id:1, key:"p1",    label:"P1",     name:"Post-op immédiat",      color:C.blue,       accent:C.blueMid,   weeks:"J0→S2" },
  { id:2, key:"p2",    label:"P2",     name:"Rééducation précoce",   color:C.amber,      accent:C.amberL,    weeks:"S2→S8" },
  { id:3, key:"p3",    label:"P3",     name:"Return to Run",         color:C.orange,     accent:C.orangeL,   weeks:"S8→M4" },
  { id:4, key:"p4",    label:"P4",     name:"Return to Sport",       color:C.blue,       accent:C.blueMid,   weeks:"M4→M9+" },
  { id:5, key:"p5",    label:"P5",     name:"Prévention long terme", color:C.green,      accent:C.greenBg,   weeks:"M12+" },
];

const TRANSITIONS = ["preop→p1","p1→p2","p2→p3","p3→p4","p4→p5"];

// ── ISOK REFERENCE VALUES ─────────────────────────────────────
// RTR (M3-4): Quad conc60: <30% deficit; Ratios: IJ/Q 60 0.5-0.7; 240 0.7-0.9; Mixte >1; Nm/kg 1.45-2.1
// RTS (M6-9): Quad conc60: <10% deficit; Nm/kg >2.5
const ISOK_NORMS = {
  rtr: { quadDeficit:30, ijDeficit:30, hq60:[0.5,0.7], hq240:[0.7,0.9], mixed:1.0, nmkg:[1.45,2.1] },
  rts: { quadDeficit:10, ijDeficit:10, hq60:[0.5,0.7], hq240:[0.7,0.9], mixed:1.0, nmkg:2.5 },
};

// ── CRITERIA DEFINITIONS ──────────────────────────────────────
// Structure: each transition has tabs, each tab has items
// item.inputType: slider|toggle|select|lsi|nmkg|isok|qualitative|number

const CRITERIA = {

// ════════════════════════════════════════
"preop→p1": {
  title:"Préhab → Post-op immédiat",
  tabs:[{
    id:"main", label:"Critères",
    items:[
      { id:"delai_bio", criticality:"critical",    label:"Délai biologique", unit:"sem", inputType:"slider", min:0, max:12, step:1, threshold:0, direction:"gte", desc:"Laisser le genou se stabiliser avant chirurgie", source:"Campus LCA" },
      { id:"ext",       criticality:"critical",    label:"Extension passive (Prone Hang)", unit:"°", inputType:"slider", min:-5, max:20, step:1, threshold:0, direction:"lte", desc:"0° requis avant chirurgie", source:"Melbourne 2.0 p.5" },
      { id:"flex",      criticality:"critical",    label:"Flexion passive", unit:"°", inputType:"slider", min:60, max:145, step:5, threshold:125, direction:"gte", desc:"≥ 125° (Norkin & White 1995)", source:"Melbourne 2.0 p.6" },
      { id:"epanch",    criticality:"critical",    label:"Épanchement (Stroke Test)", unit:"", inputType:"select", options:["3+ (sévère)","2+ (modéré)","1+ (léger)","Trace","0 (absent)"], threshold:3, direction:"gte", desc:"Zéro à 1+ requis (Sturgill 2009)", source:"Melbourne 2.0 p.6" },
      { id:"quad_lsi",  criticality:"important",   label:"Force quad (LSI)", unit:"%", inputType:"lsi", threshold:90, direction:"gte", desc:"≥ 90% LSI vs côté sain (dynamomètre)", source:"Melbourne 2.0 p.5" },
      { id:"ij_lsi",    criticality:"important",   label:"Force IJ (LSI)", unit:"%", inputType:"lsi", threshold:90, direction:"gte", desc:"≥ 90% LSI vs côté sain", source:"Melbourne 2.0 p.5" },
      { id:"hop_preop", criticality:"important",   label:"Single Hop for Distance (LSI)", unit:"%", inputType:"lsi", threshold:90, direction:"gte", desc:"≥ 90% LSI (Reid 2007)", source:"Melbourne 2.0 p.6" },
      { id:"epic_quad_iso", criticality:"informative", label:"Baseline EPIC — force quadriceps côté SAIN", unit:"", inputType:"number", threshold:0.1, direction:"gte", desc:"À mesurer AVANT l'opération (V1.14) avec le même dynamomètre, la même position et la même unité que la force de suivi (« Force quad isométrique »). Sert au calcul automatique du %EPIC.", source:"V1.14 epic_baseline_v2 · Wellsandt 2017" },
      { id:"epic_ham_iso",  criticality:"informative", label:"Baseline EPIC — force ischio-jambiers côté SAIN", unit:"", inputType:"number", threshold:0.1, direction:"gte", desc:"Avant l'opération, même outil et même unité que le suivi.", source:"V1.14 epic_baseline_v2" },
      { id:"epic_hip_abd",  criticality:"informative", label:"Baseline EPIC — abducteurs de hanche côté SAIN", unit:"% PC", inputType:"number", threshold:0.1, direction:"gte", desc:"Avant l'opération, dynamomètre manuel, en % du poids de corps (même position que le bilan retour au sport). Sert au %EPIC automatique du gate RTR-SPORT.", source:"V1.15 (J. Tubez 24/10/2026) · Aspetar §5.3" },
      { id:"epic_hip_add",  criticality:"informative", label:"Baseline EPIC — adducteurs de hanche côté SAIN", unit:"% PC", inputType:"number", threshold:0.1, direction:"gte", desc:"Avant l'opération, dynamomètre manuel, % du poids de corps.", source:"V1.15 · Aspetar §5.3" },
      { id:"epic_hip_re",   criticality:"informative", label:"Baseline EPIC — rotateurs externes de hanche côté SAIN", unit:"% PC", inputType:"number", threshold:0.1, direction:"gte", desc:"Avant l'opération, dynamomètre manuel, % du poids de corps.", source:"V1.15 · Aspetar §5.3" },
      { id:"epic_calf",     criticality:"informative", label:"Baseline EPIC — fléchisseurs plantaires côté SAIN", unit:"% PC", inputType:"number", threshold:0.1, direction:"gte", desc:"Avant l'opération, dynamomètre manuel, % du poids de corps.", source:"V1.15 · Aspetar §5.3" },
      { id:"epic_hop_triple", criticality:"informative", label:"Baseline EPIC — Triple hop côté SAIN", unit:"cm", inputType:"number", threshold:0.1, direction:"gte", desc:"Avant l'opération, mètre ruban.", source:"V1.14 epic_baseline_v2" },
      { id:"epic_sl_cmj",   criticality:"informative", label:"Baseline EPIC — CMJ unipodal côté SAIN", unit:"cm", inputType:"number", threshold:0.1, direction:"gte", desc:"Avant l'opération, My Jump Lab.", source:"V1.14 epic_baseline_v2" },
      { id:"educ",      criticality:"informative", label:"Éducation patient", unit:"", inputType:"toggle", threshold:1, direction:"gte", desc:"Patient informé du parcours post-op", source:"Campus LCA" },
    ]
  }]
},

// ════════════════════════════════════════
"p1→p2": {
  title:"Post-op immédiat → Rééducation précoce",
  tabs:[{
    id:"main", label:"Critères Phase 1",
    items:[
      { id:"delai_postop", criticality:"critical", label:"Délai post-op (viabilité greffon)", unit:"sem", inputType:"auto", auto:"op", autoNote:"Calculé automatiquement depuis la date d'opération (fiche patient) — plus de curseur (V1.15, D8)", threshold:2, direction:"gte", desc:"≥ 2 semaines post-op minimum avant progression", source:"Campus LCA" },
      { id:"ext",   criticality:"critical",    label:"Extension active 0°", unit:"°", inputType:"slider", min:-5, max:20, step:1, threshold:0, direction:"lte", desc:"Extension 0° — risque cyclope si non acquis (Delaloyle 2020)", source:"Campus LCA · Melbourne 2.0" },
      { id:"epanch",criticality:"critical",    label:"Épanchement (Stroke Test)", unit:"", inputType:"select", options:["3+ (sévère)","2+ (modéré)","1+ (léger)","Trace","0 (absent)"], threshold:2, direction:"gte", desc:"≤ 1+ (Sturgill 2009)", source:"Melbourne 2.0 p.8" },
      { id:"slr",   criticality:"critical",    label:"SLR sans extension lag", unit:"", inputType:"toggle", threshold:1, direction:"gte", desc:"Activation quad suffisante (lag 0–5°)", source:"Melbourne 2.0 p.8" },
      { id:"marche",criticality:"critical",    label:"Marche sans béquilles", unit:"", inputType:"toggle", threshold:1, direction:"gte", desc:"Marche autonome sans aide technique — attaque talon, déroulé, verrouillage quad", source:"Campus LCA" },
      { id:"derob_bilan", criticality:"critical", label:"Dérobement depuis le dernier bilan ?", unit:"", inputType:"giving_way", desc:"Question obligatoire à chaque bilan. Post-op : 0 dérobement toléré, comptés depuis la date d'opération en P1. Sans réponse ce jour → À RETESTER, jamais validé.", source:"V1.14 regle_giving_way · décision J. Tubez 22/10/2026 (convention N4)" },
      { id:"flex",  criticality:"important",   label:"Flexion passive", unit:"°", inputType:"slider", min:60, max:135, step:5, threshold:90, direction:"gte", desc:"≥ 90° à S2 (objectif 110°+)", source:"Melbourne 2.0 p.8" },
      { id:"ami",   criticality:"important",   label:"VMO visible (absence AMI sévère)", unit:"", inputType:"toggle", threshold:1, direction:"gte", desc:"Activation VMO sans NMES", source:"Campus LCA · Kinesport®" },
      { id:"kos_adls", criticality:"important", label:"KOS-ADLS (activités quotidiennes)", unit:"%", inputType:"slider", min:0, max:100, step:1, threshold:65, direction:"gte", desc:"≥ 65 % à S2-3 (jalon Delaware). Rempli automatiquement depuis Bibliothèque › Questionnaires. Cible, non bloquant.", source:"UD2021 · V1.14 §K · J. Tubez 24/10/2026" },
      { id:"douleur",criticality:"informative",label:"Douleur repos (EVA)", unit:"/10", inputType:"slider", min:0, max:10, step:1, threshold:3, direction:"lte", desc:"EVA ≤ 3 au repos", source:"Melbourne 2.0" },
    ]
  }]
},

// ════════════════════════════════════════
"p2→p3": {
  title:"Rééducation précoce → Return to Run",
  tabs:[
    {
      id:"timing", label:"⏱ Délais",
      items:[
        { id:"delai_postop", criticality:"critical",  label:"Délai post-op (viabilité greffon)", unit:"sem", inputType:"auto", auto:"op", autoNote:"Calculé automatiquement depuis la date d'opération (fiche patient) — plus de curseur (V1.15, D8)", threshold:8, direction:"gte", desc:"≥ 8 semaines post-op — viabilité biologique minimale pour course", source:"Campus LCA · Melbourne 2.0" },
        { id:"delai_p2",     criticality:"important", label:"Délai depuis validation P1→P2", unit:"sem", inputType:"auto", auto:"phase", autoNote:"Calculé automatiquement depuis le dernier GO P1→P2 enregistré (V1.15, D8)", threshold:3, direction:"gte", desc:"3–6 semaines de travail en P2 avant progression", source:"Campus LCA" },
      ]
    },
    {
      id:"quality", label:"🎯 Qualité mouvement",
      items:[
        { id:"douleur",   criticality:"critical",    label:"Douleur activité (EVA)", unit:"/10", inputType:"slider", min:0, max:10, step:1, threshold:2, direction:"lte", desc:"EVA < 2 pendant et après exercice", source:"Melbourne 2.0" },
        { id:"epanch",    criticality:"critical",    label:"Épanchement post-charge", unit:"", inputType:"select", options:["2+ (modéré)","1+ (léger)","Trace","0 (absent)"], threshold:2, direction:"gte", desc:"Genou sec après exercice", source:"Melbourne 2.0" },
        { id:"ext",       criticality:"critical",    label:"Extension complète 0°", unit:"°", inputType:"slider", min:-5, max:15, step:1, threshold:0, direction:"lte", desc:"Extension 0° active — non négociable", source:"Melbourne 2.0" },
        { id:"flex_pct",  criticality:"important",   label:"Flexion vs côté sain", unit:"%", inputType:"lsi", threshold:95, direction:"gte", desc:"Saisir les degrés des deux côtés : ≥ 95% du côté non-opéré. Le degré côté opéré alimente le hurdle RTR H4 (≥ 125°)", source:"Melbourne 2.0 p.10" },
        { id:"slsq",      criticality:"critical",    label:"Single Leg Squat (Crossley)", unit:"", inputType:"select", options:["Pauvre (Poor)","Passable (Fair)","Bon (Good)"], threshold:2, direction:"gte", desc:"Rating 'Good' : équilibre, mouvement fluide, ≥60°, pas valgus/déviation (Crossley 2011)", source:"Melbourne 2.0 p.10" },
        { id:"stepup",    criticality:"important",   label:"Step-up (20 cm) sans valgus", unit:"", inputType:"select", options:["Impossible","Valgus marqué","Valgus minime","Correct"], threshold:2, direction:"gte", desc:"Contrôle du genou en chaîne fermée", source:"Campus LCA" },
        { id:"stepdown",  criticality:"important",   label:"Step-down sans valgus", unit:"", inputType:"select", options:["Impossible","Valgus marqué","Valgus minime","Correct"], threshold:2, direction:"gte", desc:"Contrôle excentrique descente", source:"Campus LCA" },
        { id:"rot_tibia", criticality:"important",   label:"Contrôle rotatoire tibia (COC)", unit:"", inputType:"select", options:["Absent","Partiel","Correct"], threshold:2, direction:"gte", desc:"Contrôle rotatoire tibia en chaîne ouverte", source:"ACL Rehab Excel" },
        { id:"talon_fesse",criticality:"important",  label:"Talon-fesse dans l'axe", unit:"", inputType:"select", options:["Impossible","Compensation BF","Axe partiel","Axe correct"], threshold:2, direction:"gte", desc:"Sans compensation du Biceps fémoral", source:"ACL Rehab Excel" },
        { id:"aquajog",   criticality:"important",   label:"Aqua jogging / AlterG sans douleur", unit:"", inputType:"toggle", threshold:1, direction:"gte", desc:"Pré-condition à la course au sol", source:"ACL Rehab Excel" },
        { id:"pogos",     criticality:"important",   label:"Single-leg pogos — répétitions forme correcte", unit:"reps", inputType:"number", threshold:30, direction:"gte", desc:"≥ 30 répétitions unipodales côté opéré, forme correcte, sans douleur (= RTR-RUN H7, bloquant). Ancienne saisie oui/non : à refaire en répétitions.", source:"V1.13 H7 · Hughes (ACL Hub)" },
      ]
    },
    {
      id:"endurance", label:"💪 Endurance & Force",
      items:[
        { id:"sl_rise",    criticality:"important",   label:"Single Leg Rise", unit:"reps", inputType:"number", threshold:15, direction:"gte", desc:"> 15 reps, > 85% LSI (Culvenor 2016)", source:"Melbourne 2.0 p.11 · ACL Rehab Excel" },
        { id:"sl_bridge",  criticality:"important",   label:"Single Leg Bridge", unit:"reps", inputType:"number", threshold:20, direction:"gte", desc:"> 20 reps, > 85% LSI (Freckleton 2013)", source:"Melbourne 2.0 p.11" },
        { id:"sl_calf",    criticality:"important",   label:"Single Leg Calf Raise", unit:"reps", inputType:"number", threshold:20, direction:"gte", desc:"> 20 reps, > 85% LSI (Hébert 2017)", source:"Melbourne 2.0 p.11" },
        { id:"side_bridge",criticality:"important",   label:"Side Bridge Endurance Test (2 côtés)", unit:"sec", inputType:"lsi", threshold:85, minAbs:30, direction:"gte", desc:"Temps tenu en appui sur chaque côté (côté opéré = appui sur la jambe opérée) : ≥ 30 s côté opéré ET LSI ≥ 85 % (McGill 1999)", source:"Melbourne 2.0 p.11" },
        { id:"goblet",     criticality:"informative", label:"Goblet Squat (40% PDC)", unit:"reps", inputType:"number", threshold:30, direction:"gte", desc:"> 30 reps", source:"ACL Rehab Excel" },
        { id:"back_lunge", criticality:"informative", label:"Back Lunge (50% PDC)", unit:"rep", inputType:"number", threshold:1, direction:"gte", desc:"1 répétition contrôlée", source:"ACL Rehab Excel" },
        { id:"bulg_squat", criticality:"informative", label:"Bulgarian Squat", unit:"sec", inputType:"number", threshold:90, direction:"gte", desc:"> 90 secondes", source:"ACL Rehab Excel" },
        { id:"sl_deadlift",criticality:"informative", label:"Single Leg Deadlift (20% PDC)", unit:"reps", inputType:"number", threshold:20, direction:"gte", desc:"> 20 reps", source:"ACL Rehab Excel" },
        { id:"quad_iso",   criticality:"critical",    label:"Force quad isométrique (LSI)", unit:"%", inputType:"lsi", threshold:70, direction:"gte", desc:"≥ 70% LSI (dynamomètre portatif)", source:"Melbourne 2.0 · Rambaud 2018" },
        { id:"ij_iso",     criticality:"critical",    label:"Force IJ isométrique (LSI)", unit:"%", inputType:"lsi", threshold:70, direction:"gte", desc:"≥ 70% LSI", source:"Melbourne 2.0" },
        { id:"load_1_5",   criticality:"informative", label:"Charge système ≥ 1.5× PDC", unit:"", inputType:"toggle", threshold:1, direction:"gte", desc:"Squat système 1.5× poids corps", source:"ACL Rehab Excel" },
        { id:"glute",      criticality:"informative", label:"Glute strength (isométrique + SL bridge)", unit:"", inputType:"toggle", threshold:1, direction:"gte", desc:"Isométrique abd hanche 1.5×PDC + SL bridge 25-30 reps", source:"ACL Rehab Excel" },
      ]
    },
    {
      id:"hops_p2", label:"🦘 Hop tests (RTR)",
      items:[
        { id:"sl_forward_hop",  criticality:"important",   label:"Single Leg Hop for Distance (LSI)", unit:"%", inputType:"lsi", threshold:70, direction:"gte", desc:"> 70% LSI avant R2R (Reid 2007)", source:"ACL Rehab Excel" },
        { id:"triple_hop_rtr",  criticality:"informative", label:"Triple Hop for Distance (LSI)", unit:"%", inputType:"lsi", threshold:70, direction:"gte", desc:"> 70% LSI", source:"ACL Rehab Excel" },
        { id:"crossover_rtr",   criticality:"informative", label:"Cross-over Hop for Distance (LSI)", unit:"%", inputType:"lsi", threshold:70, direction:"gte", desc:"> 70% LSI", source:"ACL Rehab Excel" },
        { id:"vertical_hop_rtr",criticality:"informative", label:"Single Leg Vertical Hop (LSI)", unit:"%", inputType:"lsi", threshold:70, direction:"gte", desc:"> 70% LSI", source:"ACL Rehab Excel" },
        { id:"sidehop30",       criticality:"informative", label:"Side Hop 30\" (LSI)", unit:"%", inputType:"lsi", threshold:90, direction:"gte", desc:"Répétitions de sauts latéraux unipodaux en 30 s, chaque côté. Informatif en P2→P3 (repère : LSI ≥ 90 % au retour au sport)", source:"Gustavsson 2006 · V1.15 (J. Tubez 24/10/2026)" },
        { id:"rep_fwd_hop",     criticality:"important",   label:"Repeated Forward Hop (cone hop avant/arrière)", unit:"reps", inputType:"number", threshold:15, direction:"gte", desc:"Sauts unipodaux avant/arrière par-dessus un petit cône (~15 cm), réception contrôlée : ≥ 15 répétitions. Alimente le « cone hop » du gate RTR.", source:"ACL Rehab Excel" },
        { id:"rep_vert_hop",    criticality:"important",   label:"Repeated Vertical Hop (tuck jump unipodal)", unit:"reps", inputType:"number", threshold:15, direction:"gte", desc:"Sauts verticaux unipodaux genou ramené vers la poitrine, réception contrôlée : ≥ 15 répétitions. Alimente le « tuck jump » du gate RTR.", source:"ACL Rehab Excel" },
        { id:"sl_landing",      criticality:"critical",    label:"Single Leg Landing (qualité)", unit:"", inputType:"select", options:["Échouée","Passable","Correcte"], threshold:2, direction:"gte", desc:"Stabilité bassin-tronc-membre + absorption chocs", source:"ACL Rehab Excel" },
      ]
    },
    {
      id:"balance_p2", label:"⚖️ Balance dynamique",
      items:[
        { id:"sebt_p2",       criticality:"important",   label:"SEBT (Star Excursion Balance Test)", unit:"%", inputType:"lsi", threshold:95, direction:"gte", desc:"> 95% LSI composite (Gribble 2012)", source:"Melbourne 2.0 p.15 · ACL Rehab Excel" },
        { id:"bal_side_p2",   criticality:"important",   label:"Balance vestibulaire — Side to Side (15\")", unit:"", inputType:"toggle", threshold:1, direction:"gte", desc:"Cooper & Hughes: 60 bpm, 70-90° rotation tête, 15 sec", source:"Melbourne 2.0 p.15" },
        { id:"bal_updown_p2", criticality:"important",   label:"Balance vestibulaire — Up & Down (15\")", unit:"", inputType:"toggle", threshold:1, direction:"gte", desc:"Cooper & Hughes: 60 bpm, regard sol-plafond, 15 sec", source:"Melbourne 2.0 p.15" },
      ]
    },
    {
      id:"isok_rtr", label:"📊 Isocinétique M3-4",
      items:[
        { id:"isok_rtr", criticality:"important", label:"Bilan isocinétique (M3-4)", unit:"", inputType:"isok", phase:"rtr", threshold:70, direction:"gte", desc:"Seuils RTR: Quad déficit <30%, IJ déficit <30%, Nm/kg 1.45-2.1", source:"ACL Rehab Excel · Melbourne 2.0" },
      ]
    },
    {
      id:"psycho_p2", label:"🧠 Psychologie",
      items:[
        { id:"aclrsi_p2", criticality:"informative", label:"ACL-RSI", unit:"/100", inputType:"slider", min:0, max:100, step:1, threshold:56, direction:"gte", desc:"< 56% à M4 : facteur péjoratif de reprise à 12 mois", source:"ACL Rehab Excel · Webster 2008" },
        { id:"ikdc_p2",   criticality:"informative", label:"IKDC subjectif", unit:"pts", inputType:"slider", min:0, max:100, step:0.5, threshold:63.7, direction:"gte", desc:"> 63.7 points", source:"ACL Rehab Excel · Anderson 2006" },
      ]
    },
  ]
},

// ════════════════════════════════════════
"p3→p4": {
  title:"Return to Run → Return to Sport",
  tabs:[
    {
      id:"timing_p3", label:"⏱ Délais",
      items:[
        { id:"delai_postop_p3", criticality:"critical",  label:"Délai post-op (viabilité greffon)", unit:"sem", inputType:"auto", auto:"op", autoNote:"Calculé automatiquement depuis la date d'opération (fiche patient) — plus de curseur (V1.15, D8)", threshold:16, direction:"gte", desc:"≥ 16 semaines post-op minimum avant RTS", source:"Campus LCA · Melbourne 2.0" },
        { id:"delai_p3",        criticality:"important", label:"Délai depuis validation P2→P3", unit:"sem", inputType:"auto", auto:"phase", autoNote:"Calculé automatiquement depuis le dernier GO P2→P3 (RTR-RUN) enregistré (V1.15, D8)", threshold:6, direction:"gte", desc:"6–12 semaines de travail en P3 avant progression vers RTS", source:"Campus LCA" },
      ]
    },
    {
      id:"quality_p3", label:"🎯 Movement Quality",
      items:[
        { id:"douleur_p3",  criticality:"critical",    label:"Douleur course (EVA)", unit:"/10", inputType:"slider", min:0, max:10, step:1, threshold:2, direction:"lte", desc:"EVA < 2 pendant et après course", source:"Melbourne 2.0" },
        { id:"epanch_p3",   criticality:"critical",    label:"Épanchement post-charge", unit:"", inputType:"select", options:["2+ (modéré)","1+ (léger)","Trace","0 (absent)"], threshold:2, direction:"gte", desc:"Genou sec après course", source:"Melbourne 2.0" },
        { id:"ext_p3",      criticality:"critical",    label:"Extension complète (Prone Hang)", unit:"", inputType:"select", options:["5cm+ déficit","1-5cm déficit","0-1cm déficit"], threshold:2, direction:"gte", desc:"Égal au côté sain (Sachs 1989)", source:"Melbourne 2.0 MRSS p.20" },
        { id:"flex_p3",     criticality:"important",   label:"Flexion ROM", unit:"", inputType:"select", options:["20°+ déficit","5-20° déficit","0-5° déficit"], threshold:2, direction:"gte", desc:"0-5° de déficit vs côté sain", source:"Melbourne 2.0 MRSS p.20" },
      ]
    },
    {
      id:"endurance_p3", label:"💪 Endurance & Force",
      items:[
        { id:"sl_rise_p3",    criticality:"important",   label:"Single Leg Rise (≥ 90°)", unit:"reps", inputType:"number", threshold:22, direction:"gte", desc:"> 22 reps LSI ≥ 95% (Melbourne Phase 3)", source:"Melbourne 2.0 p.15" },
        { id:"sl_bridge_p3",  criticality:"informative", label:"Single Leg Bridge (85% LSI)", unit:"reps", inputType:"number", threshold:20, direction:"gte", desc:"> 20 reps", source:"Melbourne 2.0 p.11" },
        { id:"sl_calf_p3",    criticality:"informative", label:"Single Leg Calf Raise", unit:"reps", inputType:"number", threshold:20, direction:"gte", desc:"> 20 reps, 85% LSI", source:"Melbourne 2.0" },
        { id:"side_bridge_p3",criticality:"informative", label:"Side Bridge Endurance (2 côtés)", unit:"sec", inputType:"lsi", threshold:85, minAbs:30, direction:"gte", desc:"≥ 30 s côté opéré ET LSI ≥ 85 %", source:"Melbourne 2.0" },
        { id:"leg_press_p3",  criticality:"informative", label:"1RM Leg Press", unit:"×PDC", inputType:"slider", min:0, max:3, step:0.1, threshold:1.8, direction:"gte", desc:"≥ 1.8× poids corps (Campanholi Neto 2015)", source:"Melbourne 2.0 Supp." },
        { id:"squat_1rm_p3",  criticality:"informative", label:"1RM Squat", unit:"×PDC", inputType:"slider", min:0, max:3, step:0.1, threshold:1.8, direction:"gte", desc:"≥ 1.8× poids corps", source:"Melbourne 2.0 Supp." },
        { id:"course_20min",  criticality:"critical",    label:"Course 20 min sans douleur", unit:"", inputType:"toggle", threshold:1, direction:"gte", desc:"Course continue 20 min à 65% FC max sans symptôme", source:"Campus LCA · Rambaud 2018" },
        { id:"delaware_p3",   criticality:"important",   label:"Delaware Running Program", unit:"", inputType:"select", options:["Niv 1-2 (débutant)","Niv 3-4 (intermédiaire)","Niv 5-6 (3.2 km continu)","Niv 7-8 (4.8 km + vitesse)"], threshold:2, direction:"gte", desc:"Delaware Track ≥ Niv 5 — 3.2 km continu (Delaware PT Clinic)", source:"Delaware Running Program" },
        { id:"load_tol",      criticality:"informative", label:"Load Tolerance (10 000 pas + hopping)", unit:"", inputType:"toggle", threshold:1, direction:"gte", desc:"10 000 pas + hopping horizontal sans symptôme", source:"ACL Rehab Excel" },
        { id:"plyo_bip",      criticality:"important",   label:"Pliométrie bipodale tolérée", unit:"", inputType:"toggle", threshold:1, direction:"gte", desc:"Plyo bipodale sans douleur ni épanchement", source:"Melbourne 2.0" },
      ]
    },
    {
      id:"hops_rts", label:"🦘 Hop tests (RTS)",
      items:[
        { id:"single_hop_rts",   criticality:"important",   label:"Single Leg Hop for Distance (LSI)", unit:"%", inputType:"lsi", threshold:90, direction:"gte", desc:"> 90% LSI | Norme RTS: 163-168 cm (Welling 2020)", source:"Melbourne 2.0 p.14 · ACL Rehab Excel" },
        { id:"triple_hop_rts",   criticality:"important",   label:"Triple Hop for Distance (LSI)", unit:"%", inputType:"lsi", threshold:90, direction:"gte", desc:"> 90% LSI | Norme: 524-538 cm (Welling 2020)", source:"Melbourne 2.0 p.14 · ACL Rehab Excel" },
        { id:"crossover_rts",    criticality:"important",   label:"Cross-over Hop for Distance (LSI)", unit:"%", inputType:"lsi", threshold:90, direction:"gte", desc:"> 90% LSI (Noyes 1991)", source:"Melbourne 2.0 p.14" },
        { id:"sidehop30_rts",    criticality:"important",   label:"Side Hop 30\" (LSI)", unit:"%", inputType:"lsi", threshold:90, direction:"gte", desc:"> 90% LSI | Norme: 53-54 reps (Welling 2020)", source:"ACL Rehab Excel · Gustavsson 2006" },
        { id:"vertical_hop_rts", criticality:"important",   label:"Single Leg Vertical Hop (LSI)", unit:"%", inputType:"lsi", threshold:90, direction:"gte", desc:"> 90% LSI | Norme: 24 cm (van Melick 2019)", source:"ACL Rehab Excel" },
        { id:"sl_landing_rts",   criticality:"critical",    label:"Single Leg Landing (qualité)", unit:"", inputType:"select", options:["Échouée","Passable","Correcte"], threshold:2, direction:"gte", desc:"Stabilité bassin-tronc-membre, absorption chocs", source:"ACL Rehab Excel" },
      ]
    },
    {
      id:"balance_p3", label:"⚖️ Balance dynamique",
      items:[
        { id:"sebt_p3",       criticality:"important",   label:"SEBT (Star Excursion Balance Test)", unit:"%", inputType:"lsi", threshold:95, direction:"gte", desc:"> 95% LSI composite (Gribble 2012)", source:"Melbourne 2.0 MRSS p.27" },
        { id:"bal_side_p3",   criticality:"important",   label:"Balance vestibulaire — Side to Side (15\")", unit:"", inputType:"toggle", threshold:1, direction:"gte", desc:"Cooper & Hughes: passage les deux membres", source:"Melbourne 2.0 MRSS p.27" },
        { id:"bal_updown_p3", criticality:"important",   label:"Balance vestibulaire — Up & Down (15\")", unit:"", inputType:"toggle", threshold:1, direction:"gte", desc:"Cooper & Hughes: passage les deux membres", source:"Melbourne 2.0 MRSS p.27" },
      ]
    },
    {
      id:"cod", label:"🔄 Critères COD",
      items:[
        { id:"illinois",     criticality:"important",   label:"Modified Illinois Agility Test", unit:"", inputType:"select", options:["Non réalisé","< 85% baseline","85-94% baseline","≥ 95% baseline"], threshold:2, direction:"gte", desc:"≥ 85% vs baseline pré-blessure", source:"ACL Rehab Excel" },
        { id:"cod_straight", criticality:"critical",    label:"Course en ligne droite tolérée", unit:"", inputType:"toggle", threshold:1, direction:"gte", desc:"Tolérance course droite sans épanchement", source:"ACL Rehab Excel" },
        { id:"cod_planned",  criticality:"important",   label:"COD planifié toléré", unit:"", inputType:"toggle", threshold:1, direction:"gte", desc:"Changements de direction planifiés sans symptôme", source:"ACL Rehab Excel" },
        { id:"cod_unplanned",criticality:"informative", label:"COD non planifié toléré", unit:"", inputType:"toggle", threshold:1, direction:"gte", desc:"COD réactifs tolérés avant entraînement collectif", source:"ACL Rehab Excel" },
        { id:"sprint_5m",    criticality:"informative", label:"Sprint 5m (temps)", unit:"", inputType:"sprint_time", desc:"Temps mesuré — pas de comparaison G/D", source:"ACL Rehab Excel" },
        { id:"sprint_10m",   criticality:"informative", label:"Sprint 10m (temps)", unit:"", inputType:"sprint_time", desc:"Temps mesuré — pas de comparaison G/D", source:"ACL Rehab Excel" },
      ]
    },
    {
      id:"isok_rts", label:"📊 Isocinétique M6-9",
      items:[
        { id:"isok_rts", criticality:"critical", label:"Bilan isocinétique (M6-9)", unit:"", inputType:"isok", phase:"rts", threshold:90, direction:"gte", desc:"Seuils RTS: Quad déficit <10%, IJ déficit <10%, Nm/kg >2.5", source:"ACL Rehab Excel · Grindem 2016" },
      ]
    },
    {
      id:"rts_checklists", label:"🏅 R2R / R2Train / R2Play",
      items:[
        // Return to Restricted Training
        { id:"r2train_quad",   criticality:"critical",    label:"Force quad ≥ 85% LSI (R2Train)", unit:"%", inputType:"lsi", threshold:85, direction:"gte", desc:"Restricted Training: quad ≥ 85% LSI", source:"ACL Rehab Excel" },
        { id:"r2train_ij",     criticality:"critical",    label:"Force IJ ≥ 85% LSI (R2Train)", unit:"%", inputType:"lsi", threshold:85, direction:"gte", desc:"Restricted Training: IJ ≥ 85% LSI", source:"ACL Rehab Excel" },
        { id:"r2train_hop",    criticality:"critical",    label:"Single Hop ≥ 85% LSI (R2Train)", unit:"%", inputType:"lsi", threshold:85, direction:"gte", desc:"Restricted Training: Single Hop ≥ 85% LSI", source:"ACL Rehab Excel" },
        { id:"r2train_triple", criticality:"important",   label:"Triple/Crossover ≥ 85% LSI (R2Train)", unit:"%", inputType:"lsi", threshold:85, direction:"gte", desc:"Restricted Training: Triple + Crossover ≥ 85% LSI", source:"ACL Rehab Excel" },
        { id:"r2train_vert",   criticality:"important",   label:"SL Vertical Hop ≥ 85% LSI (R2Train)", unit:"%", inputType:"lsi", threshold:85, direction:"gte", desc:"Restricted Training", source:"ACL Rehab Excel" },
        { id:"r2train_run",    criticality:"critical",    label:"Course droite tolérée (R2Train)", unit:"", inputType:"toggle", threshold:1, direction:"gte", desc:"Trace ou absence d'épanchement post-course", source:"ACL Rehab Excel" },
        { id:"r2train_cod",    criticality:"important",   label:"COD planifié toléré (R2Train)", unit:"", inputType:"toggle", threshold:1, direction:"gte", desc:"Trace ou absence d'épanchement", source:"ACL Rehab Excel" },
        // Return to Unrestricted Training (seuils 90%)
        { id:"r2unrestr_quad", criticality:"critical",    label:"Force quad+IJ ≥ 90% LSI (R2Unrestr)", unit:"%", inputType:"lsi", threshold:90, direction:"gte", desc:"Unrestricted Training: quad & IJ ≥ 90% LSI", source:"ACL Rehab Excel" },
        { id:"r2unrestr_hop",  criticality:"critical",    label:"Hop battery ≥ 90% LSI (R2Unrestr)", unit:"%", inputType:"lsi", threshold:90, direction:"gte", desc:"Single + Triple + Crossover + Vertical ≥ 90%", source:"ACL Rehab Excel" },
        { id:"r2unrestr_side", criticality:"important",   label:"Side Hop ≥ 90% LSI (R2Unrestr)", unit:"%", inputType:"lsi", threshold:90, direction:"gte", desc:"Side Hop 30\" ≥ 90% LSI", source:"ACL Rehab Excel" },
        { id:"r2unrestr_restr",criticality:"critical",    label:"4 semaines restricted training toléré", unit:"", inputType:"toggle", threshold:1, direction:"gte", desc:"Trace ou absence d'épanchement sur 4 semaines", source:"ACL Rehab Excel" },
        { id:"r2unrestr_cod",  criticality:"important",   label:"COD + high speed toléré", unit:"", inputType:"toggle", threshold:1, direction:"gte", desc:"COD non planifié + haute vitesse sans symptôme", source:"ACL Rehab Excel" },
        // RTS final
        { id:"rts_9mois",     criticality:"critical",    label:"Délai ≥ 9 mois post-op (RTS)", unit:"sem", inputType:"auto", auto:"op", autoNote:"Calculé automatiquement depuis la date d'opération (fiche patient) — plus de curseur (V1.15, D8)", threshold:39, direction:"gte", desc:"≥ 9 mois ≈ 39 semaines — non négociable (Grindem 2016). Corrigé V1.15 : 36 sem = 8,3 mois.", source:"ACL Rehab Excel · Grindem 2016" },
        { id:"rts_sprint",    criticality:"important",   label:"Sprint 100% sans appréhension", unit:"", inputType:"toggle", threshold:1, direction:"gte", desc:"Sprint pleine vitesse physiquement et psychologiquement", source:"Campus LCA" },
      ]
    },
    {
      id:"psycho_p3", label:"🧠 Psychologie",
      items:[
        { id:"aclrsi_p3", criticality:"critical",    label:"ACL-RSI", unit:"/100", inputType:"slider", min:0, max:100, step:1, threshold:76, direction:"gte", desc:"≥ 76% pour RTS — prédicteur n°1 (Webster 2018, Du 2025 ES 1.55)", source:"ACL Rehab Excel · Webster 2008" },
        { id:"tsk11_p3",  criticality:"critical",    label:"Tampa Scale TSK-11", unit:"/44", inputType:"slider", min:11, max:44, step:1, threshold:19, direction:"lte", desc:"< 19 (kinésiophobie acceptable) — Hurdle criteria MRSS2.0", source:"Melbourne 2.0 MRSS p.26 · Woby 2005" },
        { id:"ikdc_p3",   criticality:"important",   label:"IKDC subjectif", unit:"/100", inputType:"slider", min:0, max:100, step:1, threshold:90, direction:"gte", desc:"> 90 points", source:"ACL Rehab Excel · Anderson 2006" },
        { id:"pivot_p3",  criticality:"informative", label:"Pivot Shift Test (stabilité)", unit:"", inputType:"select", options:["Grade III-IV","Grade II","Grade I","Nil"], threshold:2, direction:"gte", desc:"Nil ou Grade I (MRSS Part A)", source:"Melbourne 2.0 MRSS p.20" },
        { id:"prevention",criticality:"informative", label:"Programme prévention LCA discuté", unit:"", inputType:"toggle", threshold:1, direction:"gte", desc:"FIFA 11+, PEP Program ou équivalent prévu", source:"Melbourne 2.0 Phase 5" },
      ]
    },
  ]
},

// ════════════════════════════════════════
// T7 — sortie du programme, entrée en prévention long terme (V1.13 T7_P4RTS_vers_P5,
// + enquête mensuelle d'exposition V1.14 §L). Ajouté V1.15 (D9, J. Tubez 23/10/2026).
"p4→p5": {
  title:"Retour au sport → Prévention long terme (T7)",
  tabs:[
    {
      id:"t7", label:"🛡 Sortie T7",
      items:[
        { id:"delai_12mois", criticality:"critical", label:"Délai ≥ 12 mois post-op", unit:"sem", inputType:"auto", auto:"op", autoNote:"Calculé automatiquement depuis la date d'opération (fiche patient) — plus de curseur (V1.15, D8)", threshold:52, direction:"gte", desc:"≥ 12 mois (≈ 52 semaines)", source:"V1.13 T7" },
        { id:"matchs_ok",    criticality:"critical", label:"Matchs / compétitions complets sans réaction articulaire", unit:"", inputType:"number", threshold:4, direction:"gte", desc:"≥ 4 matchs ou sessions de compétition complets, sans douleur ni gonflement le lendemain", source:"V1.13 T7" },
        { id:"epanch",       criticality:"critical", label:"Épanchement (Stroke Test)", unit:"", inputType:"select", options:["2+ (modéré)","1+ (léger)","Trace","0 (absent)"], threshold:3, direction:"gte", desc:"0 maintenu", source:"V1.13 T7" },
        { id:"derob_bilan",  criticality:"critical", label:"Dérobement depuis le dernier bilan ?", unit:"", inputType:"giving_way", desc:"Question obligatoire. 0 dérobement depuis l'entrée en P4 (dernier GO RTR-SPORT). Sans réponse ce jour → À RETESTER, jamais validé.", source:"V1.13 T7 · V1.14 regle_giving_way" },
        { id:"prevention",   criticality:"critical", label:"Programme de prévention maintenu", unit:"", inputType:"toggle", threshold:1, direction:"gte", desc:"≥ 15 min avant chaque entraînement et match (FIFA 11+, PEP ou équivalent)", source:"V1.13 T7" },
        { id:"quad_iso",     criticality:"important", label:"Force quadriceps isométrique (dynamomètre manuel)", unit:"%", inputType:"lsi", threshold:90, direction:"gte", desc:"Même outil que la baseline pré-op : LSI ≥ 90 %. Sert au calcul du %EPIC (suivi ≥ 2 ans recommandé)", source:"V1.14 §O" },
        { id:"quad_epic",    criticality:"important", label:"%EPIC quadriceps", unit:"%", inputType:"auto", auto:"epic", epicFrom:"p4→p5_quad_iso", autoNote:"Calculé : force côté opéré (ligne ci-dessus) / force côté sain pré-op × 100. Sans baseline pré-op → non mesuré.", threshold:90, direction:"gte", desc:"≥ 90 % à 12 mois", source:"V1.13 T7 · Wellsandt 2017" },
        { id:"aclrsi",       criticality:"important", label:"ACL-RSI", unit:"/100", inputType:"slider", min:0, max:100, step:1, threshold:80, direction:"gte", desc:"≥ 80", source:"V1.13 T7" },
        { id:"koos_sport",   criticality:"important", label:"KOOS Sport / Loisirs", unit:"/100", inputType:"slider", min:0, max:100, step:1, threshold:90, direction:"gte", desc:"≥ 90", source:"V1.13 T7" },
      ]
    },
    {
      id:"suivi", label:"📅 Suivi mensuel",
      items:[
        { id:"niveau_sport",     criticality:"informative", label:"Niveau de sport pratiqué ce mois", unit:"", inputType:"select", options:["Aucun","Niveau III","Niveau II","Niveau I"], threshold:0, direction:"gte", desc:"I = pivot / contact (football, basket, handball), II = pivot léger, III = sans pivot", source:"V1.14 §L (Delaware-Oslo)" },
        { id:"seances_mois",     criticality:"informative", label:"Séances d'entraînement ce mois", unit:"", inputType:"number", threshold:0, direction:"gte", desc:"Dénominateur d'exposition", source:"V1.14 §L" },
        { id:"matchs_mois",      criticality:"informative", label:"Matchs ce mois", unit:"", inputType:"number", threshold:0, direction:"gte", desc:"Dénominateur d'exposition", source:"V1.14 §L" },
        { id:"nouvelle_blessure",criticality:"informative", label:"Nouvelle blessure du genou ce mois", unit:"", inputType:"select", options:["Oui","Non"], threshold:1, direction:"gte", desc:"Si oui : côté et description dans les notes du bilan", source:"V1.14 §L" },
        { id:"prevention_pct",   criticality:"informative", label:"Séances précédées du programme de prévention", unit:"%", inputType:"number", threshold:0, direction:"gte", desc:"% des séances du mois", source:"V1.14 §L" },
      ]
    },
  ]
},
};

// ── DEFAULT VALUES ─────────────────────────────────────────────
function getDefault(c) {
  if (!c) return 0;
  if (c.inputType === "toggle")     return 0;
  if (c.inputType === "giving_way") return undefined; // question obligatoire : jamais de réponse par défaut
  if (c.inputType === "auto")       return undefined; // calculé (délais, %EPIC), jamais saisi
  if (c.inputType === "select")     return 0;
  if (c.inputType === "lsi")        return { h:0, i:0 };
  if (c.inputType === "number")     return 0;
  if (c.inputType === "isok")       return { side:"left", weight:70, isokDate:"", qL60:0, qR60:0, qL240:0, qR240:0, ijL60:0, ijR60:0, ijL240:0, ijR240:0, ijLe30:0, ijRe30:0 };
  if (c.inputType === "sprint_time") return { time:0, date:"" };
  if (c.direction === "gte")        return c.min ?? 0;
  return c.max ?? 10;
}

// ── PASS LOGIC ─────────────────────────────────────────────────
function isPass(c, v) {
  if (!c) return false;
  if (c.inputType === "toggle")  return v === 1;
  if (c.inputType === "giving_way") return v === 0; // « non » ; les événements datés sont vérifiés par givingCriterion
  if (c.inputType === "auto")    return v !== undefined && (c.direction === "gte" ? v >= c.threshold : v <= c.threshold);
  if (c.inputType === "select")  return c.direction === "gte" ? (v ?? 0) >= c.threshold : (v ?? 0) <= c.threshold;
  if (c.inputType === "lsi")     { const l = v?.h > 0 && v?.i > 0 ? lsi(v.h, v.i) : null; return l !== null && l >= c.threshold && (!c.minAbs || v.i >= c.minAbs); }
  if (c.inputType === "number")  return c.direction === "gte" ? (v ?? 0) >= c.threshold : (v ?? 0) <= c.threshold;
  if (c.inputType === "sprint_time") return (v?.time ?? 0) > 0; // pass if a time was entered
  if (c.inputType === "isok")    {
    if (!v || !v.weight) return false;
    const norms = ISOK_NORMS[c.phase ?? "rtr"];
    const injSide = v.side ?? "left";
    const qSain  = injSide === "left" ? v.qR60  : v.qL60;
    const qLese  = injSide === "left" ? v.qL60  : v.qR60;
    if (!qSain || !qLese) return false;
    const qDef = deficit(qSain, qLese);
    return qDef !== null && qDef <= norms.quadDeficit;
  }
  return c.direction === "gte" ? (v ?? 0) >= c.threshold : (v ?? 0) <= c.threshold;
}

function dispVal(c, v) {
  if (c.inputType === "toggle")  return v ? "OUI" : "NON";
  if (c.inputType === "giving_way") return v === 1 ? "OUI" : v === 0 ? "NON" : "?";
  if (c.inputType === "auto")    return v === undefined ? "—" : `${v}${c.unit ? ` ${c.unit}` : ""}`;
  if (c.inputType === "select")  return c.options?.[v ?? 0]?.split(" ")[0] ?? "–";
  if (c.inputType === "lsi")     { const l = v?.h > 0 && v?.i > 0 ? lsi(v.h, v.i) : null; return l !== null ? `${l}%` : "–"; }
  if (c.inputType === "number")  return v > 0 ? `${v}` : "–";
  if (c.inputType === "isok")    return "→";
  if (c.inputType === "sprint_time") { const v2 = value; return v2?.time ? `${v2.time}s` : "—"; }
  if (c.inputType === "slider")  return `${v ?? 0}`;
  return `${v ?? 0}`;
}

// stale : { [test_id]: { date, ageDays, maxDays } } — règle de fraîcheur (src/measureMap.js).
// Une mesure périmée ne compte pas comme 0 : NON_MESURE, retirée du numérateur ET du
// dénominateur ; un critère CRITIQUE périmé met le passage « À RETESTER » (pas BLOQUÉ).
// Question « Dérobement depuis le dernier bilan ? » (V1.14) : échec si « oui » ou si un
// événement daté tombe dans la fenêtre ; sans réponse du jour → À RETESTER (jamais validé).
// g = { count, since } (giving_way_events, fenêtre = entrée dans la phase en cours).
function givingCriterion(v, st, g = {}) {
  const fail = v === 1 || (g.count || 0) > 0;
  if (fail) return { rawPass: false, pass: false, stale: null };
  const unanswered = st || (v !== 0 ? { question: true } : null);
  return { rawPass: !unanswered, pass: !unanswered, stale: unanswered };
}

function computeResults(trKey, values, overrides, stale = {}, giving = {}) {
  const data = CRITERIA[trKey]; if (!data) return null;
  let allItems = [];
  data.tabs.forEach(tab => tab.items.forEach(c => allItems.push(c)));

  const items = allItems.map(c => {
    const k = `${trKey}_${c.id}`;
    const v = k in values ? values[k] : getDefault(c);
    const rawPass = isPass(c, v);
    const ov = overrides[k];
    const ovValid = !rawPass && ov?.active && ov?.note?.trim() && c.criticality !== "critical";
    if (c.inputType === "giving_way") return { ...c, value:v, ...givingCriterion(v, stale[k] || null, giving[trKey]), ovValid:false, ovNote:"" };
    const st = stale[k] || null;
    const pass = !st && (rawPass || ovValid);
    return { ...c, value:v, rawPass: !st && rawPass, pass, ovValid: !st && ovValid, ovNote:ov?.note ?? "", stale: st };
  });

  const critItems = items.filter(i => i.criticality === "critical");
  const impItems  = items.filter(i => i.criticality === "important");
  const infItems  = items.filter(i => i.criticality === "informative");
  const critFail  = critItems.filter(i => !i.pass && !i.stale).length;
  const impFail   = impItems.filter(i => !i.pass && !i.stale).length;
  const critRetest = critItems.filter(i => i.stale).length;
  const staleCount = items.filter(i => i.stale).length;
  const scored = items.filter(i => !i.stale);
  const totalW = scored.reduce((a,i) => a + W[i.criticality], 0);
  const passW  = scored.filter(i => i.pass).reduce((a,i) => a + W[i.criticality], 0);
  const globalScore = totalW > 0 ? Math.round((passW/totalW)*100) : 0;
  const blocked  = critFail > 0;
  const retest   = !blocked && critRetest > 0;
  const warning  = !blocked && !retest && impFail > 0;
  const validated = !blocked && !retest && !warning;
  return { items, critItems, impItems, infItems, critFail, impFail, critRetest, staleCount, globalScore, blocked, retest, warning, validated };
}

// ── UI ATOMS ──────────────────────────────────────────────────

function ScrBadge({ color, bg, border, children, small }) {
  return <span style={{ background:bg, border:`1px solid ${border}`, color, borderRadius:3,
    padding: small ? "1px 5px" : "2px 7px", fontSize: small ? 8.5 : 9.5, fontWeight:700,
    letterSpacing:"0.07em", fontFamily:"'Segoe UI',sans-serif", whiteSpace:"nowrap" }}>{children}</span>;
}

function StatusBanner({ res }) {
  if (!res) return null;
  const { blocked, retest, warning, validated, globalScore, critFail, impFail, critRetest, staleCount, critItems, impItems, infItems } = res;
  const col = validated ? C.green : retest ? C.blueMid : warning ? C.amber : C.red;
  const bg  = validated ? C.greenBg : retest ? C.blueLight : warning ? C.amberBg : C.redBg;
  const label = validated ? "✓ PASSAGE VALIDÉ"
    : retest ? `↻ À RETESTER — ${critRetest} critère(s) critique(s) à compléter (mesure périmée ou question obligatoire sans réponse)`
    : warning ? `⚡ ${impFail} critère(s) important(s) à optimiser`
    : `✗ BLOQUÉ — ${critFail} critère(s) critique(s) non atteint(s)`;

  return (
    <div style={{ background:bg, border:`1.5px solid ${col}33`, borderRadius:8, padding:"10px 14px", marginBottom:10 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
        <span style={{ fontFamily:"'Segoe UI',sans-serif", fontSize:11, fontWeight:700, color:col }}>{label}</span>
        <span style={{ fontFamily:"'Segoe UI',sans-serif", fontSize:13, fontWeight:900, color:col }}>{globalScore}%</span>
      </div>
      <div style={{ width:"100%", height:5, background:C.border, borderRadius:5 }}>
        <div style={{ width:`${globalScore}%`, height:"100%", background:col, borderRadius:5, transition:"width 0.4s" }}/>
      </div>
      <div style={{ display:"flex", gap:6, marginTop:7 }}>
        {[
          { label:`● Critique ${critItems.filter(i=>i.pass).length}/${critItems.length}`, col:C.red },
          { label:`◆ Important ${impItems.filter(i=>i.pass).length}/${impItems.length}`, col:C.amber },
          { label:`○ Informatif ${infItems.filter(i=>i.pass).length}/${infItems.length}`, col:C.blue },
        ].map(b => (
          <div key={b.label} style={{ flex:1, textAlign:"center", fontSize:8.5, fontWeight:700, color:b.col,
            background:`${b.col}10`, border:`1px solid ${b.col}33`, borderRadius:4, padding:"3px 0",
            fontFamily:"'Segoe UI',sans-serif" }}>{b.label}</div>
        ))}
      </div>
      {staleCount > 0 && (
        <div style={{ marginTop:6, fontSize:9, color:C.blueMid, fontFamily:"'Segoe UI',sans-serif" }}>
          ↻ {staleCount} mesure(s) périmée(s) exclue(s) du score (force/hops/isocinétique &gt; 6 sem · épanchement/douleur/ROM &gt; 7 j)
        </div>
      )}
    </div>
  );
}

// ── NUMBER INPUT (fixes mobile keyboard dismiss) ─────────────
function NumInput({ value, onChange, placeholder="0", style={} }) {
  const ref = useRef(null);
  const [local, setLocal] = useState(value === 0 || value === null ? "" : String(value));

  useEffect(() => {
    setLocal(value === 0 || value === null ? "" : String(value));
  }, [value]);

  const commit = useCallback(() => {
    const n = parseFloat(local);
    onChange(isNaN(n) ? 0 : n);
  }, [local, onChange]);

  return (
    <input
      ref={ref}
      type="number"
      inputMode="decimal"
      value={local}
      placeholder={placeholder}
      onChange={e => setLocal(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === "Enter") { commit(); ref.current?.blur(); } }}
      style={{ background:C.white, border:`1.5px solid ${C.borderMid}`, borderRadius:6,
        padding:"6px 8px", color:C.textPrimary, fontSize:13, fontWeight:600,
        fontFamily:"'Segoe UI',sans-serif", width:"100%", boxSizing:"border-box", ...style }}
    />
  );
}

// ── LSI INPUT ─────────────────────────────────────────────────
function LSIInput({ c, value, onChange, pass }) {
  const v = value ?? { h:0, i:0 };
  const computed = v.h > 0 && v.i > 0 ? lsi(v.h, v.i) : null;
  const col = computed !== null ? (pass ? C.green : C.red) : C.textMuted;

  return (
    <div style={{ paddingLeft:28, marginTop:8 }}>
      <div style={{ display:"flex", gap:6, alignItems:"flex-end" }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:8.5, color:C.textMuted, marginBottom:3, textTransform:"uppercase", letterSpacing:"0.08em", fontFamily:"'Segoe UI',sans-serif" }}>CÔTÉ SAIN</div>
          <NumInput value={v.h} onChange={n => onChange({ ...v, h:n })} placeholder="ex: 180"/>
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:8.5, color:C.textMuted, marginBottom:3, textTransform:"uppercase", letterSpacing:"0.08em", fontFamily:"'Segoe UI',sans-serif" }}>CÔTÉ LÉSÉ</div>
          <NumInput value={v.i} onChange={n => onChange({ ...v, i:n })} placeholder="ex: 130"/>
        </div>
        <div style={{ flexShrink:0, textAlign:"center", minWidth:60 }}>
          <div style={{ fontSize:8.5, color:C.textMuted, marginBottom:3, textTransform:"uppercase", letterSpacing:"0.08em", fontFamily:"'Segoe UI',sans-serif" }}>LSI</div>
          <div style={{ background: computed !== null ? `${col}15` : C.surface,
            border:`1.5px solid ${computed !== null ? col+"55" : C.border}`,
            borderRadius:6, padding:"5px 0", fontSize:15, fontWeight:800,
            color: computed !== null ? col : C.textMuted, textAlign:"center",
            fontFamily:"'Segoe UI',sans-serif", transition:"all 0.2s" }}>
            {computed !== null ? `${computed}%` : "—"}
          </div>
        </div>
      </div>
      <div style={{ marginTop:4, fontSize:8.5, color:C.textMuted, fontFamily:"'Segoe UI',sans-serif" }}>
        Seuil : ≥ {c.threshold}%
      </div>
    </div>
  );
}

// ── ISOK FULL TABLE ───────────────────────────────────────────
function IsokInput({ c, value, onChange }) {
  const v = value ?? getDefault(c);
  const norms = ISOK_NORMS[c.phase ?? "rtr"];
  const injSide = v.side ?? "left";

  const setField = (field, val) => onChange({ ...v, [field]: val });

  // Compute all metrics
  const makeRow = (lVal, rVal, normDef, isIJ=false) => {
    if (!lVal || !rVal) return { def:null, pass:false };
    const sain  = injSide === "left" ? rVal : lVal;
    const lese  = injSide === "left" ? lVal : rVal;
    const defPct = deficit(sain, lese);
    const stronger = lese > sain;
    return { def:defPct, pass: defPct !== null && defPct <= normDef, stronger };
  };

  const q60   = makeRow(v.qL60,   v.qR60,   norms.quadDeficit);
  const q240  = makeRow(v.qL240,  v.qR240,  norms.quadDeficit);
  const ij60  = makeRow(v.ijL60,  v.ijR60,  norms.ijDeficit);
  const ij240 = makeRow(v.ijL240, v.ijR240, norms.ijDeficit);
  const ije30 = makeRow(v.ijLe30, v.ijRe30, norms.ijDeficit);

  const hqL60   = v.qL60  > 0 ? Math.round((v.ijL60  / v.qL60)  * 100) / 100 : null;
  const hqR60   = v.qR60  > 0 ? Math.round((v.ijR60  / v.qR60)  * 100) / 100 : null;
  const hqL240  = v.qL240 > 0 ? Math.round((v.ijL240 / v.qL240) * 100) / 100 : null;
  const hqR240  = v.qR240 > 0 ? Math.round((v.ijR240 / v.qR240) * 100) / 100 : null;
  const mixL    = v.qL240 > 0 ? Math.round((v.ijLe30 / v.qL240) * 100) / 100 : null;
  const mixR    = v.qR240 > 0 ? Math.round((v.ijRe30 / v.qR240) * 100) / 100 : null;
  const nmkgL   = v.weight > 0 ? Math.round((v.qL60 / v.weight) * 10) / 10 : null;
  const nmkgR   = v.weight > 0 ? Math.round((v.qR60 / v.weight) * 10) / 10 : null;

  const cellStyle = { padding:"5px 6px", borderRight:`1px solid ${C.border}`, textAlign:"center", fontFamily:"'Segoe UI',sans-serif", fontSize:12 };
  const headStyle = { ...cellStyle, background:C.blue, color:C.white, fontWeight:700, fontSize:10, letterSpacing:"0.06em" };

  const DefCell = ({ row }) => {
    if (row.def === null) return <td style={cellStyle}>—</td>;
    const col = row.pass ? C.green : C.red;
    return <td style={{ ...cellStyle, color:col, fontWeight:700, background:`${col}0D` }}>
      {row.stronger ? "+" : "−"}{row.def}%
    </td>;
  };

  const ValCell = ({ val }) => <td style={cellStyle}>{val != null && val > 0 ? val : "—"}</td>;
  const RatioCell = ({ val, normMin, normMax }) => {
    if (val === null) return <td style={cellStyle}>—</td>;
    const ok = normMax ? (val >= normMin && val <= normMax) : val >= normMin;
    return <td style={{ ...cellStyle, color: ok ? C.green : C.amber, fontWeight:700 }}>{val.toFixed(2)}</td>;
  };

  return (
    <div style={{ paddingLeft:4, marginTop:10 }}>
      {/* Header controls */}
      <div style={{ display:"flex", gap:8, marginBottom:10, flexWrap:"wrap" }}>
        <div style={{ flex:2, minWidth:120 }}>
          <div style={{ fontSize:9, color:C.textMuted, marginBottom:3, textTransform:"uppercase", letterSpacing:"0.08em", fontFamily:"'Segoe UI',sans-serif" }}>CÔTÉ LÉSÉ</div>
          <div style={{ display:"flex", gap:4 }}>
            {["left","right"].map(s => (
              <button key={s} onClick={() => setField("side", s)}
                style={{ flex:1, background: v.side === s ? C.orange : C.surface,
                  border:`1.5px solid ${v.side === s ? C.orange : C.border}`,
                  borderRadius:5, padding:"5px 0", color: v.side === s ? C.white : C.textSecondary,
                  fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"'Segoe UI',sans-serif" }}>
                {s === "left" ? "GAUCHE" : "DROITE"}
              </button>
            ))}
          </div>
        </div>
        <div style={{ flex:1, minWidth:80 }}>
          <div style={{ fontSize:9, color:C.textMuted, marginBottom:3, textTransform:"uppercase", letterSpacing:"0.08em", fontFamily:"'Segoe UI',sans-serif" }}>POIDS (kg)</div>
          <NumInput value={v.weight} onChange={n => setField("weight", n)} placeholder="70"/>
        </div>
        <div style={{ flex:2, minWidth:120 }}>
          <div style={{ fontSize:9, color:C.textMuted, marginBottom:3, textTransform:"uppercase", letterSpacing:"0.08em", fontFamily:"'Segoe UI',sans-serif" }}>📅 DATE DU TEST ISOK</div>
          <input type="date" value={v.isokDate ?? ""} onChange={e => setField("isokDate", e.target.value)}
            style={{ width:"100%", background:C.white, border:`1.5px solid ${C.borderMid}`,
              borderRadius:6, padding:"5px 8px", color:C.textPrimary,
              fontSize:11, fontWeight:600, fontFamily:"'Segoe UI',sans-serif", boxSizing:"border-box" }}/>
          {v.isokDate && (
            <div style={{ fontSize:8, color:C.textMuted, marginTop:2, fontFamily:"'Segoe UI',sans-serif" }}>
              {new Date(v.isokDate).toLocaleDateString("fr-BE",{day:"2-digit",month:"long",year:"numeric"})}
            </div>
          )}
        </div>
      </div>

      {/* QUADRICEPS TABLE */}
      <div style={{ marginBottom:10 }}>
        <div style={{ fontSize:9.5, fontWeight:700, color:C.orange, marginBottom:4, fontFamily:"'Segoe UI',sans-serif", letterSpacing:"0.08em" }}>QUADRICEPS</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", border:`1px solid ${C.border}`, borderRadius:6, overflow:"hidden", fontSize:12 }}>
            <thead>
              <tr>
                <th style={{ ...headStyle, textAlign:"left" }}>Mode</th>
                <th style={headStyle}>Gauche (Nm)</th>
                <th style={headStyle}>Droite (Nm)</th>
                <th style={{ ...headStyle, color: C.amberL }}>Déficit</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom:`1px solid ${C.border}` }}>
                <td style={{ ...cellStyle, textAlign:"left", color:C.textSecondary, fontWeight:600 }}>Conc 60°/s</td>
                <td style={{ ...cellStyle, padding:0 }}><NumInput value={v.qL60}  onChange={n=>setField("qL60",n)}  style={{ border:"none", borderRadius:0 }}/></td>
                <td style={{ ...cellStyle, padding:0 }}><NumInput value={v.qR60}  onChange={n=>setField("qR60",n)}  style={{ border:"none", borderRadius:0 }}/></td>
                <DefCell row={q60}/>
              </tr>
              <tr>
                <td style={{ ...cellStyle, textAlign:"left", color:C.textSecondary, fontWeight:600 }}>Conc 240°/s</td>
                <td style={{ ...cellStyle, padding:0 }}><NumInput value={v.qL240} onChange={n=>setField("qL240",n)} style={{ border:"none", borderRadius:0 }}/></td>
                <td style={{ ...cellStyle, padding:0 }}><NumInput value={v.qR240} onChange={n=>setField("qR240",n)} style={{ border:"none", borderRadius:0 }}/></td>
                <DefCell row={q240}/>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* IJ TABLE */}
      <div style={{ marginBottom:10 }}>
        <div style={{ fontSize:9.5, fontWeight:700, color:C.blue, marginBottom:4, fontFamily:"'Segoe UI',sans-serif", letterSpacing:"0.08em" }}>ISCHIO-JAMBIERS</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", border:`1px solid ${C.border}`, borderRadius:6, overflow:"hidden", fontSize:12 }}>
            <thead>
              <tr>
                <th style={{ ...headStyle, textAlign:"left" }}>Mode</th>
                <th style={headStyle}>Gauche (Nm)</th>
                <th style={headStyle}>Droite (Nm)</th>
                <th style={{ ...headStyle, color:C.amberL }}>Déficit</th>
              </tr>
            </thead>
            <tbody>
              {[
                { label:"Conc 60°/s",  lk:"ijL60",  rk:"ijR60",  row:ij60 },
                { label:"Conc 240°/s", lk:"ijL240", rk:"ijR240", row:ij240 },
                { label:"Exc 30°/s",   lk:"ijLe30", rk:"ijRe30", row:ije30 },
              ].map((r, i) => (
                <tr key={r.label} style={{ borderBottom: i < 2 ? `1px solid ${C.border}` : "none" }}>
                  <td style={{ ...cellStyle, textAlign:"left", color:C.textSecondary, fontWeight:600 }}>{r.label}</td>
                  <td style={{ ...cellStyle, padding:0 }}><NumInput value={v[r.lk]} onChange={n=>setField(r.lk,n)} style={{ border:"none", borderRadius:0 }}/></td>
                  <td style={{ ...cellStyle, padding:0 }}><NumInput value={v[r.rk]} onChange={n=>setField(r.rk,n)} style={{ border:"none", borderRadius:0 }}/></td>
                  <DefCell row={r.row}/>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* RATIOS TABLE */}
      <div style={{ marginBottom:10 }}>
        <div style={{ fontSize:9.5, fontWeight:700, color:C.anthracite, marginBottom:4, fontFamily:"'Segoe UI',sans-serif", letterSpacing:"0.08em" }}>RAPPORTS IJ/Q</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", border:`1px solid ${C.border}`, borderRadius:6, overflow:"hidden", fontSize:12 }}>
            <thead>
              <tr>
                <th style={{ ...headStyle, textAlign:"left" }}>Ratio</th>
                <th style={headStyle}>Gauche</th>
                <th style={headStyle}>Droite</th>
                <th style={headStyle}>Norme</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom:`1px solid ${C.border}` }}>
                <td style={{ ...cellStyle, textAlign:"left", color:C.textSecondary, fontWeight:600 }}>IJ/Q conc 60°/s</td>
                <RatioCell val={hqL60}  normMin={norms.hq60[0]} normMax={norms.hq60[1]}/>
                <RatioCell val={hqR60}  normMin={norms.hq60[0]} normMax={norms.hq60[1]}/>
                <td style={{ ...cellStyle, color:C.textMuted, fontSize:10 }}>{norms.hq60[0]}–{norms.hq60[1]}</td>
              </tr>
              <tr style={{ borderBottom:`1px solid ${C.border}` }}>
                <td style={{ ...cellStyle, textAlign:"left", color:C.textSecondary, fontWeight:600 }}>IJ/Q conc 240°/s</td>
                <RatioCell val={hqL240} normMin={norms.hq240[0]} normMax={norms.hq240[1]}/>
                <RatioCell val={hqR240} normMin={norms.hq240[0]} normMax={norms.hq240[1]}/>
                <td style={{ ...cellStyle, color:C.textMuted, fontSize:10 }}>{norms.hq240[0]}–{norms.hq240[1]}</td>
              </tr>
              <tr>
                <td style={{ ...cellStyle, textAlign:"left", color:C.textSecondary, fontWeight:600 }}>IJ exc30 / Q 240</td>
                <RatioCell val={mixL} normMin={norms.mixed}/>
                <RatioCell val={mixR} normMin={norms.mixed}/>
                <td style={{ ...cellStyle, color:C.textMuted, fontSize:10 }}>&gt; {norms.mixed}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Nm/kg TABLE */}
      <div>
        <div style={{ fontSize:9.5, fontWeight:700, color:C.anthracite, marginBottom:4, fontFamily:"'Segoe UI',sans-serif", letterSpacing:"0.08em" }}>FORCE RELATIVE (Nm/kg) — Q conc 60°/s</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", border:`1px solid ${C.border}`, borderRadius:6, overflow:"hidden", fontSize:12 }}>
            <thead>
              <tr>
                <th style={{ ...headStyle, textAlign:"left" }}>Test</th>
                <th style={headStyle}>Gauche</th>
                <th style={headStyle}>Droite</th>
                <th style={headStyle}>Norme</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ ...cellStyle, textAlign:"left", color:C.textSecondary, fontWeight:600 }}>MFM Q conc 60°/s</td>
                {[nmkgL, nmkgR].map((val, idx) => {
                  const norm = c.phase === "rts" ? norms.nmkg : null;
                  const normR = c.phase === "rts" ? null : norms.nmkg;
                  const ok = val !== null && (
                    c.phase === "rts" ? val >= norms.nmkg :
                    (val >= norms.nmkg[0] && val <= norms.nmkg[1])
                  );
                  const col = val !== null ? (ok ? C.green : C.red) : C.textMuted;
                  return <td key={idx} style={{ ...cellStyle, color:col, fontWeight:700, background: val !== null ? `${col}0D` : "transparent" }}>
                    {val !== null ? val : "—"}
                  </td>;
                })}
                <td style={{ ...cellStyle, color:C.textMuted, fontSize:10 }}>
                  {c.phase === "rts" ? `≥ ${norms.nmkg}` : `${norms.nmkg[0]}–${norms.nmkg[1]}`}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Global pass indicator */}
      <div style={{ marginTop:10, padding:"7px 10px",
        background: isPass(c, v) ? C.greenBg : C.redBg,
        border:`1px solid ${isPass(c, v) ? C.green+"55" : C.red+"55"}`,
        borderRadius:6, fontSize:10, fontWeight:700,
        color: isPass(c, v) ? C.green : C.red, fontFamily:"'Segoe UI',sans-serif" }}>
        {isPass(c, v)
          ? `✓ Déficit quad ≤ ${norms.quadDeficit}% — critère isocinétique validé`
          : `✗ Déficit quad > ${norms.quadDeficit}% à 60°/s — gate bloquant`}
      </div>
    </div>
  );
}

// ── OVERRIDE PANEL ────────────────────────────────────────────
function OverridePanel({ override, onChange }) {
  return (
    <div style={{ marginTop:8, paddingLeft:28 }}>
      <div style={{ background:C.amberBg, border:`1px solid ${C.amberL}55`, borderRadius:6, padding:"9px 11px" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
          <span style={{ fontSize:9, fontWeight:700, color:C.amber, fontFamily:"'Segoe UI',sans-serif", letterSpacing:"0.07em" }}>
            ◆ OVERRIDE PRATICIEN — CRITÈRE IMPORTANT
          </span>
          <button onClick={() => onChange({ ...override, active:!override?.active })}
            style={{ background: override?.active ? C.amber : C.surface,
              border:`1px solid ${override?.active ? C.amber : C.borderMid}`,
              borderRadius:4, padding:"3px 9px", cursor:"pointer",
              color: override?.active ? C.white : C.textSecondary,
              fontSize:9, fontWeight:700, fontFamily:"'Segoe UI',sans-serif" }}>
            {override?.active ? "ACTIF" : "ACTIVER"}
          </button>
        </div>
        {override?.active && (
          <>
            <div style={{ fontSize:8.5, color:C.textSecondary, marginBottom:4 }}>Justification clinique obligatoire :</div>
            <textarea value={override?.note ?? ""} rows={2}
              onChange={e => onChange({ ...override, note:e.target.value })}
              placeholder="Ex: LSI quad 88% (seuil 90%) — M9, tous autres critères RTS atteints. Décision concertée Dr Lepiece."
              style={{ width:"100%", background:C.white, border:`1px solid ${C.borderMid}`,
                borderRadius:5, padding:"6px 8px", color:C.textPrimary, fontSize:10,
                lineHeight:1.5, resize:"vertical", boxSizing:"border-box", fontFamily:"'Segoe UI',sans-serif" }}/>
            {!override?.note?.trim() &&
              <div style={{ fontSize:8.5, color:C.red, marginTop:3, fontFamily:"'Segoe UI',sans-serif" }}>
                ⚠ Justification requise
              </div>}
          </>
        )}
      </div>
    </div>
  );
}

// ── CRITERION CARD ────────────────────────────────────────────
function Card({ c, value, onChange, showSource, view, override, onOverride }) {
  const ovValid = override?.active && override?.note?.trim();
  const pass = c.pass ?? isPass(c, value);
  const cr = CRIT[c.criticality];
  const passCol = pass ? C.green : cr.color;

  return (
    <div style={{ background:C.white, border:`1.5px solid ${pass ? C.green+"44" : c.criticality === "critical" && !pass ? C.red+"33" : C.border}`,
      borderRadius:8, padding:"11px 13px", marginBottom:6, boxShadow:"0 1px 3px rgba(0,0,0,0.05)", transition:"border-color 0.2s" }}>
      <div style={{ display:"flex", alignItems:"flex-start", gap:9, marginBottom: c.inputType === "toggle" ? 0 : 9 }}>
        {/* Pass dot */}
        <div style={{ width:20, height:20, borderRadius:"50%", flexShrink:0, marginTop:2,
          background: pass ? C.greenBg : `${cr.color}10`,
          border:`1.5px solid ${pass ? C.green : cr.color}`,
          display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:9, color: pass ? C.green : cr.color, fontWeight:900, transition:"all 0.2s" }}>
          {pass ? "✓" : "·"}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:5, flexWrap:"wrap", marginBottom:3 }}>
            <span style={{ fontSize:12.5, fontWeight:600, color:C.textPrimary, lineHeight:1.3 }}>{c.label}</span>
            <ScrBadge color={cr.color} bg={cr.bg} border={cr.border} small>{cr.dot} {cr.label}</ScrBadge>
            {ovValid && !c.rawPass && !c.stale && <ScrBadge color={C.amber} bg={C.amberBg} border={C.amberL+"55"} small>OVERRIDE</ScrBadge>}
            {c.measuredOn && !c.stale && <ScrBadge color={C.textMuted} bg={C.surface} border={C.border} small>📅 mesuré le {dateStrToDisplay(c.measuredOn)}</ScrBadge>}
            {c.stale && (c.inputType === "giving_way"
              ? <ScrBadge color={C.blueMid} bg={C.blueLight} border={C.blueMid+"44"} small>↻ À RETESTER · réponse du jour requise</ScrBadge>
              : <ScrBadge color={C.blueMid} bg={C.blueLight} border={C.blueMid+"44"} small>↻ À RETESTER · {dateStrToDisplay(c.stale.date)} ({c.stale.ageDays} j &gt; {c.stale.maxDays} j)</ScrBadge>)}
          </div>
          <div style={{ fontSize:10.5, color:C.textSecondary, lineHeight:1.5 }}>{c.desc}</div>
          {showSource && view === "practitioner" &&
            <div style={{ fontSize:9, color:C.blue, marginTop:2, fontFamily:"'Segoe UI',sans-serif" }}>📖 {c.source}</div>}
        </div>
        {c.inputType !== "isok" && c.inputType !== "sprint_time" && (
          <div style={{ background: pass ? C.greenBg : C.surface,
            border:`1px solid ${pass ? C.green+"55" : C.border}`,
            borderRadius:5, padding:"3px 8px", fontSize:12, fontWeight:800,
            color: pass ? C.green : C.textMuted, flexShrink:0, minWidth:42,
            textAlign:"center", fontFamily:"'Segoe UI',sans-serif" }}>
            {dispVal(c, c.inputType === "giving_way" && c.stale ? undefined : value)}
          </div>
        )}
      </div>

      {/* SLIDER */}
      {c.inputType === "slider" && (
        <div style={{ paddingLeft:28 }}>
          <div style={{ display:"flex", alignItems:"center", gap:5 }}>
            <span style={{ fontSize:9, color:C.textMuted, minWidth:24, textAlign:"right", fontFamily:"'Segoe UI',sans-serif" }}>{c.min}</span>
            <div style={{ flex:1, position:"relative" }}>
              <input type="range" min={c.min} max={c.max} step={c.step} value={value ?? c.min}
                onChange={e => onChange(parseFloat(e.target.value))}
                style={{ width:"100%", height:3, accentColor: pass ? C.green : cr.color, cursor:"pointer", display:"block" }}/>
              <div style={{ position:"absolute", top:-4,
                left:`${Math.min(97,Math.max(2,((c.threshold-c.min)/(c.max-c.min))*100))}%`,
                transform:"translateX(-50%)", width:2, height:11,
                background:C.borderMid, borderRadius:1, pointerEvents:"none" }}/>
            </div>
            <span style={{ fontSize:9, color:C.textMuted, minWidth:24, fontFamily:"'Segoe UI',sans-serif" }}>{c.max}</span>
          </div>
          <div style={{ paddingLeft:29, fontSize:8.5, color:C.textMuted, marginTop:2, fontFamily:"'Segoe UI',sans-serif" }}>
            Seuil {c.direction === "gte" ? "≥" : "≤"} {c.threshold}{c.unit}
          </div>
        </div>
      )}

      {/* NUMBER */}
      {c.inputType === "number" && (
        <div style={{ paddingLeft:28 }}>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <div style={{ width:100 }}>
              <NumInput value={value} onChange={onChange} placeholder={`ex: ${c.threshold}`}/>
            </div>
            <span style={{ fontSize:9, color:C.textMuted, fontFamily:"'Segoe UI',sans-serif" }}>
              {c.unit} · seuil {c.direction === "gte" ? "≥" : "≤"} {c.threshold}
            </span>
          </div>
        </div>
      )}

      {/* VALEUR CALCULÉE (V1.15) */}
      {c.inputType === "auto" && (
        <div style={{ paddingLeft:28, fontSize:9.5, color:C.blueMid, fontFamily:"'Segoe UI',sans-serif" }}>
          ⚙ {c.autoNote}{value === undefined ? " — donnée manquante (date d'opération ou décision non enregistrée)" : ""} · Seuil {c.direction === "gte" ? "≥" : "≤"} {c.threshold}{c.unit ? ` ${c.unit}` : ""}
        </div>
      )}

      {/* QUESTION DÉROBEMENT (V1.14) */}
      {c.inputType === "giving_way" && (
        <div style={{ paddingLeft:28, marginTop:7 }}>
          <GivingWayQuestion answer={c.stale ? undefined : value} onAnswer={onChange} />
          {c.giving && <div style={{ marginTop:6 }}><GivingWayInput ctx={c.giving.ctx} events={c.giving.events} onDeclare={c.giving.onDeclare} defaultDate={c.giving.ctx.date} /></div>}
        </div>
      )}

      {/* TOGGLE */}
      {c.inputType === "toggle" && (
        <div style={{ paddingLeft:28, display:"flex", gap:5, marginTop:7 }}>
          {[0,1].map(v => (
            <button key={v} onClick={() => onChange(v)}
              style={{ background: value === v ? (v === 1 ? C.greenBg : C.redBg) : C.surface,
                border:`1.5px solid ${value === v ? (v === 1 ? C.green+"66" : C.red+"66") : C.border}`,
                borderRadius:6, padding:"6px 0",
                color: value === v ? (v === 1 ? C.green : C.red) : C.textMuted,
                fontSize:10.5, fontWeight:700, cursor:"pointer", flex:1,
                fontFamily:"'Segoe UI',sans-serif", letterSpacing:"0.05em" }}>
              {v === 1 ? "✓ OUI" : "✗ NON"}
            </button>
          ))}
        </div>
      )}

      {/* SELECT */}
      {c.inputType === "select" && (
        <div style={{ paddingLeft:28, display:"flex", flexDirection:"column", gap:4, marginTop:6 }}>
          {c.options.map((opt, idx) => {
            const ok = c.direction === "gte" ? idx >= c.threshold : idx <= c.threshold;
            const sel = (value ?? 0) === idx;
            return (
              <button key={idx} onClick={() => onChange(idx)}
                style={{ background: sel ? (ok ? C.greenBg : C.redBg) : C.surface,
                  border:`1px solid ${sel ? (ok ? C.green+"55" : C.red+"55") : C.border}`,
                  borderRadius:5, padding:"7px 10px",
                  color: sel ? (ok ? C.green : C.red) : C.textSecondary,
                  fontSize:10.5, fontWeight: sel ? 700 : 400, cursor:"pointer", textAlign:"left",
                  display:"flex", alignItems:"center", gap:6 }}>
                <span style={{ width:4, height:4, borderRadius:"50%", background: ok ? C.green+"66" : C.red+"33", flexShrink:0 }}/>
                {opt}
              </button>
            );
          })}
        </div>
      )}

      {c.inputType === "lsi"  && <LSIInput c={c} value={value} onChange={onChange} pass={pass}/>}
      {c.inputType === "isok" && <IsokInput c={c} value={value} onChange={onChange}/>}

      {c.inputType === "sprint_time" && (
        <div style={{ paddingLeft:28, marginTop:8 }}>
          <div style={{ display:"flex", gap:8, alignItems:"flex-end" }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:8.5, color:C.textMuted, marginBottom:3, textTransform:"uppercase", letterSpacing:"0.08em", fontFamily:"'Segoe UI',sans-serif" }}>TEMPS (secondes)</div>
              <NumInput value={value?.time ?? 0} onChange={n => onChange({ ...(value??{}), time:n })} placeholder="ex: 1.82"/>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:8.5, color:C.textMuted, marginBottom:3, textTransform:"uppercase", letterSpacing:"0.08em", fontFamily:"'Segoe UI',sans-serif" }}>📅 DATE DU TEST</div>
              <input type="date" value={value?.date ?? ""} onChange={e => onChange({ ...(value??{}), date:e.target.value })}
                style={{ width:"100%", background:C.white, border:`1.5px solid ${C.borderMid}`,
                  borderRadius:6, padding:"5px 8px", color:C.textPrimary,
                  fontSize:11, fontWeight:600, fontFamily:"'Segoe UI',sans-serif", boxSizing:"border-box" }}/>
            </div>
          </div>
          {value?.date && (
            <div style={{ fontSize:8, color:C.textMuted, marginTop:3, fontFamily:"'Segoe UI',sans-serif" }}>
              Mesuré le {new Date(value.date).toLocaleDateString("fr-BE",{day:"2-digit",month:"long",year:"numeric"})}
              {value?.time ? ` — ${value.time} s` : ""}
            </div>
          )}
          <div style={{ fontSize:8.5, color:C.textMuted, marginTop:4, fontFamily:"'Segoe UI',sans-serif" }}>
            Donnée informative — pas de seuil LSI pour ce test
          </div>
        </div>
      )}

      {/* Override */}
      {!c.rawPass && c.criticality === "important" && view === "practitioner" && (
        <OverridePanel override={override} onChange={onOverride}/>
      )}
    </div>
  );
}

// ── PATIENT VIEW ──────────────────────────────────────────────
function PatientView({ res, fromPhase, toPhase }) {
  if (!res) return null;
  const { blocked, warning, validated, globalScore, critFail, impFail, critItems, impItems } = res;
  const col = validated ? C.green : warning ? C.amber : C.red;
  const msg = validated ? "Tu es prêt(e) pour la prochaine étape !"
    : warning ? `${impFail} objectif${impFail > 1 ? "s" : ""} important${impFail > 1 ? "s" : ""} à atteindre`
    : `${critFail} test${critFail > 1 ? "s" : ""} obligatoire${critFail > 1 ? "s" : ""} à valider`;

  return (
    <div style={{ padding:"20px 16px", maxWidth:380, margin:"0 auto", textAlign:"center" }}>
      <div style={{ fontFamily:"'Segoe UI',sans-serif", fontSize:11, color:C.orange, letterSpacing:"0.3em", marginBottom:6 }}>CAMPUS LCA</div>
      <div style={{ fontFamily:"'Segoe UI',sans-serif", fontSize:20, color:C.textPrimary, letterSpacing:"0.06em", marginBottom:20 }}>
        {fromPhase.label} <span style={{ color:C.greyLight }}>→</span> {toPhase.label} {toPhase.name}
      </div>

      {/* Ring */}
      <div style={{ display:"flex", justifyContent:"center", marginBottom:16 }}>
        <svg width={130} height={130} style={{ transform:"rotate(-90deg)" }}>
          <circle cx={65} cy={65} r={55} fill="none" stroke={C.border} strokeWidth="8"/>
          <circle cx={65} cy={65} r={55} fill="none" stroke={col} strokeWidth="8"
            strokeDasharray={`${(globalScore/100)*345} 345`} strokeLinecap="round"
            style={{ transition:"stroke-dasharray 0.5s ease" }}/>
          <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" fill={col}
            fontSize="22" fontWeight="800"
            style={{ transform:"rotate(90deg)", transformOrigin:"center", fontFamily:"'Segoe UI',sans-serif" }}>
            {globalScore}%
          </text>
        </svg>
      </div>

      <div style={{ background:`${col}12`, border:`1.5px solid ${col}44`, borderRadius:8, padding:"12px 16px", marginBottom:20 }}>
        <div style={{ fontSize:14, fontWeight:700, color:col, marginBottom:4 }}>{msg}</div>
        <div style={{ fontSize:10, color:C.textSecondary }}>
          {critItems.filter(i=>i.pass).length}/{critItems.length} critiques · {impItems.filter(i=>i.pass).length}/{impItems.length} importants
        </div>
      </div>

      {[
        { items:critItems, label:"TESTS OBLIGATOIRES", col:C.red },
        { items:impItems,  label:"OBJECTIFS IMPORTANTS", col:C.amber },
      ].map(grp => grp.items.length > 0 && (
        <div key={grp.label} style={{ textAlign:"left", marginBottom:12 }}>
          <div style={{ fontSize:8.5, fontWeight:700, color:grp.col, marginBottom:6, letterSpacing:"0.12em",
            fontFamily:"'Segoe UI',sans-serif", borderBottom:`1px solid ${C.border}`, paddingBottom:4 }}>
            {grp.label}
          </div>
          {grp.items.map(c => (
            <div key={c.id} style={{ display:"flex", alignItems:"center", gap:6, marginBottom:5 }}>
              <div style={{ width:14, height:14, borderRadius:"50%",
                background: c.pass ? C.greenBg : `${grp.col}10`,
                border:`1px solid ${c.pass ? C.green : grp.col}`,
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:7, color: c.pass ? C.green : grp.col, fontWeight:900, flexShrink:0 }}>
                {c.pass ? "✓" : "·"}
              </div>
              <span style={{ fontSize:10, color: c.pass ? C.textMuted : C.textSecondary }}>{c.label}</span>
            </div>
          ))}
        </div>
      ))}

      <div style={{ fontSize:8, color:C.textMuted, fontFamily:"'Segoe UI',sans-serif", marginTop:16,
        borderTop:`1px solid ${C.border}`, paddingTop:10, letterSpacing:"0.05em" }}>
        VOTRE RETOUR N'EST PAS UNE DATE. C'EST UN SCORE.
      </div>
    </div>
  );
}

// ── PATIENT FORM ──────────────────────────────────────────────
const EMPTY_PATIENT = {
  nom:"", prenom:"", ddn:"", sport:"",
  dateBlessure:"", dateOp:"", coteOpere:"", typeGreffe:"",
  let_:"non", pathoAssociee:"", commentaire:"",
};

function PatientForm({ patient, onChange }) {
  const field = (key, label, type="text", opts=null) => (
    <div style={{ marginBottom:10 }}>
      <label style={{ display:"block", fontSize:9.5, fontWeight:700, color:C.textSecondary,
        marginBottom:3, textTransform:"uppercase", letterSpacing:"0.08em", fontFamily:"'Segoe UI',sans-serif" }}>
        {label}
      </label>
      {opts ? (
        opts.length > 4 ? (
          <select value={patient[key] ?? ""} onChange={e => onChange({ ...patient, [key]:e.target.value })}
            style={{ width:"100%", background:C.white, border:`1.5px solid ${C.borderMid}`,
              borderRadius:6, padding:"7px 10px", color:C.textPrimary,
              fontSize:12, fontWeight:500, boxSizing:"border-box",
              fontFamily:"'Segoe UI',sans-serif" }}>
            <option value="">—</option>
            {opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        ) : (
          <div style={{ display:"flex", gap:4 }}>
            {opts.map(o => (
              <button key={o.v} onClick={() => onChange({ ...patient, [key]:o.v })}
                style={{ flex:1, background: patient[key] === o.v ? C.orange : C.surface,
                  border:`1.5px solid ${patient[key] === o.v ? C.orange : C.border}`,
                  borderRadius:5, padding:"6px 0",
                  color: patient[key] === o.v ? C.white : C.textSecondary,
                  fontSize:10.5, fontWeight:700, cursor:"pointer",
                  fontFamily:"'Segoe UI',sans-serif" }}>
                {o.l}
              </button>
            ))}
          </div>
        )
      ) : type === "textarea" ? (
        <textarea value={patient[key] ?? ""} rows={2}
          onChange={e => onChange({ ...patient, [key]:e.target.value })}
          style={{ width:"100%", background:C.white, border:`1.5px solid ${C.borderMid}`,
            borderRadius:6, padding:"7px 10px", color:C.textPrimary,
            fontSize:12, lineHeight:1.5, resize:"vertical",
            boxSizing:"border-box", fontFamily:"'Segoe UI',sans-serif" }}/>
      ) : (
        <input type={type} value={patient[key] ?? ""}
          onChange={e => onChange({ ...patient, [key]:e.target.value })}
          style={{ width:"100%", background:C.white, border:`1.5px solid ${C.borderMid}`,
            borderRadius:6, padding:"7px 10px", color:C.textPrimary,
            fontSize:12, fontWeight:500, boxSizing:"border-box",
            fontFamily:"'Segoe UI',sans-serif" }}/>
      )}
    </div>
  );

  return (
    <div style={{ background:C.white, border:`1.5px solid ${C.border}`, borderRadius:10,
      padding:"16px", boxShadow:"0 2px 6px rgba(0,0,0,0.05)", marginBottom:12 }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14,
        paddingBottom:10, borderBottom:`1px solid ${C.border}` }}>
        <span style={{ fontSize:14 }}>👤</span>
        <span style={{ fontSize:13, fontWeight:700, color:C.textPrimary }}>Informations patient</span>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" }}>
        <div>{field("prenom","Prénom")}</div>
        <div>{field("nom","Nom")}</div>
        <div>{field("ddn","Date de naissance","date")}</div>
        <div>{field("sport","Sport pratiqué")}</div>
        <div>{field("dateBlessure","Date de blessure","date")}</div>
        <div>{field("dateOp","Date d'opération","date")}</div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" }}>
        <div>
          {field("coteOpere","Côté opéré", null,
            [{v:"gauche",l:"GAUCHE"},{v:"droit",l:"DROIT"}])}
        </div>
        <div>
          {field("let_","LET (ténodèse)", null,
            [{v:"non",l:"NON"},{v:"oui",l:"OUI"}])}
        </div>
      </div>

      {field("typeGreffe","Type de greffe", null, GREFFON_OPTS)}

      {field("pathoAssociee","Pathologie(s) associée(s) (ménisque, LCL, etc.)")}
      {field("commentaire","Commentaire","textarea")}
    </div>
  );
}

// ── PDF EXPORT ────────────────────────────────────────────────
function buildPdfContent(patient, testDate, allRes, values, activeTr) {
  // Build HTML string for the PDF
  const C_pdf = {
    orange:"#FF6C0E", black:"#161616", blue:"#004A72",
    green:"#16A34A", red:"#DC2626", amber:"#D97706",
    grey:"#5A5A5A", border:"#E5E5E5", white:"#FFFFFF",
  };

  const fmt = (v) => v || "—";
  const fmtDate = (d) => {
    if (!d) return "—";
    const [y,m,day] = d.split("-");
    return `${day}/${m}/${y}`;
  };

  const greffeName = GREFFON_LABELS;

  let html = `
  <html>
  <head>
    <meta charset="utf-8"/>
    <style>
      * { box-sizing:border-box; margin:0; padding:0; }
      body { font-family: Arial, sans-serif; font-size:10px; color:#1A1A1A; background:#fff; padding:24px; }
      .header { display:flex; align-items:center; justify-content:space-between; border-bottom:3px solid #FF6C0E; padding-bottom:10px; margin-bottom:14px; }
      .brand { font-size:22px; font-weight:900; letter-spacing:2px; }
      .brand span { color:#FF6C0E; }
      .subtitle { font-size:9px; color:#888; letter-spacing:1px; text-transform:uppercase; }
      .section-title { font-size:11px; font-weight:700; color:#FF6C0E; letter-spacing:1px; text-transform:uppercase; margin:12px 0 6px; border-left:3px solid #FF6C0E; padding-left:8px; }
      .patient-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:6px 12px; background:#F9F9F9; border:1px solid #E5E5E5; border-radius:6px; padding:10px; margin-bottom:10px; }
      .patient-field label { font-size:7.5px; color:#9A9A9A; text-transform:uppercase; letter-spacing:1px; display:block; margin-bottom:1px; }
      .patient-field span { font-size:10px; font-weight:600; color:#1A1A1A; }
      .phase-header { background:#161616; color:#fff; padding:6px 10px; border-radius:5px; font-size:10px; font-weight:700; letter-spacing:1px; margin-bottom:6px; display:flex; justify-content:space-between; align-items:center; }
      .score-chip { font-size:13px; font-weight:900; }
      .criteria-table { width:100%; border-collapse:collapse; margin-bottom:10px; font-size:9px; }
      .criteria-table th { background:#3C3C3B; color:#fff; padding:4px 6px; text-align:left; font-size:8px; letter-spacing:0.5px; }
      .criteria-table td { padding:4px 6px; border-bottom:1px solid #F0F0F0; vertical-align:top; }
      .criteria-table tr:nth-child(even) td { background:#FAFAFA; }
      .badge-crit { background:#FEF2F2; color:#DC2626; border:1px solid #FECACA; border-radius:3px; padding:1px 4px; font-size:7px; font-weight:700; }
      .badge-imp  { background:#FFFBEB; color:#D97706; border:1px solid #FDE68A; border-radius:3px; padding:1px 4px; font-size:7px; font-weight:700; }
      .badge-inf  { background:#E8F4FB; color:#004A72; border:1px solid #BAE6FD; border-radius:3px; padding:1px 4px; font-size:7px; font-weight:700; }
      .pass { color:#16A34A; font-weight:700; }
      .fail { color:#DC2626; font-weight:700; }
      .override { color:#D97706; font-size:7px; font-style:italic; margin-top:2px; }
      .status-ok  { background:#F0FDF4; border:1.5px solid #16A34A44; border-radius:5px; padding:5px 10px; color:#16A34A; font-weight:700; font-size:10px; }
      .status-warn{ background:#FFFBEB; border:1.5px solid #D9770644; border-radius:5px; padding:5px 10px; color:#D97706; font-weight:700; font-size:10px; }
      .status-fail{ background:#FEF2F2; border:1.5px solid #DC262644; border-radius:5px; padding:5px 10px; color:#DC2626; font-weight:700; font-size:10px; }
      .footer { margin-top:16px; padding-top:8px; border-top:1px solid #E5E5E5; font-size:7.5px; color:#9A9A9A; display:flex; justify-content:space-between; }
      .no-break { page-break-inside:avoid; }
    </style>
  </head>
  <body>
    <div class="header">
      <div>
        <div class="brand"><span>CAMPUS</span> LCA</div>
        <div class="subtitle">Paramed'Sport · Rapport d'évaluation LCA</div>
      </div>
      <div style="text-align:right;">
        <div style="font-size:9px;color:#888;">Date du bilan</div>
        <div style="font-size:13px;font-weight:700;color:#FF6C0E;">${testDate ? fmtDate(testDate) : "—"}</div>
      </div>
    </div>

    <div class="section-title">Informations patient</div>
    <div class="patient-grid">
      <div class="patient-field"><label>Nom</label><span>${fmt(patient.nom)}</span></div>
      <div class="patient-field"><label>Prénom</label><span>${fmt(patient.prenom)}</span></div>
      <div class="patient-field"><label>Date de naissance</label><span>${fmtDate(patient.ddn)}</span></div>
      <div class="patient-field"><label>Sport pratiqué</label><span>${fmt(patient.sport)}</span></div>
      <div class="patient-field"><label>Date de blessure</label><span>${fmtDate(patient.dateBlessure)}</span></div>
      <div class="patient-field"><label>Date d'opération</label><span>${fmtDate(patient.dateOp)}</span></div>
      <div class="patient-field"><label>Côté opéré</label><span>${fmt(patient.coteOpere)?.toUpperCase()}</span></div>
      <div class="patient-field"><label>Type de greffe</label><span>${patient.typeGreffe ? (greffeName[patient.typeGreffe] ?? patient.typeGreffe) : "—"}</span></div>
      <div class="patient-field"><label>LET (ténodèse)</label><span>${patient.let_ === "oui" ? "✓ Oui" : "✗ Non"}</span></div>
      ${patient.pathoAssociee ? `<div class="patient-field" style="grid-column:1/-1"><label>Pathologie(s) associée(s)</label><span>${patient.pathoAssociee}</span></div>` : ""}
      ${patient.commentaire ? `<div class="patient-field" style="grid-column:1/-1"><label>Commentaire</label><span>${patient.commentaire}</span></div>` : ""}
    </div>
  `;

  // For each transition, render results
  TRANSITIONS.forEach((tr, i) => {
    const res = allRes[i];
    if (!res) return;
    const fromPh = SCR_PHASES[i], toPh = SCR_PHASES[i+1];
    const statusClass = res.validated ? "status-ok" : res.warning ? "status-warn" : "status-fail";
    const statusTxt = res.validated ? `✓ PASSAGE VALIDÉ (${res.globalScore}%)`
      : res.warning ? `⚡ ${res.impFail} critère(s) important(s) non atteint(s) — Score ${res.globalScore}%`
      : `✗ BLOQUÉ — ${res.critFail} critique(s) manquant(s) — Score ${res.globalScore}%`;

    html += `
    <div class="no-break">
      <div class="phase-header">
        <span>${fromPh.label} ${fromPh.name.toUpperCase()} → ${toPh.label} ${toPh.name.toUpperCase()}</span>
        <span class="score-chip" style="color:${res.validated?"#22C55E":res.warning?"#F59E0B":"#EF4444"}">${res.globalScore}%</span>
      </div>
      <div class="${statusClass}" style="margin-bottom:8px;">${statusTxt}</div>
    </div>
    `;

    // Group items by tab
    const data = CRITERIA[tr];
    data.tabs.forEach(tab => {
      const tabItems = tab.items.map(c => {
        const k = `${tr}_${c.id}`;
        const v = k in values ? values[k] : getDefault(c);
        const rawPass = isPass(c, v);
        const ov = {};
        const pass = rawPass;
        return { ...c, value:v, rawPass, pass };
      }).filter(c => !c.rawPass || c.criticality !== "informative"); // show failed + all critical/important

      if (tabItems.length === 0) return;

      html += `<div style="font-size:8.5px;font-weight:700;color:#3C3C3B;text-transform:uppercase;letter-spacing:0.8px;margin:8px 0 4px;">${tab.label}</div>`;
      html += `<table class="criteria-table"><thead><tr><th style="width:35%">Test</th><th style="width:12%">Niveau</th><th style="width:38%">Résultat</th><th style="width:15%">Statut</th></tr></thead><tbody>`;

      tabItems.forEach(c => {
        const badgeCls = c.criticality === "critical" ? "badge-crit" : c.criticality === "important" ? "badge-imp" : "badge-inf";
        const badgeTxt = c.criticality === "critical" ? "● CRITIQUE" : c.criticality === "important" ? "◆ IMPORTANT" : "○ INFO";
        let resultTxt = "—";
        if (c.inputType === "toggle")  resultTxt = c.value === 1 ? "OUI" : "NON";
        else if (c.inputType === "select") resultTxt = c.options?.[c.value ?? 0] ?? "—";
        else if (c.inputType === "slider" || c.inputType === "number") resultTxt = `${c.value ?? 0} ${c.unit}`;
        else if (c.inputType === "lsi") {
          const l = c.value?.h > 0 && c.value?.i > 0 ? lsi(c.value.h, c.value.i) : null;
          resultTxt = l !== null ? `Sain: ${c.value.h} | Lésé: ${c.value.i} | LSI: ${l}%` : "Non renseigné";
        }
        else if (c.inputType === "isok") {
          const iv = c.value;
          resultTxt = iv?.isokDate ? `Test du ${iv.isokDate} — Voir détail tableau` : "Voir tableau isocinétique";
        }
        else if (c.inputType === "sprint_time") {
          resultTxt = c.value?.time ? `${c.value.time}s${c.value?.date ? " ("+c.value.date+")" : ""}` : "Non renseigné";
        }
        const statusTxt2 = c.pass ? "✓ VALIDÉ" : "✗ ÉCHEC";
        const statusCls = c.pass ? "pass" : "fail";
        html += `<tr><td>${c.label}</td><td><span class="${badgeCls}">${badgeTxt}</span></td><td>${resultTxt}</td><td class="${statusCls}">${statusTxt2}</td></tr>`;
      });

      html += `</tbody></table>`;
    });
  });

  const today = new Date().toLocaleDateString("fr-BE", { day:"2-digit", month:"2-digit", year:"numeric" });
  html += `
    <div class="footer">
      <span>Campus LCA · Paramed'Sport · Jérôme Tubez · v5.0</span>
      <span>Document généré le ${today} · Usage praticien exclusif</span>
    </div>
  </body></html>`;

  return html;
}

function exportToPDF(patient, testDate, allRes, values) {
  const html = buildPdfContent(patient, testDate, allRes, values);
  const blob = new Blob([html], { type:"text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank");
  if (win) {
    win.addEventListener("load", () => {
      setTimeout(() => { win.print(); URL.revokeObjectURL(url); }, 500);
    });
  }
}

// ── MAIN ──────────────────────────────────────────────────────
// ── SCORING DE PHASES — SOURCE UNIQUE test_results ─────────────────────────
// Une saisie = un upsert dans test_results (clé test_id = `${transition}_${critère}`
// pour CRITERIA, `rtr_*` / `mrss_*` pour les variables sans ligne V1.13), rattaché
// à la test_session (patient, phase du test, date du bilan). Le MRSS 2.0 et le gate
// RTR sont des étapes finales calculées sur les MÊMES lignes (src/measureMap.js).
const RTR_GATE_TAB = { id:"rtr_gate", label:"🚦 RTR gate" };
const MRSS_TAB     = { id:"mrss",     label:"📐 MRSS 2.0" };
const GATE_OF_TRANSITION = { "p2→p3":"RUN", "p3→p4":"SPORT" };
const RTR_INPUTS_TAB = { id:"rtr_inputs", label:"🏃 Course & tolérance" };
const RTR_SPORT_INPUTS_TAB = { id:"rtr_sport_inputs", label:"🏟 Retour au sport" };
const extraTabsFor = tr => (tr === "p3→p4" ? [MRSS_TAB, RTR_SPORT_INPUTS_TAB, RTR_GATE_TAB] : tr === "p2→p3" ? [RTR_INPUTS_TAB, RTR_GATE_TAB] : []);
// Questionnaires : réponses item par item rangées avec le score de la transition P3→P4
const Q_ITEM_TESTS = { rsi_q:"p3→p4_aclrsi_p3", tsk_q:"p3→p4_tsk11_p3", ikdc_q:"p3→p4_ikdc_p3" };
const qTestOf = key => Q_ITEM_TESTS[Object.keys(Q_ITEM_TESTS).find(p => key.startsWith(p))];
const CRITERIA_BY_KEY = (() => {
  const o = {};
  TRANSITIONS.forEach(tr => (CRITERIA[tr]?.tabs ?? []).forEach(tab => tab.items.forEach(c => { o[`${tr}_${c.id}`] = { c, tr, tab }; })));
  return o;
})();
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}
// Libellé lisible de l'endroit où une mesure se saisit (pour les valeurs RTR en lecture seule)
function testOriginLabel(testId) {
  const e = CRITERIA_BY_KEY[testId];
  if (e) return `${e.tr.replace("→"," → ").toUpperCase()} › ${e.tab.label} › ${e.c.label}`;
  if (testId === "MRSS 2.0") return "P3 → P4 › 📐 MRSS 2.0 (score calculé)";
  if (testId === "date d'opération") return "Fiche patient › date d'opération (calcul automatique)";
  if (testId === "carnet de séances") return "Carnet de séances (charge / RPE)";
  if (testId === "giving_way_events") return "Dérobements déclarés";
  const inp = RTR_RUN_INPUT_BY_TEST[testId];
  if (inp) return `P2 → P3 › ${RTR_INPUTS_TAB.label} › ${inp}`;
  const inpS = RTR_SPORT_INPUT_BY_TEST[testId];
  if (inpS) return `P3 → P4 › ${RTR_SPORT_INPUTS_TAB.label} › ${inpS}`;
  if (testId.startsWith("mrss_")) return "P3 → P4 › 📐 MRSS 2.0";
  return testId;
}

function ScoringTool({ initPatient, fullPatient, initTr, initTab, onBack, onPatientUpdate, onDataChanged }) {
  const [loadState,   setLoadState]   = useState("loading"); // loading | ready | error
  const [loadError,   setLoadError]   = useState(null);
  const [activeTr,    setActiveTr]    = useState(initTr || "p2→p3");
  const [activeTab,   setActiveTab]   = useState(initTab || "timing");
  const [values,      setValues]      = useState({});
  const [overrides,   setOverrides]   = useState({});
  const [extras,      setExtras]      = useState({}); // { [testId]: { trials?, items? } }
  const [showSources, setShowSources] = useState(false);
  const [filter,      setFilter]      = useState("all");
  const [viewMode,    setViewMode]    = useState("practitioner");
  // Pré-remplissage : les infos de la FICHE PATIENT (initPatient, toujours fraîches) écrasent
  // les valeurs du dernier bilan pour les champs non vides (nom, sport, greffon, côté, date op).
  const [patient,     setPatient]     = useState(() => {
    const fresh = {};
    if (initPatient) for (const k in initPatient) { const v = initPatient[k]; if (v !== "" && v != null) fresh[k] = v; }
    return { ...EMPTY_PATIENT, ...fresh };
  });
  const [testDate,    setTestDate]    = useState(todayISO());
  const [showPatient, setShowPatient] = useState(false); // infos patient repliées par défaut (24/10/2026)
  const [qVals,       setQVals]       = useState({});
  const [rtrProfile,  setRtrProfile]  = useState(fullPatient?.rtrProfile || "INTERMEDIAIRE");
  const [decisionStatus, setDecisionStatus] = useState(null); // null | saving | saved | error

  // measuredRef : tests dont la valeur `v` a réellement été mesurée (chargée ou saisie).
  // Les défauts CRITERIA restent affichés dans les cartes mais ne sont jamais enregistrés
  // et n'alimentent jamais MRSS/RTR (une valeur absente = NON_MESURE).
  const measuredRef = useRef(new Set());
  const dirtyRef    = useRef(new Set());
  const metaDirtyRef = useRef(false);
  const qTouchedRef = useRef(false);
  const sessionCache = useRef({}); // `${phase}|${date}` → test_session
  const loadedDatesRef = useRef({}); // test_id → date effective de la mesure chargée
  const editedRef = useRef(new Set()); // tests saisis pendant cette visite (date = date du bilan)
  const [givingWay, setGivingWay] = useState([]);  // giving_way_events du patient
  const [decisions, setDecisions] = useState([]);  // phase_decisions du patient
  const latest = useRef({});
  latest.current = { values, overrides, extras, patient, testDate };

  // ── Chargement : toutes les sessions du patient → dernière valeur par test ──
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      lcaApi.fetchPatientSessions(fullPatient.id),
      lcaApi.fetchGivingWay(fullPatient.id),
      lcaApi.fetchDecisions(fullPatient.id),
    ]).then(([sessions, gw, decs]) => {
      if (cancelled) return;
      setGivingWay(gw || []); setDecisions(decs || []);
      const idx = buildMeasureIndex(sessions || []);
      for (const [k, e] of Object.entries(idx)) loadedDatesRef.current[k] = e.date;
      const vals = {}, ovs = {}, ext = {}, q = {};
      TRANSITIONS.forEach(tr => (CRITERIA[tr]?.tabs ?? []).forEach(tab => tab.items.forEach(c => { vals[`${tr}_${c.id}`] = getDefault(c); })));
      for (const [k, e] of Object.entries(idx)) {
        const val = e.valeur || {};
        if (val.v !== undefined && val.v !== null) { vals[k] = val.v; measuredRef.current.add(k); }
        if (val.ov) ovs[k] = val.ov;
        if (val.trials || val.items) ext[k] = { trials: val.trials, items: val.items };
        if (val.items) Object.assign(q, val.items);
      }
      setValues(vals); setOverrides(ovs); setExtras(ext); setQVals(q);
      // Champs du bilan (ddn, date blessure, LET…) : notes de la session la plus récente qui en a
      const withNotes = [...(sessions || [])].filter(s => s.notes).sort((a, b) => (a.date_session < b.date_session ? 1 : -1))[0];
      if (withNotes) {
        try { const meta = JSON.parse(withNotes.notes)?.bilan; if (meta) setPatient(p => ({ ...EMPTY_PATIENT, ...meta, ...Object.fromEntries(Object.entries(p).filter(([, v]) => v !== "" && v != null)) })); } catch (e) {}
      }
      // Transition ouverte par défaut : celle de la dernière session mesurée
      if (!initTr) {
        const last = Object.values(idx).sort((a, b) => (a.date < b.date ? 1 : -1))[0];
        const tr = last ? TRANSITION_KEYS[last.phase] : null;
        if (tr && CRITERIA[tr]) setActiveTr(tr);
      }
      setLoadState("ready");
    }).catch(err => { if (!cancelled) { setLoadError(err.message); setLoadState("error"); } });
    return () => { cancelled = true; };
  }, []);

  // Marque une mesure saisie (v === undefined → mesure retirée de la session courante).
  // Effets sur les refs hors des updaters React (sûr en StrictMode).
  const markVal = useCallback((k, v) => {
    if (v === undefined) {
      if (!measuredRef.current.has(k)) return;
      measuredRef.current.delete(k); dirtyRef.current.add(k); editedRef.current.add(k);
      const crit = CRITERIA_BY_KEY[k];
      setValues(p => ({ ...p, [k]: crit ? getDefault(crit.c) : undefined }));
      return;
    }
    // Question obligatoire du bilan : re-répondre la même chose la date du bilan du jour
    const isQuestion = Object.values(GIVING_WAY_QUESTION).includes(k) && !editedRef.current.has(k);
    if (!isQuestion && measuredRef.current.has(k) && JSON.stringify(latest.current.values[k]) === JSON.stringify(v)) return;
    measuredRef.current.add(k); dirtyRef.current.add(k); editedRef.current.add(k);
    setValues(p => ({ ...p, [k]: v }));
  }, []);
  const setVal = useCallback((tr,id,v) => markVal(`${tr}_${id}`, v), [markVal]);
  const setOv  = useCallback((tr,id,v) => { const k = `${tr}_${id}`; dirtyRef.current.add(k); setOverrides(p => ({ ...p, [k]:v })); }, []);
  const updateBilan = useCallback(p => { metaDirtyRef.current = true; setPatient(p); }, []);
  const setQ = useCallback((key, value) => {
    qTouchedRef.current = true;
    setQVals(p => ({ ...p, [key]: value }));
    const tid = qTestOf(key);
    if (!tid) return;
    dirtyRef.current.add(tid);
    setExtras(e => {
      const items = { ...(e[tid]?.items || {}), [key]: value };
      if (value === "" || value == null) delete items[key];
      return { ...e, [tid]: { ...(e[tid] || {}), items } };
    });
  }, []);
  // Essais bruts (MRSS 2.0, décision D2) : stockés tels quels + valeur `v` dérivée pour le scoring
  const setTrials = useCallback((testId, trials, useAvg = false) => {
    const empty = ![...(trials.inj || []), ...(trials.healthy || [])].some(x => x !== "" && x != null);
    dirtyRef.current.add(testId);
    setExtras(e => ({ ...e, [testId]: { ...(e[testId] || {}), trials: empty ? undefined : trials } }));
    markVal(testId, empty ? undefined : valueFromTrials(testId, trials, useAvg));
  }, [markVal]);

  // ── Enregistrement : upsert des tests modifiés, groupés par phase de session ──
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
  const saveTimer = useRef(null);
  const activePhase = TRANSITIONS.indexOf(activeTr);

  async function getSession(phase, date) {
    const key = `${phase}|${date}`;
    if (!sessionCache.current[key]) sessionCache.current[key] = await lcaApi.ensureSession(fullPatient.id, phase, date);
    return sessionCache.current[key];
  }

  function buildRow(sessionId, k) {
    const { values: vals, overrides: ovs, extras: ext } = latest.current;
    const measured = measuredRef.current.has(k);
    const valeur = {};
    if (measured) valeur.v = vals[k];
    if (ovs[k]?.active || ovs[k]?.note) valeur.ov = ovs[k];
    if (ext[k]?.trials) valeur.trials = ext[k].trials;
    if (ext[k]?.items && Object.keys(ext[k].items).length) valeur.items = ext[k].items;
    if (!Object.keys(valeur).length) return null; // plus rien à stocker → suppression
    const crit = CRITERIA_BY_KEY[k]?.c;
    const statut = !measured ? "NON_MESURE" : crit ? (isPass(crit, vals[k]) ? "ATTEINT" : "NON_ATTEINT") : null;
    const dateOverride = crit?.inputType === "isok" ? vals[k]?.isokDate : crit?.inputType === "sprint_time" ? vals[k]?.date : null;
    return { session_id: sessionId, test_id: k, valeur, statut, date_override: dateOverride || null };
  }

  async function flush() {
    const keys = [...dirtyRef.current];
    // Les infos du bilan (notes de session) ne créent jamais une session à elles seules :
    // elles attendent la prochaine saisie de mesure (sinon : session vide en base).
    const metaDirty = metaDirtyRef.current && keys.length > 0;
    if (!keys.length) return;
    dirtyRef.current.clear(); if (metaDirty) metaDirtyRef.current = false;
    const date = latest.current.testDate || todayISO();
    const byPhase = {};
    for (const k of keys) { const ph = phaseOfTest(k); if (ph !== null) (byPhase[ph] = byPhase[ph] || []).push(k); }
    const metaPhase = byPhase[activePhase] ? activePhase : Number(Object.keys(byPhase)[0]);
    try {
      for (const [ph, ks] of Object.entries(byPhase)) {
        const s = await getSession(Number(ph), date);
        const rows = [], dels = [];
        for (const k of ks) { const r = buildRow(s.id, k); if (r) rows.push(r); else dels.push(k); }
        await lcaApi.upsertResults(rows);
        for (const k of dels) await lcaApi.deleteResult(s.id, k);
        if (metaDirty && Number(ph) === metaPhase) await lcaApi.updateSessionNotes(s.id, JSON.stringify({ bilan: latest.current.patient }));
      }
      setSaveStatus("saved");
      if (onDataChanged) onDataChanged();
    } catch (err) {
      console.error("Erreur sauvegarde test_results:", err);
      keys.forEach(k => dirtyRef.current.add(k));
      if (metaDirty) metaDirtyRef.current = true;
      setSaveStatus("error");
    }
  }

  useEffect(() => {
    if (loadState !== "ready") return;
    if (!dirtyRef.current.size) return;
    setSaveStatus("saving");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flush, 900);
    return () => clearTimeout(saveTimer.current);
  }, [values, overrides, extras, patient, testDate, loadState]);

  // Reset tab when transition changes (comparé à la transition précédente : l'onglet
  // initial demandé à l'ouverture est conservé, y compris sous React.StrictMode)
  const prevTr = useRef(initTab ? activeTr : null);
  useEffect(() => {
    if (prevTr.current === activeTr) return;
    prevTr.current = activeTr;
    const tabs = CRITERIA[activeTr]?.tabs ?? [];
    if (tabs.length > 0) setActiveTab(tabs[0].id);
    setFilter("all");
  }, [activeTr]);

  // ── Mesures réelles → MRSS 2.0 et gate RTR (mêmes lignes que le scoring) ──
  const measures = useMemo(() => {
    const m = {};
    const keys = new Set([...measuredRef.current, ...Object.keys(extras)]);
    for (const k of keys) {
      const e = extras[k] || {};
      const entry = {};
      if (measuredRef.current.has(k)) entry.v = values[k];
      if (e.trials) entry.trials = e.trials;
      if (e.items) entry.items = e.items;
      if (!Object.keys(entry).length) continue;
      // Date effective (règle de fraîcheur) : saisie de cette visite → date propre au test
      // (isocinétique, sprint) sinon date du bilan ; sinon date de la mesure chargée.
      const own = values[k]?.isokDate || (CRITERIA_BY_KEY[k]?.c.inputType === "sprint_time" ? values[k]?.date : null);
      entry.date = editedRef.current.has(k) ? (own || testDate) : (loadedDatesRef.current[k] || testDate);
      m[k] = entry;
    }
    return m;
  }, [values, extras, testDate]);
  const staleTests = useMemo(() => splitFresh(measures, testDate).stale, [measures, testDate]);
  // Dérobements post-op (V1.14, convention N4) : comptés (>=) depuis l'entrée dans la phase
  // en cours (dernier GO / GO_CONDITIONNEL de la transition qui y mène), sinon depuis l'opération.
  const ctxFor = tr => {
    const dateOp = fullPatient?.dateOp || patient.dateOp;
    const since = givingWayWindowStart(tr, { decisions, dateOp, date: testDate });
    return { dateOp, date: testDate, givingWayCount: countGivingWay(givingWay, { since, until: testDate }), givingWaySince: since,
      sessionLog: fullPatient?.sessionHistory || [] };
  };
  const ctx = useMemo(() => ctxFor("p3→p4"), [fullPatient, patient.dateOp, testDate, givingWay, decisions]);
  const ctxRun = useMemo(() => ctxFor("p2→p3"), [fullPatient, patient.dateOp, testDate, givingWay, decisions]);
  const ctxT3 = useMemo(() => ctxFor("p1→p2"), [fullPatient, patient.dateOp, testDate, givingWay, decisions]);
  const ctxT7 = useMemo(() => ctxFor("p4→p5"), [fullPatient, patient.dateOp, testDate, givingWay, decisions]);
  const giving = { "p1→p2": { count: ctxT3.givingWayCount, since: ctxT3.givingWaySince }, "p4→p5": { count: ctxT7.givingWayCount, since: ctxT7.givingWaySince } };
  // Valeurs calculées (V1.15, D7/D8) : délais depuis la date d'op ou le dernier GO, %EPIC.
  // Jamais enregistrées ; elles priment sur une ancienne saisie au curseur.
  const autoVals = useMemo(() => {
    const o = {}, dateOp = fullPatient?.dateOp || patient.dateOp;
    TRANSITIONS.forEach(tr => (CRITERIA[tr]?.tabs ?? []).forEach(tab => tab.items.forEach(c => {
      if (c.inputType !== "auto") return;
      o[`${tr}_${c.id}`] = c.auto === "op" ? weeksSince(dateOp, testDate)
        : c.auto === "phase" ? weeksSince(phaseEntryDate(tr, { decisions, date: testDate }), testDate)
        : c.auto === "epic" ? epicPct(measures, c.epicFrom) : undefined;
    })));
    return o;
  }, [fullPatient, patient.dateOp, testDate, decisions, measures]);
  const valuesEff = useMemo(() => ({ ...values, ...autoVals }), [values, autoVals]);
  const mrssResult  = useMemo(() => computeMrssFromMeasures(measures, ctx), [measures, ctx]);
  const runResult   = useMemo(() => { const i = buildRtrRunInput(measures, ctxRun); return computeRtrRun(i.measurements, { stale: i.stale }); }, [measures, ctxRun]);
  const sportResult = useMemo(() => { const i = buildRtrSportInput(measures, ctx); return computeRtrSport(rtrProfile, i.measurements, { stale: i.stale }); }, [measures, ctx, rtrProfile]);
  const gate = GATE_OF_TRANSITION[activeTr];
  const gateResult = gate === "RUN" ? runResult : gate === "SPORT" ? sportResult : null;
  const gateCtx = gate === "RUN" ? ctxRun : ctx;

  // Déclaration d'un dérobement (giving_way_events) — source unique des hurdles « 0 dérobement »
  async function declareGivingWay({ date, contexte }) {
    const rows = await lcaApi.insertGivingWay({ patient_id: fullPatient.id, date_event: date, contexte: contexte || null });
    setGivingWay(g => [...g, ...(rows || [])].sort((a, b) => (a.date_event < b.date_event ? -1 : 1)));
  }

  const changeProfile = p => {
    setRtrProfile(p);
    if (onPatientUpdate) onPatientUpdate({ ...fullPatient, rtrProfile: p });
  };

  // ── Décision de phase → phase_decisions (alimente le dashboard via v_dashboard) ──
  const decidingRef = useRef(false);
  async function recordDecision() {
    if (decidingRef.current || decisionStatus === "saved") return; // anti double-clic
    decidingRef.current = true;
    setDecisionStatus("saving");
    try {
      clearTimeout(saveTimer.current);
      await flush();
      const date = testDate || todayISO();
      const s = await getSession(activePhase, date);
      let row;
      if (gateResult ? gateResult.decision === "A_RETESTER" : computeResults(activeTr, valuesEff, overrides, staleTests, giving).retest) {
        throw new Error("Décision suspendue : mesure(s) périmée(s) à refaire");
      }
      if (gateResult) {
        const weakest = (gateResult.blocks || []).filter(b => b.pct !== null && b.pct !== undefined).sort((a, b) => a.pct - b.pct)[0];
        const retestDays = gateResult.decision === "GO_CONDITIONNEL" ? (gate === "RUN" ? 28 : 42) : null;
        const retest = retestDays ? new Date(new Date(date).getTime() + retestDays * 86400000).toISOString().split("T")[0] : null;
        row = { gate: gate === "RUN" ? "RTR-RUN" : `RTR-SPORT:${rtrProfile}`, decision: gateResult.decision,
          objectif_prioritaire: weakest ? weakest.label : null, restriction: gateResult.action || null, date_retest: retest };
      } else {
        const r = computeResults(activeTr, valuesEff, overrides, staleTests, giving);
        row = { gate: activeTr, decision: r.validated ? "GO" : r.warning ? "GO_CONDITIONNEL" : "NO_GO", objectif_prioritaire: null, restriction: null, date_retest: null };
      }
      await lcaApi.insertPhaseDecision({ ...row, patient_id: fullPatient.id, session_id: s.id, date_decision: date });
      setDecisions(d => [...d, { gate: row.gate, decision: row.decision, date_decision: date }]);
      setDecisionStatus("saved");
      if (onDataChanged) onDataChanged();
    } catch (err) {
      console.error("Erreur enregistrement décision:", err);
      setDecisionStatus("error");
    } finally {
      decidingRef.current = false;
    }
  }
  // Nouvelle décision possible seulement si le contexte ou les mesures changent
  useEffect(() => { setDecisionStatus(null); }, [activeTr, rtrProfile, testDate, measures]);

  const getVal  = (tr,id) => { const k=`${tr}_${id}`; return k in valuesEff ? valuesEff[k] : getDefault((CRITERIA[tr]?.tabs??[]).flatMap(t=>t.items).find(x=>x.id===id)); };
  const getOv   = (tr,id) => overrides[`${tr}_${id}`] ?? { active:false, note:"" };

  const allRes = TRANSITIONS.map(tr => computeResults(tr, valuesEff, overrides, staleTests, giving));
  const res    = computeResults(activeTr, valuesEff, overrides, staleTests, giving);
  const fromId = TRANSITIONS.indexOf(activeTr);
  const fromPhase = SCR_PHASES[fromId], toPhase = SCR_PHASES[fromId + 1];

  const status = res?.validated ? "validated" : res?.retest ? "retest" : res?.warning ? "warning" : "blocked";
  const statusCol = status === "validated" ? C.green : status === "retest" ? C.blueMid : status === "warning" ? C.amber : C.red;
  const statusLabel = res?.validated ? "✓ PASSAGE VALIDÉ"
    : res?.warning ? `⚡ ${res.impFail} important(s) non atteint(s)`
    : `✗ BLOQUÉ — ${res?.critFail} critique(s) manquant(s)`;

  const currentTabItems = (() => {
    const data = CRITERIA[activeTr];
    if (!data) return [];
    const tab = data.tabs.find(t => t.id === activeTab);
    if (!tab) return [];
    const withPass = tab.items.map(c => {
      const k = `${activeTr}_${c.id}`;
      const v = k in valuesEff ? valuesEff[k] : getDefault(c);
      const rawPass = isPass(c, v);
      const ov = overrides[k];
      const ovValid = !rawPass && ov?.active && ov?.note?.trim() && c.criticality !== "critical";
      if (c.inputType === "giving_way") {
        const ctxG = activeTr === "p1→p2" ? ctxT3 : activeTr === "p2→p3" ? ctxRun : activeTr === "p4→p5" ? ctxT7 : ctx;
        return { ...c, value:v, ...givingCriterion(v, staleTests[k] || null, { count: ctxG.givingWayCount }), giving: { ctx: ctxG, events: givingWay, onDeclare: declareGivingWay } };
      }
      const st = staleTests[k] || null;
      const on = measures[k]?.date;
      return { ...c, value:v, rawPass: !st && rawPass, pass: !st && (rawPass || ovValid), stale: st, measuredOn: on && on !== testDate ? on : null };
    });
    if (filter === "all") return withPass;
    return withPass.filter(c => c.criticality === filter);
  })();

  const activeOvCount = Object.values(overrides).filter(o => o.active && o.note?.trim()).length;
  const allTabs = [...(CRITERIA[activeTr]?.tabs ?? []), ...extraTabsFor(activeTr)];
  const isExtraTab = [RTR_GATE_TAB.id, MRSS_TAB.id, RTR_INPUTS_TAB.id, RTR_SPORT_INPUTS_TAB.id].includes(activeTab);

  if (loadState !== "ready") {
    return (
      <div style={{ minHeight:"100vh", background:C.greyWhite, fontFamily:"'Segoe UI','Helvetica Neue',sans-serif", padding:24, textAlign:"center" }}>
        {onBack && <div style={{ textAlign:"left" }}><button onClick={onBack} style={{ background:"#f1f5f9", border:"1px solid #e2e8f0", borderRadius:5, padding:"5px 10px", cursor:"pointer", color:C.textMuted, fontSize:11 }}>← Retour</button></div>}
        <div style={{ marginTop:60, fontSize:13, color: loadState === "error" ? C.red : C.textMuted }}>
          {loadState === "error" ? `Chargement des mesures impossible : ${loadError}` : "Chargement des mesures…"}
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight:"100vh", background:C.greyWhite, fontFamily:"'Segoe UI','Helvetica Neue',sans-serif", color:C.textPrimary }}>
      <style>{`
        input[type=range]{-webkit-appearance:none;background:transparent;}
        input[type=range]::-webkit-slider-runnable-track{height:3px;background:${C.border};border-radius:3px;}
        input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;width:14px;height:14px;border-radius:50%;background:${C.orange};margin-top:-5.5px;cursor:pointer;}
        input:focus,textarea:focus{outline:2px solid ${C.orange};outline-offset:0;}
        textarea{outline:none;}
        ::-webkit-scrollbar{width:4px;height:4px;}
        ::-webkit-scrollbar-track{background:${C.surface};}
        ::-webkit-scrollbar-thumb{background:${C.borderMid};border-radius:4px;}
        button:hover{opacity:0.9;}
      `}</style>

      {/* ══ HEADER ══ */}
      <div style={{ background:C.black, borderBottom:`1px solid ${C.border}`, boxShadow:"0 1px 3px rgba(15,32,64,0.04)", padding:"12px 16px 10px",
        position:"sticky", top:0, zIndex:200 }}>
        <div style={{ maxWidth:760, margin:"0 auto" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              {onBack && <button onClick={onBack} style={{ background:"#f1f5f9", border:"1px solid #e2e8f0", borderRadius:5, padding:"5px 10px", cursor:"pointer", color:C.textMuted, fontSize:11, fontFamily:"'Segoe UI',sans-serif" }}>← Retour</button>}
              <div style={{ display:"flex", alignItems:"baseline", gap:6 }}>
                <span style={{ fontFamily:"'Segoe UI',sans-serif", fontSize:22, color:C.orange, letterSpacing:"0.12em" }}>CAMPUS</span>
                <span style={{ fontFamily:"'Segoe UI',sans-serif", fontWeight:800, fontSize:22, color:C.blue, letterSpacing:"0.12em" }}>LCA</span>
                <span style={{ fontFamily:"'Segoe UI',sans-serif", fontSize:8, color:C.textMuted, background:"#f1f5f9", border:"1px solid #e2e8f0", borderRadius:2, padding:"1px 5px", marginLeft:4 }}>SCORING v5.0</span>
                {saveStatus !== "idle" && (
                  <span style={{ fontFamily:"'Segoe UI',sans-serif", fontSize:8, fontWeight:700, marginLeft:6,
                    color: saveStatus==="saving" ? C.orange : saveStatus==="error" ? C.red : C.green }}>
                    {saveStatus==="saving" ? "Sauvegarde…" : saveStatus==="error" ? "⚠ Erreur d'enregistrement — nouvel essai à la prochaine saisie" : "✓ Enregistré"}
                  </span>
                )}
              </div>
            </div>
            <div style={{ display:"flex", gap:4, alignItems:"center" }}>
              <div style={{ display:"flex", background:"#f1f5f9", border:"1px solid #e2e8f0", borderRadius:5, padding:2 }}>
                {["practitioner","patient"].map(v => (
                  <button key={v} onClick={() => setViewMode(v)}
                    style={{ background: viewMode === v ? C.orange : "transparent",
                      border:"none", borderRadius:4, padding:"4px 8px",
                      color: viewMode === v ? C.white : C.textMuted,
                      fontSize:8, fontWeight:700, cursor:"pointer",
                      fontFamily:"'Segoe UI',sans-serif", letterSpacing:"0.08em", textTransform:"uppercase" }}>
                    {v === "practitioner" ? "PRATICIEN" : "PATIENT"}
                  </button>
                ))}
              </div>
              {/* Mini ring */}
              <svg width={52} height={52} style={{ transform:"rotate(-90deg)", flexShrink:0 }}>
                <circle cx={26} cy={26} r={20} fill="none" stroke="#f1f5f9" strokeWidth="5"/>
                <circle cx={26} cy={26} r={20} fill="none" stroke={statusCol} strokeWidth="5"
                  strokeDasharray={`${((res?.globalScore??0)/100)*125} 125`} strokeLinecap="round"
                  style={{ transition:"stroke-dasharray 0.4s" }}/>
                <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" fill={statusCol}
                  fontSize="10" fontWeight="800"
                  style={{ transform:"rotate(90deg)", transformOrigin:"center", fontFamily:"'Segoe UI',sans-serif" }}>
                  {res?.globalScore ?? 0}%
                </text>
              </svg>
            </div>
          </div>

          {/* Transition pills */}
          <div style={{ display:"flex", gap:4, overflowX:"auto", paddingBottom:2, marginBottom:7 }}>
            {TRANSITIONS.map((tr, i) => {
              const r = allRes[i], active = tr === activeTr, ph = SCR_PHASES[i+1];
              const s = r?.validated ? "v" : r?.warning ? "w" : "b";
              const dc = s === "v" ? C.green : s === "w" ? C.amber : C.red;
              return (
                <button key={tr} onClick={() => setActiveTr(tr)}
                  style={{ flex:"0 0 auto", background: active ? `${ph.color}22` : "#f8fafc",
                    border:`1.5px solid ${active ? ph.color : "#e2e8f0"}`,
                    borderRadius:5, padding:"4px 10px", cursor:"pointer",
                    color: active ? ph.accent : C.textMuted,
                    fontSize:9.5, fontWeight:700, letterSpacing:"0.07em",
                    display:"flex", alignItems:"center", gap:5, fontFamily:"'Segoe UI',sans-serif" }}>
                  <span style={{ width:5, height:5, borderRadius:"50%", background:dc, flexShrink:0 }}/>
                  {tr.replace("→"," → ").toUpperCase()}
                  <span style={{ fontSize:8, color:`${dc}BB` }}>{r?.globalScore ?? 0}%</span>
                </button>
              );
            })}
          </div>

          {/* Journey bar */}
          <div style={{ display:"flex", gap:2 }}>
            {TRANSITIONS.map((tr,i) => {
              const r = allRes[i], ph = SCR_PHASES[i+1];
              const bc = r?.validated ? C.green : r?.warning ? C.amber : C.red;
              return <div key={tr} style={{ flex:1, height:2.5, borderRadius:2, background:"#f1f5f9", overflow:"hidden" }}>
                <div style={{ width:`${r?.globalScore??0}%`, height:"100%", background:bc, transition:"width 0.4s" }}/>
              </div>;
            })}
          </div>
        </div>
      </div>

      {/* ══ BODY ══ */}
      <div style={{ maxWidth:760, margin:"0 auto", padding:"14px 14px 60px" }}>

        {viewMode === "patient" ? (
          <PatientView res={res} fromPhase={fromPhase} toPhase={toPhase}/>
        ) : (
          <>
            {/* ── DATE DU BILAN (toujours visible) — tests possibles sur plusieurs jours ── */}
            <div style={{ display:"flex", alignItems:"center", gap:10, background:C.white, border:`1.5px solid ${C.border}`,
              borderRadius:10, padding:"8px 12px", marginBottom:10, boxShadow:"0 2px 6px rgba(0,0,0,0.05)" }}>
              <span style={{ fontSize:13 }}>📅</span>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:8, fontWeight:700, color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", fontFamily:"'Segoe UI',sans-serif", marginBottom:2 }}>DATE DES TESTS SAISIS</div>
                <input type="date" value={testDate} onChange={e => setTestDate(e.target.value)}
                  style={{ background:"transparent", border:"none", color:C.textPrimary, fontSize:13, fontWeight:700, fontFamily:"'Segoe UI',sans-serif", outline:"none" }}/>
                <div style={{ fontSize:9.5, color:C.textMuted, lineHeight:1.45, marginTop:2 }}>
                  Bilan sur plusieurs jours : choisis la date avant chaque groupe de tests (ex. hops le lundi, tests fonctionnels le jeudi). Chaque mesure garde sa date (📅 sur le critère) ; le gate lit la plus récente encore valide (force / hops 42 j, épanchement / douleur / ROM 7 j). La question dérobement se répond le jour de la décision.
                </div>
              </div>
            </div>

            {/* ── PATIENT SECTION ── */}
            <div style={{ background:C.white, border:`1.5px solid ${C.border}`, borderRadius:10,
              boxShadow:"0 2px 6px rgba(0,0,0,0.05)", marginBottom:10, overflow:"hidden" }}>
              <button onClick={() => setShowPatient(p => !p)}
                style={{ width:"100%", background:"transparent", border:"none",
                  padding:"11px 14px", cursor:"pointer", display:"flex",
                  alignItems:"center", justifyContent:"space-between",
                  borderBottom: showPatient ? `1px solid ${C.border}` : "none" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:14 }}>👤</span>
                  <span style={{ fontSize:12, fontWeight:700, color:C.textPrimary }}>
                    {[patient.prenom, patient.nom].filter(Boolean).join(" ") || "Informations patient — Non renseigné"}
                  </span>
                </div>
                <span style={{ color:C.textMuted, fontSize:12 }}>{showPatient ? "▲" : "▼"}</span>
              </button>
              {showPatient && (
                <div style={{ padding:"14px" }}>
                  <PatientForm patient={patient} onChange={updateBilan}/>
                </div>
              )}
            </div>

            {/* ── EXPORT PDF ── */}
            <button onClick={() => exportToPDF(patient, testDate, allRes, values)}
              style={{ width:"100%", background:C.orange, border:`2px solid ${C.orangeDim}`,
                borderRadius:8, padding:"11px 0", cursor:"pointer", color:C.white,
                fontSize:12, fontWeight:800, letterSpacing:"0.08em",
                fontFamily:"'Segoe UI',sans-serif", boxShadow:`0 4px 14px ${C.orange}44`,
                marginBottom:14, display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
              <span style={{ fontSize:16 }}>📄</span>
              EXPORTER LE RAPPORT PDF
            </button>

            {/* Status card */}
            <div style={{ background:C.white, border:`1.5px solid ${C.border}`, borderRadius:10,
              padding:"12px 14px", marginBottom:10, boxShadow:"0 2px 6px rgba(0,0,0,0.06)" }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
                <span style={{ background:`${fromPhase.color}18`, color:fromPhase.color, borderRadius:4,
                  padding:"2px 8px", fontSize:9, fontWeight:800, fontFamily:"'Segoe UI',sans-serif",
                  letterSpacing:"0.08em", border:`1px solid ${fromPhase.color}33` }}>
                  {fromPhase.label} {fromPhase.name.toUpperCase()}
                </span>
                <span style={{ color:C.greyLight, fontSize:14 }}>→</span>
                <span style={{ background:`${toPhase.color}18`, color:toPhase.color, borderRadius:4,
                  padding:"2px 8px", fontSize:9, fontWeight:800, fontFamily:"'Segoe UI',sans-serif",
                  letterSpacing:"0.08em", border:`1px solid ${toPhase.color}33` }}>
                  {toPhase.label} {toPhase.name.toUpperCase()}
                </span>
                <span style={{ marginLeft:"auto", fontSize:8.5, color:C.textMuted, fontFamily:"'Segoe UI',sans-serif" }}>
                  {toPhase.weeks}
                </span>
              </div>

              <StatusBanner res={res}/>

              {gateResult && (
                <button onClick={() => setActiveTab(RTR_GATE_TAB.id)}
                  style={{ marginTop:8, width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between", gap:8,
                    background:RTR_DECISION_BG[gateResult.decision], border:`1.5px solid ${RTR_DECISION_COLOR[gateResult.decision]}55`,
                    borderRadius:6, padding:"7px 10px", cursor:"pointer", fontFamily:"'Segoe UI',sans-serif" }}>
                  <span style={{ fontSize:10, fontWeight:700, color:C.textSecondary, letterSpacing:"0.06em" }}>
                    ÉTAPE FINALE · {gate === "RUN" ? "RTR-RUN" : `RTR-SPORT ${RTR_PROFILE_LABELS[rtrProfile]}`}
                  </span>
                  <span style={{ fontSize:12, fontWeight:900, color:RTR_DECISION_COLOR[gateResult.decision] }}>
                    {RTR_DECISION_LABEL[gateResult.decision]} ›
                  </span>
                </button>
              )}

              <button onClick={recordDecision} disabled={decisionStatus === "saving" || decisionStatus === "saved" || (gateResult ? gateResult.decision === "A_RETESTER" : !!res?.retest)}
                style={{ marginTop:8, width:"100%", background:C.blue, border:"none", borderRadius:6, padding:"8px 0",
                  color:C.white, fontSize:10.5, fontWeight:800, letterSpacing:"0.06em", cursor:"pointer", fontFamily:"'Segoe UI',sans-serif",
                  opacity: decisionStatus === "saving" || (gateResult ? gateResult.decision === "A_RETESTER" : res?.retest) ? 0.6 : 1 }}>
                {(gateResult ? gateResult.decision === "A_RETESTER" : res?.retest) && decisionStatus !== "saving" ? "↻ DÉCISION SUSPENDUE — COMPLÉTER LE BILAN (MESURE PÉRIMÉE / QUESTION OBLIGATOIRE)"
                  : decisionStatus === "saving" ? "ENREGISTREMENT…"
                  : decisionStatus === "saved" ? "✓ DÉCISION ENREGISTRÉE"
                  : decisionStatus === "error" ? "⚠ ÉCHEC — RÉESSAYER"
                  : `ENREGISTRER LA DÉCISION ${gateResult ? `(${RTR_DECISION_LABEL[gateResult.decision]})` : `(${res?.validated ? "GO" : res?.warning ? "GO CONDITIONNEL" : "NO GO"})`} · ${dateStrToDisplay(testDate)}`}
              </button>

              {activeOvCount > 0 && (
                <div style={{ marginTop:6, fontSize:9, color:C.amber, background:C.amberBg,
                  border:`1px solid ${C.amberL}44`, borderRadius:4, padding:"3px 8px",
                  fontFamily:"'Segoe UI',sans-serif", display:"inline-block" }}>
                  {activeOvCount} override{activeOvCount > 1 ? "s" : ""} actif{activeOvCount > 1 ? "s" : ""}
                </div>
              )}
            </div>

            {/* Sub-tabs */}
            <div style={{ display:"flex", gap:3, overflowX:"auto", paddingBottom:4, marginBottom:10 }}>
              {allTabs.map(tab => {
                const active = tab.id === activeTab;
                return (
                  <button key={tab.id} onClick={() => { setActiveTab(tab.id); setFilter("all"); }}
                    style={{ flex:"0 0 auto", background: active ? C.orange : C.white,
                      border:`1.5px solid ${active ? C.orange : C.border}`,
                      borderRadius:6, padding:"6px 12px", cursor:"pointer",
                      color: active ? C.white : C.textSecondary,
                      fontSize:10, fontWeight:700, whiteSpace:"nowrap",
                      boxShadow: active ? `0 2px 8px ${C.orange}44` : "none" }}>
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {activeTab === RTR_INPUTS_TAB.id && (
              <RtrInputsPanel gate="RUN" result={runResult} measures={measures} onSet={markVal} ctx={ctxRun} givingWay={givingWay} onDeclareGivingWay={declareGivingWay} defaultDate={testDate} />
            )}
            {activeTab === RTR_SPORT_INPUTS_TAB.id && (
              <RtrInputsPanel gate="SPORT" result={sportResult} profile={rtrProfile} onProfile={changeProfile} measures={measures} onSet={markVal} ctx={ctx} givingWay={givingWay} onDeclareGivingWay={declareGivingWay} defaultDate={testDate} />
            )}
            {activeTab === RTR_GATE_TAB.id && gateResult && (
              <RtrGatePanel gate={gate} result={gateResult} profile={rtrProfile} onProfile={changeProfile} readOnly
                measures={measures} onSet={markVal} ctx={gateCtx} givingWay={givingWay} onDeclareGivingWay={declareGivingWay} defaultDate={testDate} />
            )}
            {activeTab === MRSS_TAB.id && (
              <MrssPanel result={mrssResult} measures={measures} ctx={ctx} onSet={markVal} onTrials={setTrials} staleTests={staleTests} />
            )}

            {!isExtraTab && (<>
            {/* Filter pills */}
            <div style={{ display:"flex", gap:3, marginBottom:10 }}>
              {[
                { id:"all",        lbl:`Tous (${currentTabItems.length})`,                                           col:C.anthracite },
                { id:"critical",   lbl:`● Critique (${currentTabItems.filter(i=>i.criticality==="critical").length})`,  col:C.red },
                { id:"important",  lbl:`◆ Important (${currentTabItems.filter(i=>i.criticality==="important").length})`,col:C.amber },
                { id:"informative",lbl:`○ Info (${currentTabItems.filter(i=>i.criticality==="informative").length})`,   col:C.blue },
              ].map(tab => (
                <button key={tab.id} onClick={() => setFilter(tab.id)}
                  style={{ flex:1, background: filter === tab.id ? `${tab.col}12` : C.surface,
                    border:`1px solid ${filter === tab.id ? tab.col+"55" : C.border}`,
                    borderRadius:5, padding:"5px 0", cursor:"pointer",
                    color: filter === tab.id ? tab.col : C.textMuted,
                    fontSize:8.5, fontWeight:700, fontFamily:"'Segoe UI',sans-serif" }}>
                  {tab.lbl}
                </button>
              ))}
            </div>

            {/* Criteria */}
            {currentTabItems.length === 0 && (
              <div style={{ textAlign:"center", color:C.textMuted, padding:"30px 0", fontSize:12 }}>
                Aucun critère dans cet onglet pour ce filtre.
              </div>
            )}
            {currentTabItems.map(c => (
              <Card key={c.id} c={c} value={getVal(activeTr, c.id)}
                onChange={v => setVal(activeTr, c.id, v)}
                showSource={showSources} view={viewMode}
                override={getOv(activeTr, c.id)}
                onOverride={v => setOv(activeTr, c.id, v)}/>
            ))}

            {/* Sources toggle */}
            <button onClick={() => setShowSources(!showSources)}
              style={{ background:"transparent", border:`1px solid ${C.border}`, borderRadius:6,
                color:C.textMuted, fontSize:9, padding:"7px 14px", cursor:"pointer",
                width:"100%", marginTop:10, fontFamily:"'Segoe UI',sans-serif", letterSpacing:"0.08em" }}>
              {showSources ? "▲ MASQUER SOURCES BIBLIOGRAPHIQUES" : "▼ AFFICHER SOURCES BIBLIOGRAPHIQUES"}
            </button>


            </>)}

            <div style={{ marginTop:14, padding:"9px 12px", background:C.surface, border:`1px solid ${C.border}`, borderRadius:8, fontSize:10.5, color:C.textSecondary, lineHeight:1.5 }}>
              📋 Questionnaires (IKDC, ACL-RSI, TSK-11, KOS-ADLS) : à remplir dans <b>Bibliothèque › Questionnaires</b>. Le score s'inscrit automatiquement dans le critère correspondant du scoring.
            </div>

            <div style={{ marginTop:10, padding:"8px 12px", background:C.surface,
              border:`1px solid ${C.border}`, borderRadius:6,
              fontSize:8.5, color:C.textMuted, lineHeight:1.7, fontFamily:"'Segoe UI',sans-serif" }}>
              OUTIL PÉDAGOGIQUE — USAGE PRATICIEN EXCLUSIF. Les critères ● CRITIQUE ne peuvent être outrepassés.
              Les overrides ◆ IMPORTANT requièrent une justification clinique écrite.
              Ne remplace pas le jugement clinique. Adapter selon greffon (DT4/KJ/QT) et contexte chirurgical.
              CAMPUS LCA · PARAMED'SPORT · JÉRÔME TUBEZ · v5.0 · MAI 2026
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// ── SCORING ADMIN WRAPPER ─────────────────────────────────────────────────
function ScoringAdmin({ patient, initTr, initTab, onBack, onPatientUpdate, onDataChanged }) {
  const parts = (patient.name || "").split(" ");
  const initP = {
    prenom: parts[0] || "",
    nom: parts.slice(1).join(" ") || "",
    sport: patient.sport || "",
    dateOp: patient.dateOp || "",
    typeGreffe: patient.greffon || "",
    coteOpere: (patient.cote || "").toLowerCase(), // fiche: "Droit"/"Gauche" → PatientForm: "droit"/"gauche"
  };
  return <ScoringTool initPatient={initP} fullPatient={patient} initTr={initTr} initTab={initTab} onBack={onBack} onPatientUpdate={onPatientUpdate} onDataChanged={onDataChanged} />;
}

// ── MRSS 2.0 — étape du scoring P3→P4, calculée sur test_results ──────────
// Plus de formulaire séparé : le score est calculé par src/mrssEngine.js sur les
// mêmes lignes que le scoring de phases. Seules les données propres au MRSS se
// saisissent ici (essais bruts des hop tests, partie F, partie E, dominance,
// vestibulaire, prévention) ; le reste est affiché avec son onglet d'origine.
const MRSS_VERDICT = {
  green:     { col:C.green, bg:C.greenBg, icon:"✅", label:"Feu vert RTS", sub:"Score ≥ 95 · TSK OK · Fitness OK · Critères complémentaires validés" },
  wait:      { col:C.amber, bg:C.amberBg, icon:"⏳", label:"Score atteint", sub:"Valider aussi : programme de prévention + délai post-op ≥ 9 mois" },
  no:        { col:C.red,   bg:C.redBg,   icon:"❌", label:"Sous le seuil", sub:`Score < ${MRSS_THRESHOLD} — poursuivre la rééducation ciblée` },
  tsk_fail:  { col:C.red,   bg:C.redBg,   icon:"🚫", label:"TSK-11 > 18 — test interrompu", sub:"Kinésiophobie trop élevée · RTS non envisagé · poursuivre le travail psychologique" },
  e_fail:    { col:C.red,   bg:C.redBg,   icon:"🚫", label:"Condition physique non validée", sub:"Un ou deux tests de condition physique sport-spécifiques non réussis" },
  incomplete:{ col:C.textMuted, bg:C.surface, icon:"📋", label:"À compléter", sub:"Renseignez les parties A, B, D et F" },
};
const MRSS_D_ROWS = [
  ["sebt", "SEBT — Star Excursion Balance (composite)", "cm", true, 10],
  ["sh", "Single Hop", "cm", false, 5],
  ["th", "Triple Hop", "cm", false, 5],
  ["ch", "Triple Crossover Hop", "cm", false, 5],
  ["sideh", "Side Hop", "reps/30s", false, 5],
  ["rise", "Single Leg Rise", "reps", false, 10],
];
const MRSS_F_ROWS = [
  ["fsh", "Single Hop (fatigue)", "cm"],
  ["fth", "Triple Hop (fatigue)", "cm"],
  ["fch", "Triple Crossover Hop (fatigue)", "cm"],
  ["fsideh", "Side Hop (fatigue)", "reps/30s"],
];

function MrssPassFail({ value, onChange, passValue = true, failValue = false }) {
  const cur = value === passValue ? "pass" : value === failValue ? "fail" : "";
  return (
    <select value={cur} onChange={e => onChange(e.target.value === "pass" ? passValue : e.target.value === "fail" ? failValue : undefined)}
      style={{ fontSize:12, padding:"5px 6px", borderRadius:6, border:`1px solid ${C.border}`, background:C.white, color:C.textPrimary, minWidth:96 }}>
      <option value="">—</option>
      <option value="pass">✓ Réussi</option>
      <option value="fail">✗ Échec</option>
    </select>
  );
}

function MrssTrialRow({ label, unit, trials, detail, maxPts, onChange }) {
  const t = trials || { inj:[], healthy:[] };
  const set = (side, i, x) => {
    const arr = [...(t[side] || [])];
    arr[i] = x === "" ? "" : parseFloat(x);
    onChange({ inj: side === "inj" ? arr : (t.inj || []), healthy: side === "healthy" ? arr : (t.healthy || []) });
  };
  const p10 = detail?.lsi != null ? (maxPts === 10 ? detail.pts : detail.pts * 2) : null;
  return (
    <div style={{ padding:"10px 14px", borderBottom:`1px solid ${C.border}` }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
        <span style={{ fontSize:12, fontWeight:600, color:C.textPrimary }}>{label} <span style={{ fontSize:10, color:C.textMuted }}>({unit})</span></span>
        <div style={{ display:"flex", gap:6, alignItems:"center" }}>
          {detail?.lsi != null && <span style={{ fontSize:11, fontWeight:700, color: p10 >= 8 ? C.green : p10 >= 4 ? C.amber : C.red }}>LSI {detail.lsi}%</span>}
          <span style={{ fontSize:11, fontWeight:700, color:C.textMuted }}>{detail?.pts ?? 0}/{maxPts} pts</span>
        </div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:6 }}>
        {[["inj",0,"Op. T1"],["inj",1,"Op. T2"],["healthy",0,"Sain T1"],["healthy",1,"Sain T2"]].map(([side,i,pl]) => (
          <div key={side+i} style={{ display:"flex", flexDirection:"column", gap:2 }}>
            <label style={{ fontSize:9, color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.05em" }}>{pl}</label>
            <input type="number" step="0.1" value={t[side]?.[i] ?? ""} onChange={e => set(side, i, e.target.value)} placeholder="—"
              style={{ width:"100%", fontSize:12, padding:"4px 5px", borderRadius:5, border:`1px solid ${C.border}`, textAlign:"center", fontFamily:"'Segoe UI',sans-serif" }} />
          </div>
        ))}
      </div>
    </div>
  );
}

function MrssReadRow({ label, value, origin }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:10, padding:"9px 14px", borderBottom:`1px solid ${C.border}` }}>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:12.5, color:C.textPrimary }}>{label}</div>
        <div style={{ fontSize:9.5, color:C.textMuted }}>↳ saisi dans : {origin}</div>
      </div>
      <span style={{ fontSize:12, fontWeight:700, color: value === null || value === undefined ? C.textMuted : C.textPrimary }}>{value ?? "non mesuré"}</span>
    </div>
  );
}

function MrssSection({ letter, title, meta, score, max, hurdle, children }) {
  return (
    <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:10, marginBottom:10, overflow:"hidden" }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", background:C.surface, borderBottom:`1px solid ${C.border}` }}>
        <div style={{ width:28, height:28, borderRadius:7, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", background: hurdle ? C.orange : C.blue, color:C.white, fontSize:12, fontWeight:700 }}>{letter}</div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:13, fontWeight:700, color:C.textPrimary }}>{title}</div>
          <div style={{ fontSize:10, color:C.textMuted }}>{meta}</div>
        </div>
        {max != null && <div style={{ fontSize:15, fontWeight:800, color:C.textPrimary }}>{score !== null && score !== undefined ? Math.round(score * 10) / 10 : "—"}<span style={{ fontSize:10, color:C.textMuted }}>/{max}</span></div>}
      </div>
      {children}
    </div>
  );
}

function MrssPanel({ result, measures, ctx, onSet, onTrials, staleTests = {} }) {
  const inp = buildMrssInputs(splitFresh(measures, ctx.date).fresh, ctx); // une mesure périmée = non mesurée
  const vi = MRSS_VERDICT[result.verdict] || MRSS_VERDICT.incomplete;
  const raw = id => measures[id]?.v;
  const delay = sportDelayMonths(measures, ctx);
  const ptsTxt = p => (p === undefined ? null : `${p} pts`);
  return (
    <div>
      <div style={{ background:vi.bg, border:`2px solid ${vi.col}40`, borderRadius:10, padding:"12px 14px", marginBottom:10 }}>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <div style={{ textAlign:"center", minWidth:64 }}>
            <div style={{ fontSize:34, fontWeight:800, color:vi.col, lineHeight:1 }}>{Math.round(result.total)}</div>
            <div style={{ fontSize:10, color:C.textMuted }}>/ 100 · seuil ≥ {MRSS_THRESHOLD}</div>
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:13, fontWeight:800, color:vi.col }}>{vi.icon} {vi.label}</div>
            <div style={{ fontSize:11, color:C.textSecondary, marginTop:2 }}>{vi.sub}</div>
            {!result.complete && <div style={{ fontSize:10, color:C.amber, marginTop:4 }}>Bilan incomplet : le MRSS n'alimente pas encore le hurdle RTR-SPORT H5 (non mesuré ou à retester).</div>}
            {(() => {
              const used = [...Object.values(MRSS_TRIAL_SOURCES), "p3→p4_epanch_p3", "p3→p4_flex_p3", "p3→p4_ext_p3"].filter(id => staleTests[id]);
              return used.length ? <div style={{ fontSize:10, color:C.blueMid, marginTop:4 }}>↻ Mesure(s) périmée(s), comptée(s) comme non mesurée(s) : {used.map(id => `${testOriginLabel(id).split(" › ").pop()} (${dateStrToDisplay(staleTests[id].date)})`).join(", ")}</div> : null;
            })()}
          </div>
        </div>
      </div>

      <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:10, padding:"10px 14px", marginBottom:10 }}>
        <div style={{ fontSize:10, color:C.textMuted, fontWeight:700, marginBottom:6, letterSpacing:"0.06em" }}>DOMINANCE DU CÔTÉ OPÉRÉ (barème LSI)</div>
        <div style={{ display:"flex", gap:8 }}>
          {[[false,"Côté opéré = non dominant"],[true,"Côté opéré = dominant"]].map(([v,lbl]) => (
            <button key={String(v)} onClick={() => onSet("mrss_dominance", v)}
              style={{ fontSize:11.5, padding:"6px 12px", borderRadius:99, cursor:"pointer", border:`1.5px solid ${inp.dom === v && raw("mrss_dominance") !== undefined ? C.blue : C.border}`,
                background: inp.dom === v && raw("mrss_dominance") !== undefined ? C.blue : C.white, color: inp.dom === v && raw("mrss_dominance") !== undefined ? C.white : C.textMuted }}>
              {lbl}
            </button>
          ))}
        </div>
      </div>

      <MrssSection letter="A" title="Examen clinique" meta="Brut /20 → /10 · saisi dans le scoring P3→P4" score={result.scA} max={10}>
        <MrssReadRow label="Épanchement (stroke test)" value={ptsTxt(inp.ep)} origin={testOriginLabel("p3→p4_epanch_p3")} />
        <MrssReadRow label="Stabilité — pivot shift" value={ptsTxt(inp.piv)} origin={testOriginLabel("p3→p4_pivot_p3")} />
        <MrssReadRow label="Flexion (déficit vs côté sain)" value={ptsTxt(inp.flex)} origin={testOriginLabel("p3→p4_flex_p3")} />
        <MrssReadRow label="Extension (prone hang)" value={ptsTxt(inp.ext)} origin={testOriginLabel("p3→p4_ext_p3")} />
      </MrssSection>

      <MrssSection letter="B" title="IKDC subjectif & ACL-RSI" meta="IKDC /10 + ACL-RSI 10 pts si ≥ 90 %" score={result.scB} max={20}>
        <MrssReadRow label="IKDC" value={result.ikdcScore !== null ? `${result.ikdcScore}/100 → ${Math.round(result.ikdcPts * 10) / 10} pts` : null} origin={testOriginLabel("p3→p4_ikdc_p3")} />
        <MrssReadRow label="ACL-RSI" value={result.rsiScore !== null ? `${result.rsiScore}% → ${result.rsiPts} pts` : null} origin={testOriginLabel("p3→p4_aclrsi_p3")} />
      </MrssSection>

      <MrssSection letter="C" title="Kinésiophobie — TSK-11" meta="Éliminatoire · ≤ 18 = OK" hurdle>
        <MrssReadRow label="TSK-11" value={result.tsk !== null ? `${result.tsk}/44 — ${result.tskPass ? "✓ réussi" : "✗ échec"}` : null} origin={testOriginLabel("p3→p4_tsk11_p3")} />
      </MrssSection>

      <MrssSection letter="D" title="Tests fonctionnels" meta={`Essais bruts · meilleur essai (moyenne pour le SEBT) · barème côté opéré ${inp.dom ? "dominant" : "non dominant"}`} score={result.scD} max={50}>
        {MRSS_D_ROWS.map(([k, label, unit, useAvg, maxPts]) => (
          <MrssTrialRow key={k} label={label} unit={unit} maxPts={maxPts} detail={result.details[k]}
            trials={trialsOf(measures[MRSS_TRIAL_SOURCES[k]])}
            onChange={t => onTrials(MRSS_TRIAL_SOURCES[k], t, !!useAvg)} />
        ))}
        <div style={{ padding:"10px 14px", display:"flex", gap:14, flexWrap:"wrap", alignItems:"center" }}>
          <span style={{ fontSize:12, fontWeight:600, color:C.textPrimary }}>Équilibre vestibulaire <span style={{ fontSize:10, color:C.textMuted }}>5 pts chacun</span></span>
          {[["p3→p4_bal_side_p3","Latéral"],["p3→p4_bal_updown_p3","Haut/bas"]].map(([id, lbl]) => (
            <span key={id} style={{ display:"flex", alignItems:"center", gap:6 }}>
              <span style={{ fontSize:11.5, color:C.textMuted }}>{lbl}</span>
              <MrssPassFail value={raw(id)} passValue={1} failValue={0} onChange={v => onSet(id, v)} />
            </span>
          ))}
        </div>
      </MrssSection>

      <MrssSection letter="E" title="Condition physique générale" meta="Éliminatoire · 2 tests ≥ pré-blessure · alimente aussi RTR-SPORT H6" hurdle>
        {[["mrss_e1","Test 1 — ≥ résultat pré-blessure (Yo-Yo, T-test, Illinois…)"],["mrss_e2","Test 2 — ≥ résultat pré-blessure"]].map(([id, lbl]) => (
          <div key={id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"9px 14px", borderBottom:`1px solid ${C.border}` }}>
            <span style={{ fontSize:12, flex:1, color:C.textPrimary }}>{lbl}</span>
            <MrssPassFail value={raw(id)} onChange={v => onSet(id, v)} />
          </div>
        ))}
      </MrssSection>

      <MrssSection letter="F" title="Tests fonctionnels en état de fatigue" meta="Fatigue ≥ 7/10 (EVA) · essais bruts" score={result.scF} max={20}>
        {MRSS_F_ROWS.map(([k, label, unit]) => (
          <MrssTrialRow key={k} label={label} unit={unit} maxPts={5} detail={result.details[k]}
            trials={trialsOf(measures[MRSS_TRIAL_SOURCES[k]])}
            onChange={t => onTrials(MRSS_TRIAL_SOURCES[k], t, false)} />
        ))}
      </MrssSection>

      <MrssSection letter="+" title="Critères complémentaires (phase 4)" meta="Requis pour le feu vert MRSS">
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"9px 14px", borderBottom:`1px solid ${C.border}` }}>
          <span style={{ fontSize:12, flex:1, color:C.textPrimary }}>Programme de prévention LCA ≥ 15 min avant chaque séance <span style={{ fontSize:9.5, color:C.textMuted }}>(= RTR-SPORT H7)</span></span>
          <MrssPassFail value={raw("rtr_sport_prevention")} onChange={v => onSet("rtr_sport_prevention", v)} />
        </div>
        <MrssReadRow label="Délai post-op ≥ 9 mois" value={delay === undefined ? null : `${delay} mois — ${delay >= 9 ? "✓" : "✗"}`}
          origin="Calculé depuis la date d'opération (fiche patient), en mois complets" />
      </MrssSection>

      <div style={{ fontSize:10, color:C.textMuted, lineHeight:1.5, margin:"4px 2px 12px" }}>
        Sources : Cooper R. &amp; Hughes M., Melbourne ACL Rehabilitation Guide 2.0 · IKDC : Anderson et al. 2006 · ACL-RSI : Webster et al. 2008 · TSK-11 : Woby et al. 2005
      </div>
    </div>
  );
}

// ── RTR SCORE — ÉTAPE FINALE DU SCORING DE PHASES ─────────────────────────
// UI branchée sur le moteur pur src/rtrEngine.js + les règles src/rtrRules.js
// (V1.13). Aucune logique de décision ici : ce composant affiche l'état
// renvoyé par computeRtrRun/computeRtrSport, il ne recalcule jamais un
// verdict lui-même. Palette/police reprises telles quelles du reste de
// l'app (navy/orange/C.* + Segoe UI) — pas de nouveau design system.

const RTR_STATUS_COLOR = { PASS: C.green, FAIL: C.red, NON_MESURE: C.textMuted, A_RETESTER: C.blueMid };
const RTR_DECISION_COLOR = { GO: C.green, GO_CONDITIONNEL: C.amber, NO_GO: C.red, A_RETESTER: C.blueMid };
const RTR_DECISION_BG = { GO: C.greenBg, GO_CONDITIONNEL: C.amberBg, NO_GO: C.redBg, A_RETESTER: C.blueLight };
const RTR_DECISION_LABEL = { GO: "GO", GO_CONDITIONNEL: "GO CONDITIONNEL", NO_GO: "NO GO", A_RETESTER: "À RETESTER" };
const RTR_CROSSLEY_OPTIONS = ["Pauvre (Poor)", "Passable (Fair)", "Bon (Good)"];
const RTR_EPANCH_OPTIONS = ["0 (absent)", "Trace", "1+ (léger)", "2+ (modéré)", "3+ (sévère)"];

function rtrEmpty(v) { return v === undefined || v === null || v === ""; }

function RtrMini({ label, val, onC, unit }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2 }}>
      <span style={{ fontSize:8, color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.05em" }}>{label}</span>
      <input type="number" value={rtrEmpty(val) ? "" : val} onChange={e => onC(e.target.value === "" ? undefined : parseFloat(e.target.value))}
        style={{ width:52, padding:"4px 5px", borderRadius:6, border:`1px solid ${C.border}`, fontSize:12, textAlign:"center", fontFamily:"'Segoe UI',sans-serif" }} />
      {unit && <span style={{ fontSize:7.5, color:C.textMuted }}>{unit}</span>}
    </div>
  );
}

function RtrBool({ value, onChange }) {
  return (
    <div style={{ display:"flex", gap:5 }}>
      <button onClick={() => onChange(true)} style={{ padding:"5px 11px", borderRadius:7, border:`1.5px solid ${value===true?C.green:C.border}`, background:value===true?C.greenBg:"#fff", color:value===true?C.green:C.textMuted, fontWeight:700, fontSize:11, cursor:"pointer" }}>Oui</button>
      <button onClick={() => onChange(false)} style={{ padding:"5px 11px", borderRadius:7, border:`1.5px solid ${value===false?C.red:C.border}`, background:value===false?C.redBg:"#fff", color:value===false?C.red:C.textMuted, fontWeight:700, fontSize:11, cursor:"pointer" }}>Non</button>
    </div>
  );
}

function RtrNumber({ value, onChange, unit }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:5 }}>
      <input type="number" value={rtrEmpty(value) ? "" : value} onChange={e => onChange(e.target.value === "" ? undefined : parseFloat(e.target.value))}
        style={{ width:64, padding:"5px 7px", borderRadius:7, border:`1px solid ${C.border}`, fontSize:12, fontFamily:"'Segoe UI',sans-serif" }} />
      {unit && <span style={{ fontSize:10, color:C.textMuted }}>{unit}</span>}
    </div>
  );
}

function RtrSelect({ value, onChange, options }) {
  return (
    <select value={value ?? ""} onChange={e => onChange(e.target.value || undefined)}
      style={{ padding:"5px 7px", borderRadius:7, border:`1px solid ${C.border}`, fontSize:12, fontFamily:"'Segoe UI',sans-serif", background:"#fff" }}>
      <option value="">—</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function RtrEpanch({ value, onChange }) {
  return (
    <select value={rtrEmpty(value) ? "" : value} onChange={e => onChange(e.target.value === "" ? undefined : parseInt(e.target.value))}
      style={{ padding:"5px 7px", borderRadius:7, border:`1px solid ${C.border}`, fontSize:12, fontFamily:"'Segoe UI',sans-serif", background:"#fff" }}>
      <option value="">—</option>
      {RTR_EPANCH_OPTIONS.map((l, i) => <option key={i} value={i}>{l}</option>)}
    </select>
  );
}

function RtrSymmetric({ value, onChange, unit }) {
  const v = value || {};
  const set = k => x => onChange({ ...v, [k]: x });
  return (
    <div style={{ display:"flex", gap:6 }}>
      <RtrMini label="Abs" unit={unit} val={v.abs} onC={set("abs")} />
      <RtrMini label="%EPIC" unit="%" val={v.epic} onC={set("epic")} />
      <RtrMini label="LSI" unit="%" val={v.lsi} onC={set("lsi")} />
    </div>
  );
}

function RtrDlSl({ value, onChange }) {
  const v = value || {};
  const set = k => x => onChange({ ...v, [k]: x });
  return (
    <div style={{ display:"flex", gap:6 }}>
      <RtrMini label="DL" val={v.dl} onC={set("dl")} />
      <RtrMini label="SL" val={v.sl} onC={set("sl")} />
    </div>
  );
}

function RtrQuadLsi({ value, onChange }) {
  const v = value || {};
  const set = k => x => onChange({ ...v, [k]: x });
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
      <div style={{ display:"flex", gap:6, alignItems:"center" }}>
        <RtrMini label="Isok LSI" unit="%" val={v.isok} onC={set("isok")} />
        <span style={{ fontSize:9, color:C.textMuted }}>prioritaire si renseigné</span>
      </div>
      <div style={{ display:"flex", gap:6 }}>
        <RtrMini label="Fallback MicroFET" unit="%" val={v.microfet} onC={set("microfet")} />
        <RtrMini label="%EPIC" unit="%" val={v.epic} onC={set("epic")} />
      </div>
    </div>
  );
}

function rtrHurdleKind(h) {
  if (h.id === "quad_lsi" && h.fallback) return "quad_lsi";
  if (h.direction === "lte_qualitative") return "epanch";
  if (h.direction === "gte_categorical") return "crossley";
  if (h.direction === "eq_bool" || h.direction === "gte_baseline") return "bool";
  if (h.direction === "eq" && typeof h.threshold === "string") return "bool";
  return "number";
}

function RtrHurdleInput({ hurdle, value, onChange }) {
  const kind = rtrHurdleKind(hurdle);
  if (kind === "quad_lsi") return <RtrQuadLsi value={value} onChange={onChange} />;
  if (kind === "epanch") return <RtrEpanch value={value} onChange={onChange} />;
  if (kind === "crossley") return <RtrSelect value={value} onChange={onChange} options={RTR_CROSSLEY_OPTIONS} />;
  if (kind === "bool") return <RtrBool value={value} onChange={onChange} />;
  return <RtrNumber value={value} onChange={onChange} unit={hurdle.unit} />;
}

function RtrCriterionInput({ criterion, value, onChange }) {
  const m = criterion.scale.mode;
  if (m === "boolean") return <RtrBool value={value} onChange={onChange} />;
  if (m === "categorical") return <RtrSelect value={value} onChange={onChange} options={Object.keys(criterion.scale.map)} />;
  return <RtrNumber value={value} onChange={onChange} unit={criterion.unit} />;
}

function RtrEntryInput({ ruleEntry, value, onChange }) {
  const kind = ruleEntry.inputKind;
  if (kind === "meets_bool") return <RtrBool value={value} onChange={onChange} />;
  if (kind === "symmetric") return <RtrSymmetric value={value} onChange={onChange} unit={ruleEntry.unit} />;
  if (kind === "dlsl") return <RtrDlSl value={value} onChange={onChange} />;
  if (kind === "informative_number") return <RtrNumber value={value} onChange={onChange} />;
  return null;
}

// Valeur de référence affichée sous chaque intitulé du gate RTR
const fmtRtrNum = x => (typeof x === "number" ? String(x).replace(".", ",") : x);
function rtrHurdleRef(h) {
  if (h.ref) return h.ref;
  const u = h.unit ? ` ${h.unit}` : "";
  switch (h.direction) {
    case "gte": return `≥ ${fmtRtrNum(h.threshold)}${u}`;
    case "gt": return `> ${fmtRtrNum(h.threshold)}${u}`;
    case "lte": return `≤ ${fmtRtrNum(h.threshold)}${u}`;
    case "eq": return typeof h.threshold === "number" ? `= ${h.threshold}${u}` : h.threshold;
    case "eq_bool": return h.threshold ? "Oui" : "Non";
    case "lte_qualitative": return `≤ ${h.threshold}`;
    case "gte_categorical": return "Bon (Crossley)";
    case "gte_baseline": return h.threshold === "exigé" ? "Exigé (≥ baseline / cible du sport)" : `${h.threshold}`;
    case "zero_giving_way": return "0 dérobement + réponse « non » au bilan du jour";
    default: return null;
  }
}
function rtrCriterionRef(c) {
  if (c.ref) return c.ref;
  const s = c.scale || {}, u = c.unit ? ` ${c.unit}` : "";
  switch (s.mode) {
    case "linear_range": return `0 pt à ${fmtRtrNum(s.min)}${u} → max à ${fmtRtrNum(s.max)}${u}`;
    case "linear_range_inverse": return `max à ${s.min}${u} → 0 pt à ${s.max}${u} (plus bas = mieux)`;
    case "ratio_to_threshold": return `max à ${fmtRtrNum(s.threshold)}${u}`;
    case "direct_percent": return "% direct (100 % = max)";
    case "boolean": return "Oui = max";
    case "categorical": return "Bon (Good) = max";
    default: return null;
  }
}

// Acronymes utilisés dans les gates RTR / MRSS
const RTR_GLOSSARY = [
  ["LSI", "Limb Symmetry Index — côté opéré / côté sain × 100"],
  ["EPIC", "Estimated Pre-Injury Capacity — force (ou saut) du côté opéré / valeur du côté SAIN mesurée avant l'opération × 100. Plus exigeant que le LSI : le côté sain se déconditionne aussi"],
  ["OLR", "One-Leg Rise — relevé unipodal depuis une chaise basse, répétitions côté opéré"],
  ["RSI", "Reactive Strength Index — hauteur de saut / temps de contact au sol (drop jump), en m/s"],
  ["DJ", "Drop jump — saut en contrebas puis rebond immédiat"],
  ["CMJ", "Countermovement jump — saut vertical avec contre-mouvement"],
  ["RPE", "Rating of Perceived Exertion — effort perçu de la séance, échelle 0-10"],
  ["IKDC", "International Knee Documentation Committee — questionnaire subjectif du genou /100"],
  ["ACL-RSI", "ACL Return to Sport after Injury — confiance psychologique /100"],
  ["TSK-11", "Tampa Scale of Kinesiophobia (11 items) — peur du mouvement /44, plus bas = mieux"],
  ["MRSS", "Melbourne Return to Sport Score 2.0 /100"],
  ["COD", "Change of direction — changement de direction"],
  ["UDel", "Programme de course de l'Université du Delaware (8 niveaux)"],
  ["PC", "Poids corporel (% PC)"],
];

function RtrGlossary() {
  return (
    <details style={{ background:"#fff", borderRadius:14, border:`1px solid ${C.border}`, padding:"10px 14px", marginBottom:12 }}>
      <summary style={{ fontSize:12, fontWeight:700, color:C.textSecondary, cursor:"pointer" }}>📖 Acronymes (LSI, EPIC, OLR, RSI…)</summary>
      <div style={{ marginTop:8 }}>
        {RTR_GLOSSARY.map(([k, v]) => (
          <div key={k} style={{ fontSize:11, color:C.textSecondary, lineHeight:1.5, marginBottom:3 }}><b style={{ color:C.textPrimary }}>{k}</b> — {v}</div>
        ))}
      </div>
    </details>
  );
}

function RtrRow({ label, sourceRef, status, detail, note, reference, children }) {
  const col = status ? RTR_STATUS_COLOR[status] : null;
  return (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:10, padding:"10px 0", borderBottom:`1px solid ${C.border}` }}>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          {col && <span style={{ width:8, height:8, borderRadius:"50%", background:col, flexShrink:0 }} />}
          <span style={{ fontSize:12.5, fontWeight:600, color:C.textPrimary }}>{label}</span>
          {sourceRef && <span style={{ fontSize:9, color:C.textMuted }}>({sourceRef})</span>}
        </div>
        {reference && <div style={{ fontSize:10.5, color:C.blueMid, fontWeight:600, marginTop:2 }}>🎯 Réf. : {reference}</div>}
        {detail && <div style={{ fontSize:10.5, color:C.textMuted, marginTop:2 }}>{detail}</div>}
        {note && <div style={{ fontSize:10, color:C.amber, marginTop:2 }}>{note}</div>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function RtrSectionCard({ title, sub, note, children }) {
  return (
    <div style={{ background:"#fff", borderRadius:14, border:`1px solid ${C.border}`, padding:14, marginBottom:12 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:note ? 4 : 8 }}>
        <span style={{ fontSize:13, fontWeight:800, color:C.textPrimary }}>{title}</span>
        {sub != null && <span style={{ fontSize:12, fontWeight:700, color:orange }}>{sub}</span>}
      </div>
      {note && <div style={{ fontSize:10, color:C.textMuted, marginBottom:8 }}>{note}</div>}
      {children}
    </div>
  );
}

// Valeur dérivée / convertie : pas de saisie ici, on indique où la mesure se saisit
const fmtRtrValue = v => {
  if (v === undefined || v === null || v === "") return null;
  if (v === true) return "Oui";
  if (v === false) return "Non";
  if (typeof v === "number") return fmtRtrNum(Math.round(v * 10) / 10);
  if (typeof v !== "object") return String(v);
  const n = x => (x === undefined || x === null || x === "" ? null : fmtRtrNum(Math.round(Number(x) * 100) / 100));
  const parts = [["Abs", v.abs], ["%EPIC", v.epic], ["LSI", v.lsi], ["DL", v.dl], ["SL", v.sl]].filter(([, x]) => n(x) !== null).map(([k, x]) => `${k} ${n(x)}`);
  if (v.meets !== undefined) parts.push(v.meets ? "Atteint" : "Non atteint");
  return parts.length ? parts.join(" · ") : null;
};

function RtrReadOnly({ info, value }) {
  const shown = fmtRtrValue(value);
  return (
    <div style={{ maxWidth:190, textAlign:"right", fontSize:9.5, color:C.textMuted, lineHeight:1.35 }}>
      {shown !== null && <div style={{ fontSize:12.5, fontWeight:800, color:C.textPrimary }}>{shown}</div>}
      ↳ {info.from.includes("date d'opération") || info.from.includes("carnet de séances") ? "" : "saisi dans : "}{info.from.map(testOriginLabel).join(" + ")}
    </div>
  );
}

// ── Saisies propres au gate RTR-RUN (V1.15, D1) ────────────────────────────
// Le gate n'est plus qu'une synthèse : ce qui n'existe dans aucun test de phase se saisit
// ici, une fois par bilan. Chaque entrée renvoie à une règle de src/rtrRules.js (`key`).
const RTR_RUN_INPUTS = [
  { section: "Drills de course — dernière séance", items: [
    { key: "epanchement_drills", testId: "rtr_run_epanch_drills", kind: "epanch", desc: "Stroke test pendant / juste après la dernière séance de drills de course." },
    { key: "douleur_course", testId: "rtr_run_douleur_drills", kind: "number", unit: "/10", desc: "EVA pendant les drills de course (pas la douleur à l'activité générale du bilan)." },
    { key: "reaction_j1", testId: "rtr_run_reaction_j1", kind: "bool", desc: "Oui = douleur ou gonflement le lendemain de la dernière séance de drills." },
    { key: "bassin_neutre", testId: "rtr_run_bassin_neutre", kind: "bool", desc: "Runeasi ou vidéo de face : bassin horizontal en phase d'appui. Informatif." },
  ]},
  { section: "Force réactive", items: [
    { key: "hop_vh_tolere", testId: "rtr_run_hop_vh_tolere", kind: "bool", desc: "Hopping vertical ET horizontal enchaînés sans douleur ni réaction J+1." },
    { key: "dj_rsi_sl", testId: "rtr_run_dj_rsi_sl", kind: "number", unit: "m/s", desc: "My Jump Lab : drop jump unipodal côté opéré. RSI = hauteur de saut (m) / temps de contact (s). Runeasi si module saut disponible." },
    { key: "dj_rsi_lsi", testId: "rtr_run_dj_rsi_lsi", kind: "number", unit: "%", desc: "Optionnel : RSI côté opéré / côté sain × 100." },
  ]},
  { section: "Tolérance de charge", items: [
    { key: "pain_next_morning_14j", testId: "rtr_run_pain_next_morning_14j", kind: "bool", desc: "Oui = aucune douleur au réveil sur les 14 jours précédant le bilan." },
    { key: "rpe_session_stable", testId: "rtr_run_rpe_stable", kind: "bool", auto: "rpe", desc: "Calculé depuis le carnet de séances si ≥ 2 séances notées sur 14 j ; sinon réponse manuelle." },
    { key: "freq_prescrite", testId: "rtr_run_freq_prescrite", kind: "number", unit: "séances/sem", label: "Séances prescrites par semaine", ref: "Sert au calcul de l'adhérence", desc: "Toutes séances confondues (cabinet, domicile, terrain)." },
    { key: "adherence_pct", testId: "rtr_run_adherence_pct", kind: "number", unit: "%", auto: "adherence", desc: "Calculée : séances notées au carnet sur 28 j ÷ (séances prescrites/sem × 4). Sinon saisie manuelle." },
  ]},
  { section: "Psychologique", items: [
    { key: "tsk11", testId: "p3→p4_tsk11_p3", kind: "number", unit: "/44", desc: "TSK-11 (questionnaire de kinésiophobie). Même ligne que le MRSS en P3→P4." },
  ]},
];
const RTR_RUN_INPUT_BY_TEST = Object.fromEntries(RTR_RUN_INPUTS.flatMap(g => g.items).filter(i => !i.testId.startsWith("p3→p4_"))
  .map(i => [i.testId, i.label || rtrRuleOf(i.key)?.label || i.key]));
function rtrRuleOf(key) {
  return RTR_RUN.hurdles.find(h => h.id === key) || (RTR_RUN.informative || []).find(h => h.id === key)
    || RTR_RUN.blocks.flatMap(b => b.criteria).find(c => c.id === key) || null;
}

// Saisies propres au gate RTR-SPORT (V1.15) : tout ce qui n'existe dans aucun test P3→P4 / MRSS.
// Repris automatiquement : délai, TSK-11, épanchement, MRSS, condition physique (MRSS E),
// prévention (MRSS), symétrie (LSI minimum), isocinétique (% PC + LSI), hop / CMJ vs baseline pré-op.
const RTR_SPORT_INPUTS = [
  { section: "Clinique & administratif", items: [
    { key: "rom", testId: "rtr_sport_rom", desc: "Goniomètre : extension complète ET flexion passive ≥ 130°. (Les écarts vs côté sain du MRSS ne donnent pas la valeur absolue.)" },
    { key: "accord_chirurgien", testId: "rtr_sport_accord_chirurgien", desc: "Accord écrit du chirurgien pour le retour au sport (courrier ou mail archivé)." },
  ]},
  { section: "Contrôle moteur", items: [
    { key: "motor_control", testId: "rtr_sport_motor_control", desc: "Squat unipodal > 90° de flexion, genou-hanche alignés dans le plan frontal." },
  ]},
  { section: "Force — dynamomètre manuel (% du poids de corps)", items: [
    { key: "hip_abd", testId: "rtr_sport_hip_abd", desc: "Abs = côté opéré en % PC ; %EPIC si baseline pré-op ; LSI = opéré / sain." },
    { key: "hip_add", testId: "rtr_sport_hip_add", desc: "Idem abducteurs." },
    { key: "hip_re", testId: "rtr_sport_hip_re", desc: "Rotateurs externes de hanche, isométrique." },
    { key: "calf_mvc", testId: "rtr_sport_calf_mvc", desc: "Fléchisseurs plantaires, isométrique de pointe." },
    { key: "ratio_add_abd", testId: "rtr_sport_ratio_add_abd", desc: "Informatif uniquement (non prédictif, Quintana-Cepedal 2025)." },
  ]},
  { section: "Explosivité & force réactive (My Jump Lab)", items: [
    { key: "cmj_height", testId: "rtr_sport_cmj_height", auto: "cmj", desc: "Calculé si la baseline CMJ unipodal pré-op et le vertical hop P3→P4 existent ; sinon réponse manuelle (≥ baseline / moyenne du sport)." },
    { key: "sl_hop_distance", testId: "rtr_sport_sl_hop_distance", auto: "hop", desc: "Calculé si la baseline single hop pré-op et le single hop P3→P4 existent ; sinon réponse manuelle." },
    { key: "drop_jump", testId: "rtr_sport_drop_jump", desc: "Drop jump bipodal et unipodal ≥ baseline / moyenne du sport." },
    { key: "contact_time", testId: "rtr_sport_contact_time", desc: "Temps de contact au sol (s), bipodal (DL) et unipodal (SL)." },
    { key: "rsi", testId: "rtr_sport_rsi", desc: "RSI (m/s) bipodal (DL) et unipodal côté opéré (SL)." },
  ]},
  { section: "Course, COD & charge sportive", items: [
    { key: "running_biomeca", testId: "rtr_sport_running_biomeca", desc: "Tapis (Runeasi) à la vitesse cible du profil : bassin / hanche neutres en appui." },
    { key: "cod", testId: "rtr_sport_cod", desc: "COD 90° planifié ET non planifié ≥ cible du profil (vidéo / chrono). Plus exigeant que « COD toléré » des critères P3→P4." },
    { key: "conditioning", testId: "rtr_sport_conditioning", desc: "Yo-Yo ou test spécifique au sport ≥ cible du profil." },
    { key: "sports_specific", testId: "rtr_sport_sports_specific", desc: "Programme sport-spécifique complété : scénarios validés, drills fermés et ouverts, sans réaction." },
  ]},
];
const RTR_SPORT_INPUT_BY_TEST = Object.fromEntries(RTR_SPORT_INPUTS.flatMap(g => g.items)
  .map(i => [i.testId, RTR_SPORT.thresholds[i.key]?.label || RTR_SPORT.hurdlesCommon.find(h => h.id === i.key)?.label || i.key]));
const RTR_SPORT_ALL_HURDLES = [...RTR_SPORT.hurdlesCommon, ...Object.values(RTR_SPORT.hurdlesByProfile).flat()];

function RtrInputsPanel({ gate = "RUN", result, profile, onProfile, measures, onSet, ctx, givingWay = [], onDeclareGivingWay, defaultDate }) {
  const sport = gate === "SPORT";
  const raw = id => measures[id]?.v;
  const qid = GIVING_WAY_QUESTION[sport ? "p3→p4" : "p2→p3"];
  const answer = measures[qid]?.date === defaultDate ? raw(qid) : undefined;
  const derob = result.hurdles.find(h => h.id === "derobements");
  const autoTxt = item => {
    if (item.auto === "rpe") {
      const v = rpeStableFromLog(ctx.sessionLog, ctx.date);
      return v === undefined ? "Carnet : moins de 2 séances notées sur 14 j → réponse manuelle ci-contre" : `Carnet de séances : ${v ? "stable" : "instable"} (prime sur la saisie manuelle)`;
    }
    if (item.auto === "adherence") {
      const v = adherenceFromLog(ctx.sessionLog, raw("rtr_run_freq_prescrite"), ctx.date);
      return v === undefined ? "Carnet vide sur 28 j ou fréquence prescrite absente → saisie manuelle ci-contre" : `Carnet de séances : ${v} % (prime sur la saisie manuelle)`;
    }
    if (item.auto === "hop" || item.auto === "cmj") {
      const v = vsPreopBaseline(measures, item.auto);
      return v ? `Calculé : ${fmtRtrNum(v.cur)} cm côté opéré vs ${fmtRtrNum(v.base)} cm côté sain pré-op (${fmtRtrNum(v.pct)} %) → ${v.meets ? "atteint" : "non atteint"} — prime sur la saisie manuelle`
        : "Baseline pré-op ou mesure P3→P4 absente → réponse manuelle ci-contre";
    }
    return null;
  };
  // Règle, référence et saisie de chaque entrée (hurdle, critère de score ou seuil par profil)
  const ruleOf = key => {
    if (!sport) {
      const r = rtrRuleOf(key) || {};
      return { label: r.label, sourceRef: r.sourceRef, ref: r.scale ? rtrCriterionRef(r) : rtrHurdleRef(r),
        input: (v, set, item) => item.kind === "epanch" ? <RtrEpanch value={v} onChange={set} /> : item.kind === "bool" ? <RtrBool value={v} onChange={set} /> : <RtrNumber value={v} onChange={set} unit={item.unit} /> };
    }
    const t = RTR_SPORT.thresholds[key];
    if (t) {
      const target = t[profile];
      return { label: t.label, ref: `${typeof target === "number" ? `${target}${t.unit ? ` ${t.unit}` : ""}` : target} (profil ${RTR_PROFILE_LABELS[profile]})`,
        input: (v, set) => <RtrEntryInput ruleEntry={t} value={v} onChange={set} /> };
    }
    const h = RTR_SPORT_ALL_HURDLES.find(x => x.id === key) || {};
    return { label: h.label, sourceRef: h.sourceRef, ref: rtrHurdleRef(h), input: (v, set) => <RtrHurdleInput hurdle={h} value={v} onChange={set} /> };
  };
  const statusOf = key => {
    const h = result.hurdles.find(x => x.id === key);
    if (h) return h.status;
    const entries = sport ? Object.values(result.composantes || {}).flatMap(c => c.entries) : result.blocks.flatMap(b => b.criteria);
    const c = entries.find(x => x.id === key);
    return c?.stale ? "A_RETESTER" : null;
  };
  const inputs = sport ? RTR_SPORT_INPUTS : RTR_RUN_INPUTS;
  return (
    <div>
      {sport && (
        <div style={{ display:"flex", gap:6, marginBottom:10 }}>
          {RTR_PROFILES.map(p => (
            <button key={p} onClick={() => onProfile(p)} style={{ flex:1, padding:"7px", borderRadius:8, border:`1.5px solid ${profile===p?C.orange:C.border}`, background:profile===p?`${C.orange}15`:C.white, color:profile===p?C.orange:C.textMuted, fontWeight:700, fontSize:11.5, cursor:"pointer" }}>
              {RTR_PROFILE_LABELS[p]}
            </button>
          ))}
        </div>
      )}
      <div style={{ fontSize:11, color:C.textSecondary, background:C.surface, borderRadius:10, padding:"9px 12px", marginBottom:12, lineHeight:1.5 }}>
        {sport
          ? "Saisies propres au gate RTR-SPORT qui n'existent dans aucun autre test. Repris automatiquement : délai (date d'op), TSK-11, épanchement, MRSS 2.0 et sa partie E, prévention (onglet MRSS), symétrie (LSI le plus bas des tests P3→P4), isocinétique (% poids de corps + LSI), single hop et CMJ vs baseline pré-op. Le résultat s'affiche dans « 🚦 RTR gate »."
          : "Saisies propres au gate RTR-RUN qui n'existent dans aucun autre test de phase. Le reste (délais, ROM, épanchement, force, hops, OLR, mollet, pogos, isocinétique, questionnaires) est repris automatiquement des onglets P2→P3. Le résultat s'affiche dans « 🚦 RTR gate »."}
      </div>

      <RtrSectionCard title="Dérobement post-op (hurdle bloquant)" note="Question obligatoire à chaque bilan. Sans réponse ce jour → À RETESTER, jamais GO.">
        <RtrRow label="Dérobement depuis le dernier bilan ?" status={derob?.status} detail={derob?.detail} reference={derob ? rtrHurdleRef(derob) : null}>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:5, maxWidth:230 }}>
            <GivingWayQuestion answer={answer} onAnswer={v => onSet(qid, v)} />
            <GivingWayInput ctx={ctx} events={givingWay} onDeclare={onDeclareGivingWay} defaultDate={defaultDate} />
          </div>
        </RtrRow>
      </RtrSectionCard>

      {inputs.map(group => (
        <RtrSectionCard key={group.section} title={group.section}>
          {group.items.map(item => {
            const r = ruleOf(item.key);
            return (
              <RtrRow key={item.key} label={item.label || r.label || item.key} sourceRef={r.sourceRef} status={statusOf(item.key)}
                reference={item.ref || r.ref} detail={item.desc} note={autoTxt(item)}>
                {r.input(raw(item.testId), v => onSet(item.testId, v), item)}
              </RtrRow>
            );
          })}
        </RtrSectionCard>
      ))}

      {!sport && <PlyoJorisCard value={measures[PLYO_JORIS.testId]?.v} onSet={v => onSet(PLYO_JORIS.testId, v)} />}
    </div>
  );
}

// Bloc « Pour passer au GO » : ce qui bloque, valeur actuelle vs cible, et où le saisir
const RTR_STATUS_TXT = { FAIL: "Échec", NON_MESURE: "Non mesuré", A_RETESTER: "À compléter / refaire" };
function RtrToGo({ result, map }) {
  const todo = result.hurdles.filter(h => h.status !== "PASS");
  const where = key => (rtrSourceInfo(map, key)?.from || []).map(testOriginLabel).join(" + ");
  const weak = (result.blocks || []).filter(b => b.pct !== null && b.pct !== undefined && b.pct < 60);
  const quad = result.hurdles.find(h => h.id === "quad_lsi");
  return (
    <RtrSectionCard title={todo.length ? `Pour passer au GO — ${todo.length} point(s)` : "✓ Tous les hurdles sont validés"}
      note={todo.length ? "Liste exhaustive des hurdles qui empêchent le GO (la décision ne dépend que d'eux, R1)." : "Pistes de progression (n'influencent pas la décision)."}>
      {todo.map(h => (
        <div key={h.id} style={{ padding:"7px 0", borderBottom:`1px solid ${C.border}` }}>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ width:8, height:8, borderRadius:"50%", background:RTR_STATUS_COLOR[h.status], flexShrink:0 }} />
            <span style={{ fontSize:12, fontWeight:700, color:C.textPrimary }}>{h.label}</span>
            <span style={{ fontSize:9.5, fontWeight:700, color:RTR_STATUS_COLOR[h.status] }}>{RTR_STATUS_TXT[h.status]}</span>
          </div>
          <div style={{ fontSize:10.5, color:C.textSecondary, marginTop:2 }}>Actuel : {h.detail} · <b>Cible : {rtrHurdleRef(h)}</b></div>
          {where(h.id) && <div style={{ fontSize:9.5, color:C.textMuted, marginTop:1 }}>↳ {where(h.id)}</div>}
        </div>
      ))}
      {!todo.length && result.decisionSource === "hurdle_fallback" && (
        <div style={{ fontSize:11, color:C.textSecondary, padding:"4px 0" }}>GO conditionnel par le fallback MicroFET + %EPIC : un bilan isocinétique ≥ 70 % lève le plafond.</div>
      )}
      {quad && quad.status === "PASS" && /isocinétique (\d+)/.test(quad.detail || "") && Number((quad.detail.match(/isocinétique (\d+)/) || [])[1]) < 80 && (
        <div style={{ fontSize:11, color:C.amber, padding:"4px 0" }}>LSI quadriceps sous 80 % : priorité au renforcement quadriceps (prévention de la récidive).</div>
      )}
      {weak.map(b => (
        <div key={b.id} style={{ fontSize:11, color:C.textSecondary, padding:"3px 0" }}>Bloc faible : {b.label} — {Math.round(b.pct)} %</div>
      ))}
    </RtrSectionCard>
  );
}

// Étape finale du scoring de phases (P2→P3 : RTR-RUN, P3→P4 : RTR-SPORT).
// Calcul 100 % délégué à src/rtrEngine.js sur les mesures test_results résolues par
// src/measureMap.js. Décision GO/GO_CONDITIONNEL/NO_GO = hurdles uniquement (R1) ;
// le score /100 reste un indicateur de suivi (R5).
// Dérobements : lus dans giving_way_events (pas de fenêtre de fraîcheur), comptés (>=) depuis
// l'entrée dans la phase en cours (V1.14, convention N4). Déclaration directe depuis le bilan.
// Question obligatoire « Dérobement depuis le dernier bilan ? » : 1 = oui, 0 = non.
function GivingWayQuestion({ answer, onAnswer }) {
  return (
    <div style={{ display:"flex", gap:5 }}>
      {[0, 1].map(v => (
        <button key={v} onClick={() => onAnswer(v)}
          style={{ flex:1, padding:"6px 8px", borderRadius:6, fontSize:10.5, fontWeight:700, cursor:"pointer",
            background: answer === v ? (v === 0 ? C.greenBg : C.redBg) : C.surface,
            border:`1.5px solid ${answer === v ? (v === 0 ? C.green+"66" : C.red+"66") : C.border}`,
            color: answer === v ? (v === 0 ? C.green : C.red) : C.textMuted }}>
          {v === 0 ? "✓ NON, aucun" : "✗ OUI — dater l'épisode"}
        </button>
      ))}
    </div>
  );
}

function GivingWayInput({ ctx, events, onDeclare, defaultDate }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(defaultDate);
  const [contexte, setContexte] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const counted = events.filter(e => (!ctx.givingWaySince || e.date_event >= ctx.givingWaySince) && e.date_event <= ctx.date);
  const submit = async () => {
    setBusy(true); setErr(null);
    try { await onDeclare({ date, contexte }); setOpen(false); setContexte(""); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  };
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4, maxWidth:200 }}>
      <span style={{ fontSize:9.5, color:C.textMuted, textAlign:"right" }}>
        {counted.length} événement(s) {ctx.givingWaySince ? `depuis le ${dateStrToDisplay(ctx.givingWaySince)}` : "au total"}{counted.length ? ` : ${counted.map(e => dateStrToDisplay(e.date_event)).join(", ")}` : ""}
      </span>
      {!open ? (
        <button onClick={() => { setDate(defaultDate); setOpen(true); }} style={{ fontSize:10, padding:"4px 9px", borderRadius:6, border:`1px solid ${C.red}55`, background:C.redBg, color:C.red, fontWeight:700, cursor:"pointer" }}>+ Déclarer un dérobement</button>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:4, alignItems:"flex-end" }}>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ fontSize:11, padding:"3px 5px", borderRadius:5, border:`1px solid ${C.border}` }} />
          <input value={contexte} onChange={e => setContexte(e.target.value)} placeholder="Contexte (optionnel)" style={{ fontSize:11, padding:"3px 5px", borderRadius:5, border:`1px solid ${C.border}`, width:160 }} />
          <div style={{ display:"flex", gap:4 }}>
            <button onClick={() => setOpen(false)} style={{ fontSize:10, padding:"3px 8px", borderRadius:5, border:`1px solid ${C.border}`, background:C.white, color:C.textMuted, cursor:"pointer" }}>Annuler</button>
            <button onClick={submit} disabled={busy || !date} style={{ fontSize:10, padding:"3px 8px", borderRadius:5, border:"none", background:C.red, color:C.white, fontWeight:700, cursor:"pointer" }}>{busy ? "…" : "Enregistrer"}</button>
          </div>
          {err && <span style={{ fontSize:9, color:C.red }}>{err}</span>}
        </div>
      )}
    </div>
  );
}

// Consigne chirurgicale plyométrie légère (Dr Joris, V1.14) — affichée, JAMAIS bloquante :
// n'entre ni dans les hurdles ni dans le score. Valeur stockée (test_results) :
// { ok: true } critères remplis · { ok: false, override: true, justification } override kiné tracé.
function PlyoJorisCard({ value, onSet }) {
  const v = value && typeof value === "object" ? value : {};
  const [just, setJust] = useState(v.justification || "");
  useEffect(() => { setJust(v.justification || ""); }, [v.justification]);
  const state = v.ok === true ? "ok" : v.override ? "override" : v.ok === false ? "ko" : null;
  const btn = (id, label, col, bg, next) => (
    <button onClick={() => onSet(next)} style={{ flex:1, padding:"6px 6px", borderRadius:6, fontSize:10, fontWeight:700, cursor:"pointer",
      background: state === id ? bg : C.surface, border:`1.5px solid ${state === id ? col+"66" : C.border}`, color: state === id ? col : C.textMuted }}>{label}</button>
  );
  return (
    <RtrSectionCard title={PLYO_JORIS.label} sub="consigne · non bloquant" note={`${PLYO_JORIS.statut}. ${PLYO_JORIS.override}. Source : ${PLYO_JORIS.source}.`}>
      <div style={{ fontSize:11, color:C.textSecondary, lineHeight:1.6, marginBottom:6 }}>
        {PLYO_JORIS.ouverture}<br/>
        {PLYO_JORIS.criteres.map(c => <span key={c}>• {c}<br/></span>)}
        <span style={{ color:C.textMuted }}>Cible : {PLYO_JORIS.cible}</span>
      </div>
      <div style={{ display:"flex", gap:5 }}>
        {btn("ok", "✓ Critères remplis", C.green, C.greenBg, { ok: true })}
        {btn("ko", "✗ Pas encore", C.red, C.redBg, { ok: false })}
        {btn("override", "⚡ Override kiné", C.amber, C.amberBg, { ok: false, override: true, justification: just })}
      </div>
      {state === "override" && (
        <div style={{ marginTop:6 }}>
          <textarea value={just} rows={2} placeholder="Justification obligatoire (ex. extension -2°, genou sec, squat unipodal bon — plyo bipodale basse intensité autorisée)"
            onChange={e => setJust(e.target.value)} onBlur={() => onSet({ ok: false, override: true, justification: just.trim() })}
            style={{ width:"100%", boxSizing:"border-box", fontSize:10.5, padding:"6px 8px", borderRadius:5, border:`1px solid ${C.borderMid}` }} />
          {!just.trim() && <div style={{ fontSize:9, color:C.red, marginTop:2 }}>⚠ Justification requise pour tracer l'override</div>}
        </div>
      )}
      <div style={{ fontSize:9.5, color:C.textMuted, marginTop:6 }}>{PLYO_JORIS.hors_perimetre}</div>
    </RtrSectionCard>
  );
}

function RtrGatePanel({ gate, result, profile, onProfile, measures, onSet, ctx, givingWay = [], onDeclareGivingWay, defaultDate, readOnly = false }) {
  const map = gate === "RUN" ? RTR_RUN_MAP : RTR_SPORT_MAP;
  const raw = id => measures[id]?.v;
  const qid = GIVING_WAY_QUESTION[gate === "RUN" ? "p2→p3" : "p3→p4"];
  const input = (key, render, value) => {
    const info = rtrSourceInfo(map, key);
    if (!info) return null;
    if (readOnly) return <RtrReadOnly info={info} value={value !== undefined ? value : info.testId && !info.derive ? raw(info.testId) : undefined} />;
    if (info.from.includes("giving_way_events")) {
      if (key !== "derobements") return <span style={{ fontSize:9.5, color:C.textMuted }}>↑ voir hurdle « Dérobement post-op »</span>;
      // Réponse affichée seulement si elle date de ce bilan (sinon : question à reposer)
      const answer = measures[qid]?.date === defaultDate ? raw(qid) : undefined;
      return (
        <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:5, maxWidth:230 }}>
          <span style={{ fontSize:9.5, fontWeight:700, color:C.textSecondary }}>Dérobement depuis le dernier bilan ?</span>
          <GivingWayQuestion answer={answer} onAnswer={v => onSet(qid, v)} />
          <GivingWayInput ctx={ctx} events={givingWay} onDeclare={onDeclareGivingWay} defaultDate={defaultDate} />
        </div>
      );
    }
    if (!info.editable) return <RtrReadOnly info={info} />;
    return render(raw(info.testId), v => onSet(info.testId, v));
  };
  const fallbackNote = key => {
    const info = rtrSourceInfo(map, key);
    return info?.fallbackLabel && raw(info.testId) === undefined ? `Non saisi — ${info.fallbackLabel}` : null;
  };
  const decisionCol = RTR_DECISION_COLOR[result.decision];

  return (
    <div>
      {gate === "SPORT" && (
        <div style={{ display:"flex", gap:6, marginBottom:10 }}>
          {RTR_PROFILES.map(p => (
            <button key={p} onClick={() => onProfile(p)} style={{ flex:1, padding:"7px", borderRadius:8, border:`1.5px solid ${profile===p?C.orange:C.border}`, background:profile===p?`${C.orange}15`:C.white, color:profile===p?C.orange:C.textMuted, fontWeight:700, fontSize:11.5, cursor:"pointer" }}>
              {RTR_PROFILE_LABELS[p]}
            </button>
          ))}
        </div>
      )}

      <div style={{ padding:14, borderRadius:10, background:RTR_DECISION_BG[result.decision], border:`2px solid ${decisionCol}66`, marginBottom:12 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
          <span style={{ fontSize:19, fontWeight:900, color:decisionCol }}>{RTR_DECISION_LABEL[result.decision]}</span>
          <span style={{ fontSize:14, fontWeight:800, color:C.textPrimary, textAlign:"right" }}>{result.score !== null ? `${result.score}/100` : "— /100"} <span style={{ fontSize:9, color:C.textMuted, fontWeight:600 }}>suivi</span>
            {result.scoreCoverage != null && <span style={{ display:"block", fontSize:9, fontWeight:600, color: result.scoreCoverage < 80 ? C.amber : C.textMuted }}>calculé sur {result.scoreCoverage} % des points mesurables</span>}
          </span>
        </div>
        <div style={{ fontSize:10, fontWeight:700, color:C.textSecondary, marginBottom:4 }}>{gate === "RUN" ? "RTR-RUN · gate T4 (P2→P3)" : `RTR-SPORT · gate T6 (P4→RTS) · profil ${RTR_PROFILE_LABELS[profile]}`} · règles {RTR_VERSION}</div>
        {result.action && <div style={{ fontSize:12, fontWeight:600, color:C.textPrimary, marginBottom:6 }}>{result.action}</div>}
        <div style={{ fontSize:11.5, color:C.textSecondary, lineHeight:1.5 }}>{result.justification}</div>
      </div>

      <RtrToGo result={result} map={map} />
      {readOnly && <div style={{ fontSize:10.5, color:C.textMuted, margin:"-4px 2px 12px" }}>Synthèse en lecture seule : les valeurs se saisissent dans les onglets {gate === "RUN" ? "P2→P3" : "P3→P4 (dont 📐 MRSS 2.0)"} et « {(gate === "RUN" ? RTR_INPUTS_TAB : RTR_SPORT_INPUTS_TAB).label} ».</div>}

      <RtrGlossary />

      <RtrSectionCard title="Hurdles bloquants (R1)" note="Un seul en échec ou non mesuré → NO_GO, quel que soit le score. Mesure périmée → À RETESTER (pas un NO_GO, mais pas de GO). Fraîcheur : force/hops/isocinétique 6 sem · épanchement/douleur/ROM 7 j. Valeurs lues dans test_results — une mesure partagée se saisit une seule fois.">
        {result.hurdles.map(h => (
          <RtrRow key={h.id} label={h.label} sourceRef={h.sourceRef} status={h.status} detail={h.detail} reference={rtrHurdleRef(h)} note={fallbackNote(h.id) || h.flag || h.note}>
            {input(h.id, (value, onChange) => <RtrHurdleInput hurdle={h} value={value} onChange={onChange} />)}
          </RtrRow>
        ))}
      </RtrSectionCard>

      {gate === "RUN" && (RTR_RUN.informative || []).length > 0 && (
        <RtrSectionCard title="Informatif — non bloquant" note="Affiché pour le suivi : n'influence ni la décision ni le score.">
          {RTR_RUN.informative.map(h => (
            <RtrRow key={h.id} label={h.label} sourceRef={h.sourceRef} reference={rtrHurdleRef(h)} note={h.note}>
              {input(h.id, (value, onChange) => <RtrHurdleInput hurdle={h} value={value} onChange={onChange} />)}
            </RtrRow>
          ))}
        </RtrSectionCard>
      )}

      {gate === "RUN" && !readOnly && <PlyoJorisCard value={measures[PLYO_JORIS.testId]?.v} onSet={v => onSet(PLYO_JORIS.testId, v)} />}

      {gate === "RUN" ? (
        result.blocks.map(block => (
          <RtrSectionCard key={block.id} title={`${block.label} — ${block.weight} pts`} sub={block.pct !== null ? `${Math.round(block.pct)}%` : "non mesuré"} note={block.note}>
            <ProgressBar value={block.pct || 0} max={100} color={orange} />
            <div style={{ marginTop:8 }}>
              {block.criteria.map(c => (
                <RtrRow key={c.id} label={`${c.label} (${c.weight} pts)`} status={c.stale ? "A_RETESTER" : null} reference={rtrCriterionRef(c)}
                  detail={c.stale ? `Mesure périmée (${dateStrToDisplay(c.stale.date)}, ${c.stale.ageDays} j > ${c.stale.maxDays} j) — exclue du score (R3), à refaire` : c.nonMesure ? "Non mesuré — exclu du score (R3)" : null}>
                  {input(c.id, (value, onChange) => <RtrCriterionInput criterion={c} value={value} onChange={onChange} />, c.value)}
                </RtrRow>
              ))}
            </div>
          </RtrSectionCard>
        ))
      ) : (
        RTR_SPORT_COMPOSANTES.map(comp => {
          if (comp.id === "clinical") {
            return (
              <RtrSectionCard key="clinical" title={comp.label} sub="100%">
                <div style={{ fontSize:11, color:C.textMuted, marginBottom:6 }}>Couvert par les hurdles bloquants communs (épanchement H3, ROM H4). Si un score est affiché, ces hurdles sont déjà validés (§5.1 R1) — bloc automatiquement à 100%.</div>
                <ProgressBar value={100} max={100} color={C.green} />
              </RtrSectionCard>
            );
          }
          const compResult = result.composantes[comp.id];
          if (!compResult) return null;
          const entries = compResult.entries.filter(e => e.applicable !== false);
          if (entries.length === 0) return null; // catégorie retirée pour ce profil (ex. COD en récréatif)
          return (
            <RtrSectionCard key={comp.id} title={comp.label} sub={compResult.fraction !== null ? `${Math.round(compResult.fraction * 100)}%` : "—"}>
              <ProgressBar value={(compResult.fraction || 0) * 100} max={100} color={orange} />
              <div style={{ marginTop:8 }}>
                {entries.map(e => {
                  const ruleEntry = RTR_SPORT.thresholds[e.id];
                  const targetTxt = typeof ruleEntry[profile] === "number" ? `${ruleEntry[profile]}${ruleEntry.unit ? ` ${ruleEntry.unit}` : ""}` : ruleEntry[profile];
                  return (
                    <RtrRow key={e.id} label={e.label} status={e.stale ? "A_RETESTER" : null} reference={targetTxt ? `${targetTxt} (profil ${RTR_PROFILE_LABELS[profile]})` : null}
                      detail={e.nonMesure ? e.note : e.stale ? `Mesure périmée (${dateStrToDisplay(e.stale.date)}, ${e.stale.ageDays} j > ${e.stale.maxDays} j) — exclue du score, à refaire` : `Cible ${RTR_PROFILE_LABELS[profile]} : ${targetTxt} · Tier ${e.tier || ruleEntry.tier}`}>
                      {ruleEntry.inputKind === "non_mesure"
                        ? <Badge label="NON MESURÉ" bg={C.amberBg} color={C.amber} />
                        : input(e.id, (value, onChange) => <RtrEntryInput ruleEntry={ruleEntry} value={value} onChange={onChange} />, e.value)}
                    </RtrRow>
                  );
                })}
              </div>
            </RtrSectionCard>
          );
        })
      )}

      {gate === "SPORT" && (
        <div style={{ padding:"0 4px", fontSize:10, color:C.textMuted, marginBottom:16, lineHeight:1.5 }}>
          Poids de bloc non détaillés par critère en {RTR_VERSION} pour les blocs B2/B3/B4/B6/B7 → répartition égale documentée dans src/rtrRules.js (champ weightSource). N'affecte jamais la décision GO/GO_CONDITIONNEL/NO_GO (R5) — score de suivi uniquement.
        </div>
      )}
    </div>
  );
}

// ── MOT DE PASSE : formulaire partagé (réinitialisation + changement depuis le profil) ──
function PasswordChangeForm({ token, onDone, submitLabel = "Enregistrer le mot de passe" }) {
  const [pw1, setPw1]       = useState("");
  const [pw2, setPw2]       = useState("");
  const [show, setShow]     = useState(false);
  const [err, setErr]       = useState("");
  const [saving, setSaving] = useState(false);

  async function save() {
    if (pw1.length < 8) { setErr("8 caractères minimum."); return; }
    if (pw1 !== pw2)    { setErr("Les deux mots de passe ne correspondent pas."); return; }
    if (!token)         { setErr("Session expirée — reconnecte-toi."); return; }
    setSaving(true); setErr("");
    try {
      await sbUpdatePassword(token, pw1);
      setPw1(""); setPw2("");
      onDone && onDone();
    } catch (e) {
      setErr(e.message || "Impossible d'enregistrer le mot de passe.");
    }
    setSaving(false);
  }

  const inp = { width:"100%", padding:"12px 44px 12px 14px", border:`1.5px solid ${border}`, borderRadius:12, fontSize:15, outline:"none", boxSizing:"border-box", background:"#f8fafc", fontFamily:"inherit", color:textCol };
  // iOS Safari : un champ "new-password" reçoit la suggestion « Mot de passe fort » (texte masqué,
  // impossible à afficher) et ne réagit pas toujours au changement de type. Quand on affiche :
  // remontage du champ (key) en type text, sans autocomplétion.
  const field = (id, value, setValue, placeholder, extra = {}) => (
    <div style={{ position:"relative", marginBottom:10 }}>
      <input key={`${id}-${show ? "t" : "p"}`} type={show ? "text" : "password"} value={value}
        onChange={e => setValue(e.target.value)} placeholder={placeholder}
        autoComplete={show ? "off" : "new-password"} autoCorrect="off" autoCapitalize="none" spellCheck={false}
        style={inp} {...extra} />
      <button type="button" onClick={() => setShow(!show)} aria-label={show ? "Masquer" : "Afficher"}
        style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", padding:4 }}>
        <Icon name={show ? "eyeOff" : "eye"} size={18} color={muted} />
      </button>
    </div>
  );
  return (
    <div>
      {field("pw1", pw1, setPw1, "Nouveau mot de passe (8 caractères min.)")}
      {field("pw2", pw2, setPw2, "Confirmer le mot de passe", { onKeyDown: e => { if (e.key === "Enter") save(); } })}
      <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:12.5, color:muted, marginBottom:12, cursor:"pointer" }}>
        <input type="checkbox" checked={show} onChange={e => setShow(e.target.checked)} /> Afficher les mots de passe
      </label>
      {err && <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:10, padding:"10px 14px", color:"#dc2626", fontSize:13, marginBottom:12 }}>{err}</div>}
      <button onClick={save} disabled={saving} style={{ width:"100%", padding:"14px", background:saving ? "#cbd5e1" : "linear-gradient(135deg,#f97316,#ea580c)", color:white, border:"none", borderRadius:12, fontSize:15, fontWeight:800, cursor:saving ? "not-allowed" : "pointer" }}>
        {saving ? "Enregistrement..." : submitLabel}
      </button>
    </div>
  );
}

// Écran affiché quand le patient revient via le lien "réinitialiser le mot de passe"
function ResetPasswordScreen({ recovery, onFinish }) {
  const [done, setDone] = useState(false);
  return (
    <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column", justifyContent:"flex-end", background:`linear-gradient(160deg,${navy} 0%,${navyL} 45%,#1e4080 100%)` }}>
      <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:10 }}>
        <span style={{ background:orange, color:white, fontWeight:900, fontSize:16, padding:"3px 10px", borderRadius:5 }}>CAMPUS</span>
        <span style={{ color:white, fontWeight:800, fontSize:26 }}>LCA</span>
      </div>
      <div style={{ background:white, borderRadius:"28px 28px 0 0", padding:"32px 24px 40px" }}>
        {recovery.error ? (
          <>
            <h3 style={{ color:textCol, fontSize:20, fontWeight:800, margin:"0 0 10px" }}>Lien non valide</h3>
            <p style={{ color:muted, fontSize:14, margin:"0 0 20px" }}>{recovery.error}</p>
            <button onClick={onFinish} style={{ width:"100%", padding:"14px", background:navy, color:white, border:"none", borderRadius:12, fontSize:15, fontWeight:800, cursor:"pointer" }}>Retour à la connexion</button>
          </>
        ) : done ? (
          <>
            <div style={{ fontSize:30, marginBottom:6 }}>✅</div>
            <h3 style={{ color:textCol, fontSize:20, fontWeight:800, margin:"0 0 10px" }}>Mot de passe modifié</h3>
            <p style={{ color:muted, fontSize:14, margin:"0 0 20px" }}>Tu peux maintenant te connecter avec ton nouveau mot de passe.</p>
            <button onClick={onFinish} style={{ width:"100%", padding:"14px", background:"linear-gradient(135deg,#f97316,#ea580c)", color:white, border:"none", borderRadius:12, fontSize:15, fontWeight:800, cursor:"pointer" }}>Se connecter →</button>
          </>
        ) : (
          <>
            <h3 style={{ color:textCol, fontSize:20, fontWeight:800, margin:"0 0 6px" }}>Nouveau mot de passe</h3>
            <p style={{ color:muted, fontSize:13, margin:"0 0 20px" }}>Choisis le mot de passe que tu utiliseras pour te connecter.</p>
            <PasswordChangeForm token={recovery.token} onDone={() => setDone(true)} />
          </>
        )}
      </div>
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const [email,   setEmail]   = useState("");
  const [pw,      setPw]      = useState("");
  const [showPw,  setShowPw]  = useState(false);
  const [err,     setErr]     = useState("");
  const [loading, setLoading] = useState(false);
  const [mode,    setMode]    = useState("login"); // login | forgot
  const [info,    setInfo]    = useState("");

  async function sendReset() {
    const e = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { setErr("Indique ton adresse email."); return; }
    setLoading(true); setErr(""); setInfo("");
    try {
      await sbRequestPasswordReset(e);
      // Message volontairement neutre : ne révèle pas si un compte existe pour cet email
      setInfo("Si un compte existe pour cette adresse, un email avec un lien de réinitialisation vient d'être envoyé. Pense à vérifier tes spams.");
    } catch (ex) {
      setErr(ex.message || "Envoi impossible. Réessaie dans un instant.");
    }
    setLoading(false);
  }

  async function go() {
    if (mode === "forgot") return sendReset();
    setLoading(true); setErr("");
    // 1. Compte de démo patient (local, instantané)
    const local = USERS[email.toLowerCase()];
    if (local && local.password === pw) {
      onLogin({ ...local, email });
      setLoading(false);
      return;
    }
    // 2. Sinon, vraie authentification Supabase — le rôle est déterminé côté serveur :
    //    - une ligne patients.auth_user_id correspond → connexion PATIENT (RLS: sa ligne uniquement)
    //    - présent dans public.admins → connexion ADMIN (RLS: toutes les lignes)
    try {
      const auth = await sbSignIn(email.trim(), pw);
      sbSetSession(auth);
      const uid = auth.user?.id;
      let own = null;
      try { own = uid ? await sbFetchOwnPatientRow(uid) : null; } catch (e2) { own = null; }
      if (own) {
        onLogin({ email, role: "patient", name: (own.name || "").split(" ")[0] || own.name, patient: own });
        setLoading(false);
        return;
      }
      const isAdmin = uid ? await sbIsAdmin(uid) : false;
      if (isAdmin) {
        onLogin({ email, role: "admin", name: "Jérôme" });
      } else {
        sbSetAccessToken(null);
        // RLS : une fiche en pause/archivée n'est plus lisible par le patient → même cas
        setErr("Accès suspendu ou compte non associé à un dossier. Contacte ton kiné.");
      }
    } catch (e) {
      // Distingue la vraie cause plutôt que d'afficher un message générique unique
      // (sbSignIn capture déjà error_description/msg/error du corps de réponse Supabase — voir plus haut).
      const detail = (e && e.message) || "";
      if (/email not confirmed/i.test(detail)) {
        setErr("Adresse email non confirmée — vérifie ta boîte mail et clique le lien de confirmation avant de te reconnecter.");
      } else if (/banned|user_banned/i.test(detail)) {
        setErr("Ton accès Campus LCA est suspendu. Contacte ton kiné pour le réactiver.");
      } else if (/invalid login credentials|invalid_credentials/i.test(detail)) {
        setErr("Email ou mot de passe incorrect.");
      } else {
        setErr(detail ? `Connexion impossible : ${detail}` : "Connexion impossible. Réessaie dans un instant.");
      }
    }
    setLoading(false);
  }

  return (
    <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column", background:`linear-gradient(160deg,${navy} 0%,${navyL} 45%,#1e4080 100%)`, position:"relative", overflow:"hidden" }}>
      <div style={{ position:"absolute", top:-60, right:-60, width:200, height:200, borderRadius:"50%", background:orange, opacity:0.07 }} />
      <div style={{ position:"absolute", top:120, left:-40, width:120, height:120, borderRadius:"50%", background:orange, opacity:0.07 }} />
      <div style={{ flex:1, display:"flex", flexDirection:"column", justifyContent:"center", alignItems:"center", paddingTop:60 }}>
        <div style={{ width:80, height:80, borderRadius:22, background:"linear-gradient(135deg,#f97316,#ea580c)", display:"flex", alignItems:"center", justifyContent:"center", marginBottom:20, boxShadow:"0 12px 40px rgba(249,115,22,0.4)" }}>
          <Icon name="trophy" size={38} color={white} />
        </div>
        <div style={{ textAlign:"center" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10, marginBottom:4 }}>
            <span style={{ background:orange, color:white, fontWeight:900, fontSize:16, padding:"3px 10px", borderRadius:5 }}>CAMPUS</span>
            <span style={{ color:white, fontWeight:800, fontSize:26 }}>LCA</span>
          </div>
          <span style={{ color:"rgba(255,255,255,0.45)", fontSize:12, letterSpacing:"0.1em", textTransform:"uppercase" }}>by Paramed Sport</span>
        </div>
      </div>
      <div style={{ background:white, borderRadius:"28px 28px 0 0", padding:"32px 24px 40px", boxShadow:"0 -8px 40px rgba(0,0,0,0.2)" }}>
        <h3 style={{ color:textCol, fontSize:20, fontWeight:800, margin:"0 0 6px" }}>{mode === "forgot" ? "Mot de passe oublié" : "Connexion"}</h3>
        <p style={{ color:muted, fontSize:13, margin:"0 0 24px" }}>{mode === "forgot" ? "Indique ton email : tu recevras un lien pour choisir un nouveau mot de passe." : "Accédez à votre programme personnalisé"}</p>
        <div style={{ marginBottom:14 }}>
          <div style={{ position:"relative" }}>
            <div style={{ position:"absolute", left:14, top:"50%", transform:"translateY(-50%)" }}><Icon name="mail" size={16} color={muted} /></div>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="votre@email.com" onKeyDown={e => { if (e.key === "Enter") go(); }} style={{ width:"100%", padding:"13px 14px 13px 42px", border:`1.5px solid ${border}`, borderRadius:12, fontSize:15, outline:"none", boxSizing:"border-box", background:"#f8fafc", fontFamily:"inherit", color:textCol }} />
          </div>
        </div>
        {mode === "login" && <div style={{ marginBottom:8 }}>
          <div style={{ position:"relative" }}>
            <div style={{ position:"absolute", left:14, top:"50%", transform:"translateY(-50%)" }}><Icon name="lock" size={16} color={muted} /></div>
            <input type={showPw ? "text" : "password"} value={pw} onChange={e => setPw(e.target.value)} placeholder="••••••••" onKeyDown={e => { if (e.key === "Enter") go(); }} style={{ width:"100%", padding:"13px 42px 13px 42px", border:`1.5px solid ${border}`, borderRadius:12, fontSize:15, outline:"none", boxSizing:"border-box", background:"#f8fafc", fontFamily:"inherit", color:textCol }} />
            <button onClick={() => setShowPw(!showPw)} style={{ position:"absolute", right:14, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", padding:0 }}>
              <Icon name={showPw ? "eyeOff" : "eye"} size={16} color={muted} />
            </button>
          </div>
        </div>}
        <div style={{ textAlign:"right", marginBottom:20 }}>
          <button onClick={() => { setMode(mode === "login" ? "forgot" : "login"); setErr(""); setInfo(""); }}
            style={{ background:"none", border:"none", padding:0, color:orange, fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
            {mode === "login" ? "Mot de passe oublié ?" : "← Retour à la connexion"}
          </button>
        </div>
        {err && <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:10, padding:"10px 14px", color:"#dc2626", fontSize:13, marginBottom:14 }}>{err}</div>}
        {info && <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:10, padding:"10px 14px", color:"#166534", fontSize:13, marginBottom:14 }}>{info}</div>}
        <button onClick={go} disabled={loading} style={{ width:"100%", padding:"15px", background:loading ? "#cbd5e1" : "linear-gradient(135deg,#f97316,#ea580c)", color:white, border:"none", borderRadius:14, fontSize:16, fontWeight:800, cursor:loading ? "not-allowed" : "pointer", boxShadow:loading ? "none" : "0 6px 20px rgba(249,115,22,0.35)" }}>
          {mode === "forgot" ? (loading ? "Envoi..." : "Recevoir le lien →") : (loading ? "Connexion..." : "Se connecter →")}
        </button>
      </div>
    </div>
  );
}

// ── PATIENT: DASHBOARD ───────────────────────────────────
function DashboardPatient({ user, onNav, extraTop = 0 }) {
  const today    = new Date().getDay();
  const todayIdx = today === 0 ? 6 : today - 1;
  const progDays = [0, 1, 3, 4];
  const doneDays = [0, 1];
  const hasToday = progDays.includes(todayIdx);
  const pt       = extraTop + 20;
  // Données réelles du dossier (connexion patient Supabase) — sinon valeurs démo
  const rec       = user.patient || null;
  const phaseNum  = rec ? rec.phaseId : 2;
  const semainePO = rec && rec.dateOp ? Math.max(1, Math.floor((Date.now() - new Date(rec.dateOp).getTime()) / 604800000) + 1) : 6;
  const pctPhase  = rec && rec.total > 0 ? Math.round((rec.sessions / rec.total) * 100) : 50;

  return (
    <div style={{ paddingBottom:80 }}>
      <div style={{ background:`linear-gradient(135deg,${navy},${navyL})`, padding:`${pt}px 20px 24px`, position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", top:-30, right:-30, width:120, height:120, borderRadius:"50%", background:"rgba(249,115,22,0.12)" }} />
        <LogoBadge />
        <div style={{ marginTop:18 }}>
          <p style={{ color:"rgba(255,255,255,0.6)", fontSize:13, margin:0 }}>Bonjour 👋</p>
          <h2 style={{ color:white, fontSize:24, fontWeight:800, margin:"2px 0 0" }}>{user.name}</h2>
        </div>
      </div>
      <div style={{ padding:"18px 14px 0" }}>
        <div style={{ background:"#fff7ed", border:"1.5px solid #fed7aa", borderRadius:14, padding:"12px 14px", display:"flex", alignItems:"center", gap:10, marginBottom:18 }}>
          <span style={{ fontSize:24 }}>🔥</span>
          <div>
            <div style={{ fontWeight:800, color:orange, fontSize:14 }}>2 jours consécutifs</div>
            <div style={{ color:"#9a3412", fontSize:12 }}>Continue comme ça, {user.name} !</div>
          </div>
        </div>
        <h3 style={{ fontSize:12, fontWeight:700, color:muted, letterSpacing:"0.08em", textTransform:"uppercase", margin:"0 0 10px" }}>Programme du jour</h3>
        {hasToday ? (
          <div style={{ background:`linear-gradient(135deg,${navy},${navyL})`, borderRadius:18, padding:18, marginBottom:18, position:"relative", overflow:"hidden" }}>
            <div style={{ position:"absolute", top:-20, right:-20, width:80, height:80, borderRadius:"50%", background:"rgba(249,115,22,0.15)" }} />
            <span style={{ background:orange, color:white, fontSize:10, fontWeight:700, padding:"3px 8px", borderRadius:6 }}>PHASE {phaseNum}</span>
            <h3 style={{ color:white, fontSize:18, fontWeight:800, margin:"8px 0 3px" }}>Rééducation LCA</h3>
            <p style={{ color:"rgba(255,255,255,0.6)", fontSize:13, margin:0 }}>Semaine {semainePO} post-op · 6 exercices · ~50 min</p>
            <button onClick={() => onNav("programme")} style={{ marginTop:16, width:"100%", padding:"12px", background:orange, color:white, border:"none", borderRadius:12, fontSize:15, fontWeight:800, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
              <Icon name="play" size={16} color={white} />
              Commencer la séance
            </button>
          </div>
        ) : (
          <div style={{ background:white, borderRadius:18, padding:18, marginBottom:18, textAlign:"center", border:`1.5px solid ${border}` }}>
            <div style={{ fontSize:34, marginBottom:8 }}>😴</div>
            <p style={{ color:muted, fontSize:14, margin:0 }}>Pas de séance aujourd&apos;hui. Profitez de votre récupération !</p>
          </div>
        )}
        <h3 style={{ fontSize:12, fontWeight:700, color:muted, letterSpacing:"0.08em", textTransform:"uppercase", margin:"0 0 10px" }}>Ma semaine</h3>
        <div style={{ background:white, borderRadius:16, padding:14, display:"flex", justifyContent:"space-between", marginBottom:18, boxShadow:"0 2px 12px rgba(0,0,0,0.06)" }}>
          {WEEK_DAYS.map((day, i) => {
            const hasProg  = progDays.includes(i);
            const done     = doneDays.includes(i);
            const isToday  = i === todayIdx;
            const circleBg = done ? success : isToday ? orange : hasProg ? "#dbeafe" : "transparent";
            const circleBo = isToday ? `2px solid ${orange}` : (hasProg && !done) ? "2px solid #93c5fd" : "2px solid transparent";
            return (
              <div key={i} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:5 }}>
                <span style={{ fontSize:11, fontWeight:700, color:isToday ? orange : muted }}>{day}</span>
                <div style={{ width:30, height:30, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", background:circleBg, border:circleBo }}>
                  {done ? <Icon name="check" size={12} color={white} /> : <span style={{ fontSize:11, fontWeight:700, color:isToday ? white : hasProg ? "#1d4ed8" : muted }}>{i + 1}</span>}
                </div>
                {hasProg && <div style={{ width:5, height:5, borderRadius:"50%", background:done ? success : orange }} />}
              </div>
            );
          })}
        </div>
        <div style={{ background:white, borderRadius:16, padding:"14px 18px", boxShadow:"0 2px 12px rgba(0,0,0,0.06)" }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
            <span style={{ fontSize:14, fontWeight:700, color:textCol }}>{rec ? `Phase ${phaseNum} — ${rec.sessions}/${rec.total} séances` : "Semaine 6 / 12"}</span>
            <span style={{ fontSize:14, fontWeight:800, color:orange }}>{pctPhase}%</span>
          </div>
          <div style={{ background:"#e2e8f0", borderRadius:99, height:7 }}>
            <div style={{ width:`${pctPhase}%`, height:"100%", borderRadius:99, background:"linear-gradient(90deg,#f97316,#fbbf24)" }} />
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", marginTop:14 }}>
            {[{l:"Séances",v:"8"},{l:"Cette semaine",v:"2/4"},{l:"Objectif",v:"Reprise"}].map((s, i) => (
              <div key={i} style={{ textAlign:"center" }}>
                <div style={{ fontSize:18, fontWeight:800, color:textCol }}>{s.v}</div>
                <div style={{ fontSize:11, color:muted, marginTop:2 }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── PATIENT: PROGRAMMES LIST ─────────────────────────────
function ProgrammesPatient({ exlib, progs, sessions, onStartSession, extraTop = 0 }) {
  const todoSessions = sessions.filter(s => s.status === "todo");
  const doneSessions = sessions.filter(s => s.status === "done");

  function SessionCard({ s, dim }) {
    const prog = progs.find(p => p.id === s.progId);
    if (!prog) return null;
    const ph  = PHASES.find(p => p.id === prog.phaseId);
    const pc  = phaseColor(prog.phaseId);
    const dis = dateStrToDisplay(s.dateStr);
    return (
      <button onClick={() => { if (!dim) onStartSession(s); }} style={{ width:"100%", background:dim ? "#f8fafc" : white, border:`1.5px solid ${border}`, borderRadius:14, padding:14, marginBottom:10, cursor:dim ? "default" : "pointer", textAlign:"left", boxShadow:dim ? "none" : "0 2px 12px rgba(0,0,0,0.06)", opacity:dim ? 0.6 : 1 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
          <div>
            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:5 }}>
              <Badge label={ph ? ph.label : ""} bg={`${pc}20`} color={pc} />
              {s.status === "done" && <Badge label="✓ Terminé" bg="#dcfce7" color="#16a34a" />}
            </div>
            <div style={{ fontWeight:700, color:textCol, fontSize:14 }}>{prog.name}</div>
            <div style={{ color:muted, fontSize:12, marginTop:2 }}>{dis} · {prog.exos.length} exercices · ~{prog.dur} min</div>
          </div>
          {s.status === "todo" && (
            <div style={{ background:orange, borderRadius:10, padding:"8px 10px" }}><Icon name="play" size={16} color={white} /></div>
          )}
        </div>
      </button>
    );
  }

  return (
    <div style={{ paddingBottom:80 }}>
      <PageHeader title="Mes programmes" sub={`${todoSessions.length} séance(s) à réaliser`} extraTop={extraTop} />
      <div style={{ padding:"16px 14px" }}>
        {todoSessions.length > 0 && (
          <>
            <h3 style={{ fontSize:12, fontWeight:700, color:muted, letterSpacing:"0.08em", textTransform:"uppercase", margin:"0 0 10px" }}>À faire</h3>
            {todoSessions.map(s => <SessionCard key={s.id} s={s} dim={false} />)}
          </>
        )}
        {doneSessions.length > 0 && (
          <>
            <h3 style={{ fontSize:12, fontWeight:700, color:muted, letterSpacing:"0.08em", textTransform:"uppercase", margin:"16px 0 10px" }}>Terminées</h3>
            {doneSessions.map(s => <SessionCard key={s.id} s={s} dim={true} />)}
          </>
        )}
      </div>
    </div>
  );
}

// ── PATIENT: SESSION ACTIVE ──────────────────────────────
function SessionActive({ prog, exlib, startedAt, onBack, onFinish }) {
  const [exIdx,   setExIdx]   = useState(0);
  const [view,    setView]    = useState("overview");
  const [mods,    setMods]    = useState(() => prog.exos.map(e => ({ ...e.p })));
  const [elapsed, setElapsed] = useState(0);
  const [showRPE, setShowRPE] = useState(false);
  const [sessionRPE, setSessionRPE] = useState(0);
  const [sessionRemarks, setSessionRemarks] = useState("");
  const startRef = useRef(startedAt);

  useEffect(() => {
    const iv = setInterval(() => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)), 1000);
    return () => clearInterval(iv);
  }, []);

  const ex     = prog.exos[exIdx];
  const exData = exlib.find(e => e.id === ex.exId);
  const phase  = PHASES.find(p => p.id === prog.phaseId);
  const isLast = exIdx === prog.exos.length - 1;

  function updateMod(field, val) {
    setMods(prev => prev.map((m, i) => {
      if (i !== exIdx) return m;
      const parsed = val === "" ? null : isNaN(Number(val)) ? val : Number(val);
      return { ...m, [field]: parsed };
    }));
  }

  function ChronoBar() {
    const backLabel = view === "exercise" ? "Vue d'ensemble" : "Mes séances";
    return (
      <div style={{ background:`linear-gradient(135deg,${navy},${navyL})`, padding:"10px 16px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <button onClick={() => { if (view === "exercise") { setView("overview"); } else { onBack(); } }} style={{ background:"rgba(255,255,255,0.12)", border:"none", borderRadius:8, padding:"5px 10px", color:white, fontSize:12, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:5 }}>
          <Icon name="arrowL" size={13} color={white} />
          {backLabel}
        </button>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <Icon name="timer" size={16} color={orange} />
          <span style={{ color:white, fontWeight:800, fontSize:18, letterSpacing:"0.06em" }}>{fmtT(elapsed)}</span>
        </div>
        <div style={{ background:"rgba(255,255,255,0.08)", borderRadius:8, padding:"4px 10px" }}>
          <span style={{ color:"rgba(255,255,255,0.7)", fontSize:11, fontWeight:700 }}>{prog.exos.length} exos</span>
        </div>
      </div>
    );
  }

  // ── Écran RPE fin de séance
  if (showRPE) {
    const durationMin = Math.round(elapsed / 60);
    const chargeCalc = sessionRPE > 0 ? sessionRPE * durationMin : null;
    const rpeData = sessionRPE > 0 ? RPE_SCALE[sessionRPE - 1] : null;
    return (
      <div style={{ paddingBottom:80 }}>
        <div style={{ background:`linear-gradient(135deg,${navy},${navyL})`, padding:"18px 16px 16px" }}>
          <LogoBadge />
          <h2 style={{ color:white, fontWeight:800, fontSize:18, margin:"8px 0 2px" }}>Séance terminée 🎉</h2>
          <p style={{ color:"rgba(255,255,255,0.5)", fontSize:12, margin:0 }}>{prog.name} · {durationMin} min chrono</p>
        </div>
        <div style={{ padding:"16px 14px" }}>

          {/* Durée affichée (depuis chrono) */}
          <div style={{ background:white, borderRadius:12, padding:"12px 14px", marginBottom:14, boxShadow:"0 1px 6px rgba(0,0,0,0.06)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div>
              <div style={{ fontSize:11, color:muted, marginBottom:2 }}>Durée chrono</div>
              <div style={{ fontSize:22, fontWeight:800, color:textCol }}>{durationMin} <span style={{ fontSize:13, fontWeight:400 }}>min</span></div>
            </div>
            <Icon name="timer" size={28} color={orange} />
          </div>

          {/* RPE picker */}
          <div style={{ background:white, borderRadius:12, padding:"14px", marginBottom:14, boxShadow:"0 1px 6px rgba(0,0,0,0.06)" }}>
            <div style={{ fontWeight:700, color:textCol, fontSize:13, marginBottom:10 }}>Comment as-tu ressenti cette séance ?</div>
            <RPEPicker value={sessionRPE} onChange={setSessionRPE} />
          </div>

          {/* Confirmation RPE sélectionné — charge masquée côté patient */}
          {sessionRPE > 0 && (
            <div style={{ background:"#f0fdf4", border:"1.5px solid #86efac", borderRadius:10, padding:"10px 12px", marginBottom:14, display:"flex", alignItems:"center", gap:8 }}>
              <Icon name="check" size={15} color={success} />
              <span style={{ fontSize:12, color:"#166534", fontWeight:600 }}>RPE enregistré — ton kiné verra l&apos;analyse complète</span>
            </div>
          )}

          {/* Remarques */}
          <div style={{ background:white, borderRadius:12, padding:"12px 14px", marginBottom:16, boxShadow:"0 1px 6px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize:11, color:muted, marginBottom:6 }}>Remarque (optionnel)</div>
            <textarea
              value={sessionRemarks}
              onChange={e => setSessionRemarks(e.target.value)}
              placeholder="Douleur, fatigue particulière, point à signaler au kiné..."
              rows={3}
              style={{ width:"100%", fontSize:13, padding:"8px 10px", border:`1.5px solid ${border}`, borderRadius:8, background:"#f8fafc", color:textCol, resize:"vertical", fontFamily:"inherit", boxSizing:"border-box" }}
            />
          </div>

          <button
            onClick={() => { if (sessionRPE > 0) onFinish({ rpe:sessionRPE, durationMin, load:chargeCalc, remarks:sessionRemarks }); }}
            style={{ width:"100%", padding:"14px", background:sessionRPE > 0 ? success : "#cbd5e1", border:"none", borderRadius:14, fontWeight:800, color:white, cursor:sessionRPE > 0 ? "pointer" : "not-allowed", fontSize:15, display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}
          >
            <Icon name="check" size={18} color={white} />
            Valider et terminer →
          </button>
          {sessionRPE === 0 && <div style={{ textAlign:"center", fontSize:11, color:muted, marginTop:8 }}>Sélectionne ton RPE pour valider</div>}
        </div>
      </div>
    );
  }

  if (view === "overview") {
    return (
      <div style={{ paddingBottom:80 }}>
        <ChronoBar />
        <div style={{ background:`linear-gradient(135deg,${navy},${navyL})`, padding:"0 16px 20px" }}>
          <Badge label={phase ? phase.label : ""} bg={`${phaseColor(prog.phaseId)}30`} color={phaseColor(prog.phaseId)} />
          <h2 style={{ color:white, fontWeight:800, fontSize:18, margin:"6px 0 2px" }}>{prog.name}</h2>
          <p style={{ color:"rgba(255,255,255,0.5)", fontSize:12, margin:0 }}>~{prog.dur} min</p>
          <button onClick={() => { setExIdx(0); setView("exercise"); }} style={{ marginTop:14, width:"100%", padding:"12px", background:orange, color:white, border:"none", borderRadius:12, fontSize:15, fontWeight:800, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
            <Icon name="play" size={16} color={white} />
            Démarrer la séance
          </button>
        </div>
        <div style={{ padding:14 }}>
          {prog.exos.map((e, i) => {
            const ed = exlib.find(x => x.id === e.exId);
            return (
              <button key={i} onClick={() => { setExIdx(i); setView("exercise"); }} style={{ width:"100%", background:white, border:`1.5px solid ${border}`, borderRadius:12, padding:"12px 14px", marginBottom:8, cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", gap:12, boxShadow:"0 1px 8px rgba(0,0,0,0.05)" }}>
                <div style={{ width:32, height:32, borderRadius:10, background:`${orange}18`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontWeight:800, color:orange, fontSize:14 }}>{i + 1}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, color:textCol, fontSize:13 }}>{ed ? ed.name : "—"}</div>
                  <div style={{ color:muted, fontSize:11, marginTop:2 }}>{fmtMod(e.p)}</div>
                </div>
                <Icon name="arrow" size={16} color={muted} />
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const pct = Math.round(((exIdx + 1) / prog.exos.length) * 100);
  return (
    <div style={{ paddingBottom:80 }}>
      <ChronoBar />
      <div style={{ background:navy, padding:"8px 16px 12px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
          <span style={{ color:"rgba(255,255,255,0.6)", fontSize:12 }}>Exercice {exIdx + 1} / {prog.exos.length}</span>
          <span style={{ color:orange, fontWeight:700, fontSize:12 }}>{pct}%</span>
        </div>
        <div style={{ background:"rgba(255,255,255,0.15)", borderRadius:99, height:4 }}>
          <div style={{ width:`${pct}%`, height:"100%", borderRadius:99, background:orange }} />
        </div>
      </div>
      <div style={{ padding:"14px 14px 0" }}>
        <div style={{ borderRadius:14, overflow:"hidden", marginBottom:14, background:"#000", aspectRatio:"16/9" }}>
          {exData && <iframe width="100%" height="100%" src={`https://www.youtube.com/embed/${exData.ytId}?rel=0`} frameBorder="0" allowFullScreen style={{ display:"block" }} />}
        </div>
        <h2 style={{ color:textCol, fontWeight:800, fontSize:18, margin:"0 0 4px" }}>{exData ? exData.name : "—"}</h2>
        <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginBottom:10 }}>
          {exData && exData.cats.map(c => <Badge key={c} label={c} bg={`${orange}15`} color={orange} />)}
        </div>
        {exData && <p style={{ color:muted, fontSize:13, lineHeight:1.5, margin:"0 0 14px" }}>{exData.desc}</p>}
        <div style={{ background:`${navy}10`, border:`1.5px solid ${navy}20`, borderRadius:12, padding:"12px 14px", marginBottom:12 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
            <Icon name="flag" size={14} color={navy} />
            <span style={{ fontSize:11, fontWeight:800, color:navy, letterSpacing:"0.06em", textTransform:"uppercase" }}>Prescrit par Jérôme</span>
          </div>
          <span style={{ fontSize:14, fontWeight:700, color:textCol }}>{fmtMod(ex.p)}</span>
          {ex.p.repos > 0 && <span style={{ fontSize:12, color:muted }}> · Repos {ex.p.repos}s</span>}
        </div>
        <div style={{ background:"#f0fdf4", border:"1.5px solid #86efac", borderRadius:12, padding:"12px 14px", marginBottom:16 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:10 }}>
            <Icon name="edit" size={14} color="#16a34a" />
            <span style={{ fontSize:11, fontWeight:800, color:"#15803d", letterSpacing:"0.06em", textTransform:"uppercase" }}>Ce que j&apos;ai fait</span>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
            {[{key:"series",label:"Séries",unit:"",inputType:"number"},{key:"reps",label:"Répét.",unit:"",inputType:"number"},{key:"charge",label:"Charge",unit:"kg",inputType:"number"},{key:"duree",label:"Durée",unit:"s",inputType:"number"},{key:"cote",label:"Côté",unit:"",inputType:"text"}].map(f => (
              <div key={f.key} style={{ background:white, borderRadius:10, padding:"8px 10px", border:"1px solid #bbf7d0" }}>
                <div style={{ fontSize:10, color:"#16a34a", fontWeight:700, marginBottom:4 }}>{f.label}</div>
                <div style={{ display:"flex", alignItems:"center", gap:3 }}>
                  <input type={f.inputType} value={mods[exIdx] ? (mods[exIdx][f.key] ?? "") : ""} onChange={e => updateMod(f.key, e.target.value)} placeholder="—" style={{ width:"100%", border:"none", background:"transparent", fontSize:16, fontWeight:800, color:textCol, outline:"none", fontFamily:"inherit", padding:0 }} />
                  {f.unit && <span style={{ fontSize:11, color:muted }}>{f.unit}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display:"flex", gap:10 }}>
          {exIdx > 0 && (
            <button onClick={() => setExIdx(i => i - 1)} style={{ flex:1, padding:"12px", background:white, border:`1.5px solid ${border}`, borderRadius:12, fontWeight:700, color:textCol, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:6, fontSize:14 }}>
              <Icon name="arrowL" size={16} color={textCol} />Précédent
            </button>
          )}
          {isLast ? (
            <button onClick={() => setShowRPE(true)} style={{ flex:2, padding:"12px", background:success, border:"none", borderRadius:12, fontWeight:800, color:white, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:6, fontSize:14 }}>
              <Icon name="check" size={16} color={white} />Terminer la séance
            </button>
          ) : (
            <button onClick={() => setExIdx(i => i + 1)} style={{ flex:2, padding:"12px", background:orange, border:"none", borderRadius:12, fontWeight:800, color:white, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:6, fontSize:14 }}>
              Suivant<Icon name="arrow" size={16} color={white} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── PATIENT: CALENDRIER ──────────────────────────────────
function CalendrierPatient({ sessions, setSessions, progs, extraTop = 0 }) {
  const now   = new Date();
  const [year,  setYear]  = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  const [moveModal, setMoveModal]       = useState(null);

  const daysInMonth  = getDaysInMonth(year, month);
  const firstDay     = getFirstDayOfMonth(year, month);
  const todayStr     = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}`;

  function sessionForDay(d) {
    const ds = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
    return sessions.find(s => s.dateStr === ds);
  }

  function moveSession(sessionId, newD) {
    const ds = `${year}-${String(month+1).padStart(2,"0")}-${String(newD).padStart(2,"0")}`;
    setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, dateStr: ds } : s));
    setMoveModal(null);
    setSelectedDate(newD);
  }

  function prevMonth() { if (month === 0) { setMonth(11); setYear(y => y - 1); } else { setMonth(m => m - 1); } setSelectedDate(null); }
  function nextMonth() { if (month === 11) { setMonth(0); setYear(y => y + 1); } else { setMonth(m => m + 1); } setSelectedDate(null); }

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const selSession = selectedDate ? sessionForDay(selectedDate) : null;
  const selProg    = selSession ? progs.find(p => p.id === selSession.progId) : null;
  const selPhase   = selProg ? PHASES.find(p => p.id === selProg.phaseId) : null;

  return (
    <div style={{ paddingBottom:80 }}>
      <PageHeader title="Mon calendrier" sub="Glissez vos séances selon vos disponibilités" extraTop={extraTop} />

      {/* Month navigation */}
      <div style={{ background:white, padding:"14px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", borderBottom:`1px solid ${border}` }}>
        <button onClick={prevMonth} style={{ background:`${orange}15`, border:"none", borderRadius:8, padding:"6px 10px", cursor:"pointer" }}>
          <Icon name="arrowL" size={16} color={orange} />
        </button>
        <span style={{ fontWeight:800, color:textCol, fontSize:16 }}>{MONTH_NAMES[month]} {year}</span>
        <button onClick={nextMonth} style={{ background:`${orange}15`, border:"none", borderRadius:8, padding:"6px 10px", cursor:"pointer" }}>
          <Icon name="arrowR" size={16} color={orange} />
        </button>
      </div>

      {/* Legend */}
      <div style={{ padding:"10px 16px 0", display:"flex", gap:12 }}>
        {[{color:orange,label:"À faire"},{color:success,label:"Terminée"},{color:"#94a3b8",label:"Aujourd'hui"}].map((l,i)=>(
          <div key={i} style={{ display:"flex", alignItems:"center", gap:4 }}>
            <div style={{ width:8, height:8, borderRadius:"50%", background:l.color }} />
            <span style={{ fontSize:11, color:muted }}>{l.label}</span>
          </div>
        ))}
      </div>

      {/* Day headers */}
      <div style={{ padding:"10px 12px 0" }}>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(7, 1fr)", marginBottom:4 }}>
          {DAY_NAMES.map(d => (
            <div key={d} style={{ textAlign:"center", fontSize:10, fontWeight:700, color:muted, padding:"4px 0" }}>{d}</div>
          ))}
        </div>

        {/* Calendar grid */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(7, 1fr)", gap:3 }}>
          {cells.map((d, i) => {
            if (!d) return <div key={i} />;
            const ds       = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
            const sess     = sessions.find(s => s.dateStr === ds);
            const isToday  = ds === todayStr;
            const isSel    = selectedDate === d;
            const isDone   = sess && sess.status === "done";
            const isTodo   = sess && sess.status === "todo";
            return (
              <button key={i} onClick={() => setSelectedDate(d === selectedDate ? null : d)} style={{ aspectRatio:"1", borderRadius:10, border:"none", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", background: isSel ? navy : isToday ? `${orange}15` : "transparent", position:"relative", padding:2 }}>
                <span style={{ fontSize:13, fontWeight:isToday ? 800 : isSel ? 800 : 500, color: isSel ? white : isToday ? orange : textCol }}>{d}</span>
                {(isDone || isTodo) && (
                  <div style={{ width:6, height:6, borderRadius:"50%", background: isDone ? success : orange, marginTop:2 }} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected day detail */}
      {selectedDate && (
        <div style={{ margin:"14px 12px 0" }}>
          {selSession && selProg ? (
            <div style={{ background:white, borderRadius:14, padding:14, boxShadow:"0 2px 12px rgba(0,0,0,0.06)", border:`1.5px solid ${border}` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                <div>
                  <Badge label={selPhase ? selPhase.label : ""} bg={`${phaseColor(selProg.phaseId)}20`} color={phaseColor(selProg.phaseId)} />
                  <div style={{ fontWeight:700, color:textCol, fontSize:15, marginTop:5 }}>{selProg.name}</div>
                  <div style={{ color:muted, fontSize:12, marginTop:2 }}>{dateStrToDisplay(selSession.dateStr)} · ~{selProg.dur} min</div>
                </div>
                <Badge label={selSession.status === "done" ? "✓ Terminée" : "À faire"} bg={selSession.status === "done" ? "#dcfce7" : "#fff7ed"} color={selSession.status === "done" ? "#16a34a" : orange} />
              </div>
              {selSession.status === "todo" && (
                <button onClick={() => setMoveModal(selSession)} style={{ width:"100%", padding:"10px", border:`1.5px solid ${border}`, borderRadius:10, background:"transparent", color:textCol, fontWeight:700, fontSize:13, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                  <Icon name="move" size={15} color={textCol} />
                  Déplacer cette séance
                </button>
              )}
            </div>
          ) : (
            <div style={{ background:white, borderRadius:14, padding:14, boxShadow:"0 2px 12px rgba(0,0,0,0.06)", border:`1.5px solid ${border}`, textAlign:"center" }}>
              <p style={{ color:muted, fontSize:14, margin:0 }}>Pas de séance le {selectedDate} {MONTH_NAMES[month].toLowerCase()}</p>
            </div>
          )}
        </div>
      )}

      {/* Move modal */}
      {moveModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:200, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
          <div style={{ background:white, borderRadius:"20px 20px 0 0", padding:"22px 18px 36px", width:"100%", maxWidth:430 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <h3 style={{ margin:0, color:textCol, fontWeight:800 }}>Déplacer la séance</h3>
              <button onClick={() => setMoveModal(null)} style={{ background:"none", border:"none", cursor:"pointer" }}><Icon name="close" size={22} color={muted} /></button>
            </div>
            <p style={{ color:muted, fontSize:13, marginBottom:16 }}>Choisissez un nouveau jour dans {MONTH_NAMES[month]}</p>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(7, 1fr)", gap:6, marginBottom:16 }}>
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(d => {
                const ds    = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                const busy  = sessions.find(s => s.dateStr === ds && s.id !== moveModal.id);
                const isCur = moveModal.dateStr === ds;
                return (
                  <button key={d} disabled={!!busy || isCur} onClick={() => moveSession(moveModal.id, d)} style={{ padding:"8px 0", borderRadius:8, border:`1.5px solid ${busy || isCur ? border : orange}`, background: busy ? "#f8fafc" : isCur ? `${orange}15` : white, color: busy ? muted : isCur ? orange : textCol, fontWeight:700, fontSize:13, cursor: busy || isCur ? "default" : "pointer", opacity: busy ? 0.4 : 1 }}>
                    {d}
                  </button>
                );
              })}
            </div>
            <div style={{ background:"#eff6ff", border:"1.5px solid #bfdbfe", borderRadius:10, padding:"10px 13px", fontSize:12, color:"#1d4ed8" }}>
              Les jours grisés ont déjà une séance planifiée.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── PATIENT: DOCUMENTS ───────────────────────────────────
function DocumentsScreen({ extraTop = 0 }) {
  const [tab, setTab] = useState("questionnaires");
  const qs = [
    { title:"Bilan initial LCA",         sub:"À compléter avant la 1ère séance",    st:"done",      icon:"form" },
    { title:"Suivi douleur — Semaine 6", sub:"Évaluation hebdomadaire",              st:"todo",      icon:"form" },
    { title:"Questionnaire IKDC",         sub:"Évaluation fonctionnelle genou",      st:"todo",      icon:"form" },
    { title:"Satisfaction — Phase 1",     sub:"Retour sur les 4 premières semaines", st:"done",      icon:"form" },
  ];
  const ds = [
    { title:"Consentement éclairé",       sub:"Document légal signé",               st:"signed",    icon:"sign" },
    { title:"Programme exercices S6",     sub:"PDF — Semaine en cours",              st:"available", icon:"doc" },
    { title:"Recommandations post-op.",   sub:"Protocole général LCA",              st:"available", icon:"doc" },
  ];
  const cfg = { done:{label:"Complété ✓",bg:"#dcfce7",color:"#16a34a"}, todo:{label:"À faire",bg:"#fff7ed",color:"#ea580c"}, signed:{label:"Signé ✓",bg:"#dcfce7",color:"#16a34a"}, available:{label:"Disponible",bg:"#eff6ff",color:"#2563eb"} };
  const ic  = { done:"#16a34a", todo:orange, signed:"#16a34a", available:"#2563eb" };
  const ib  = { done:"#dcfce7", todo:"#fff7ed", signed:"#dcfce7", available:"#eff6ff" };
  const items    = tab === "questionnaires" ? qs : ds;
  const todoCount = qs.filter(q => q.st === "todo").length;
  return (
    <div style={{ paddingBottom:80 }}>
      <PageHeader title="Mes documents" sub="Questionnaires et fichiers coaching" extraTop={extraTop} />
      <div style={{ background:white, display:"flex", borderBottom:`1px solid ${border}` }}>
        {[{id:"questionnaires",label:"Questionnaires",icon:"form"},{id:"documents",label:"Documents",icon:"doc"}].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ flex:1, padding:"13px 8px", border:"none", background:"none", cursor:"pointer", borderBottom:tab === t.id ? `2.5px solid ${orange}` : "2.5px solid transparent", color:tab === t.id ? orange : muted, fontWeight:tab === t.id ? 700 : 500, fontSize:13, display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
            <Icon name={t.icon} size={15} color={tab === t.id ? orange : muted} />
            {t.label}
            {t.id === "questionnaires" && todoCount > 0 && <span style={{ background:orange, color:white, fontSize:10, fontWeight:800, width:17, height:17, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center" }}>{todoCount}</span>}
          </button>
        ))}
      </div>
      <div style={{ padding:14 }}>
        {tab === "questionnaires" && todoCount > 0 && (
          <div style={{ background:"#fff7ed", border:"1.5px solid #fed7aa", borderRadius:12, padding:"10px 13px", marginBottom:12, display:"flex", gap:8, alignItems:"center" }}>
            <Icon name="warning" size={16} color={orange} />
            <div>
              <div style={{ fontWeight:700, color:"#9a3412", fontSize:13 }}>{todoCount} questionnaire(s) à compléter</div>
              <div style={{ color:"#c2410c", fontSize:12 }}>Votre kiné attend vos réponses</div>
            </div>
          </div>
        )}
        {items.map((it, i) => (
          <div key={i} style={{ background:white, borderRadius:14, padding:13, marginBottom:8, boxShadow:"0 2px 10px rgba(0,0,0,0.05)", display:"flex", alignItems:"center", gap:12, border:it.st === "todo" ? "1.5px solid #fed7aa" : `1.5px solid ${border}` }}>
            <div style={{ width:42, height:42, borderRadius:11, flexShrink:0, background:ib[it.st], display:"flex", alignItems:"center", justifyContent:"center" }}>
              <Icon name={it.icon} size={19} color={ic[it.st]} />
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:700, color:textCol, fontSize:14, marginBottom:2 }}>{it.title}</div>
              <div style={{ color:muted, fontSize:11, marginBottom:5 }}>{it.sub}</div>
              <Badge label={cfg[it.st].label} bg={cfg[it.st].bg} color={cfg[it.st].color} />
            </div>
            {(it.st === "todo" || it.st === "available") && (
              <button style={{ background:it.st === "todo" ? orange : navy, color:white, border:"none", borderRadius:9, padding:"7px 11px", cursor:"pointer", fontSize:11, fontWeight:700, flexShrink:0 }}>
                {it.st === "todo" ? "Remplir" : "Ouvrir"}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── PATIENT: PROFIL ──────────────────────────────────────
function ProfilScreen({ user, onLogout, extraTop = 0 }) {
  const pt = extraTop + 24;
  const [showPwForm, setShowPwForm] = useState(false);
  const [pwChanged,  setPwChanged]  = useState(false);
  // Uniquement pour un vrai compte Supabase (pas les comptes démo ni l'aperçu praticien)
  const canChangePw = !!(onLogout && sbAccessToken);
  return (
    <div style={{ paddingBottom:80 }}>
      <div style={{ background:`linear-gradient(135deg,${navy},${navyL})`, padding:`${pt}px 20px 50px`, textAlign:"center" }}>
        <div style={{ width:76, height:76, borderRadius:"50%", background:orange, margin:"0 auto 12px", display:"flex", alignItems:"center", justifyContent:"center", fontSize:30, fontWeight:800, color:white }}>{user.name[0]}</div>
        <h2 style={{ color:white, fontWeight:800, margin:"0 0 4px" }}>{user.name}</h2>
        <p style={{ color:"rgba(255,255,255,0.5)", fontSize:13, margin:0 }}>{user.email}</p>
      </div>
      <div style={{ padding:"18px 14px" }}>
        {(user.patient ? [
          {l:"Kiné référent",      v:"Jérôme Tubez"},
          {l:"Programme actuel",   v:(PHASES.find(p => p.id === user.patient.phaseId) || {}).label || "—"},
          {l:"Date d'opération",   v:user.patient.dateOp ? dateStrToDisplay(user.patient.dateOp) : "—"},
          {l:"Greffon",            v:GREFFON_LABELS[user.patient.greffon] || user.patient.greffon || "—"},
          {l:"Séances effectuées", v:`${user.patient.sessions} / ${user.patient.total}`},
        ] : [{l:"Kiné référent",v:"Jérôme Lambrecht"},{l:"Programme actuel",v:"Rééducation LCA — Phase 2"},{l:"Début du programme",v:"10 février 2025"},{l:"Prochain RDV",v:"2 avril 2025"}]).map((it, i) => (
          <div key={i} style={{ background:white, borderRadius:12, padding:"13px 16px", marginBottom:8, display:"flex", justifyContent:"space-between" }}>
            <span style={{ color:muted, fontSize:14 }}>{it.l}</span>
            <span style={{ color:textCol, fontWeight:700, fontSize:14 }}>{it.v}</span>
          </div>
        ))}
        {canChangePw && (
          <div style={{ background:white, borderRadius:12, padding:"13px 16px", marginTop:14 }}>
            <button onClick={() => { setShowPwForm(!showPwForm); setPwChanged(false); }}
              style={{ width:"100%", background:"none", border:"none", padding:0, display:"flex", justifyContent:"space-between", alignItems:"center", cursor:"pointer", fontFamily:"inherit" }}>
              <span style={{ display:"flex", alignItems:"center", gap:8, color:textCol, fontWeight:700, fontSize:14 }}><Icon name="lock" size={16} color={navy} /> Changer mon mot de passe</span>
              <span style={{ color:muted, fontSize:16 }}>{showPwForm ? "▴" : "▾"}</span>
            </button>
            {pwChanged && <div style={{ marginTop:12, background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:10, padding:"10px 14px", color:"#166534", fontSize:13 }}>✅ Mot de passe modifié. Utilise-le à ta prochaine connexion.</div>}
            {showPwForm && (
              <div style={{ marginTop:14 }}>
                <PasswordChangeForm token={sbAccessToken} onDone={() => { setPwChanged(true); setShowPwForm(false); }} />
              </div>
            )}
          </div>
        )}
        {onLogout && (
          <button onClick={onLogout} style={{ marginTop:14, width:"100%", padding:"13px", background:"#fef2f2", border:"1.5px solid #fecaca", borderRadius:12, color:"#dc2626", fontWeight:700, fontSize:15, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
            <Icon name="logout" size={18} color="#dc2626" />
            Se déconnecter
          </button>
        )}
      </div>
    </div>
  );
}

// ── ADMIN: CALENDRIER SEMAINE ────────────────────────────
function CalendrierAdminSemaine({ onBack }) {
  const now     = new Date();
  const dayOfW  = now.getDay() === 0 ? 6 : now.getDay() - 1;
  const monday  = new Date(now); monday.setDate(now.getDate() - dayOfW);

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday); d.setDate(monday.getDate() + i);
    return d;
  });

  const weekSessions = [
    { patient:"Thomas D.", prog:"Séance B — Renforcement S6", day:1, heure:"09h00", phase:2 },
    { patient:"Thomas D.", prog:"Séance B — Renforcement S6", day:3, heure:"09h00", phase:2 },
    { patient:"Lucas D.",  prog:"Séance C — Pliométrie",      day:1, heure:"10h30", phase:3 },
    { patient:"Lucas D.",  prog:"Séance C — Pliométrie",      day:4, heure:"10h30", phase:3 },
    { patient:"Marie L.",  prog:"Séance A — Activation",      day:2, heure:"11h00", phase:1 },
    { patient:"Marie L.",  prog:"Séance A — Activation",      day:4, heure:"11h00", phase:1 },
  ];

  const dayLabels = ["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"];

  return (
    <div style={{ paddingBottom:80 }}>
      <PageHeader title="Semaine en cours" sub={`${weekDays[0].getDate()}/${weekDays[0].getMonth()+1} — ${weekDays[6].getDate()}/${weekDays[6].getMonth()+1}/${weekDays[6].getFullYear()}`} onBack={onBack} />
      <div style={{ padding:14 }}>
        <div style={{ background:"#eff6ff", border:"1.5px solid #bfdbfe", borderRadius:11, padding:"10px 13px", marginBottom:14, display:"flex", gap:8, alignItems:"center" }}>
          <Icon name="stat" size={15} color="#2563eb" />
          <span style={{ color:"#1d4ed8", fontSize:12, fontWeight:600 }}>8 séances planifiées cette semaine · 3 patients actifs</span>
        </div>
        {weekDays.map((d, i) => {
          const daySessions = weekSessions.filter(s => s.day === i);
          const isToday     = i === dayOfW;
          return (
            <div key={i} style={{ marginBottom:10 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                <div style={{ width:32, height:32, borderRadius:8, background: isToday ? orange : `${navy}15`, display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <span style={{ fontSize:13, fontWeight:800, color: isToday ? white : navy }}>{d.getDate()}</span>
                </div>
                <span style={{ fontWeight:700, color: isToday ? orange : textCol, fontSize:14 }}>
                  {dayLabels[i]} {isToday ? "— Aujourd'hui" : ""}
                </span>
                {daySessions.length === 0 && <span style={{ color:muted, fontSize:12 }}>Pas de séance</span>}
              </div>
              {daySessions.map((s, j) => {
                const pc = phaseColor(s.phase);
                return (
                  <div key={j} style={{ background:white, borderRadius:12, padding:"11px 13px", marginBottom:6, marginLeft:40, boxShadow:"0 1px 8px rgba(0,0,0,0.05)", border:`1px solid ${border}`, display:"flex", alignItems:"center", gap:10 }}>
                    <div style={{ width:4, height:36, borderRadius:99, background:pc, flexShrink:0 }} />
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, color:textCol, fontSize:13 }}>{s.patient}</div>
                      <div style={{ color:muted, fontSize:11, marginTop:2 }}>{s.prog}</div>
                    </div>
                    <span style={{ color:muted, fontSize:12, fontWeight:600 }}>{s.heure}</span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── ADMIN: TAUX DE COMPLÉTION ────────────────────────────
function TauxCompletion({ onBack }) {
  const data = [
    { phase:"Phase 1", label:"Activation", pct:83, patient:"Marie L.",  co:"#0ea5e9" },
    { phase:"Phase 2", label:"Renforcement", pct:50, patient:"Thomas D.", co:"#f97316" },
    { phase:"Phase 3", label:"Pliométrie",  pct:92, patient:"Lucas D.",  co:"#22c55e" },
  ];

  function Gauge({ pct, color: col, size = 110 }) {
    const r       = 40;
    const cx      = size / 2;
    const cy      = size / 2;
    const circum  = 2 * Math.PI * r;
    const filled  = (pct / 100) * circum;
    const rotate  = -90;
    const gColor  = pct >= 80 ? "#22c55e" : pct >= 50 ? "#f97316" : "#ef4444";
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e2e8f0" strokeWidth="10" />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={gColor} strokeWidth="10"
          strokeDasharray={`${filled} ${circum}`}
          strokeLinecap="round"
          transform={`rotate(${rotate} ${cx} ${cy})`}
          style={{ transition:"stroke-dasharray 0.8s ease" }}
        />
        <text x={cx} y={cy + 6} textAnchor="middle" fontSize="18" fontWeight="800" fill={gColor}>{pct}%</text>
      </svg>
    );
  }

  const globalPct = Math.round(data.reduce((acc, d) => acc + d.pct, 0) / data.length);
  const globalCol = globalPct >= 80 ? "#22c55e" : globalPct >= 50 ? "#f97316" : "#ef4444";

  return (
    <div style={{ paddingBottom:80 }}>
      <PageHeader title="Taux de complétion" sub="Par patient et par phase" onBack={onBack} />
      <div style={{ padding:14 }}>

        {/* Global gauge */}
        <div style={{ background:white, borderRadius:16, padding:20, marginBottom:14, boxShadow:"0 2px 12px rgba(0,0,0,0.06)", display:"flex", alignItems:"center", gap:20 }}>
          <div style={{ flexShrink:0 }}>
            <svg width={120} height={120} viewBox="0 0 120 120">
              <circle cx={60} cy={60} r={48} fill="none" stroke="#e2e8f0" strokeWidth="12" />
              <circle cx={60} cy={60} r={48} fill="none" stroke={globalCol} strokeWidth="12"
                strokeDasharray={`${(globalPct/100)*2*Math.PI*48} ${2*Math.PI*48}`}
                strokeLinecap="round"
                transform="rotate(-90 60 60)"
              />
              <text x={60} y={66} textAnchor="middle" fontSize="22" fontWeight="900" fill={globalCol}>{globalPct}%</text>
            </svg>
          </div>
          <div>
            <div style={{ fontWeight:800, color:textCol, fontSize:18 }}>Complétion globale</div>
            <div style={{ color:muted, fontSize:13, marginTop:4 }}>Moyenne des 3 patients actifs</div>
            <div style={{ marginTop:10 }}>
              <Badge label={globalPct >= 80 ? "Excellent 🎉" : globalPct >= 50 ? "En cours ✓" : "Attention ⚠️"} bg={`${globalCol}15`} color={globalCol} />
            </div>
          </div>
        </div>

        {/* Par patient */}
        <h3 style={{ fontSize:12, fontWeight:700, color:muted, letterSpacing:"0.08em", textTransform:"uppercase", margin:"0 0 10px" }}>Par patient</h3>
        {data.map((d, i) => {
          const gCol = d.pct >= 80 ? "#22c55e" : d.pct >= 50 ? "#f97316" : "#ef4444";
          return (
            <div key={i} style={{ background:white, borderRadius:14, padding:14, marginBottom:10, boxShadow:"0 2px 10px rgba(0,0,0,0.06)", display:"flex", alignItems:"center", gap:14 }}>
              <div style={{ flexShrink:0 }}><Gauge pct={d.pct} color={gCol} size={90} /></div>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:800, color:textCol, fontSize:15 }}>{d.patient}</div>
                <Badge label={d.phase} bg={`${d.co}15`} color={d.co} />
                <div style={{ color:muted, fontSize:12, marginTop:4 }}>{d.label}</div>
                <div style={{ marginTop:8 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                    <span style={{ fontSize:11, color:muted }}>Séances complétées</span>
                    <span style={{ fontSize:11, fontWeight:700, color:gCol }}>{d.pct}%</span>
                  </div>
                  <ProgressBar value={d.pct} max={100} color={gCol} />
                </div>
              </div>
            </div>
          );
        })}

        {/* Légende couleurs */}
        <div style={{ background:white, borderRadius:12, padding:14, boxShadow:"0 1px 8px rgba(0,0,0,0.05)" }}>
          <div style={{ fontSize:12, fontWeight:700, color:muted, marginBottom:10, textTransform:"uppercase", letterSpacing:"0.06em" }}>Légende</div>
          {[{c:"#22c55e",l:"Excellent (≥ 80%)"},{c:"#f97316",l:"En cours (50–79%)"},{c:"#ef4444",l:"Attention (< 50%)"}].map((l,i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
              <div style={{ width:12, height:12, borderRadius:"50%", background:l.c }} />
              <span style={{ fontSize:13, color:textCol }}>{l.l}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── ADMIN: DASHBOARD ─────────────────────────────────────
function AdminDashboard({ user, patients: allPatients = INIT_PATIENTS, onLogout, onNav, onSelectPatient }) {
  const patients = allPatients.filter(p => patientStatus(p) === "active");
  return (
    <div style={{ paddingBottom:80 }}>
      <div style={{ background:`linear-gradient(135deg,${navy},${navyL})`, padding:"22px 18px 26px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
              <LogoBadge />
              <span style={{ background:"rgba(249,115,22,0.2)", color:orange, fontSize:10, padding:"2px 6px", borderRadius:4, fontWeight:700 }}>ADMIN</span>
            </div>
            <h2 style={{ color:white, fontWeight:800, fontSize:22, margin:0 }}>Bonjour, {user.name} 👋</h2>
          </div>
          {/* Bouton de déconnexion retiré ici — le LogoutFab global (rendu sur tous les écrans, voir plus bas dans le fichier) fait déjà ce travail. Doublon supprimé le 23/07/2026. */}
        </div>
      </div>
      <div style={{ padding:"18px 14px" }}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:18 }}>
          {[
            { l:"Patients actifs",   v:String(patients.length),   ic:"people",   co:navy,    dest:"patients" },
            { l:"Séances / semaine", v:"8",   ic:"dumbbell", co:orange,  dest:"calendrier-semaine" },
            { l:"Exercices lib.",    v:"10",  ic:"play",     co:purple,  dest:"biblio" },
            { l:"Taux complétion",   v:"87%", ic:"check",    co:success, dest:"taux-completion" },
          ].map((s, i) => (
            <button key={i} onClick={() => onNav(s.dest)} style={{ background:white, borderRadius:14, padding:14, boxShadow:"0 2px 12px rgba(0,0,0,0.06)", border:`1.5px solid ${border}`, cursor:"pointer", textAlign:"left", position:"relative" }}>
              <div style={{ position:"absolute", top:10, right:10 }}><Icon name="arrow" size={13} color={s.co} /></div>
              <div style={{ width:34, height:34, borderRadius:10, background:`${s.co}18`, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:8 }}>
                <Icon name={s.ic} size={17} color={s.co} />
              </div>
              <div style={{ fontSize:24, fontWeight:800, color:textCol }}>{s.v}</div>
              <div style={{ fontSize:12, color:muted, marginTop:2 }}>{s.l}</div>
            </button>
          ))}
        </div>
        <h3 style={{ fontSize:12, fontWeight:700, color:muted, letterSpacing:"0.08em", textTransform:"uppercase", margin:"0 0 10px" }}>Patients actifs</h3>
        {patients.map((p, i) => {
          const ph = PHASES.find(ph => ph.id === p.phaseId);
          const pc = phaseColor(p.phaseId);
          return (
            <button key={i} onClick={() => onSelectPatient && onSelectPatient(p)}
              style={{ width:"100%", textAlign:"left", background:white, border:"none", borderRadius:14, padding:14, marginBottom:8, boxShadow:"0 2px 10px rgba(0,0,0,0.06)", cursor:"pointer", display:"block", fontFamily:"inherit" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                <div style={{ width:40, height:40, borderRadius:"50%", background:orange, color:white, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:16, flexShrink:0 }}>{p.name[0]}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, color:textCol, fontSize:14 }}>{p.name}</div>
                  <Badge label={ph ? ph.label : ""} bg={`${pc}15`} color={pc} />
                </div>
                <Icon name="arrow" size={15} color={muted} />
              </div>
              <ProgressBar value={p.sessions} max={p.total} />
              {/* Dernier bilan de tests + dernière décision — dérivés de v_dashboard (test_sessions / phase_decisions) */}
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:8, fontSize:11, color:muted }}>
                <span>{p.lastScoringDate ? `Dernier bilan : ${scoringPhaseLabel(p.lastScoringPhase)} · ${dateStrToDisplay(p.lastScoringDate)}` : "Aucun bilan de tests"}</span>
                {p.lastDecision && <span style={{ fontWeight:700, color:DECISION_COLOR[p.lastDecision] || muted }}>{(p.lastGate || "").split(":")[0]} · {p.lastDecision.replace("_", " ")}</span>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── ADMIN: IDENTIFIANTS CRÉÉS + EMAIL DE BIENVENUE ───────
// Affiché juste après la création d'un accès patient : c'est le seul moment où le mot de
// passe temporaire est connu. L'email part de la messagerie du praticien (mailto:), donc
// sans dépendre du SMTP Supabase.
function CredentialsModal({ creds, onClose }) {
  const [copied, setCopied] = useState("");
  const [firstName, setFirstName] = useState(() => guessFirstName(creds && creds.name));
  useEffect(() => { setFirstName(guessFirstName(creds && creds.name)); }, [creds]);
  if (!creds) return null;
  const { subject, body } = buildWelcomeMessage({ ...creds, firstName });
  const mailto = `mailto:${encodeURIComponent(creds.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const whatsapp = `https://wa.me/?text=${encodeURIComponent(body)}`;

  async function copy(text, what) {
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = text; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } catch (e2) {}
      document.body.removeChild(ta);
    }
    setCopied(what);
    setTimeout(() => setCopied(""), 2000);
  }
  function close() {
    if (!window.confirm("Le mot de passe ne sera plus affiché ensuite. As-tu bien envoyé ou noté les identifiants ?")) return;
    onClose();
  }

  const row = { display:"flex", justifyContent:"space-between", alignItems:"center", gap:10, padding:"10px 12px", background:"#f8fafc", border:`1.5px solid ${border}`, borderRadius:10, marginBottom:8 };
  const btn = (bgc, col = white) => ({ width:"100%", padding:"13px", background:bgc, color:col, border:"none", borderRadius:12, fontWeight:800, fontSize:14, cursor:"pointer", marginBottom:8, textDecoration:"none", display:"block", textAlign:"center", boxSizing:"border-box" });

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:300, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
      <div style={{ background:white, borderRadius:"20px 20px 0 0", padding:"24px 20px 32px", width:"100%", maxWidth:430, boxSizing:"border-box", maxHeight:"92vh", overflowY:"auto" }}>
        <div style={{ fontSize:28, marginBottom:4 }}>✅</div>
        <h3 style={{ margin:"0 0 4px", color:textCol, fontWeight:800 }}>Accès créé — {creds.name}</h3>
        <p style={{ color:muted, fontSize:12, margin:"0 0 14px" }}>Envoie maintenant ses identifiants au patient. Il pourra changer son mot de passe depuis son profil.</p>

        <div style={row}>
          <div><div style={{ fontSize:10.5, color:muted, fontWeight:700 }}>Identifiant</div><div style={{ fontSize:14, color:textCol, fontWeight:700, wordBreak:"break-all" }}>{creds.email}</div></div>
        </div>
        <div style={row}>
          <div><div style={{ fontSize:10.5, color:muted, fontWeight:700 }}>Mot de passe temporaire</div><div style={{ fontSize:17, color:textCol, fontWeight:800, fontFamily:"Menlo,Consolas,monospace", letterSpacing:"0.06em" }}>{creds.password}</div></div>
          <button onClick={() => copy(creds.password, "pw")} style={{ padding:"7px 10px", background:white, border:`1.5px solid ${border}`, borderRadius:8, fontSize:11, fontWeight:700, color:navy, cursor:"pointer", whiteSpace:"nowrap" }}>{copied === "pw" ? "Copié ✓" : "Copier"}</button>
        </div>

        <div style={{ margin:"14px 0 8px", fontSize:11, fontWeight:800, color:navy }}>✉️ Email de bienvenue</div>
        <label style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8, fontSize:11.5, color:muted, fontWeight:700 }}>
          Prénom
          <input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Prénom du patient"
            style={{ flex:1, padding:"8px 10px", border:`1.5px solid ${border}`, borderRadius:8, fontSize:13, outline:"none", fontFamily:"inherit", color:textCol }} />
        </label>
        <div style={{ fontSize:11.5, color:textCol, background:"#f8fafc", border:`1.5px solid ${border}`, borderRadius:10, padding:"10px 12px", whiteSpace:"pre-wrap", maxHeight:170, overflowY:"auto", marginBottom:12, lineHeight:1.45 }}>
          <b>Objet : {subject}</b>{"\n\n"}{body}
        </div>

        <a href={mailto} style={btn("linear-gradient(135deg,#f97316,#ea580c)")}>✉️ Envoyer l'email de bienvenue</a>
        <a href={whatsapp} target="_blank" rel="noopener noreferrer" style={btn("#16a34a")}>💬 Envoyer par WhatsApp</a>
        <button onClick={() => copy(`${subject}\n\n${body}`, "msg")} style={{ ...btn(white, navy), border:`1.5px solid ${border}` }}>
          {copied === "msg" ? "Message copié ✓" : "📋 Copier le message"}
        </button>
        <button onClick={close} style={{ ...btn("#f1f5f9", muted), marginBottom:0 }}>Terminé</button>
      </div>
    </div>
  );
}

// ── ADMIN: FICHE PATIENT ─────────────────────────────────
function FichePatient({ patient, progs, onPreview, onBack, onSessionLoad, onScoring, onUpdate, onDelete }) {
  const [showDelete, setShowDelete] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleting,   setDeleting]   = useState(false);
  const [deleteErr,  setDeleteErr]  = useState(null);
  async function confirmDelete() {
    if (deleteText.trim().toUpperCase() !== "SUPPRIMER" || !onDelete) return;
    setDeleting(true); setDeleteErr(null);
    try { await onDelete(patient); }
    catch (err) { setDeleting(false); setDeleteErr(err.message || "Suppression impossible."); }
  }
  const [showAssign, setShowAssign] = useState(false);
  const [selProg, setSelProg]       = useState(null);
  const [showEdit, setShowEdit]     = useState(false);
  const [editForm, setEditForm]     = useState(null); // initialisé à l'ouverture du modal
  const [savingEdit, setSavingEdit] = useState(false);
  const [showAccess, setShowAccess]   = useState(false);
  const [accessForm, setAccessForm]   = useState({ email:"", pw:"" });
  const [accessErr, setAccessErr]     = useState(null);
  const [creatingAccess, setCreatingAccess] = useState(false);
  const [sentCreds, setSentCreds]     = useState(null); // { name, email, password } → CredentialsModal

  async function createAccess() {
    const email = accessForm.email.trim().toLowerCase();
    const password = accessForm.pw.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setAccessErr("Email invalide."); return; }
    if (password.length < 8) { setAccessErr("Mot de passe : 8 caractères minimum."); return; }
    setCreatingAccess(true); setAccessErr(null);
    try {
      const authUserId = await sbSignUpPatient(email, password);
      onUpdate && onUpdate({ ...patient, authUserId, patientEmail: email });
      setSentCreds({ name: patient.name, email, password });
      setCreatingAccess(false); setShowAccess(false); setAccessForm({ email:"", pw:"" });
    } catch (err) {
      setCreatingAccess(false);
      setAccessErr(err.message || "Erreur lors de la création du compte.");
    }
  }
  const ph = PHASES.find(p => p.id === patient.phaseId);
  const pc = phaseColor(patient.phaseId);

  const fieldLbl = { fontSize:11, fontWeight:700, color:muted, display:"block", marginBottom:5 };
  const fieldInp = { width:"100%", padding:"10px 12px", border:`1.5px solid ${border}`, borderRadius:10, fontSize:13, outline:"none", boxSizing:"border-box", fontFamily:"inherit", color:textCol, background:white };

  function openEdit() {
    setEditForm({
      name: patient.name || "", sport: patient.sport || "", dateOp: patient.dateOp || "",
      greffon: patient.greffon || "DT4", cote: patient.cote || "", phaseId: patient.phaseId ?? 1,
      total: patient.total ?? 6,
    });
    setShowEdit(true);
  }

  function saveEdit() {
    if (!editForm.name.trim() || !onUpdate) return;
    setSavingEdit(true);
    onUpdate({
      ...patient, name: editForm.name.trim(), sport: editForm.sport, dateOp: editForm.dateOp,
      greffon: editForm.greffon, cote: editForm.cote, phaseId: Number(editForm.phaseId),
      total: Number(editForm.total) || patient.total,
    });
    setSavingEdit(false);
    setShowEdit(false);
  }

  return (
    <div style={{ paddingBottom:80 }}>
      <PageHeader title={patient.name} sub={ph ? ph.label : ""} onBack={onBack} />
      <div style={{ padding:"14px 14px 0" }}>

        {/* Info clinique */}
        <div style={{ background:white, borderRadius:14, padding:14, marginBottom:12, boxShadow:"0 2px 10px rgba(0,0,0,0.06)" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
            <h3 style={{ fontSize:12, fontWeight:700, color:muted, letterSpacing:"0.08em", textTransform:"uppercase", margin:0 }}>Informations cliniques</h3>
            {onUpdate && (
              <button onClick={openEdit} style={{ background:"none", border:"none", cursor:"pointer", fontSize:12, color:orange, fontWeight:700 }}>✏️ Modifier</button>
            )}
          </div>
          {[
            {l:"Greffon",      v: GREFFON_LABELS[patient.greffon] || patient.greffon || "—"},
            {l:"Chirurgien",   v: patient.chirurgien || "—"},
            {l:"Date op.",     v: patient.dateOp ? dateStrToDisplay(patient.dateOp) : "—"},
            {l:"Sport",        v: patient.niveau ? `${patient.sport} — ${patient.niveau}` : (patient.sport || "—")},
            {l:"Semaine",      v: patient.semaine != null ? `Semaine ${patient.semaine}` : "—"},
          ].map((it, i) => (
            <div key={i} style={{ display:"flex", justifyContent:"space-between", paddingTop: i === 0 ? 0 : 8, borderTop: i === 0 ? "none" : `1px solid ${border}` }}>
              <span style={{ color:muted, fontSize:13 }}>{it.l}</span>
              <span style={{ color:textCol, fontWeight:700, fontSize:13 }}>{it.v}</span>
            </div>
          ))}
        </div>

        {/* Accès patient (compte Supabase Auth lié à la fiche) */}
        <div style={{ background:white, borderRadius:14, padding:14, marginBottom:12, boxShadow:"0 2px 10px rgba(0,0,0,0.06)" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <h3 style={{ fontSize:12, fontWeight:700, color:muted, letterSpacing:"0.08em", textTransform:"uppercase", margin:0 }}>Accès patient</h3>
            {patient.authUserId
              ? <Badge label="Actif" bg="#dcfce7" color="#15803d" />
              : <Badge label="Aucun" bg={`${muted}18`} color={muted} />}
          </div>
          {patient.authUserId ? (
            <div style={{ display:"flex", justifyContent:"space-between", marginTop:10 }}>
              <span style={{ color:muted, fontSize:13 }}>Identifiant</span>
              <span style={{ color:textCol, fontWeight:700, fontSize:13 }}>{patient.patientEmail || "—"}</span>
            </div>
          ) : (
            <button onClick={() => { setShowAccess(true); setAccessErr(null); }} style={{ marginTop:10, width:"100%", padding:"10px", background:"transparent", border:`1.5px dashed ${orange}`, borderRadius:10, color:orange, fontWeight:700, fontSize:13, cursor:"pointer" }}>
              🔑 Créer l'accès patient
            </button>
          )}
        </div>

        {/* Statut du suivi (soft delete : pause / archive, réactivable) */}
        {onUpdate && (
          <div style={{ background:white, borderRadius:14, padding:14, marginBottom:12, boxShadow:"0 2px 10px rgba(0,0,0,0.06)" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
              <h3 style={{ fontSize:12, fontWeight:700, color:muted, letterSpacing:"0.08em", textTransform:"uppercase", margin:0 }}>Statut du suivi</h3>
              <Badge label={PATIENT_STATUS[patientStatus(patient)].label} bg={PATIENT_STATUS[patientStatus(patient)].bg} color={PATIENT_STATUS[patientStatus(patient)].color} />
            </div>
            <div style={{ display:"flex", gap:6 }}>
              {Object.entries(PATIENT_STATUS).map(([k, st]) => {
                const on = patientStatus(patient) === k;
                return (
                  <button key={k} disabled={on} onClick={() => {
                    if (k === "archived" && !window.confirm(`Archiver ${patient.name} ? La fiche et l'historique sont conservés et réactivables.`)) return;
                    onUpdate({ ...patient, status: k });
                  }} style={{ flex:1, padding:"9px 4px", borderRadius:10, border:`1.5px solid ${on ? st.color : border}`, background: on ? st.bg : white, color: on ? st.color : muted, fontWeight:700, fontSize:12, cursor: on ? "default" : "pointer" }}>
                    {st.label}
                  </button>
                );
              })}
            </div>
            <p style={{ fontSize:11, color:muted, margin:"8px 0 0" }}>En pause ou archivé : le patient disparaît du tableau de bord et de la liste « Actifs ». Rien n'est supprimé.</p>
          </div>
        )}

        {/* Progression */}
        <div style={{ background:white, borderRadius:14, padding:14, marginBottom:12, boxShadow:"0 2px 10px rgba(0,0,0,0.06)" }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
            <span style={{ fontWeight:700, color:textCol, fontSize:14 }}>Progression phase</span>
            <span style={{ fontWeight:800, color:pc, fontSize:14 }}>{Math.round((patient.sessions / patient.total) * 100)}%</span>
          </div>
          <ProgressBar value={patient.sessions} max={patient.total} color={pc} />
          <div style={{ display:"flex", justifyContent:"space-between", marginTop:10 }}>
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:20, fontWeight:800, color:textCol }}>{patient.sessions}</div>
              <div style={{ fontSize:11, color:muted }}>Séances</div>
            </div>
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:20, fontWeight:800, color:textCol }}>{patient.total - patient.sessions}</div>
              <div style={{ fontSize:11, color:muted }}>Restantes</div>
            </div>
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:20, fontWeight:800, color:textCol }}>{patient.semaine}</div>
              <div style={{ fontSize:11, color:muted }}>Semaine</div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <button onClick={() => onPreview(patient)} style={{ width:"100%", padding:"12px", background:navy, border:"none", borderRadius:12, color:white, fontWeight:700, fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8, marginBottom:10 }}>
          <Icon name="eye" size={17} color={white} />
          Voir son interface
        </button>
        <button onClick={() => onScoring && onScoring(patient)} style={{ width:"100%", padding:"12px", background:`${purple}15`, border:`1.5px solid ${purple}`, borderRadius:12, color:purple, fontWeight:700, fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8, marginBottom:6 }}>
          <Icon name="target" size={17} color={purple} />
          Scoring critères de phase
          {patient.lastScoringDate && <span style={{ fontSize:10, background:purple+"25", padding:"1px 6px", borderRadius:99, marginLeft:6 }}>{scoringPhaseLabel(patient.lastScoringPhase)} · {dateStrToDisplay(patient.lastScoringDate)}</span>}
        </button>
        {/* Raccourcis vers les étapes finales du même scoring (même source test_results) */}
        <div style={{ display:"flex", gap:6, marginBottom:10 }}>
          <button onClick={() => onScoring && onScoring(patient, "p3→p4", "mrss")} style={{ flex:1, padding:"8px", background:`${navy}10`, border:`1px solid ${navy}55`, borderRadius:10, color:navy, fontWeight:700, fontSize:12, cursor:"pointer" }}>
            📐 MRSS 2.0
          </button>
          <button onClick={() => onScoring && onScoring(patient, patient.lastScoringPhase === 3 ? "p3→p4" : "p2→p3", "rtr_gate")} style={{ flex:1, padding:"8px", background:`${orange}10`, border:`1px solid ${orange}55`, borderRadius:10, color:orange, fontWeight:700, fontSize:12, cursor:"pointer" }}>
            🚦 RTR gate{patient.lastDecision ? ` · ${patient.lastDecision.replace("_", " ")}` : ""}
          </button>
        </div>
        <button onClick={() => onSessionLoad && onSessionLoad(patient)} style={{ width:"100%", padding:"12px", background:`${success}15`, border:`1.5px solid ${success}`, borderRadius:12, color:"#166534", fontWeight:700, fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8, marginBottom:10 }}>
          <Icon name="chart" size={17} color={success} />
          Suivi charge des séances
          {patient.sessionHistory && patient.sessionHistory.length > 0 && <span style={{ fontSize:10, background:success+"25", padding:"1px 6px", borderRadius:99, marginLeft:6 }}>{patient.sessionHistory.length} séance(s)</span>}
        </button>
        <button onClick={() => setShowAssign(true)} style={{ width:"100%", padding:"12px", background:`${orange}15`, border:`1.5px solid ${orange}`, borderRadius:12, color:orange, fontWeight:700, fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
          <Icon name="assign" size={17} color={orange} />
          Assigner un programme
        </button>

        {onDelete && (
          <button onClick={() => { setShowDelete(true); setDeleteText(""); setDeleteErr(null); }} style={{ width:"100%", marginTop:22, padding:"11px", background:"transparent", border:"1.5px solid #fca5a5", borderRadius:12, color:"#dc2626", fontWeight:700, fontSize:13, cursor:"pointer" }}>
            🗑️ Supprimer définitivement ce patient
          </button>
        )}
      </div>

      {showDelete && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:300, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
          <div style={{ background:white, borderRadius:"20px 20px 0 0", padding:"24px 20px 32px", width:"100%", maxWidth:430, boxSizing:"border-box" }}>
            <h3 style={{ margin:"0 0 8px", color:"#dc2626", fontWeight:800 }}>Supprimer {patient.name} ?</h3>
            <p style={{ fontSize:13, color:textCol, margin:"0 0 8px", lineHeight:1.45 }}>
              Action <b>irréversible</b> : la fiche, tous les bilans de tests, décisions RTR, dérobements{patient.authUserId ? " et son compte de connexion" : ""} seront effacés.
            </p>
            <p style={{ fontSize:12, color:muted, margin:"0 0 12px" }}>Pour une simple interruption de suivi, préfère « En pause » ou « Archivé » (réactivable).</p>
            <label style={{ fontSize:11.5, fontWeight:700, color:muted, display:"block", marginBottom:5 }}>Tape SUPPRIMER pour confirmer</label>
            <input value={deleteText} onChange={e => setDeleteText(e.target.value)} autoCapitalize="characters" placeholder="SUPPRIMER"
              style={{ width:"100%", padding:"10px 12px", border:`1.5px solid ${border}`, borderRadius:10, fontSize:14, outline:"none", boxSizing:"border-box", fontFamily:"inherit", marginBottom:10 }} />
            {deleteErr && <div style={{ background:"#fef2f2", border:"1px solid #fecaca", color:"#dc2626", borderRadius:10, padding:"8px 10px", fontSize:12, marginBottom:10 }}>{deleteErr}</div>}
            <button onClick={confirmDelete} disabled={deleting || deleteText.trim().toUpperCase() !== "SUPPRIMER"}
              style={{ width:"100%", padding:"13px", background: deleteText.trim().toUpperCase() === "SUPPRIMER" ? "#dc2626" : "#fca5a5", color:white, border:"none", borderRadius:12, fontWeight:800, fontSize:14, cursor: deleting ? "wait" : "pointer", marginBottom:8 }}>
              {deleting ? "Suppression…" : "Supprimer définitivement"}
            </button>
            <button onClick={() => setShowDelete(false)} disabled={deleting} style={{ width:"100%", padding:"12px", background:"transparent", border:`1.5px solid ${border}`, borderRadius:12, color:muted, fontWeight:700, fontSize:14, cursor:"pointer" }}>Annuler</button>
          </div>
        </div>
      )}

      {/* Assign modal */}
      {showAssign && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:200, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
          <div style={{ background:white, borderRadius:"20px 20px 0 0", padding:"22px 18px 36px", width:"100%", maxWidth:430, maxHeight:"80vh", overflowY:"auto" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <h3 style={{ margin:0, color:textCol, fontWeight:800 }}>Assigner un programme</h3>
              <button onClick={() => setShowAssign(false)} style={{ background:"none", border:"none", cursor:"pointer" }}><Icon name="close" size={22} color={muted} /></button>
            </div>
            <p style={{ color:muted, fontSize:13, marginBottom:14 }}>Sélectionnez un modèle pour {patient.name.split(" ")[0]}</p>
            {PHASES.map(ph => {
              const phProgs = progs.filter(p => p.phaseId === ph.id);
              if (phProgs.length === 0) return null;
              const pc2 = phaseColor(ph.id);
              return (
                <div key={ph.id} style={{ marginBottom:12 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:muted, letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:6 }}>{ph.label}</div>
                  {phProgs.map(p => (
                    <button key={p.id} onClick={() => setSelProg(selProg === p.id ? null : p.id)} style={{ width:"100%", padding:"11px 13px", border:`1.5px solid ${selProg === p.id ? pc2 : border}`, borderRadius:11, background:selProg === p.id ? `${pc2}10` : white, marginBottom:6, cursor:"pointer", textAlign:"left", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <div>
                        <div style={{ fontWeight:700, color:textCol, fontSize:13 }}>{p.name}</div>
                        <div style={{ color:muted, fontSize:11, marginTop:2 }}>{p.exos.length} exercices · ~{p.dur} min</div>
                      </div>
                      {selProg === p.id && <Icon name="check" size={18} color={pc2} />}
                    </button>
                  ))}
                </div>
              );
            })}
            <button onClick={() => { if (selProg) { setShowAssign(false); setSelProg(null); } }} disabled={!selProg} style={{ width:"100%", padding:"13px", background:selProg ? "linear-gradient(135deg,#f97316,#ea580c)" : "#cbd5e1", color:white, border:"none", borderRadius:12, fontWeight:800, fontSize:15, cursor:selProg ? "pointer" : "not-allowed" }}>
              Confirmer l&apos;assignation
            </button>
          </div>
        </div>
      )}

      {/* Modal modification informations patient */}
      {showEdit && editForm && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:200, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
          <div style={{ background:white, borderRadius:"20px 20px 0 0", padding:"24px 20px 36px", width:"100%", maxWidth:430, maxHeight:"85vh", overflowY:"auto", boxSizing:"border-box" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
              <h3 style={{ margin:0, color:textCol, fontWeight:800 }}>Modifier le patient</h3>
              <button onClick={() => setShowEdit(false)} style={{ background:"none", border:"none", cursor:"pointer" }}><Icon name="close" size={22} color={muted} /></button>
            </div>

            <label style={fieldLbl}>Nom complet *</label>
            <input value={editForm.name} onChange={e => setEditForm(f=>({...f, name:e.target.value}))}
              style={{ ...fieldInp, marginBottom:14 }} />

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
              <div>
                <label style={fieldLbl}>Sport</label>
                <input value={editForm.sport} onChange={e => setEditForm(f=>({...f, sport:e.target.value}))} style={fieldInp} />
              </div>
              <div>
                <label style={fieldLbl}>Date opération</label>
                <input type="date" value={editForm.dateOp} onChange={e => setEditForm(f=>({...f, dateOp:e.target.value}))} style={fieldInp} />
              </div>
              <div>
                <label style={fieldLbl}>Greffon</label>
                <select value={editForm.greffon} onChange={e => setEditForm(f=>({...f, greffon:e.target.value}))} style={fieldInp}>
                  {GREFFON_OPTS.map(g => <option key={g.v} value={g.v}>{g.l}</option>)}
                </select>
              </div>
              <div>
                <label style={fieldLbl}>Côté opéré</label>
                <select value={editForm.cote} onChange={e => setEditForm(f=>({...f, cote:e.target.value}))} style={fieldInp}>
                  <option value="">—</option>
                  <option value="Droit">Droit</option>
                  <option value="Gauche">Gauche</option>
                </select>
              </div>
              <div>
                <label style={fieldLbl}>Séances prévues (programme)</label>
                <input type="number" min="1" value={editForm.total} onChange={e => setEditForm(f=>({...f, total:e.target.value}))} style={fieldInp} />
              </div>
              <div>
                <label style={fieldLbl}>Phase actuelle</label>
                <select value={editForm.phaseId} onChange={e => setEditForm(f=>({...f, phaseId:e.target.value}))} style={fieldInp}>
                  {PHASES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </div>
            </div>

            <button onClick={saveEdit} disabled={savingEdit}
              style={{ width:"100%", padding:"13px", background: "linear-gradient(135deg,#f97316,#ea580c)", color:white, border:"none", borderRadius:12, fontWeight:800, fontSize:15, cursor:"pointer" }}>
              Enregistrer les modifications
            </button>
          </div>
        </div>
      )}

      {showAccess && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:200, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
          <div style={{ background:white, borderRadius:"20px 20px 0 0", padding:"24px 20px 36px", width:"100%", maxWidth:430, boxSizing:"border-box" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
              <h3 style={{ margin:0, color:textCol, fontWeight:800 }}>Accès patient — {patient.name}</h3>
              <button onClick={() => setShowAccess(false)} style={{ background:"none", border:"none", cursor:"pointer" }}><Icon name="close" size={22} color={muted} /></button>
            </div>
            <p style={{ color:muted, fontSize:12, margin:"0 0 16px" }}>Le patient se connectera avec ces identifiants et n'aura accès qu'à son propre dossier.</p>
            <label style={fieldLbl}>Email du patient</label>
            <input type="email" value={accessForm.email} onChange={e => setAccessForm(f=>({...f, email:e.target.value}))} placeholder="patient@email.com" style={{ ...fieldInp, marginBottom:12 }} />
            <label style={fieldLbl}>Mot de passe temporaire</label>
            <div style={{ display:"flex", gap:8, marginBottom:12 }}>
              <input value={accessForm.pw} onChange={e => setAccessForm(f=>({...f, pw:e.target.value}))} placeholder="min. 8 caractères" style={{ ...fieldInp, flex:1 }} />
              <button onClick={() => setAccessForm(f=>({...f, pw: genTempPassword()}))} style={{ padding:"0 14px", background:navy, color:white, border:"none", borderRadius:10, fontSize:12, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>Générer</button>
            </div>
            {accessForm.pw && <div style={{ fontSize:10.5, color:"#9a3412", background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:8, padding:"7px 10px", marginBottom:12 }}>✉️ À l'étape suivante, tu pourras envoyer au patient l'email de bienvenue avec ses identifiants.</div>}
            {accessErr && <div style={{ marginBottom:12, padding:"9px 12px", background:"#fee2e2", color:"#991b1b", borderRadius:8, fontSize:12 }}>{accessErr}</div>}
            <button onClick={createAccess} disabled={creatingAccess}
              style={{ width:"100%", padding:"13px", background: creatingAccess ? muted : "linear-gradient(135deg,#f97316,#ea580c)", color:white, border:"none", borderRadius:12, fontWeight:800, fontSize:15, cursor: creatingAccess ? "default" : "pointer" }}>
              {creatingAccess ? "Création…" : "Créer le compte"}
            </button>
          </div>
        </div>
      )}
      <CredentialsModal creds={sentCreds} onClose={() => setSentCreds(null)} />
    </div>
  );
}

// ── ADMIN: VUE PATIENT (liste) ───────────────────────────
function VuePatientScreen({ patients = INIT_PATIENTS, progs, onPreview, onSelectPatient, onAddPatient }) {
  const [showAdd, setShowAdd]   = useState(false);
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState(null);
  const [form, setForm] = useState({ name:"", sport:"", dateOp:"", greffon:"DT4", cote:"", phaseId:1, total:"6", email:"", pw:"" });
  const [sentCreds, setSentCreds] = useState(null); // { name, email, password } → CredentialsModal
  const [statusFilter, setStatusFilter] = useState("active"); // all | active | paused | archived
  const shownPatients = sortPatientsAlpha(patients.filter(p => statusFilter === "all" || patientStatus(p) === statusFilter));
  const lettersPresent = new Set(shownPatients.map(p => patientLetter(p.name)));
  const firstOfLetter = {};
  shownPatients.forEach((p, i) => { const l = patientLetter(p.name); if (!(l in firstOfLetter)) firstOfLetter[l] = i; });
  function jumpTo(letter) {
    const el = document.getElementById(`pl-letter-${letter}`);
    if (el) el.scrollIntoView({ behavior:"smooth", block:"start" });
  }
  const letterHeader = (l) => (
    <div id={`pl-letter-${l}`} style={{ scrollMarginTop:12, fontSize:12, fontWeight:800, color:navy, padding:"8px 4px 6px", letterSpacing:"0.04em" }}>{l}</div>
  );

  function resetForm() {
    setForm({ name:"", sport:"", dateOp:"", greffon:"DT4", cote:"", phaseId:1, total:"6", email:"", pw:"" });
    setCreateErr(null);
  }

  async function submitAdd() {
    if (!form.name.trim()) { setCreateErr("Le nom est obligatoire."); return; }
    if (!onAddPatient) return;
    const email = form.email.trim().toLowerCase();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setCreateErr("Email d'accès patient invalide."); return; }
    const password = form.pw.trim();
    if (email && password.length < 8) { setCreateErr("Mot de passe : 8 caractères minimum (utilise « Générer »)."); return; }
    setCreating(true);
    setCreateErr(null);
    try {
      // 1. Compte Supabase Auth d'abord (si un accès est demandé) — si ça échoue, on ne crée pas la fiche
      let authUserId = null;
      if (email) authUserId = await sbSignUpPatient(email, password);
      // 2. Fiche patient, liée au compte le cas échéant
      await onAddPatient({ ...form, name: form.name.trim(), phaseId: Number(form.phaseId), total: Number(form.total) || 6,
        authUserId, patientEmail: email || null });
      // 3. Écran d'envoi des identifiants (email de bienvenue) si un accès a été créé
      if (email) setSentCreds({ name: form.name.trim(), email, password });
      setCreating(false); setShowAdd(false); resetForm();
    } catch (err) {
      setCreating(false);
      setCreateErr(err.message || "Erreur lors de la création du patient.");
    }
  }

  const fieldLbl = { fontSize:11, fontWeight:700, color:muted, display:"block", marginBottom:5 };
  const fieldInp = { width:"100%", padding:"10px 12px", border:`1.5px solid ${border}`, borderRadius:10, fontSize:13, outline:"none", boxSizing:"border-box", fontFamily:"inherit", color:textCol, background:white };

  return (
    <div style={{ paddingBottom:80 }}>
      <PageHeader title="Patients" sub={`Fiches et vue interface · version ${APP_VERSION}`} />
      <div style={{ padding:14 }}>
        <div style={{ display:"flex", gap:6, marginBottom:12 }}>
          <button onClick={() => setStatusFilter("all")} style={{ flex:1, padding:"8px 4px", borderRadius:10, border:`1.5px solid ${statusFilter === "all" ? navy : border}`, background: statusFilter === "all" ? `${navy}12` : white, color: statusFilter === "all" ? navy : muted, fontWeight:700, fontSize:12, cursor:"pointer" }}>
            Tous · {patients.length}
          </button>
          {Object.entries(PATIENT_STATUS).map(([k, st]) => {
            const n = patients.filter(p => patientStatus(p) === k).length;
            const on = statusFilter === k;
            return (
              <button key={k} onClick={() => setStatusFilter(k)} style={{ flex:1, padding:"8px 4px", borderRadius:10, border:`1.5px solid ${on ? st.color : border}`, background: on ? st.bg : white, color: on ? st.color : muted, fontWeight:700, fontSize:12, cursor:"pointer" }}>
                {st.short} · {n}
              </button>
            );
          })}
        </div>
        {shownPatients.length === 0 && (
          <div style={{ textAlign:"center", color:muted, fontSize:13, padding:"18px 0" }}>{statusFilter === "all" ? "Aucun patient." : `Aucun patient ${PATIENT_STATUS[statusFilter].label.toLowerCase()}.`}</div>
        )}
        {/* Index alphabétique : toucher une lettre fait défiler jusqu'au premier patient correspondant */}
        {shownPatients.length > 0 && (
          <div style={{ position:"fixed", top:"50%", transform:"translateY(-50%)", right:"max(2px, calc((100vw - 430px) / 2 + 2px))", zIndex:50, display:"flex", flexDirection:"column", alignItems:"center", background:"rgba(255,255,255,0.85)", borderRadius:10, padding:"4px 1px", boxShadow:"0 1px 6px rgba(0,0,0,0.08)" }}>
            {ALPHABET.map(l => {
              const on = lettersPresent.has(l);
              return (
                <button key={l} disabled={!on} onClick={() => jumpTo(l)} aria-label={`Aller à la lettre ${l}`}
                  style={{ background:"none", border:"none", padding:"1px 5px", fontSize:10.5, lineHeight:"14px", fontWeight:800, color: on ? orange : "#cbd5e1", cursor: on ? "pointer" : "default", fontFamily:"inherit" }}>
                  {l}
                </button>
              );
            })}
          </div>
        )}
        {statusFilter === "all" && shownPatients.map((p, i) => {
          const l = patientLetter(p.name);
          const first = firstOfLetter[l] === i;
          return (
            <Fragment key={p.id || i}>
              {first && letterHeader(l)}
              <button onClick={() => onSelectPatient(p)} style={{ width:"calc(100% - 22px)", display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, padding:"12px 14px", background:white, border:`1.5px solid ${border}`, borderRadius:10, marginBottom:4, cursor:"pointer", textAlign:"left", fontFamily:"inherit", fontSize:14, fontWeight:600, color:textCol }}>
                <span>{p.name}</span>
                <Icon name="arrow" size={14} color={muted} />
              </button>
            </Fragment>
          );
        })}
        {statusFilter !== "all" && shownPatients.map((p, i) => {
          const ph = PHASES.find(ph => ph.id === p.phaseId);
          const pc = phaseColor(p.phaseId);
          const l = patientLetter(p.name);
          const first = firstOfLetter[l] === i;
          return (
            <Fragment key={p.id || i}>
            {first && letterHeader(l)}
            <button onClick={() => onSelectPatient(p)} style={{ width:"calc(100% - 22px)", background:white, border:`1.5px solid ${border}`, borderRadius:14, padding:"13px 16px", marginBottom:10, boxShadow:"0 2px 10px rgba(0,0,0,0.05)", cursor:"pointer", display:"flex", alignItems:"center", gap:12, textAlign:"left" }}>
              <div style={{ width:44, height:44, borderRadius:"50%", background:"linear-gradient(135deg,#f97316,#ea580c)", color:white, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:17, flexShrink:0 }}>{p.name[0]}</div>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700, color:textCol, fontSize:14, marginBottom:2 }}>{p.name}</div>
                <Badge label={ph ? ph.label : ""} bg={`${pc}15`} color={pc} />
                <div style={{ marginTop:6 }}><ProgressBar value={p.sessions} max={p.total} color={pc} /></div>
              </div>
              <Icon name="arrow" size={17} color={muted} />
            </button>
            </Fragment>
          );
        })}

        <button onClick={() => setShowAdd(true)} style={{ width:"100%", padding:"13px", background:"transparent", border:`2px dashed ${orange}`, borderRadius:14, color:orange, fontWeight:700, fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
          <Icon name="plus" size={18} color={orange} />Ajouter un patient
        </button>
      </div>

      {showAdd && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:200, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
          <div style={{ background:white, borderRadius:"20px 20px 0 0", padding:"24px 20px 36px", width:"100%", maxWidth:430, maxHeight:"85vh", overflowY:"auto", boxSizing:"border-box" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
              <h3 style={{ margin:0, color:textCol, fontWeight:800 }}>Nouveau patient</h3>
              <button onClick={() => { setShowAdd(false); resetForm(); }} style={{ background:"none", border:"none", cursor:"pointer" }}><Icon name="close" size={22} color={muted} /></button>
            </div>

            <label style={fieldLbl}>Nom complet *</label>
            <input value={form.name} onChange={e => setForm(f=>({...f, name:e.target.value}))} placeholder="ex: Lucas Dupont"
              style={{ ...fieldInp, marginBottom:14 }} />

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
              <div>
                <label style={fieldLbl}>Sport</label>
                <input value={form.sport} onChange={e => setForm(f=>({...f, sport:e.target.value}))} placeholder="ex: Football" style={fieldInp} />
              </div>
              <div>
                <label style={fieldLbl}>Date opération</label>
                <input type="date" value={form.dateOp} onChange={e => setForm(f=>({...f, dateOp:e.target.value}))} style={fieldInp} />
              </div>
              <div>
                <label style={fieldLbl}>Greffon</label>
                <select value={form.greffon} onChange={e => setForm(f=>({...f, greffon:e.target.value}))} style={fieldInp}>
                  {GREFFON_OPTS.map(g => <option key={g.v} value={g.v}>{g.l}</option>)}
                </select>
              </div>
              <div>
                <label style={fieldLbl}>Côté opéré</label>
                <select value={form.cote} onChange={e => setForm(f=>({...f, cote:e.target.value}))} style={fieldInp}>
                  <option value="">—</option>
                  <option value="Droit">Droit</option>
                  <option value="Gauche">Gauche</option>
                </select>
              </div>
              <div>
                <label style={fieldLbl}>Séances prévues (programme)</label>
                <input type="number" min="1" value={form.total} onChange={e => setForm(f=>({...f, total:e.target.value}))} style={fieldInp} />
              </div>
            </div>

            <label style={fieldLbl}>Phase initiale</label>
            <select value={form.phaseId} onChange={e => setForm(f=>({...f, phaseId:e.target.value}))} style={{ ...fieldInp, marginBottom:16 }}>
              {PHASES.map(ph => <option key={ph.id} value={ph.id}>{ph.label}</option>)}
            </select>

            {/* Accès patient (optionnel) — crée un compte Supabase Auth lié à la fiche */}
            <div style={{ border:`1.5px solid ${border}`, borderRadius:12, padding:"12px 12px 4px", marginBottom:16, background:"#f8fafc" }}>
              <div style={{ fontSize:11, fontWeight:800, color:navy, marginBottom:2 }}>🔑 Accès patient (optionnel)</div>
              <div style={{ fontSize:10.5, color:muted, marginBottom:10 }}>Le patient se connectera avec ces identifiants et ne verra que son propre dossier.</div>
              <label style={fieldLbl}>Email du patient</label>
              <input type="email" value={form.email} onChange={e => setForm(f=>({...f, email:e.target.value}))} placeholder="patient@email.com" style={{ ...fieldInp, marginBottom:10 }} />
              <label style={fieldLbl}>Mot de passe temporaire</label>
              <div style={{ display:"flex", gap:8, marginBottom:10 }}>
                <input value={form.pw} onChange={e => setForm(f=>({...f, pw:e.target.value}))} placeholder="min. 8 caractères" style={{ ...fieldInp, flex:1 }} />
                <button onClick={() => setForm(f=>({...f, pw: genTempPassword()}))} style={{ padding:"0 14px", background:navy, color:white, border:"none", borderRadius:10, fontSize:12, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>Générer</button>
              </div>
              {form.email.trim() && form.pw && <div style={{ fontSize:10.5, color:"#9a3412", background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:8, padding:"7px 10px", marginBottom:8 }}>✉️ À l'étape suivante, tu pourras envoyer au patient l'email de bienvenue avec ses identifiants.</div>}
            </div>

            {createErr && <div style={{ marginBottom:12, padding:"9px 12px", background:"#fee2e2", color:"#991b1b", borderRadius:8, fontSize:12 }}>{createErr}</div>}

            <button onClick={submitAdd} disabled={creating}
              style={{ width:"100%", padding:"13px", background: creating ? muted : "linear-gradient(135deg,#f97316,#ea580c)", color:white, border:"none", borderRadius:12, fontWeight:800, fontSize:15, cursor: creating ? "default" : "pointer" }}>
              {creating ? "Création…" : "Créer le patient"}
            </button>
          </div>
        </div>
      )}
      <CredentialsModal creds={sentCreds} onClose={() => setSentCreds(null)} />
    </div>
  );
}

// ── ADMIN: PROGRAMMES ────────────────────────────────────
function ProgrammesAdmin({ progs, exlib, onUpdate }) {
  const [selPhase,  setSelPhase]  = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [newName,   setNewName]   = useState("");

  function addProg() {
    if (!newName.trim()) return;
    onUpdate([...progs, { id:Date.now(), phaseId:selPhase, name:newName.trim(), dur:45, exos:[] }]);
    setNewName(""); setShowModal(false);
  }

  if (selPhase !== null) {
    const ph        = PHASES.find(p => p.id === selPhase);
    const phaseProgs = progs.filter(p => p.phaseId === selPhase);
    const pc        = phaseColor(selPhase);
    return (
      <div style={{ paddingBottom:80 }}>
        <div style={{ background:`linear-gradient(135deg,${navy},${navyL})`, padding:"20px 16px 22px" }}>
          <button onClick={() => setSelPhase(null)} style={{ background:"rgba(255,255,255,0.12)", border:"none", borderRadius:8, padding:"5px 10px", color:white, fontSize:12, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:5, marginBottom:12 }}>
            <Icon name="arrowL" size={13} color={white} />
            Toutes les phases
          </button>
          <Badge label={ph ? ph.label : ""} bg={`${pc}30`} color={pc} />
          <h2 style={{ color:white, fontWeight:800, fontSize:18, margin:"6px 0 2px" }}>{ph ? ph.sub : ""}</h2>
          <p style={{ color:"rgba(255,255,255,0.5)", fontSize:12, margin:0 }}>{phaseProgs.length} modèle(s)</p>
        </div>
        <div style={{ padding:14 }}>
          {phaseProgs.map((pr, i) => (
            <div key={i} style={{ background:white, borderRadius:14, padding:14, marginBottom:10, boxShadow:"0 2px 10px rgba(0,0,0,0.06)" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                <div>
                  <div style={{ fontWeight:700, color:textCol, fontSize:15 }}>{pr.name}</div>
                  <div style={{ color:muted, fontSize:12, marginTop:2 }}>{pr.exos.length} exercices · ~{pr.dur} min</div>
                </div>
                <button style={{ background:`${orange}18`, border:"none", borderRadius:8, padding:"6px 8px", cursor:"pointer" }}>
                  <Icon name="edit" size={15} color={orange} />
                </button>
              </div>
              {pr.exos.slice(0,3).map((e, j) => {
                const ed = exlib.find(x => x.id === e.exId);
                return (
                  <div key={j} style={{ display:"flex", alignItems:"center", gap:8, padding:"5px 0", borderTop:j === 0 ? `1px solid ${border}` : "none" }}>
                    <div style={{ width:22, height:22, borderRadius:6, background:`${orange}15`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, color:orange }}>{j+1}</div>
                    <div style={{ flex:1, fontSize:12, color:textCol, fontWeight:500 }}>{ed ? ed.name : "—"}</div>
                    <div style={{ fontSize:11, color:muted }}>{fmtMod(e.p)}</div>
                  </div>
                );
              })}
              {pr.exos.length > 3 && <div style={{ fontSize:12, color:muted, marginTop:4, paddingTop:4, borderTop:`1px solid ${border}` }}>+{pr.exos.length-3} exercice(s) supplémentaire(s)</div>}
            </div>
          ))}
          <button onClick={() => setShowModal(true)} style={{ width:"100%", padding:"13px", background:"transparent", border:`2px dashed ${orange}`, borderRadius:14, color:orange, fontWeight:700, fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
            <Icon name="plus" size={18} color={orange} />Nouveau modèle
          </button>
        </div>
        {showModal && (
          <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:200, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
            <div style={{ background:white, borderRadius:"20px 20px 0 0", padding:"24px 20px 36px", width:"100%", maxWidth:430 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
                <h3 style={{ margin:0, color:textCol, fontWeight:800 }}>Nouveau modèle</h3>
                <button onClick={() => setShowModal(false)} style={{ background:"none", border:"none", cursor:"pointer" }}><Icon name="close" size={22} color={muted} /></button>
              </div>
              <label style={{ fontSize:12, fontWeight:700, color:muted, textTransform:"uppercase", letterSpacing:"0.06em", display:"block", marginBottom:6 }}>Nom du modèle</label>
              <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="ex: Séance D — Force maximale" style={{ width:"100%", padding:"12px 14px", border:`1.5px solid ${border}`, borderRadius:12, fontSize:14, outline:"none", boxSizing:"border-box", fontFamily:"inherit", color:textCol, marginBottom:16 }} />
              <button onClick={addProg} style={{ width:"100%", padding:"13px", background:"linear-gradient(135deg,#f97316,#ea580c)", color:white, border:"none", borderRadius:12, fontWeight:800, fontSize:15, cursor:"pointer" }}>Créer le modèle</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ paddingBottom:80 }}>
      <PageHeader title="Modèles de programmes" sub="Organisés par phase de rééducation" />
      <div style={{ padding:14 }}>
        {PHASES.map((ph, i) => {
          const count = progs.filter(p => p.phaseId === ph.id).length;
          const pc    = phaseColor(ph.id);
          return (
            <button key={i} onClick={() => setSelPhase(ph.id)} style={{ width:"100%", background:white, border:`1.5px solid ${border}`, borderRadius:14, padding:"14px 16px", marginBottom:10, cursor:"pointer", textAlign:"left", boxShadow:"0 2px 10px rgba(0,0,0,0.05)", display:"flex", alignItems:"center", gap:14 }}>
              <div style={{ width:42, height:42, borderRadius:12, background:`${pc}20`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <span style={{ fontSize:18, fontWeight:900, color:pc }}>{ph.id}</span>
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:800, color:textCol, fontSize:14 }}>{ph.label}</div>
                <div style={{ color:muted, fontSize:12, marginTop:2 }}>{ph.sub}</div>
                <div style={{ marginTop:5 }}><Badge label={`${count} modèle(s)`} bg={`${pc}18`} color={pc} /></div>
              </div>
              <Icon name="arrow" size={17} color={muted} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── ADMIN: BIBLIOTHÈQUE ──────────────────────────────────
function BiblioScreen({ exlib, onExlibUpdate, patients = [] }) {
  const [openQ, setOpenQ] = useState(null); // questionnaire clinique en cours de remplissage
  const [tab,          setTab]          = useState("exercices");
  const [filters,      setFilters]      = useState([]);
  const [search,       setSearch]       = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingId,    setEditingId]    = useState(null);
  const [newEx,        setNewEx]        = useState({ name:"", ytId:"", desc:"", cats:[] });
  const [selTest,      setSelTest]      = useState(null);
  const [selQ,         setSelQ]         = useState(null);

  function toggleCat(c)    { setFilters(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]); }
  function toggleNewCat(c) { setNewEx(prev => ({ ...prev, cats: prev.cats.includes(c) ? prev.cats.filter(x => x !== c) : [...prev.cats, c] })); }

  const filtered = [...exlib].filter(e => !search || e.name.toLowerCase().includes(search.toLowerCase())).filter(e => filters.length === 0 || filters.every(f => e.cats.includes(f))).sort((a,b) => a.name.localeCompare(b.name));

  function addEx() {
    if (!newEx.name.trim()) return;
    onExlibUpdate([...exlib, { id:Date.now(), name:newEx.name.trim(), cats:newEx.cats, ytId:newEx.ytId||"dQw4w9WgXcQ", desc:newEx.desc }]);
    setNewEx({ name:"", ytId:"", desc:"", cats:[] }); setShowAddModal(false);
  }
  function renameEx(id, name) { onExlibUpdate(exlib.map(e => e.id === id ? { ...e, name } : e)); }

  const catColors = { Fonctionnel:"#0ea5e9", Psychologique:"#7c3aed", Pliométrie:"#f97316", Force:"#ef4444", Composite:"#22c55e", Proprioception:"#f59e0b" };
  const qCatColors = { Intake:"#0ea5e9", Légal:"#7c3aed", Monitoring:"#f97316", Satisfaction:"#22c55e", RTS:"#ef4444" };

  return (
    <div style={{ paddingBottom:80 }}>
      <PageHeader title="Bibliothèques" sub="Exercices · Tests · Questionnaires" />
      <div style={{ background:white, display:"flex", borderBottom:`1px solid ${border}` }}>
        {[{id:"exercices",label:"Exercices"},{id:"tests",label:"Tests ACL"},{id:"questionnaires",label:"Questionnaires"}].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ flex:1, padding:"12px 4px", border:"none", background:"none", cursor:"pointer", borderBottom:tab === t.id ? `2.5px solid ${orange}` : "2.5px solid transparent", color:tab === t.id ? orange : muted, fontWeight:tab === t.id ? 700 : 500, fontSize:11 }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* EXERCICES */}
      {tab === "exercices" && (
        <div style={{ padding:14 }}>
          <div style={{ position:"relative", marginBottom:12 }}>
            <div style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)" }}><Icon name="filter" size={15} color={muted} /></div>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un exercice..." style={{ width:"100%", padding:"11px 14px 11px 38px", border:`1.5px solid ${border}`, borderRadius:12, fontSize:14, outline:"none", boxSizing:"border-box", fontFamily:"inherit", color:textCol, background:"#f8fafc" }} />
          </div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:14 }}>
            {ALL_CATS.map(c => (
              <button key={c} onClick={() => toggleCat(c)} style={{ padding:"4px 10px", borderRadius:20, border:filters.includes(c) ? `1.5px solid ${orange}` : `1.5px solid ${border}`, background:filters.includes(c) ? `${orange}15` : "transparent", color:filters.includes(c) ? orange : muted, fontSize:11, fontWeight:filters.includes(c) ? 700 : 500, cursor:"pointer" }}>{c}</button>
            ))}
            {filters.length > 0 && <button onClick={() => setFilters([])} style={{ padding:"4px 10px", borderRadius:20, border:"1.5px solid #fecaca", background:"#fef2f2", color:"#dc2626", fontSize:11, fontWeight:700, cursor:"pointer" }}>Effacer</button>}
          </div>
          {filtered.map((e, i) => (
            <div key={i} style={{ background:white, borderRadius:13, padding:"12px 13px", marginBottom:8, boxShadow:"0 1px 8px rgba(0,0,0,0.05)", border:`1px solid ${border}` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                <div style={{ flex:1 }}>
                  {editingId === e.id ? (
                    <input autoFocus defaultValue={e.name} onBlur={ev => { renameEx(e.id, ev.target.value); setEditingId(null); }} onKeyDown={ev => { if (ev.key === "Enter") { renameEx(e.id, ev.target.value); setEditingId(null); } }} style={{ fontWeight:700, fontSize:14, color:textCol, border:`1.5px solid ${orange}`, borderRadius:8, padding:"3px 8px", outline:"none", fontFamily:"inherit", width:"90%" }} />
                  ) : (
                    <div style={{ fontWeight:700, color:textCol, fontSize:14, marginBottom:4 }}>{e.name}</div>
                  )}
                  <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                    {e.cats.map(c => <Badge key={c} label={c} bg={`${orange}12`} color={orange} />)}
                  </div>
                </div>
                <button onClick={() => setEditingId(editingId === e.id ? null : e.id)} style={{ background:`${orange}15`, border:"none", borderRadius:8, padding:"6px 7px", cursor:"pointer", flexShrink:0, marginLeft:8 }}>
                  <Icon name="edit" size={14} color={orange} />
                </button>
              </div>
            </div>
          ))}
          <button onClick={() => setShowAddModal(true)} style={{ width:"100%", padding:"13px", background:"transparent", border:`2px dashed ${orange}`, borderRadius:14, color:orange, fontWeight:700, fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8, marginTop:4 }}>
            <Icon name="plus" size={18} color={orange} />Ajouter un exercice
          </button>
        </div>
      )}

      {/* TESTS ACL */}
      {tab === "tests" && (
        <div style={{ padding:14 }}>
          <div style={{ background:"#eff6ff", border:"1.5px solid #bfdbfe", borderRadius:11, padding:"10px 13px", marginBottom:14, display:"flex", gap:8, alignItems:"center" }}>
            <Icon name="info" size={15} color="#2563eb" />
            <span style={{ color:"#1d4ed8", fontSize:12, fontWeight:600 }}>Critères evidence-based pour Campus LCA (Aspetar CPG 2023 · MOON · MGH 2024)</span>
          </div>
          {TESTS_LIB.map((t, i) => {
            const isOpen = selTest === t.id;
            const cc     = catColors[t.cat] || orange;
            return (
              <div key={i} style={{ background:white, borderRadius:13, marginBottom:8, boxShadow:"0 1px 8px rgba(0,0,0,0.05)", border:`1px solid ${border}`, overflow:"hidden" }}>
                <button onClick={() => setSelTest(isOpen ? null : t.id)} style={{ width:"100%", padding:"12px 13px", border:"none", background:"none", cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", gap:12 }}>
                  <div style={{ width:38, height:38, borderRadius:10, background:`${cc}15`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <Icon name="stat" size={18} color={cc} />
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, color:textCol, fontSize:13 }}>{t.name}</div>
                    <div style={{ display:"flex", gap:6, marginTop:3 }}>
                      <Badge label={t.cat} bg={`${cc}15`} color={cc} />
                      <Badge label={t.freq} bg="#f1f5f9" color={muted} />
                    </div>
                  </div>
                  <Icon name={isOpen ? "arrowL" : "arrow"} size={16} color={muted} />
                </button>
                {isOpen && (
                  <div style={{ padding:"0 13px 13px", borderTop:`1px solid ${border}` }}>
                    <p style={{ color:muted, fontSize:13, lineHeight:1.5, margin:"10px 0 8px" }}>{t.desc}</p>
                    <div style={{ background:`${cc}10`, border:`1.5px solid ${cc}30`, borderRadius:8, padding:"8px 12px", display:"flex", alignItems:"center", gap:8 }}>
                      <Icon name="flag" size={14} color={cc} />
                      <span style={{ fontSize:12, fontWeight:700, color:cc }}>Seuil RTS : {t.seuil}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* QUESTIONNAIRES */}
      {tab === "questionnaires" && (
        <div style={{ padding:14 }}>
          <h3 style={{ fontSize:12, fontWeight:700, color:muted, letterSpacing:"0.08em", textTransform:"uppercase", margin:"0 0 8px" }}>Questionnaires cliniques — remplir pour un patient</h3>
          <div style={{ fontSize:12, color:muted, marginBottom:10, lineHeight:1.5 }}>Le score est calculé et s'inscrit automatiquement dans le critère correspondant du scoring de phases (et du MRSS / gate RTR).</div>
          {openQ ? <QuestionnaireFiller key={openQ} q={CLINICAL_QUESTIONNAIRES.find(x => x.id === openQ)} patients={patients} onClose={() => setOpenQ(null)} />
            : CLINICAL_QUESTIONNAIRES.map(q => (
              <button key={q.id} onClick={() => setOpenQ(q.id)} style={{ width:"100%", textAlign:"left", background:white, borderRadius:13, marginBottom:8, padding:"12px 13px", border:`1px solid ${border}`, boxShadow:"0 1px 8px rgba(0,0,0,0.05)", cursor:"pointer", display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ width:38, height:38, borderRadius:10, background:`${orange}15`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}><Icon name="form" size={18} color={orange} /></div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, color:textCol, fontSize:13 }}>{q.name}</div>
                  <div style={{ fontSize:11, color:muted, marginTop:2 }}>{q.sub}</div>
                  <div style={{ fontSize:10.5, color:orange, marginTop:3 }}>→ {q.targets.map(t => t.label).join(" · ")}</div>
                </div>
                <Icon name="arrow" size={16} color={muted} />
              </button>
            ))}
          <h3 style={{ fontSize:12, fontWeight:700, color:muted, letterSpacing:"0.08em", textTransform:"uppercase", margin:"16px 0 8px" }}>Questionnaires de parcours (formulaires externes)</h3>
          <div style={{ background:"#f0fdf4", border:"1.5px solid #86efac", borderRadius:11, padding:"10px 13px", marginBottom:14, display:"flex", gap:8, alignItems:"center" }}>
            <Icon name="check" size={15} color="#16a34a" />
            <span style={{ color:"#15803d", fontSize:12, fontWeight:600 }}>{QUESTIONNAIRES_LIB.length} questionnaires actifs dans votre parcours Campus LCA</span>
          </div>
          {QUESTIONNAIRES_LIB.map((q, i) => {
            const isOpen = selQ === q.id;
            const cc     = qCatColors[q.cat] || orange;
            return (
              <div key={i} style={{ background:white, borderRadius:13, marginBottom:8, boxShadow:"0 1px 8px rgba(0,0,0,0.05)", border:`1px solid ${border}`, overflow:"hidden" }}>
                <button onClick={() => setSelQ(isOpen ? null : q.id)} style={{ width:"100%", padding:"12px 13px", border:"none", background:"none", cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", gap:12 }}>
                  <div style={{ width:38, height:38, borderRadius:10, background:`${cc}15`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <Icon name="form" size={18} color={cc} />
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, color:textCol, fontSize:13 }}>{q.name}</div>
                    <div style={{ display:"flex", gap:6, marginTop:3 }}>
                      <Badge label={q.cat} bg={`${cc}15`} color={cc} />
                      <Badge label={q.timing} bg="#f1f5f9" color={muted} />
                    </div>
                  </div>
                  <Icon name={isOpen ? "arrowL" : "arrow"} size={16} color={muted} />
                </button>
                {isOpen && (
                  <div style={{ padding:"0 13px 13px", borderTop:`1px solid ${border}` }}>
                    <p style={{ color:muted, fontSize:13, lineHeight:1.5, margin:"10px 0 10px" }}>{q.desc}</p>
                    <button style={{ width:"100%", padding:"10px", background:`${cc}15`, border:`1.5px solid ${cc}30`, borderRadius:9, color:cc, fontWeight:700, fontSize:13, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                      <Icon name="link" size={14} color={cc} />
                      Ouvrir / Copier le lien Google Form
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          <button style={{ width:"100%", padding:"13px", background:"transparent", border:`2px dashed ${success}`, borderRadius:14, color:success, fontWeight:700, fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8, marginTop:4 }}>
            <Icon name="plus" size={18} color={success} />Ajouter un questionnaire
          </button>
        </div>
      )}

      {/* Add Exercise Modal */}
      {showAddModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:200, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
          <div style={{ background:white, borderRadius:"20px 20px 0 0", padding:"22px 18px 36px", width:"100%", maxWidth:430, maxHeight:"90vh", overflowY:"auto" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <h3 style={{ margin:0, color:textCol, fontWeight:800 }}>Nouvel exercice</h3>
              <button onClick={() => setShowAddModal(false)} style={{ background:"none", border:"none", cursor:"pointer" }}><Icon name="close" size={22} color={muted} /></button>
            </div>
            {[{label:"Nom de l'exercice",key:"name",ph:"ex: Split squat bulgare"},{label:"ID vidéo YouTube",key:"ytId",ph:"ex: dQw4w9WgXcQ"},{label:"Description",key:"desc",ph:"Consigne pour le patient..."}].map(f => (
              <div key={f.key} style={{ marginBottom:12 }}>
                <label style={{ fontSize:11, fontWeight:700, color:muted, textTransform:"uppercase", letterSpacing:"0.06em", display:"block", marginBottom:5 }}>{f.label}</label>
                <input value={newEx[f.key]} onChange={e => setNewEx(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.ph} style={{ width:"100%", padding:"10px 12px", border:`1.5px solid ${border}`, borderRadius:10, fontSize:14, outline:"none", boxSizing:"border-box", fontFamily:"inherit", color:textCol }} />
              </div>
            ))}
            <label style={{ fontSize:11, fontWeight:700, color:muted, textTransform:"uppercase", letterSpacing:"0.06em", display:"block", marginBottom:8 }}>Catégories</label>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:16 }}>
              {ALL_CATS.map(c => (
                <button key={c} onClick={() => toggleNewCat(c)} style={{ padding:"4px 10px", borderRadius:20, border:newEx.cats.includes(c) ? `1.5px solid ${orange}` : `1.5px solid ${border}`, background:newEx.cats.includes(c) ? orange : "transparent", color:newEx.cats.includes(c) ? white : muted, fontSize:11, fontWeight:700, cursor:"pointer" }}>{c}</button>
              ))}
            </div>
            <button onClick={addEx} style={{ width:"100%", padding:"13px", background:"linear-gradient(135deg,#f97316,#ea580c)", color:white, border:"none", borderRadius:12, fontWeight:800, fontSize:15, cursor:"pointer" }}>Ajouter à la bibliothèque</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── PATIENT PREVIEW ──────────────────────────────────────
function PatientPreview({ patient, exlib, progs, onBack }) {
  const [screen,       setScreen]       = useState("home");
  const [sessions,     setSessions]     = useState(INIT_SESSIONS);
  const [sessionState, setSessionState] = useState(null);
  const fakeUser = { name:patient.name.split(" ")[0], email:patient.email };

  function startSession(s)  { setSessionState({ sessionId:s.id, progId:s.progId, startedAt:Date.now() }); setScreen("session-active"); }
  function finishSession(data)  { setSessionState(null); setScreen("programme"); }

  function renderScreen() {
    if (screen === "session-active" && sessionState) {
      const prog = progs.find(p => p.id === sessionState.progId);
      if (!prog) return null;
      return <SessionActive prog={prog} exlib={exlib} startedAt={sessionState.startedAt} onBack={() => { setSessionState(null); setScreen("programme"); }} onFinish={finishSession} />;
    }
    switch (screen) {
      case "home":       return <DashboardPatient user={fakeUser} onNav={setScreen} extraTop={BANNER_H} />;
      case "programme":  return <ProgrammesPatient exlib={exlib} progs={progs} sessions={sessions} onStartSession={startSession} extraTop={BANNER_H} />;
      case "calendrier": return <CalendrierPatient sessions={sessions} setSessions={setSessions} progs={progs} extraTop={BANNER_H} />;
      case "documents":  return <DocumentsScreen extraTop={BANNER_H} />;
      case "profil":     return <ProfilScreen user={fakeUser} onLogout={null} extraTop={BANNER_H} />;
      default:           return <DashboardPatient user={fakeUser} onNav={setScreen} extraTop={BANNER_H} />;
    }
  }

  return (
    <>
      <PreviewBanner patientName={patient.name} onBack={onBack} />
      {renderScreen()}
      {screen !== "session-active" && <PatientNav active={screen} onNav={setScreen} />}
    </>
  );
}

// ── MAIN APP ─────────────────────────────────────────────
export default function App() {
  const [user,         setUser]         = useState(null);
  const [screen,       setScreen]       = useState("home");
  const [sessions,     setSessions]     = useState(INIT_SESSIONS);
  const [adminTab,       setAdminTab]       = useState("dashboard");
  const [adminSubscreen, setAdminSubscreen] = useState(null);
  const [preview,        setPreview]        = useState(null);
  const [selPatient,     setSelPatient]     = useState(null);
  const [patients,       setPatients]       = useState([]);
  const [dbStatus,       setDbStatus]       = useState("loading"); // loading | ready | error
  const [dbError,        setDbError]        = useState(null);
  const [sessionLoadView, setSessionLoadView] = useState(null);
  const [scoringView,     setScoringView]     = useState(null); // { id, tr?, tab? }
  const [dashboard,       setDashboard]       = useState({});   // v_dashboard par patient_id
  const [exlib,        setExlib]        = useState(INIT_EXLIB);
  const [progs,        setProgs]        = useState(INIT_PROGS);
  const [sessionState, setSessionState] = useState(null);
  // Retour depuis le lien "réinitialiser le mot de passe" (fragment #access_token…&type=recovery)
  const [recovery,     setRecovery]     = useState(() => readRecoveryFromUrl());


  // Chargement des patients depuis Supabase — déclenché après connexion admin
  // (nécessite un token Supabase Auth valide désormais que la RLS restreint l'accès anon)
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    let cancelled = false;
    setDbStatus("loading");
    sbFetchPatients()
      .then(list => {
        if (cancelled) return;
        setPatients(list);
        setDbStatus("ready");
      })
      .catch(err => {
        if (cancelled) return;
        console.error("Erreur chargement patients Supabase:", err);
        setDbError(err.message || "Connexion impossible");
        setDbStatus("error");
        setPatients(INIT_PATIENTS); // fallback local en cas d'erreur réseau
      });
    return () => { cancelled = true; };
  }, [user]);

  // Dashboard dérivé (v_dashboard) : dernière session de tests + dernière décision.
  // Rechargé après chaque enregistrement de mesure → plus aucun champ orphelin à maintenir.
  const refreshDashboard = useCallback(() => {
    lcaApi.fetchDashboard()
      .then(rows => setDashboard(Object.fromEntries((rows || []).map(r => [r.patient_id, r]))))
      .catch(err => console.error("Erreur chargement v_dashboard:", err));
  }, []);
  useEffect(() => {
    if (user && user.role === "admin" && dbStatus === "ready") refreshDashboard();
  }, [user, dbStatus, refreshDashboard]);
  const patientsView = useMemo(
    () => patients.map(p => ({ ...p, ...dashRowToFields(dashboard[p.id]) })),
    [patients, dashboard]
  );

  // Helper : met à jour un patient en mémoire ET dans Supabase
  const updatePatient = useCallback((updated) => {
    setPatients(prev => prev.map(p => p.id === updated.id ? updated : p));
    sbUpdatePatient(updated.id, updated).catch(err => {
      console.error("Erreur sauvegarde Supabase:", err);
    });
  }, []);

  // Helper : crée un nouveau patient dans Supabase, puis l'ajoute à la liste locale.
  // Retourne une Promise pour que l'appelant (formulaire) puisse gérer chargement/erreur.
  const addPatient = useCallback((data) => {
    const base = {
      name: data.name, sport: data.sport || "", dateOp: data.dateOp || "",
      greffon: data.greffon || "", cote: data.cote || "", phaseId: data.phaseId || 1,
      sessions: 0, total: data.total || 6, goalShort: "", goalMid: "", goalLong: "",
      sessionHistory: [], rtrProfile: null,
      authUserId: data.authUserId || null, patientEmail: data.patientEmail || null,
      status: "active",
    };
    return sbInsertPatient(base).then(inserted => {
      setPatients(prev => [...prev, inserted]);
      return inserted;
    });
  }, []);

  // Suppression définitive (RPC serveur) puis retrait de la liste locale
  const deletePatient = useCallback(async (p) => {
    await sbAdminDeletePatient(p.id);
    setPatients(prev => prev.filter(x => x.id !== p.id));
    setSelPatient(null);
  }, []);

  // Le bouton de déconnexion est masqué pendant une séance active (perte de données)
  const hideLogoutFab = user && user.role !== "admin" && screen === "session-active" && sessionState;

  function wrap(children, opts = {}) {
    return (
      <div style={{ fontFamily:"'Segoe UI','Helvetica Neue',sans-serif", maxWidth:430, margin:"0 auto", minHeight:"100vh", background:bg, position:"relative" }}>
        {children}
        {user && !hideLogoutFab && <LogoutFab onLogout={handleLogout} top={opts.fabTop ?? 12} />}
      </div>
    );
  }

  function handleLogin(u)  { setUser(u); setScreen("home"); setAdminTab("dashboard"); }
  function handleLogout()  { sbSetAccessToken(null); setUser(null); setScreen("home"); setPreview(null); setSelPatient(null); setAdminSubscreen(null); }
  // Renouvellement du jeton impossible (refresh token expiré / révoqué) → retour à l'écran de connexion
  useEffect(() => {
    sbSetOnSessionExpired(() => {
      handleLogout();
      try { window.alert("Session expirée — reconnecte-toi pour continuer."); } catch (e) {}
    });
    return () => sbSetOnSessionExpired(null);
  }, []);

  if (recovery) return wrap(<ResetPasswordScreen recovery={recovery} onFinish={() => { clearUrlHash(); setRecovery(null); handleLogout(); }} />);
  if (!user) return wrap(<LoginScreen onLogin={handleLogin} />);

  // Écran de chargement / erreur base de données (uniquement pour l'admin, qui a besoin des patients)
  if (user.role === "admin" && dbStatus === "loading") {
    return wrap(
      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"100vh", gap:14, padding:20, textAlign:"center" }}>
        <div style={{ width:36, height:36, border:`3px solid ${border}`, borderTopColor:navy, borderRadius:"50%", animation:"spin 0.8s linear infinite" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ fontSize:14, color:muted }}>Connexion à la base de données...</div>
      </div>
    );
  }

  if (user.role === "admin" && dbStatus === "error") {
    return wrap(
      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"100vh", gap:14, padding:24, textAlign:"center" }}>
        <div style={{ fontSize:32 }}>⚠️</div>
        <div style={{ fontSize:15, fontWeight:700, color:textCol }}>Connexion à la base de données impossible</div>
        <div style={{ fontSize:12, color:muted, maxWidth:280 }}>{dbError}</div>
        <div style={{ fontSize:11, color:muted, maxWidth:280 }}>Les données affichées sont temporaires (démo) et ne seront pas sauvegardées tant que la connexion n'est pas rétablie.</div>
        <button onClick={() => { setDbStatus("loading"); sbFetchPatients().then(l => { setPatients(l); setDbStatus("ready"); }).catch(e => { setDbError(e.message); setDbStatus("error"); }); }}
          style={{ marginTop:8, padding:"9px 20px", background:navy, color:white, border:"none", borderRadius:8, fontSize:13, fontWeight:600, cursor:"pointer" }}>
          Réessayer
        </button>
      </div>
    );
  }



  // Admin: preview mode
  if (user.role === "admin" && preview) {
    return wrap(<PatientPreview patient={preview} exlib={exlib} progs={progs} onBack={() => setPreview(null)} />, { fabTop: BANNER_H + 10 });
  }

  // Admin: session load view
  if (user.role === "admin" && sessionLoadView) {
    return wrap(
      <>
        <SessionLoadAdmin patient={sessionLoadView} onBack={() => setSessionLoadView(null)} onUpdatePatient={updated => { updatePatient(updated); setSessionLoadView(updated); }} />
        <AdminNav active={adminTab} onNav={tab => { setSessionLoadView(null); setAdminTab(tab); }} />
      </>
    );
  }

  // Admin: scoring de phases (MRSS 2.0 et gate RTR = étapes finales du même écran)
  if (user.role === "admin" && scoringView) {
    const sp = patientsView.find(p => p.id === scoringView.id);
    if (sp) return wrap(
      <>
        <ScoringAdmin patient={sp} initTr={scoringView.tr} initTab={scoringView.tab} onBack={() => { setScoringView(null); setSelPatient(sp); }}
          onPatientUpdate={updatePatient} onDataChanged={refreshDashboard} />
        <AdminNav active={adminTab} onNav={tab => { setScoringView(null); setAdminTab(tab); }} />
      </>
    );
  }

  // Admin: fiche patient
  if (user.role === "admin" && selPatient) {
    return wrap(
      <>
        <FichePatient patient={patientsView.find(p => p.id === selPatient.id) || selPatient} progs={progs} onPreview={p => { setSelPatient(null); setPreview(p); }} onBack={() => setSelPatient(null)} onSessionLoad={p => { setSelPatient(null); setSessionLoadView(p); }} onScoring={(p, tr, tab) => { setSelPatient(null); setScoringView({ id:p.id, tr, tab }); }} onUpdate={updatePatient} onDelete={deletePatient} />
        <AdminNav active={adminTab} onNav={tab => { setSelPatient(null); setAdminTab(tab); }} />
      </>
    );
  }

  // Admin: normal
  if (user.role === "admin") {
    function handleAdminNav(dest) {
      if (["dashboard","patients","programmes","biblio","settings"].includes(dest)) {
        setAdminTab(dest);
        setAdminSubscreen(null);
      } else {
        setAdminSubscreen(dest);
      }
    }

    if (adminSubscreen === "calendrier-semaine") {
      return wrap(<><CalendrierAdminSemaine onBack={() => setAdminSubscreen(null)} /><AdminNav active={adminTab} onNav={handleAdminNav} /></>);
    }
    if (adminSubscreen === "taux-completion") {
      return wrap(<><TauxCompletion onBack={() => setAdminSubscreen(null)} /><AdminNav active={adminTab} onNav={handleAdminNav} /></>);
    }

    let adminContent;
    switch (adminTab) {
      case "patients":   adminContent = <VuePatientScreen patients={patientsView} progs={progs} onPreview={setPreview} onSelectPatient={setSelPatient} onAddPatient={addPatient} />; break;
      case "programmes": adminContent = <ProgrammesAdmin progs={progs} exlib={exlib} onUpdate={setProgs} />; break;
      case "biblio":     adminContent = <BiblioScreen exlib={exlib} onExlibUpdate={setExlib} patients={patientsView} />; break;
      default:           adminContent = <AdminDashboard user={user} patients={patientsView} onLogout={handleLogout} onNav={handleAdminNav} onSelectPatient={setSelPatient} />;
    }
    return wrap(<>{adminContent}<AdminNav active={adminTab} onNav={handleAdminNav} /></>);
  }

  // Patient
  function startSession(s) { setSessionState({ sessionId:s.id, progId:s.progId, startedAt:Date.now() }); setScreen("session-active"); }
  function finishSession(data)  { setSessionState(null); setScreen("programme"); }

  let patientContent;
  if (screen === "session-active" && sessionState) {
    const prog = progs.find(p => p.id === sessionState.progId);
    patientContent = prog
      ? <SessionActive prog={prog} exlib={exlib} startedAt={sessionState.startedAt} onBack={() => { setSessionState(null); setScreen("programme"); }} onFinish={finishSession} />
      : null;
  } else {
    switch (screen) {
      case "programme":  patientContent = <ProgrammesPatient exlib={exlib} progs={progs} sessions={sessions} onStartSession={startSession} />; break;
      case "calendrier": patientContent = <CalendrierPatient sessions={sessions} setSessions={setSessions} progs={progs} />; break;
      case "documents":  patientContent = <DocumentsScreen />; break;
      case "profil":     patientContent = <ProfilScreen user={user} onLogout={handleLogout} />; break;
      default:           patientContent = <DashboardPatient user={user} onNav={setScreen} />;
    }
  }

  return wrap(
    <>
      {patientContent}
      {screen !== "session-active" && <PatientNav active={screen} onNav={setScreen} />}
    </>
  );
}


// Exposé pour le test de rendu du scoring (src/scoring.smoke.test.js)
export { ScoringAdmin, CredentialsModal, buildWelcomeMessage };
// Exposé pour les tests uniquement (session Supabase)
export const __test__ = { sbSetSession, sbSetAccessToken, sbFetch, QuestionnaireFiller, CLINICAL_QUESTIONNAIRES };
